import {
  ArrowRight,
  BadgeCheck,
  Calculator,
  Check,
  ChevronDown,
  Clock3,
  Download,
  FileText,
  Home,
  LockKeyhole,
  Mail,
  Menu,
  Moon,
  Phone,
  RefreshCw,
  Search,
  Settings,
  ShieldCheck,
  Sun,
  X
} from "lucide-react";
import { FormEvent, useEffect, useRef, useState } from "react";
import contactImage from "../assets/visual-advisor-contact.jpg";
import offerImage from "../assets/visual-advisor-map.jpg";
import aboutPortraitImage from "../assets/about-martyna-mostek.jpg";
import educationImage from "../assets/visual-family-protection.jpg";
import heroImage from "../assets/visual-hero-family.jpg";
import addonsImage from "../assets/visual-health-umbrella.jpg";
import guideAddonsImage from "../assets/guide-addons-health.jpg";
import guideProcessImage from "../assets/guide-buying-process.jpg";
import guideChooseImage from "../assets/guide-choose-policy.jpg";
import guideProtectionImage from "../assets/guide-protection-works.jpg";
import guideRetirementImage from "../assets/guide-retirement-savings.jpg";
import { CalculatorHub } from "./calculators/CalculatorHub";
import { ProductCalculator, type LeadContext } from "./calculators/ProductCalculator";
import { LEAD_PRODUCT_OPTIONS, PRODUCTS } from "./calculators/products";
import type { CalcInputs, ProductFaq, ProductId } from "./calculators/types";
import AdminApp from "./admin/AdminApp";
import { AdvisorCard } from "./AdvisorCard";
import ChatWidget from "./ChatWidget";
import { getAttributionSource, syncTrackingIntegrations, trackEvent } from "./analytics";
import {
  COMPANY,
  CONTACT_CONSENT_TEXT,
  COOKIE_STORAGE_KEY,
  formatPhoneInput,
  hasMinimumPhoneDigits,
  LEGAL_UPDATED_AT,
  PRIVACY_CONSENT_TEXT,
  PRIVACY_VERSION,
  submitLead
} from "./leadCapture";

type GuideRoute =
  | "guideAfter50"
  | "guideChoose"
  | "guideProtection"
  | "guideMortgage"
  | "guideAddons"
  | "guideRetirement"
  | "guideProcess"
  | "guideCost"
  | "guide500k"
  | "guideSum"
  | "guideNoExam"
  | "guideSmokers"
  | "guideBankPolicy"
  | "guideExclusions"
  | "guideAfter60";
type StaticRoute =
  | "home"
  | "pricing"
  | "life"
  | "addons"
  | GuideRoute
  | "guideHub"
  | "faq"
  | "contact"
  | "about"
  | "privacy"
  | "cookies"
  | "terms"
  | "admin";
export type RouteKey = StaticRoute | ProductId;
type FontScale = "normal" | "large" | "xlarge";
type PageMeta = { title: string; description: string; canonical: string };
type LegalPageKind = "privacy" | "cookies" | "terms";

type LeadForm = {
  name: string;
  phone: string;
  email: string;
  city: string;
  product: string;
  contactTime: string;
  message: string;
  contactConsent: boolean;
  privacyConsent: boolean;
  marketingConsent: boolean;
  website: string;
};

const initialForm: LeadForm = {
  name: "",
  phone: "",
  email: "",
  city: "",
  product: "life",
  contactTime: "dowolnie",
  message: "",
  contactConsent: false,
  privacyConsent: false,
  marketingConsent: false,
  website: ""
};

function trackPhoneClick(source: string) {
  trackEvent("telefon_click", { source });
}

const staticMeta: Record<StaticRoute, PageMeta> = {
  home: {
    title: "Kalkulator ubezpieczenia na życie i zdrowie | Ochro.pl",
    description:
      "Kalkulatory i cennik ubezpieczeń na życie 2026: ochrona rodziny, polisa pod kredyt, życie i dożycie, zdrowie oraz FAQ.",
    canonical: "https://ochro.pl/"
  },
  pricing: {
    title: "Ubezpieczenie na życie cennik 2026 - przykładowe ceny polis | Ochro.pl",
    description:
      "Cennik ubezpieczenia na życie 2026: przykładowe składki, kalkulator ceny, czynniki wpływające na koszt, dodatki i FAQ dla klientów.",
    canonical: "https://ochro.pl/ubezpieczenie-na-zycie-cennik/"
  },
  life: {
    title: "Ubezpieczenie na życie 2026 - produkty, kalkulator i FAQ | Ochro.pl",
    description:
      "Ubezpieczenie na życie 2026: rodzaje polis, zakresy ochrony, dodatki, cennik, kalkulator składki, FAQ i praktyczny przewodnik dla klientów.",
    canonical: "https://ochro.pl/ubezpieczenie-na-zycie/"
  },
  addons: {
    title: "Umowy dodatkowe do ubezpieczenia na życie | Ochro.pl",
    description:
      "Umowy dodatkowe do polisy na życie: ciężkie choroby, szpital, operacje, NNW, uszczerbek, złamania, leczenie za granicą, partner i dzieci.",
    canonical: "https://ochro.pl/umowy-dodatkowe/"
  },
  guideChoose: {
    title: "Jak wybrać ubezpieczenie na życie i nie przepłacić? | Ochro.pl",
    description:
      "Poradnik: jak wybrać polisę na życie, dobrać sumę, nie przepłacić za dodatki i poprosić doradcę o 3 warianty ochrony.",
    canonical: "https://ochro.pl/poradnik/jak-wybrac-polise/"
  },
  guideAfter50: {
    title: "Polisa po 50-tce - jak dobrać ochronę? | Ochro.pl",
    description:
      "Poradnik Polisa po 50-tce: jak porównać polisę terminową, dożywotnią i dodatki, co wpływa na składkę oraz jak poprosić o 3 warianty ochrony.",
    canonical: "https://ochro.pl/poradnik/ubezpieczenie-na-zycie-po-50/"
  },
  guideProtection: {
    title: "Jak działa ochrona w ubezpieczeniu na życie? | Ochro.pl",
    description:
      "Wyjaśniamy, jak działa polisa na życie: suma ubezpieczenia, uposażeni, wypłata świadczenia, ocena ryzyka, karencje, wyłączenia i OWU.",
    canonical: "https://ochro.pl/poradnik/jak-dziala-ochrona/"
  },
  guideMortgage: {
    title: "Ubezpieczenie na życie pod kredyt hipoteczny - poradnik | Ochro.pl",
    description:
      "Polisa na życie pod kredyt hipoteczny: czy musi być z banku, jak działa cesja, jak dobrać sumę i okres oraz kiedy sprawdzić polisę zewnętrzną.",
    canonical: "https://ochro.pl/poradnik/ubezpieczenie-na-zycie-pod-kredyt-hipoteczny/"
  },
  guideAddons: {
    title: "Ubezpieczenia dodatkowe do polisy na życie - poradnik | Ochro.pl",
    description:
      "Praktyczny poradnik o dodatkach: ciężkie choroby, szpital, operacje, NNW, uszczerbek, leczenie za granicą, partner i dzieci.",
    canonical: "https://ochro.pl/poradnik/ubezpieczenia-dodatkowe/"
  },
  guideRetirement: {
    title: "Jak oszczędzać na emeryturę? Polisa, kapitał i ochrona | Ochro.pl",
    description:
      "Poradnik o oszczędzaniu na emeryturę: ile odkładać, kiedy zacząć, jak działa polisa na życie i dożycie, premie, gwarancje, wykup i błędy klientów.",
    canonical: "https://ochro.pl/poradnik/oszczedzanie-na-emeryture/"
  },
  guideProcess: {
    title: "Proces zakupu polisy na życie - od kalkulatora do umowy | Ochro.pl",
    description:
      "Zobacz, jak wygląda zakup ubezpieczenia na życie: kalkulator, analiza potrzeb, ankieta medyczna, ocena ryzyka, decyzja i dokument polisy.",
    canonical: "https://ochro.pl/poradnik/proces-zakupu-polisy/"
  },
  guideCost: {
    title: "Ile kosztuje ubezpieczenie na życie w 2026? Przykładowe składki | Ochro.pl",
    description:
      "Ile kosztuje ubezpieczenie na życie w 2026 roku? Przykładowe składki za 100-500 tys. zł i 1 mln zł, wpływ wieku, palenia, zakresu i okresu oraz dane z realnych kalkulacji.",
    canonical: "https://ochro.pl/poradnik/ile-kosztuje-ubezpieczenie-na-zycie/"
  },
  guide500k: {
    title: "Ubezpieczenie na życie 500 tys. zł - cena i składki | Ochro.pl",
    description:
      "Ile kosztuje ubezpieczenie na życie na 500 tys. zł? Składki według wieku, porównanie z sumą 300 tys. i 1 mln, wariant pod kredyt oraz formalności przy tej sumie.",
    canonical: "https://ochro.pl/poradnik/ubezpieczenie-na-zycie-500-tys-cena/"
  },
  guideAfter60: {
    title: "Ubezpieczenie na życie po 60-tce - ile kosztuje | Ochro.pl",
    description:
      "Ubezpieczenie na życie po 60. roku życia: do jakiego wieku można je kupić, ile kosztuje polisa terminowa i dożywotnia, jakie dodatki są jeszcze dostępne.",
    canonical: "https://ochro.pl/poradnik/ubezpieczenie-na-zycie-po-60/"
  },
  guideExclusions: {
    title: "Karencja i wyłączenia w polisie na życie - co oznaczają | Ochro.pl",
    description:
      "Karencja i wyłączenia w ubezpieczeniu na życie: ile trwają okresy wyczekiwania, czego polisa nie obejmuje, jak czytać OWU i ile kosztuje szerszy zakres ochrony.",
    canonical: "https://ochro.pl/poradnik/karencja-wylaczenia-polisa-na-zycie/"
  },
  guideBankPolicy: {
    title: "Polisa do kredytu z banku czy własna - co wybrać | Ochro.pl",
    description:
      "Polisa do kredytu hipotecznego z banku czy własna: zakaz sprzedaży wiązanej, cesja, przykładowe składki i jak zmienić polisę w trakcie spłaty kredytu.",
    canonical: "https://ochro.pl/poradnik/polisa-z-banku-czy-wlasna/"
  },
  guideSmokers: {
    title: "Ubezpieczenie na życie dla palących - o ile drożej | Ochro.pl",
    description:
      "Ubezpieczenie na życie dla palących: o ile wyższa jest składka, przykłady dla wieku 25-55 lat, e-papierosy w ankiecie i powrót do stawek dla niepalących.",
    canonical: "https://ochro.pl/poradnik/polisa-na-zycie-dla-palacych/"
  },
  guideNoExam: {
    title: "Ubezpieczenie na życie bez badań - progi i ankieta | Ochro.pl",
    description:
      "Ubezpieczenie na życie bez badań: kiedy wystarczy ankieta medyczna, od jakiej sumy PZU kieruje na badania, ile kosztuje taka polisa i czym grozi zatajenie.",
    canonical: "https://ochro.pl/poradnik/ubezpieczenie-na-zycie-bez-badan/"
  },
  guideSum: {
    title: "Jak dobrać sumę ubezpieczenia na życie - przepis | Ochro.pl",
    description:
      "Jak dobrać sumę ubezpieczenia na życie? Przepis: saldo zobowiązań, 2-3 roczne dochody i koszty dzieci. Trzy policzone przykłady ze składkami oraz limity sumy w PZU.",
    canonical: "https://ochro.pl/poradnik/jak-dobrac-sume-ubezpieczenia-na-zycie/"
  },
  guideHub: {
    title: "Poradnik ubezpieczeń na życie - wszystkie artykuły | Ochro.pl",
    description:
      "Poradnik Ochro.pl: ceny i składki, wybór sumy i zakresu, polisa pod kredyt, dodatki, emerytura i proces zakupu - praktyczne artykuły doradcy ubezpieczeniowego.",
    canonical: "https://ochro.pl/poradnik/"
  },
  faq: {
    title: "FAQ ubezpieczenia na życie i kalkulatory | Ochro.pl",
    description:
      "Najczęstsze i zaawansowane pytania o kalkulatory, składki, ankietę medyczną, cesję polisy, dodatki, OWU i kontakt z doradcą.",
    canonical: "https://ochro.pl/faq/"
  },
  contact: {
    title: "Kontakt z doradcą ubezpieczeniowym | Ochro.pl",
    description:
      "Skontaktuj się z doradcą Ochro.pl, poproś o dokładniejszą kalkulację ubezpieczenia i umów rozmowę o ochronie.",
    canonical: "https://ochro.pl/kontakt/"
  },
  about: {
    title: "O nas - doradca ubezpieczeniowy i dane firmy | Ochro.pl",
    description:
      "Poznaj Ochro.pl: serwis doradcy ubezpieczeniowego Martyny Mostek. Dane firmy, NIP, REGON, kontakt i zasady działania kalkulatorów.",
    canonical: "https://ochro.pl/o-nas/"
  },
  privacy: {
    title: "Polityka prywatności | Ochro.pl",
    description:
      "Informacja o przetwarzaniu danych osobowych przez Doradcę Ubezpieczeniowego MARTYNĘ MOSTEK, NIP: 5732953601, w serwisie Ochro.pl.",
    canonical: "https://ochro.pl/polityka-prywatnosci/"
  },
  cookies: {
    title: "Polityka cookies | Ochro.pl",
    description: "Zasady korzystania z cookies, localStorage i podobnych technologii w serwisie Ochro.pl.",
    canonical: "https://ochro.pl/polityka-cookies/"
  },
  terms: {
    title: "Regulamin serwisu | Ochro.pl",
    description:
      "Regulamin korzystania z serwisu Ochro.pl, kalkulatorów ubezpieczeniowych i formularza kontaktowego.",
    canonical: "https://ochro.pl/regulamin/"
  },
  admin: {
    title: "Panel administracyjny | Ochro.pl",
    description: "Chroniony panel administracyjny do obsługi zgłoszeń z formularza Ochro.pl.",
    canonical: "https://ochro.pl/admin/"
  }
};

function isStaticRoute(route: RouteKey): route is StaticRoute {
  return route in staticMeta;
}

function isPaidLandingVisit() {
  if (typeof window === "undefined") return false;

  const url = new URL(window.location.href);
  const medium = (url.searchParams.get("utm_medium") || "").toLowerCase();
  const source = (url.searchParams.get("utm_source") || "").toLowerCase();

  return Boolean(
    url.searchParams.get("gclid") ||
      url.searchParams.get("gbraid") ||
      url.searchParams.get("wbraid") ||
      medium === "cpc" ||
      medium === "ppc" ||
      medium === "paid" ||
      medium === "paid_social" ||
      (source === "google" && medium.includes("cpc")) ||
      (["facebook", "fb", "meta", "instagram"].includes(source) && medium.includes("paid"))
  );
}

export function getRouteMeta(route: RouteKey): PageMeta {
  if (isStaticRoute(route)) {
    return staticMeta[route];
  }
  const product = PRODUCTS[route];
  return {
    title: product.metaTitle,
    description: product.metaDescription,
    canonical: `https://ochro.pl/${product.slug}/`
  };
}

function calcOchronneMonthly(age: number, sum: number, term = 30) {
  const result = PRODUCTS.ochronne.compute({
    age,
    sum,
    term: String(term),
    coverage: "smierc",
    smoker: false,
    lz: "bez"
  });
  return `${Math.round(result.amount).toLocaleString("pl-PL")} zł`;
}

const productParameterRows = [
  { label: "Wiek wstępu", value: "18-65 lat", note: "wiek polisowy ustalany według zasad ubezpieczyciela" },
  { label: "Okres ochrony", value: "5-30 lat", note: "nie dłużej niż do rocznicy umowy w roku, w którym wiek polisowy wynosi 70 lat" },
  { label: "Suma ubezpieczenia", value: "od 50 000 zł", note: "brak górnej sumy w materiałach produktowych; ograniczeniem jest ocena ryzyka" },
  { label: "Składka minimalna", value: "45 zł/mies.", note: "minimalna składka regularna dla płatności miesięcznej" }
];

const pricingByAge = [25, 30, 35, 40, 45, 50, 55].map((age) => ({
  age: `${age} lat`,
  su100: calcOchronneMonthly(age, 100000),
  su300: calcOchronneMonthly(age, 300000),
  su500: calcOchronneMonthly(age, 500000)
}));

const pricingBySum = [100000, 200000, 300000, 500000, 1000000].map((sum) => ({
  sum: `${sum.toLocaleString("pl-PL")} zł`,
  age35: calcOchronneMonthly(35, sum),
  age45: calcOchronneMonthly(45, sum),
  age55: calcOchronneMonthly(55, sum)
}));

const comparisonRows = [
  {
    Icon: BadgeCheck,
    label: "Cena startowa",
    value: "od 45 zł/mies.",
    note: "To punkt wyjścia dla prostej ochrony na życie. Finalna składka zależy od sumy ubezpieczenia, czasu trwania umowy i decyzji po ocenie ryzyka."
  },
  {
    Icon: Calculator,
    label: "Kwota dla bliskich",
    value: "ustalasz sumę",
    note: "Najpierw określamy, ile pieniędzy ma trafić do rodziny. Wyższa suma i dłuższy okres zwykle podnoszą składkę, więc dopasowujemy je do budżetu."
  },
  {
    Icon: FileText,
    label: "Wiek i zdrowie",
    value: "krótka ankieta",
    note: "Kilka pytań o wiek, zdrowie, pracę i styl życia pomaga potwierdzić warunki. Przy większych sumach ubezpieczyciel może poprosić o dodatkowe dane."
  },
  {
    Icon: ShieldCheck,
    label: "Dodatkowa ochrona",
    value: "osobna wycena",
    note: "Ciężkie choroby, szpital, operacje i NNW dobieramy jako dodatki. Każdy ma własny zakres, limity i zasady wypłaty, dlatego jest liczony oddzielnie."
  }
];

type InfoCard = {
  title: string;
  copy: string;
  points?: string[];
};

type OfferDetail = {
  lead: string;
  bestFor: string[];
  parameters: Array<{ label: string; value: string; note: string }>;
  scope: string[];
  decision: string[];
};

const basicInfoCards: InfoCard[] = [
  {
    title: "Jak działa ubezpieczenie na życie?",
    copy:
      "W najprostszej wersji polisa wypłaca wskazaną w umowie sumę ubezpieczenia osobom uposażonym, jeśli ubezpieczony umrze w okresie ochrony. Pieniądze mogą pomóc rodzinie utrzymać budżet, spłacić kredyt albo pokryć koszty organizacyjne po śmierci bliskiej osoby."
  },
  {
    title: "Ile kosztuje polisa?",
    copy:
      "Cena zależy przede wszystkim od wieku, sumy ubezpieczenia, okresu ochrony, zdrowia, zawodu, hobby i dodatków. Kalkulator pokazuje orientacyjny budżet, a finalna propozycja wymaga ankiety i oceny ryzyka."
  },
  {
    title: "Co można rozszerzyć?",
    copy:
      "Do umowy podstawowej można dobrać m.in. ciężkie choroby, leczenie szpitalne, operacje, uszczerbek po wypadku, złamania kości, utratę sprawności, leczenie za granicą oraz ochronę dla partnera lub dzieci."
  },
  {
    title: "Kiedy polisa może nie zadziałać?",
    copy:
      "Najważniejsze ograniczenia znajdują się w OWU: wyłączenia odpowiedzialności, karencje, definicje chorób, warunki wypłaty i zasady oceny roszczenia. Dlatego porównujemy nie tylko składkę, ale też konkretne zapisy."
  }
];

const offerHighlights = [
  {
    id: "ochronne" as ProductId,
    badge: "ochrona rodziny",
    title: "Terminowe ubezpieczenie na życie",
    copy:
      "Podstawowa polisa ochronna: suma ubezpieczenia trafia do bliskich, uposażonych albo banku, jeśli śmierć nastąpi w okresie ochrony.",
    bullets: ["min. 45 zł/mies.", "wiek wstępu 18-65 lat", "okres 5-30 lat", "suma od 50 000 zł"],
    href: "/kalkulator-ubezpieczenia-na-zycie/"
  },
  {
    id: "kredyt" as ProductId,
    badge: "hipoteka",
    title: "Polisa na życie pod kredyt",
    copy:
      "Rozwiązanie dla kredytobiorców: suma może odpowiadać saldu kredytu, a bank może zostać wskazany w cesji jako zabezpieczenie spłaty.",
    bullets: ["cesja na bank", "suma pod saldo kredytu", "alternatywa dla polisy bankowej", "możliwe dodatki zdrowotne"],
    href: "/kalkulator-ubezpieczenia-na-zycie-pod-kredyt/"
  },
  {
    id: "dozycie" as ProductId,
    badge: "kapitał",
    title: "Ubezpieczenie na życie i dożycie",
    copy:
      "Ochrona życia połączona z budowaniem kapitału. Na koniec umowy wypłacane jest świadczenie z tytułu dożycia, a w trakcie mogą pojawiać się premie.",
    bullets: ["min. 200 zł/mies.", "wariant Konserwatywny", "wariant Umiarkowany", "gwarantowana suma od początku"],
    href: "/kalkulator-ubezpieczenia-na-zycie-i-dozycie/"
  },
  {
    id: "wsparcie" as ProductId,
    badge: "dożywotnio",
    title: "Ubezpieczenie na całe życie",
    copy:
      "Ochrona do końca życia, przy składce opłacanej tylko przez określony czas. Często wybierana z myślą o kosztach pogrzebu i spokojnym przekazaniu środków bliskim.",
    bullets: ["min. 125 zł/mies.", "wiek wstępu 13-80 lat", "ochrona dożywotnia", "składka płacona czasowo"],
    href: "/kalkulator-ubezpieczenia-na-cale-zycie/"
  },
  {
    id: "zdrowotne" as ProductId,
    badge: "zdrowie",
    title: "Pakiet zdrowotny i opieka medyczna",
    copy:
      "Prywatna opieka medyczna: telekonsultacje, specjaliści, diagnostyka i warianty dla jednej osoby, pary, rodziny, dziecka, studenta lub seniora.",
    bullets: ["pakiety Mini-Premium", "warianty rodzinne", "diagnostyka i specjaliści", "orientacyjna cena online"],
    href: "/kalkulator-ubezpieczenia-zdrowotnego/"
  }
];

const addonCards: InfoCard[] = [
  {
    title: "Ciężkie choroby",
    copy:
      "Wsparcie finansowe po diagnozie choroby objętej OWU. W materiałach PZU wariant Standard obejmuje 20 chorób, a Premium 67 chorób; suma dla dorosłych może mieścić się od 25 000 zł do 1 000 000 zł.",
    points: ["wypłata po spełnieniu definicji choroby", "maksymalnie do 200% sumy w ramach grup chorób", "osobny wariant dla dzieci: 36 chorób"]
  },
  {
    title: "Leczenie szpitalne",
    copy:
      "Świadczenie za pobyt w szpitalu po wypadku lub chorobie. W zależności od wariantu wypłata może zacząć się od 2 dni hospitalizacji po wypadku albo od 4 dni przy chorobie.",
    points: ["suma zwykle 25 000-100 000 zł", "do 180 dni pobytu w roku umowy", "możliwe świadczenia za OIOM, rekonwalescencję i kartę apteczną"]
  },
  {
    title: "Operacje chirurgiczne",
    copy:
      "Dodatek wypłaca procent sumy ubezpieczenia według klasy operacji. W materiałach produktowych pojawia się limit łącznych wypłat do 500% sumy w okresie umowy.",
    points: ["klasy operacji od 5% do 100% sumy", "osobny zakres dla dzieci", "ochrona może działać na całym świecie"]
  },
  {
    title: "Leczenie za granicą",
    copy:
      "Dodatek otwiera dostęp do organizacji i finansowania leczenia w zagranicznych placówkach. Wariant Standard ma limit 1 mln euro, a Premium 2 mln euro.",
    points: ["Standard: świat bez USA", "Premium: cały świat", "druga opinia medyczna i organizacja leczenia"]
  },
  {
    title: "Wypadek, uszczerbek i złamania",
    copy:
      "Dodatki NNW, wypadek komunikacyjny, uszczerbek i złamania zabezpieczają zdarzenia nagłe. Zakres różni się oceną ryzyka, liczbą objętych zdarzeń i procentami wypłat.",
    points: ["NW/WK nawet do 3 000 000 zł", "uszczerbek dorosłych: 30 000-150 000 zł", "złamania: 30 000-100 000 zł"]
  },
  {
    title: "Ochrona partnera i dzieci",
    copy:
      "Część dodatków można dobrać dla małżonka, partnera albo dziecka, m.in. ciężkie choroby dziecka, pobyt w szpitalu, operacje, uszczerbek czy leczenie za granicą dziecka.",
    points: ["dziecko zwykle od 3 miesięcy do 17 lat", "ochrona często na 5 lat", "możliwa jedna rozmowa o całym pakiecie rodzinnym"]
  }
];

type AdditionalContractCard = {
  title: string;
  code: string;
  audience: string;
  sum: string;
  period: string;
  copy: string;
  facts: string[];
};

const additionalContractGroups = [
  {
    Icon: ShieldCheck,
    title: "Dla ubezpieczonego",
    copy:
      "Rozszerzenia do umowy podstawowej mogą chronić nie tylko na wypadek śmierci, ale też przy chorobie, wypadku, hospitalizacji, operacji albo leczeniu za granicą.",
    facts: ["NW, WK, LS, CC, OP, ZK, TU, MS, US, LZ", "część dodatków wymaga oceny ryzyka", "sumy i limity są ustalane osobno dla dodatku"]
  },
  {
    Icon: Home,
    title: "Dla małżonka lub partnera",
    copy:
      "Można omówić ochronę osoby bliskiej w ramach tej samej rozmowy o polisie. W praktyce punktem wejścia dla partnera jest dodatkowe terminowe ubezpieczenie na życie.",
    facts: ["TE umożliwia objęcie partnera ochroną", "dla partnera zawierana jest odrębna umowa", "rezygnacja z TE kończy dodatki partnera"]
  },
  {
    Icon: BadgeCheck,
    title: "Dla dziecka",
    copy:
      "Dodatki dziecięce mogą obejmować ciężkie choroby, szpital, operacje, uszczerbek, leczenie za granicą oraz terminową ochronę życia dziecka.",
    facts: ["DC, DL, DO, DU, DZ i DT", "zwykle od 3 miesięcy albo od 1 roku", "dla każdego dziecka zawiera się osobną umowę"]
  }
];

const additionalContractCards: AdditionalContractCard[] = [
  {
    title: "Śmierć wskutek nieszczęśliwego wypadku",
    code: "NW",
    audience: "ubezpieczony / partner",
    sum: "50 000 - 3 000 000 zł",
    period: "do rocznicy w roku 75. urodzin",
    copy:
      "Dodatkowa wypłata, jeśli śmierć nastąpi wskutek nieszczęśliwego wypadku w okresie ochrony. Suma dodatku może być inna niż suma umowy podstawowej.",
    facts: ["świadczenie 100% aktualnej sumy dodatku", "wiek wstępu 13-74 lata", "możliwa coroczna indeksacja sumy"]
  },
  {
    title: "Śmierć wskutek wypadku komunikacyjnego",
    code: "WK",
    audience: "ubezpieczony / partner",
    sum: "50 000 - 3 000 000 zł",
    period: "do rocznicy w roku 75. urodzin",
    copy:
      "Rozszerzenie skupione na wypadkach w ruchu komunikacyjnym. Przydatne szczególnie wtedy, gdy klient dużo jeździ, pracuje w trasie albo chce podbić wypłatę w najpoważniejszym scenariuszu.",
    facts: ["świadczenie 100% sumy", "wypadek i śmierć muszą wystąpić w okresie ochrony", "składka zależy od sumy i oceny ryzyka"]
  },
  {
    title: "Leczenie szpitalne",
    code: "LS / DL",
    audience: "dorosły / dziecko",
    sum: "25 000 - 100 000 zł",
    period: "najczęściej 5 lat",
    copy:
      "Wypłata za pobyt w szpitalu po chorobie lub wypadku. W wariantach mogą pojawić się świadczenia za OIOM, rekonwalescencję, zawał, udar, chemioterapię, radioterapię i karta apteczna.",
    facts: ["od 2 dni pobytu po wypadku", "od 4 dni pobytu przy chorobie", "do 180 dni pobytu w roku umowy"]
  },
  {
    title: "Ciężkie choroby",
    code: "CC / DC",
    audience: "dorosły / dziecko",
    sum: "dorosły: 25 000 - 1 000 000 zł; dziecko: 25 000 - 100 000 zł",
    period: "najczęściej 5 lat",
    copy:
      "Świadczenie po wystąpieniu choroby objętej OWU. Dla dorosłych występują warianty Standard i Premium; w materiałach produktowych wskazano 20 chorób w Standardzie i 67 chorób w Premium.",
    facts: ["maksymalnie 200% sumy w ramach dodatku", "dziecko: 36 ciężkich chorób", "najważniejsze są definicje i dokumentacja medyczna"]
  },
  {
    title: "Operacje chirurgiczne",
    code: "OP / DO",
    audience: "dorosły / dziecko",
    sum: "25 000 - 100 000 zł",
    period: "najczęściej 5 lat",
    copy:
      "Dodatek wypłaca procent sumy ubezpieczenia zależnie od klasy operacji. Materiały PZU wskazują 593 operacje dorosłych i blisko 398 operacji dzieci.",
    facts: ["klasy operacji od 5% do 100% sumy", "limit wypłat do 500% sumy w okresie umowy", "ochrona może działać na całym świecie"]
  },
  {
    title: "Złamanie kości",
    code: "ZK",
    audience: "dorosły",
    sum: "30 000 - 100 000 zł",
    period: "najczęściej 5 lat",
    copy:
      "Prostszy dodatek wypadkowy na złamania kości objęte OWU. Może pasować osobom aktywnym, rodzicom, osobom pracującym fizycznie albo klientom, którzy chcą zrozumiałej ochrony od urazów.",
    facts: ["brak pełnej oceny ryzyka w materiałach", "wypłata według tabeli złamań", "łączny limit 100% sumy"]
  },
  {
    title: "Uszczerbek na zdrowiu po wypadku",
    code: "TU / DU",
    audience: "dorosły / dziecko",
    sum: "dorosły: 30 000 - 150 000 zł; dziecko: 30 000 - 100 000 zł",
    period: "najczęściej 5 lat",
    copy:
      "Szersza ochrona od następstw nieszczęśliwego wypadku. Dla dorosłych materiały wskazują tabelę 728 uszczerbków, dla dzieci 232 rodzaje uszczerbków.",
    facts: ["maks. 200% sumy w okresie umowy dla dorosłych", "maks. 100% sumy za jeden wypadek", "możliwa ocena ryzyka za zawód lub sport"]
  },
  {
    title: "Miesięczna wypłata świadczenia",
    code: "MS",
    audience: "ubezpieczony",
    sum: "od 1 000 zł miesięcznie",
    period: "wypłata przez 5-10 lat",
    copy:
      "Po śmierci ubezpieczonego wskazane osoby mogą otrzymywać dodatkowe miesięczne świadczenie przez z góry wybrany okres. To inny sposób zabezpieczenia domowego budżetu niż jednorazowa wypłata.",
    facts: ["wiek wstępu 18-60 lat", "okres umowy 5-30 lat", "świadczenie po śmierci rośnie automatycznie o 3% rocznie"]
  },
  {
    title: "Utrata sprawności w życiu codziennym",
    code: "US",
    audience: "dorosły",
    sum: "30 000 - 1 000 000 zł",
    period: "do 5 lat",
    copy:
      "Dodatek na wypadek utraty zdolności lub sprawności wskazanych w OWU, np. widzenia, słyszenia, mówienia, chodzenia, stania, siedzenia, sprawności obu rąk lub kończyny górnej.",
    facts: ["wiek wstępu 18-60 lat", "wymaga oceny ryzyka", "możliwość zmiany sumy na wniosek"]
  },
  {
    title: "Leczenie za granicą",
    code: "LZ / DZ",
    audience: "dorosły / dziecko",
    sum: "1 mln euro albo 2 mln euro",
    period: "umowa roczna",
    copy:
      "Organizacja i pokrycie kosztów leczenia za granicą przy poważnym stanie chorobowym. Wariant Standard ma limit 1 mln euro, a Premium 2 mln euro.",
    facts: ["druga opinia medyczna", "Standard: świat bez USA", "Premium: cały świat i szerszy katalog świadczeń"]
  },
  {
    title: "Terminowe ubezpieczenie partnera",
    code: "TE",
    audience: "małżonek / partner",
    sum: "od 25 000 zł",
    period: "5-30 lat",
    copy:
      "Dodatkowa ochrona życia partnera lub małżonka. W razie jego śmierci wypłacana jest suma ubezpieczenia, a samo TE umożliwia zawarcie kolejnych dodatków dla partnera.",
    facts: ["wiek wstępu 18-65 lat", "maksymalny wiek końca 70 lat", "osobna umowa dla partnera"]
  },
  {
    title: "Terminowe ubezpieczenie dziecka",
    code: "DT",
    audience: "dziecko",
    sum: "100 zł",
    period: "5-30 lat",
    copy:
      "Techniczny fundament dla dodatków dziecięcych. Pozwala objąć dziecko ochroną i dobrać kolejne rozszerzenia takie jak choroby, szpital, operacje, uszczerbek albo leczenie za granicą.",
    facts: ["wiek wstępu 3 miesiące - 17 lat", "suma stała 100 zł", "rozwiązanie DT kończy pozostałe dodatki dziecka"]
  }
];

const additionalContractChecklist = [
  "najpierw ustalamy, czy umowa podstawowa ma wystarczającą sumę na wypadek śmierci",
  "potem dobieramy dodatki do realnego ryzyka: choroba, szpital, wypadek, dziecko, partner albo leczenie za granicą",
  "każdy dodatek sprawdzamy osobno pod kątem sumy, wieku, okresu, karencji, wyłączeń i definicji",
  "przy dodatkach medycznych liczy się nie tylko liczba chorób, ale też dokładna definicja w OWU",
  "jeżeli budżet jest ograniczony, lepiej wybrać mniej dodatków, ale z sensowną sumą"
];

const additionalContractFaqs: ProductFaq[] = [
  {
    question: "Czy oferujemy umowy dodatkowe podobne do strony PRU?",
    answer:
      "Tak, możemy dobrać umowy dodatkowe do polisy na życie, ale zakres opieramy na produktach i OWU PZU Życie SA, a nie na ofercie PRU. Dostępne są m.in. ciężkie choroby, leczenie szpitalne, operacje, NNW, wypadek komunikacyjny, uszczerbek, złamania, miesięczne świadczenie, leczenie za granicą oraz dodatki dla partnera i dzieci."
  },
  {
    question: "Czy dodatki można kupić bez umowy podstawowej?",
    answer:
      "Co do zasady są to umowy dodatkowe, czyli wymagają odpowiedniej umowy podstawowej. W materiałach PZU wskazano, że dodatki można zawrzeć przy zawieraniu umowy podstawowej albo dokupić później w dniu najbliższej płatności składki, jeśli umowa spełnia warunki."
  },
  {
    question: "Czy warto brać wszystkie dodatki?",
    answer:
      "Nie zawsze. Dodatki powinny odpowiadać konkretnym potrzebom: utracie dochodu po chorobie, kosztom leczenia, ryzyku pracy, dzieciom, kredytowi albo brakowi oszczędności. Zbyt szeroki pakiet może zabrać budżet z najważniejszej sumy na życie."
  },
  {
    question: "Które dodatki zwykle wymagają dokładniejszej oceny zdrowia?",
    answer:
      "Najczęściej szczególnej uwagi wymagają dodatki medyczne i wysokie sumy, np. ciężkie choroby, leczenie za granicą, utrata sprawności, uszczerbek albo miesięczne świadczenie. Ubezpieczyciel może poprosić o ankietę, dokumentację lub badania."
  },
  {
    question: "Czym różni się ZK od TU?",
    answer:
      "ZK dotyczy złamań kości i jest prostszym dodatkiem z mniejszą liczbą zdarzeń. TU obejmuje szerszą tabelę uszczerbków po nieszczęśliwym wypadku, ale może mieć dokładniejszą ocenę ryzyka oraz szersze wyłączenia."
  },
  {
    question: "Czy leczenie za granicą to zwykły pakiet medyczny?",
    answer:
      "Nie. Leczenie za granicą jest dodatkiem na poważne stany chorobowe, z organizacją i finansowaniem leczenia w zagranicznych placówkach w granicach limitu. Nie zastępuje codziennej prywatnej opieki medycznej."
  }
];

const buyingSteps = [
  {
    Icon: Calculator,
    title: "Policz składkę",
    copy: "Wybierz zakres i zobacz orientacyjny budżet."
  },
  {
    Icon: Phone,
    title: "Doprecyzuj potrzeby",
    copy: "Ustalamy rodzinę, kredyt, dochód i sensowną sumę."
  },
  {
    Icon: FileText,
    title: "Wypełnij ankietę",
    copy: "Ubezpieczyciel potwierdza zdrowie, pracę i hobby."
  },
  {
    Icon: BadgeCheck,
    title: "Odbierz polisę",
    copy: "Po akceptacji warunków dostajesz dokument ochrony."
  }
];

const offerDetails: Record<ProductId, OfferDetail> = {
  ochronne: {
    lead:
      "Terminowa polisa ochronna jest najprostszym sposobem zabezpieczenia bliskich. Jej zadaniem nie jest budowanie kapitału, tylko szybka wypłata ustalonej sumy, gdyby ubezpieczonego zabrakło w okresie ochrony.",
    bestFor: [
      "rodziców i partnerów, których dochód utrzymuje dom",
      "osób z kredytem, leasingiem lub większymi zobowiązaniami",
      "przedsiębiorców, którzy chcą zabezpieczyć rodzinę i wspólników",
      "klientów, którzy chcą wysokiej sumy przy możliwie prostej konstrukcji"
    ],
    parameters: [
      { label: "Wiek wstępu", value: "18-65 lat", note: "wiek polisowy liczony według zasad ubezpieczyciela" },
      { label: "Okres ochrony", value: "5-30 lat", note: "nie dłużej niż do rocznicy umowy w roku, w którym wiek polisowy wynosi 70 lat" },
      { label: "Suma ubezpieczenia", value: "od 50 000 zł", note: "brak sztywnej górnej granicy, decyduje ocena ryzyka" },
      { label: "Składka minimalna", value: "45 zł/mies.", note: "minimalna składka regularna przy płatności miesięcznej" }
    ],
    scope: [
      "wypłata aktualnej sumy ubezpieczenia osobom uposażonym",
      "ochrona na całym świecie, 24 godziny na dobę",
      "możliwość wskazania i zmiany uposażonych w trakcie umowy",
      "ochrona tymczasowa po złożeniu wniosku i opłaceniu składki: do 90 dni, maksymalnie 200 000 zł, dla śmierci wskutek nieszczęśliwego wypadku"
    ],
    decision: [
      "jaką kwotę rodzina realnie potrzebuje przez 2-5 lat po utracie dochodu",
      "czy suma ma pokryć tylko kredyt, czy również koszty życia",
      "które dodatki zdrowotne mają sens przy Twojej pracy i historii zdrowia",
      "czy lepiej wybrać jedną wysoką polisę, czy połączyć kilka zakresów"
    ]
  },
  kredyt: {
    lead:
      "Polisa pod kredyt to w praktyce ochrona na życie dopasowana do zobowiązania hipotecznego. Jej celem jest zabezpieczenie banku i rodziny, aby dług nie został po śmierci kredytobiorcy wyłącznie na bliskich.",
    bestFor: [
      "osób kupujących mieszkanie lub dom na kredyt",
      "kredytobiorców, którzy chcą porównać polisę bankową z zewnętrzną",
      "par spłacających wspólny kredyt z nierównymi dochodami",
      "klientów, którzy chcą dodać choroby lub utratę sprawności do samej cesji"
    ],
    parameters: [
      { label: "Suma", value: "zwykle saldo kredytu", note: "czasem warto zostawić nadwyżkę dla rodziny" },
      { label: "Okres", value: "do okresu spłaty", note: "najczęściej 20-30 lat, zgodnie z wymaganiem banku" },
      { label: "Cesja", value: "na bank", note: "bank może wymagać konkretnej treści cesji i potwierdzenia opłacenia składki" },
      { label: "Zakres", value: "śmierć lub szerszy", note: "można rozważyć ciężkie choroby, utratę sprawności albo NNW" }
    ],
    scope: [
      "wypłata może w pierwszej kolejności spłacić zobowiązanie wobec banku",
      "zewnętrzna polisa często daje większą kontrolę nad zakresem i kontynuacją po spłacie",
      "po zmniejszeniu salda kredytu można sprawdzić, czy aktualizacja sumy ma sens",
      "ochrona rodziny nie musi kończyć się na wymaganym przez bank minimum"
    ],
    decision: [
      "czy bank akceptuje zewnętrzną polisę i jakie ma wymagania",
      "czy suma ma być równa saldu, czy wyższa o bufor dla bliskich",
      "czy druga osoba w kredycie też potrzebuje ochrony",
      "czy składka bankowa nie jest droższa od indywidualnej polisy z cesją"
    ]
  },
  dozycie: {
    lead:
      "Ubezpieczenie na życie i dożycie łączy ochronę z długoterminowym gromadzeniem kapitału. W razie śmierci świadczenie trafia do uposażonych, a przy dożyciu końca umowy pieniądze otrzymuje ubezpieczony.",
    bestFor: [
      "osób, które chcą połączyć dyscyplinę oszczędzania z ochroną życia",
      "rodziców odkładających kapitał na przyszłość dziecka",
      "klientów myślących o dodatkowych środkach na emeryturę",
      "osób, które wolą znać gwarantowaną kwotę świadczenia już przy starcie"
    ],
    parameters: [
      { label: "Wiek wstępu", value: "13-65 lat", note: "dla składki regularnej; jednorazowa może mieć inne limity" },
      { label: "Okres", value: "10-30 lat", note: "Konserwatywny; Umiarkowany zwykle 12-30 lat i wielokrotność 3 lat" },
      { label: "Składka minimalna", value: "200 zł/mies.", note: "regularnie: miesięcznie, kwartalnie, półrocznie lub rocznie" },
      { label: "Warianty", value: "2", note: "Konserwatywny i Umiarkowany różnią się sposobem lokowania środków i premiami" }
    ],
    scope: [
      "gwarantowana początkowa suma ubezpieczenia znana przy zawarciu umowy",
      "premia okresowa, a w wariancie Umiarkowanym także premia końcowa",
      "możliwość waloryzacji składki i sumy ubezpieczenia",
      "możliwość dołożenia ochrony zdrowotnej, wypadkowej i rodzinnej"
    ],
    decision: [
      "czy ważniejsza jest gwarancja, czy potencjał wyższej premii",
      "jak długo możesz regularnie opłacać składkę bez przeciążenia budżetu",
      "czy produkt ma służyć emeryturze, dziecku, rezerwie czy mieszanej potrzebie",
      "jak rozumiesz wykup i konsekwencje wcześniejszej rezygnacji"
    ]
  },
  zdrowotne: {
    lead:
      "Pakiet zdrowotny nie zastępuje polisy na życie, ale pomaga szybciej dostać się do lekarzy, diagnostyki i konsultacji. To dobry dodatek do ochrony finansowej, szczególnie gdy liczy się czas i wygoda leczenia.",
    bestFor: [
      "osób, które chcą prywatnych konsultacji bez długiego oczekiwania",
      "rodzin z dziećmi często korzystających z lekarzy",
      "przedsiębiorców i pracowników bez rozbudowanego pakietu firmowego",
      "seniorów i studentów, dla których ważny jest przewidywalny dostęp do pomocy"
    ],
    parameters: [
      { label: "Warianty", value: "Mini-Premium", note: "zakres rośnie wraz z pakietem" },
      { label: "Dla kogo", value: "1 osoba lub rodzina", note: "warianty dla pary, rodziny, dziecka, seniora i studenta" },
      { label: "Rozliczenie", value: "najczęściej roczne", note: "w kalkulatorze pokazujemy orientacyjny koszt miesięczny i roczny" },
      { label: "Zakres", value: "lekarze i diagnostyka", note: "telekonsultacje, specjaliści, badania i placówki partnerskie" }
    ],
    scope: [
      "dostęp do konsultacji specjalistycznych zależnie od wariantu",
      "diagnostyka laboratoryjna i obrazowa w wybranym zakresie",
      "telekonsultacje i umawianie wizyt",
      "wariant rodzinny może uporządkować opiekę dla kilku osób w jednym budżecie"
    ],
    decision: [
      "jak często korzystasz z lekarzy i badań",
      "czy potrzebujesz tylko podstawy, czy szerokiej diagnostyki",
      "czy pakiet ma obejmować dzieci lub partnera",
      "czy masz już benefit pracowniczy i czego w nim brakuje"
    ]
  },
  wsparcie: {
    lead:
      "Dożywotnia polisa na życie działa do końca życia ubezpieczonego, mimo że składkę opłaca się tylko przez określony czas. To rozwiązanie dla osób, które chcą mieć pewność wypłaty dla bliskich niezależnie od wieku.",
    bestFor: [
      "osób planujących środki na koszty pogrzebu i sprawy spadkowe",
      "rodziców chcących zostawić rodzinie określoną kwotę",
      "klientów, którzy nie chcą, aby ochrona skończyła się po 20 czy 30 latach",
      "osób starszych, które nie mieszczą się już w klasycznym terminowym zakresie"
    ],
    parameters: [
      { label: "Wiek wstępu", value: "13-80 lat", note: "szerszy niż w klasycznej polisie terminowej" },
      { label: "Czas ochrony", value: "dożywotnio", note: "umowa na czas nieokreślony, do końca życia" },
      { label: "Składka minimalna", value: "125 zł/mies.", note: "możliwe też płatności kwartalne, półroczne i roczne" },
      { label: "Suma", value: "według oceny ryzyka", note: "dla niskich sum procedura może być prostsza" }
    ],
    scope: [
      "wypłata sumy ubezpieczenia po śmierci ubezpieczonego",
      "składka opłacana tylko do wieku określonego w OWU, a ochrona trwa dalej",
      "możliwość wskazania osób uprawnionych do świadczenia",
      "ochrona na całym świecie, 24 godziny na dobę"
    ],
    decision: [
      "czy zależy Ci na pewnej wypłacie, czy na najwyższej sumie w niskiej składce",
      "jaką kwotę chcesz zostawić bliskim po śmierci",
      "jak długo chcesz i możesz opłacać składkę",
      "czy potrzebujesz dodatków zdrowotnych, czy tylko ochrony życia"
    ]
  }
};

const quoteCards: Array<{ id: ProductId; title: string; params: string[]; input: CalcInputs }> = [
  {
    id: "ochronne" as ProductId,
    title: "Ochrona rodziny",
    params: ["35 lat", "300 000 zł", "20 lat", "sama śmierć"],
    input: { age: 35, sum: 300000, term: "20", coverage: "smierc", smoker: false, lz: "bez" }
  },
  {
    id: "kredyt" as ProductId,
    title: "Kredyt hipoteczny",
    params: ["35 lat", "400 000 zł", "25 lat", "cesja na bank"],
    input: { age: 35, sum: 400000, term: 25, coverage: "smierc", bank: "inny", smoker: false }
  },
  {
    id: "dozycie" as ProductId,
    title: "Kapitał na przyszłość",
    params: ["40 lat", "15 lat", "50 000 zł", "wariant Konserwatywny"],
    input: { age: 40, term: 15, sum: 50000, wariant: "konserwatywny" }
  }
];

const faqs: ProductFaq[] = [
  {
    question: "Czy kalkulator pokazuje finalną cenę polisy?",
    answer:
      "Nie. Wynik jest orientacyjny i pomaga ustalić realistyczny budżet. Finalna składka zależy od ankiety medycznej, zakresu ochrony, zasad konkretnego ubezpieczyciela i ewentualnych zwyżek."
  },
  {
    question: "Od czego najbardziej zależy cena ubezpieczenia na życie?",
    answer:
      "Największy wpływ zwykle mają wiek, suma ubezpieczenia, stan zdrowia, palenie, zawód, hobby, okres ochrony i liczba umów dodatkowych."
  },
  {
    question: "Jaka suma ubezpieczenia jest rozsądna?",
    answer:
      "Najczęściej liczy się zobowiązania, kredyt, roczne koszty rodziny i czas potrzebny bliskim na spokojne poukładanie finansów. Dla rodzin często wychodzi kilka rocznych dochodów plus saldo kredytu."
  },
  {
    question: "Czy polisa pod kredyt musi być z banku?",
    answer:
      "Nie zawsze. Często można użyć zewnętrznej polisy z cesją na bank, ale trzeba sprawdzić wymagania banku dotyczące sumy, zakresu i okresu ochrony."
  },
  {
    question: "Czy warto kupić polisę, jeśli mam ubezpieczenie grupowe w pracy?",
    answer:
      "Często tak, bo polisa grupowa zwykle ma niższe sumy i działa tylko tak długo, jak trwa zatrudnienie lub członkostwo w grupie. Indywidualna polisa może być wyższa, dłuższa i dopasowana do kredytu, rodziny oraz zdrowia."
  },
  {
    question: "Czym różni się ubezpieczenie terminowe od dożywotniego?",
    answer:
      "Terminowe działa przez wybrany okres, np. 20 lub 30 lat, i jest zwykle tańsze przy wysokiej sumie. Dożywotnie działa do końca życia, ale składka jest wyższa, bo wypłata jest co do zasady pewna."
  },
  {
    question: "Czy świadczenie z polisy wchodzi do spadku?",
    answer:
      "Świadczenie wypłacane uposażonym co do zasady trafia poza postępowaniem spadkowym. Dlatego warto regularnie aktualizować osoby uposażone i ich udziały."
  },
  {
    question: "Kogo mogę wskazać jako uposażonego?",
    answer:
      "Możesz wskazać jedną lub kilka osób, niekoniecznie spokrewnionych. W wielu przypadkach uposażonym może być także bank, jeśli polisa zabezpiecza kredyt."
  },
  {
    question: "Czy mogę zmienić uposażonych w trakcie umowy?",
    answer:
      "Tak, zwykle można zmienić uposażonych i procentowy podział świadczenia. Warto robić to po ważnych zmianach życiowych: ślubie, rozwodzie, narodzinach dziecka, spłacie kredytu."
  },
  {
    question: "Co to jest karencja?",
    answer:
      "Karencja to okres po zawarciu umowy, w którym ochrona dla wybranego ryzyka jeszcze nie działa albo działa w ograniczonym zakresie. Najczęściej dotyczy dodatków chorobowych i zdrowotnych."
  },
  {
    question: "Co to są wyłączenia odpowiedzialności?",
    answer:
      "To sytuacje opisane w OWU, w których ubezpieczyciel może odmówić wypłaty. Wyłączenia trzeba czytać osobno dla umowy podstawowej i każdego dodatku."
  },
  {
    question: "Czy ubezpieczenie działa za granicą?",
    answer:
      "Klasyczna ochrona życia zwykle działa na całym świecie, 24 godziny na dobę. Dodatki, np. szpitalne lub leczenie za granicą, mają własne definicje, limity i terytorium ochrony."
  },
  {
    question: "Czy palenie papierosów wpływa na składkę?",
    answer:
      "Może wpływać, podobnie jak stan zdrowia, BMI, zawód, sporty podwyższonego ryzyka i historia leczenia. Kalkulator pokazuje przybliżenie, a finalną decyzję podejmuje ubezpieczyciel po ocenie ryzyka."
  },
  {
    question: "Jaką sumę ubezpieczenia wybrać przy kredycie hipotecznym?",
    answer:
      "Najczęściej punktem wyjścia jest aktualne saldo kredytu. W praktyce wiele rodzin wybiera sumę wyższą, aby po spłacie zobowiązania została dodatkowa kwota na codzienne koszty."
  },
  {
    question: "Czy mogę dokupić dodatki później?",
    answer:
      "W wielu produktach dodatki można zawrzeć przy starcie albo dokupić później w dniu najbliższej płatności składki, o ile umowa podstawowa spełnia warunki. Dokupienie może wymagać oceny ryzyka."
  },
  {
    question: "Czy dzieci mogą być objęte ochroną w jednej polisie?",
    answer:
      "Tak, część dodatków można rozszerzyć na dziecko, np. ciężkie choroby, leczenie szpitalne, operacje, uszczerbek albo leczenie za granicą dziecka. Zakres i wiek wstępu zależą od OWU."
  },
  {
    question: "Czy ubezpieczenie na życie i dożycie jest lokatą?",
    answer:
      "Nie. To ubezpieczenie z elementem gromadzenia kapitału, a nie lokata bankowa. Ma gwarantowane elementy, zasady premii, koszty i konsekwencje wcześniejszej rezygnacji opisane w dokumentach."
  },
  {
    question: "Czy polisa zdrowotna zastępuje ubezpieczenie na życie?",
    answer:
      "Nie. Pakiet zdrowotny pomaga korzystać z prywatnej opieki medycznej, ale nie wypłaca rodzinie wysokiej sumy po śmierci. Najczęściej pełni inną funkcję niż polisa ochronna."
  },
  {
    question: "Jak przygotować się do rozmowy z doradcą?",
    answer:
      "Warto znać wysokość kredytu, miesięczne koszty rodziny, dochody, liczbę osób zależnych finansowo, oczekiwaną sumę oraz informacje o dotychczasowym leczeniu. To skraca rozmowę i poprawia jakość porównania."
  },
  {
    question: "Czy najtańsza polisa jest najlepsza?",
    answer:
      "Nie zawsze. Niska składka może oznaczać niższą sumę, krótszy okres, węższą listę chorób, dłuższe karencje albo brak dodatków. Dobra decyzja porównuje cenę, zakres i definicje świadczeń."
  }
];

const advancedFaqs: ProductFaq[] = [
  {
    question: "Dlaczego dwie osoby w tym samym wieku mogą dostać inną składkę?",
    answer:
      "Bo wiek to tylko jeden parametr. Znaczenie mają także stan zdrowia, historia leczenia, BMI, palenie, zawód, hobby, suma ubezpieczenia, czas trwania ochrony i wybrane dodatki. Ubezpieczyciel może zaproponować składkę standardową, zwyżkę, wyłączenie albo odmówić wybranego zakresu."
  },
  {
    question: "Czy ankieta medyczna zawsze oznacza badania?",
    answer:
      "Nie zawsze. Przy części sum i zakresów wystarczy ankieta, ale przy wyższych kwotach, wieku, chorobach w historii albo szerszych dodatkach ubezpieczyciel może poprosić o dokumentację medyczną, badania lub dodatkowe pytania."
  },
  {
    question: "Co oznacza wyłączenie odpowiedzialności w decyzji ubezpieczyciela?",
    answer:
      "To ograniczenie ochrony dla konkretnego ryzyka, choroby, aktywności lub skutków wcześniejszego stanu zdrowia. Wyłączenie trzeba czytać razem z OWU i decyzją ubezpieczyciela, bo może zmienić sens danego dodatku."
  },
  {
    question: "Czym różni się suma ubezpieczenia od limitu świadczenia?",
    answer:
      "Suma ubezpieczenia to główna kwota dla danego ryzyka, np. śmierci. Limit świadczenia może dotyczyć dodatku, procedury, pobytu w szpitalu, operacji albo leczenia za granicą i bywa niższy niż suma z umowy podstawowej."
  },
  {
    question: "Czy kalkulator uwzględnia wszystkie zniżki i zwyżki?",
    answer:
      "Nie. Kalkulator pokazuje rozsądny punkt odniesienia na podstawie parametrów wejściowych. Nie zastępuje taryfy ubezpieczyciela, ankiety medycznej, aktualnych promocji, decyzji underwritingowej ani indywidualnych warunków."
  },
  {
    question: "Jak działa cesja polisy na bank po spłacie kredytu?",
    answer:
      "Cesja oznacza, że bank może być uprawniony do świadczenia w zakresie zabezpieczenia kredytu. Po spłacie kredytu zwykle warto złożyć dyspozycję zwolnienia cesji i zaktualizować uposażonych, aby polisa odpowiadała aktualnej sytuacji rodzinnej."
  },
  {
    question: "Czy można mieć kilka polis na życie jednocześnie?",
    answer:
      "Tak, co do zasady można mieć kilka polis, np. grupową, indywidualną i pod kredyt. Trzeba jednak sprawdzić, czy łączna suma jest uzasadniona potrzebami i czy ubezpieczyciel nie wymaga dodatkowej oceny ryzyka."
  },
  {
    question: "Co sprawdzić w OWU przy dodatku ciężkich chorób?",
    answer:
      "Najważniejsze są definicje chorób, liczba jednostek chorobowych, karencje, okres przeżycia, wyłączenia, zasady wypłaty po wcześniejszej diagnozie oraz to, czy świadczenia można otrzymać więcej niż raz."
  },
  {
    question: "Co oznacza indeksacja lub waloryzacja składki i sumy?",
    answer:
      "To mechanizm aktualizacji składki, sumy ubezpieczenia albo wartości świadczeń w czasie. Może chronić realną wartość polisy, ale wpływa na przyszłe płatności, dlatego trzeba sprawdzić zasady akceptacji lub rezygnacji z indeksacji."
  },
  {
    question: "Czy można zawiesić składkę albo zrezygnować z polisy?",
    answer:
      "Zależy od produktu i etapu umowy. Polisy ochronne, oszczędnościowo-ochronne i dożywotnie mają różne zasady opóźnienia, wznowienia, wypowiedzenia, wartości wykupu i konsekwencji braku składki."
  }
];

function getFaqPageItems(): ProductFaq[] {
  return uniqueFaqs([
    ...faqs,
    ...Object.values(PRODUCTS).flatMap((product) => product.faqs),
    ...advancedFaqs
  ]);
}

const guideRouteKeys: GuideRoute[] = [
  "guideAfter60",
  "guideExclusions",
  "guideBankPolicy",
  "guideSmokers",
  "guideNoExam",
  "guideSum",
  "guide500k",
  "guideCost",
  "guideAfter50",
  "guideChoose",
  "guideProtection",
  "guideMortgage",
  "guideAddons",
  "guideRetirement",
  "guideProcess"
];

function isGuideRoute(route: RouteKey): route is GuideRoute {
  return guideRouteKeys.includes(route as GuideRoute);
}

type GuideSection = {
  heading: string;
  lead: string;
  body: string[];
  bullets?: string[];
};

type GuideSource = {
  label: string;
  href: string;
  desc: string;
};

type GuidePage = {
  slug: string;
  label: string;
  kicker: string;
  title: string;
  copy: string;
  image: string;
  /** Daty ISO (YYYY-MM-DD) — widoczne na stronie i w schema Article; wymagane dla E-E-A-T. */
  datePublished: string;
  dateModified: string;
  calculatorHref?: string;
  calculatorLabel?: string;
  defaultProduct?: string;
  summary: string[];
  sections: GuideSection[];
  checklistTitle: string;
  checklist: string[];
  mistakesTitle: string;
  mistakes: string[];
  faqs: ProductFaq[];
  verificationNote?: string;
  sources?: GuideSource[];
  related: Array<{ label: string; href: string; desc: string }>;
};

const retirementGuidePage: GuidePage = {
  slug: "poradnik/oszczedzanie-na-emeryture",
  datePublished: "2026-05-30",
  dateModified: "2026-08-12",
  label: "Oszczędzanie na emeryturę",
  kicker: "Poradnik emerytalny",
  title: "Jak oszczędzać na emeryturę i połączyć kapitał z ochroną?",
  copy:
    "Emerytura nie jest jedną decyzją, tylko długim procesem: budżet, regularność, ochrona rodziny i wybór momentu, w którym kapitał ma być dostępny. Polisa na życie i dożycie może być jednym z narzędzi w takim planie.",
  image: guideRetirementImage,
  calculatorHref: "/kalkulator-ubezpieczenia-na-zycie-i-dozycie/",
  calculatorLabel: "Policz kapitał",
  defaultProduct: "endowment",
  summary: [
    "najważniejsza jest regularność i okres oszczędzania, nie jednorazowy zryw",
    "polisa na życie i dożycie łączy wypłatę przy dożyciu z ochroną na wypadek śmierci",
    "warto rozumieć gwarancję, premie, waloryzację, wykup i konsekwencje wcześniejszej rezygnacji",
    "produkt emerytalny dobiera się do budżetu, wieku, rodziny i tolerancji na ryzyko"
  ],
  sections: [
    {
      heading: "Dlaczego warto zacząć od luki emerytalnej",
      lead: "Pytanie nie brzmi tylko: ile odkładać, ale ile pieniędzy będzie brakowało między przyszłymi wydatkami a dochodem po zakończeniu pracy.",
      body: [
        "Luka emerytalna to różnica między stylem życia, który chcesz utrzymać, a przewidywanymi wpływami z publicznego systemu, pracy, firmy, najmu, oszczędności i innych źródeł. Nie trzeba znać dokładnej przyszłości, żeby zacząć. Wystarczy policzyć dzisiejsze koszty i zastanowić się, które z nich zostaną po przejściu na emeryturę.",
        "Dla jednej osoby najważniejsza będzie dopłata do codziennych kosztów życia. Dla innej: jednorazowy kapitał na spłatę zobowiązań, leczenie, remont mieszkania, pomoc dzieciom albo przejście na lżejszą pracę. Im konkretniejszy cel, tym łatwiej dobrać kwotę, okres i narzędzie.",
        "Plan emerytalny warto układać warstwowo. Część pieniędzy powinna być łatwo dostępna na poduszkę finansową, część może pracować długoterminowo, a część może jednocześnie chronić rodzinę, gdyby oszczędzający zmarł przed końcem planu."
      ],
      bullets: [
        "policz obecne koszty stałe i zobowiązania",
        "ustal, kiedy kapitał ma być potrzebny",
        "oddziel poduszkę finansową od pieniędzy długoterminowych",
        "sprawdź, czy rodzina potrzebuje ochrony w trakcie oszczędzania"
      ]
    },
    {
      heading: "Czym różni się zwykłe oszczędzanie od polisy",
      lead: "Konto oszczędnościowe, lokata, fundusz, IKE, IKZE, PPK i polisa mają różne zadania. Polisa na życie i dożycie nie jest zwykłym rachunkiem do dowolnych wypłat.",
      body: [
        "W ubezpieczeniu na życie i dożycie klient wybiera okres oraz sumę, a składka regularna buduje kapitał przez wiele lat. Jeśli ubezpieczony dożyje końca umowy, otrzymuje świadczenie z tytułu dożycia. Jeśli umrze w okresie ochrony, świadczenie trafia do wskazanych osób zgodnie z warunkami umowy.",
        "To rozwiązanie jest bardziej zdyscyplinowane niż luźne odkładanie na koncie. Daje plan, regularną składkę i określony koniec umowy. Ma też drugą stronę: wcześniejsza rezygnacja może oznaczać wypłatę wartości wykupu, która nie musi odpowiadać sumie wpłaconych składek.",
        "Dlatego polisy tego typu nie powinno się wybierać na pieniądze, które mogą być potrzebne za pół roku. To raczej element długoterminowego planu: na przyszłą emeryturę, kapitał dla dziecka, spokojniejszą zmianę pracy albo finansowy bufor na późniejsze lata."
      ],
      bullets: [
        "ochrona życia działa w czasie budowania kapitału",
        "świadczenie końcowe jest powiązane z dożyciem końca umowy",
        "regularna składka pomaga utrzymać dyscyplinę",
        "przed podpisaniem trzeba zrozumieć wartość wykupu"
      ]
    },
    {
      heading: "Jak działa PZU Perspektywa na Przyszłość",
      lead: "W materiałach produktowych PZU Perspektywa na Przyszłość jest opisana jako indywidualne ubezpieczenie na życie i dożycie z premiami.",
      body: [
        "Produkt łączy trzy elementy: ochronę na wypadek śmierci, gromadzenie środków na przyszłość oraz możliwość zwiększania świadczenia dzięki premiom i waloryzacji składki. Na koniec umowy klient może otrzymać świadczenie z tytułu dożycia, czyli kapitał zgromadzony w polisie zgodnie z warunkami produktu.",
        "Wariant Konserwatywny jest zbudowany wokół bezpieczniejszej konstrukcji lokowania środków i premii okresowej wyznaczanej co roku. Wariant Umiarkowany dopuszcza szerszy zestaw instrumentów, przy czym część środków pozostaje w papierach dłużnych emitowanych, poręczanych lub gwarantowanych przez Skarb Państwa; premie są tam rozliczane co 3 lata i może pojawić się premia końcowa.",
        "W praktyce wybór wariantu powinien wynikać z tolerancji na ryzyko, czasu do emerytury i potrzeby gwarancji. Osoba, która chce przede wszystkim przewidywalności, zwykle będzie inaczej patrzeć na produkt niż ktoś, kto akceptuje większą zmienność w zamian za potencjał wyższych premii."
      ],
      bullets: [
        "składka regularna miesięczna w materiałach zaczyna się od 200 zł",
        "wariant Konserwatywny: okres przy składce regularnej od 10 do 30 lat",
        "wariant Umiarkowany: okres przy składce regularnej od 12 do 30 lat",
        "ochrona przy składce regularnej kończy się najpóźniej przy wieku polisowym 75 lat"
      ]
    },
    {
      heading: "Ile odkładać miesięcznie",
      lead: "Dobra składka to taka, którą da się płacić regularnie także wtedy, gdy budżet jest trochę trudniejszy.",
      body: [
        "Najprostszy sposób to zacząć od kwoty, która nie zjada poduszki finansowej. Jeżeli po opłaceniu stałych kosztów zostaje niewielka rezerwa, lepiej wybrać mniejszą składkę i utrzymać ją przez lata niż zadeklarować ambitną kwotę, która po kilku miesiącach stanie się problemem.",
        "W kalkulatorze na życie i dożycie można ustawić wiek, okres, wariant oraz sumę gwarantowaną. Wynik pokazuje orientacyjną składkę i pomaga zobaczyć, jak bardzo okres oszczędzania wpływa na miesięczny budżet. Zwykle im wcześniej zaczynasz, tym łatwiej rozłożyć cel na niższe wpłaty.",
        "Warto też zdecydować, czy składka ma rosnąć przez waloryzację. Waloryzacja może pomagać ograniczać wpływ inflacji na realną wartość świadczenia, ale oznacza wyższe przyszłe płatności. To trzeba dopasować do dochodu i planów rodzinnych."
      ],
      bullets: [
        "ustal kwotę, którą możesz płacić bez naruszania poduszki",
        "porównaj kilka okresów: 12, 15, 20, 25 i 30 lat",
        "sprawdź wariant z waloryzacją oraz bez niej",
        "zostaw miejsce w budżecie na ochronę zdrowia i rodziny"
      ]
    },
    {
      heading: "Kiedy polisa emerytalna ma sens",
      lead: "Nie każdy cel emerytalny powinien być realizowany polisą. Dobre doradztwo polega też na powiedzeniu, kiedy produkt nie pasuje.",
      body: [
        "Polisa na życie i dożycie może mieć sens, gdy klient chce regularnie odkładać, zależy mu na określonym horyzoncie, chce połączyć kapitał z ochroną życia i nie planuje korzystać z tych pieniędzy w krótkim terminie. Może być szczególnie czytelna dla osób, które potrzebują dyscypliny i dokumentu z jasnym planem płatności.",
        "Inne narzędzie może być lepsze, jeśli klient potrzebuje pełnej płynności, chce samodzielnie inwestować, korzysta już z rozwiązań emerytalnych w pracy albo ma bardzo nieregularne dochody. Przed zawarciem umowy trzeba też sprawdzić, czy składka nie ograniczy pieniędzy potrzebnych na kredyt, leczenie, dzieci albo bieżące bezpieczeństwo.",
        "Najzdrowszy plan zwykle nie opiera się na jednym produkcie. Może łączyć poduszkę finansową, konto emerytalne, inwestycje, polisę ochronną, polisę na życie i dożycie oraz uporządkowane zobowiązania. Ważne, aby każdy element miał własną rolę."
      ]
    },
    {
      heading: "Co sprawdzić przed podpisaniem umowy",
      lead: "W produktach długoterminowych najdroższe błędy nie wychodzą w pierwszym miesiącu, tylko po kilku latach.",
      body: [
        "Przed decyzją trzeba przeczytać OWU, kartę produktu, informację o kosztach, zasady premii, wartość wykupu i wyłączenia odpowiedzialności. Warto poprosić o pokazanie kilku scenariuszy: utrzymanie składki do końca, waloryzacja, brak waloryzacji, wcześniejsza rezygnacja oraz śmierć ubezpieczonego w trakcie umowy.",
        "Szczególnie ważne jest rozróżnienie kwoty gwarantowanej od kwot dodatkowych. Premie mogą podwyższać świadczenie, ale są przyznawane na zasadach opisanych w umowie. Nie należy traktować ich jak pewnego zysku w każdej sytuacji.",
        "Dobra rozmowa powinna zakończyć się prostą odpowiedzią: po co jest ta polisa, ile kosztuje dzisiaj, co może kosztować w przyszłości, co stanie się przy wcześniejszej rezygnacji i kto dostanie pieniądze, gdyby ubezpieczony zmarł przed końcem umowy."
      ],
      bullets: [
        "sprawdź gwarantowaną sumę i prognozowane premie osobno",
        "zapytaj o wartość wykupu w kolejnych latach",
        "porównaj składkę bez waloryzacji i z waloryzacją",
        "ustal uposażonych i aktualizuj ich po zmianach rodzinnych"
      ]
    }
  ],
  checklistTitle: "Lista przed rozpoczęciem oszczędzania",
  checklist: [
    "policz, ile możesz odkładać miesięcznie bez napięcia w budżecie",
    "ustal, czy pieniądze mają być potrzebne dokładnie przy emeryturze czy wcześniej",
    "sprawdź, jaką ochronę życia ma już rodzina",
    "zostaw osobną poduszkę finansową na nieprzewidziane wydatki",
    "porównaj wariant Konserwatywny i Umiarkowany",
    "poproś o scenariusz wcześniejszego wykupu i zakończenia umowy"
  ],
  mistakesTitle: "Najczęstsze błędy przy planie emerytalnym",
  mistakes: [
    "wybór zbyt wysokiej składki bez rezerwy w budżecie",
    "traktowanie polisy długoterminowej jak krótkiej lokaty",
    "pomijanie wartości wykupu i konsekwencji wcześniejszej rezygnacji",
    "mylenie kwoty gwarantowanej z premiami, które zależą od warunków umowy",
    "brak aktualizacji uposażonych po zmianach rodzinnych",
    "odkładanie decyzji o 5-10 lat, mimo że mniejsza regularna składka byłaby możliwa już dziś"
  ],
  faqs: [
    {
      question: "Czy polisa na życie i dożycie to produkt emerytalny?",
      answer:
        "Może pełnić funkcję elementu planu emerytalnego, bo pozwala budować kapitał na określony moment w przyszłości i jednocześnie daje ochronę życia. Nie jest jednak jedynym narzędziem emerytalnym i trzeba ją porównać z budżetem, płynnością oraz innymi formami oszczędzania."
    },
    {
      question: "Czy pieniądze są gwarantowane?",
      answer:
        "W produkcie występuje gwarantowana suma lub gwarantowana wartość świadczenia wskazana w dokumentach. Premie i kwoty dodatkowe zależą od zasad produktu i warunków opisanych w OWU, dlatego trzeba rozdzielać część gwarantowaną od potencjalnych dodatków."
    },
    {
      question: "Czy można zrezygnować wcześniej?",
      answer:
        "Takie produkty przewidują zasady wykupu, ale wcześniejsza rezygnacja może być finansowo niekorzystna. Przed podpisaniem umowy warto poprosić o tabelę wartości wykupu i sprawdzić, co dzieje się po kilku latach płacenia składki."
    },
    {
      question: "Czy lepszy jest wariant Konserwatywny czy Umiarkowany?",
      answer:
        "Konserwatywny zwykle lepiej pasuje do osób, które cenią przewidywalność. Umiarkowany może być interesujący dla osób akceptujących większą zmienność i dłuższy horyzont. Decyzja powinna wynikać z wieku, czasu do emerytury, celu i tolerancji na ryzyko."
    }
  ],
  verificationNote:
    "Parametry produktowe w tym poradniku sprawdzono na podstawie materiałów PZU dostępnych 30 maja 2026 r. Treść ma charakter edukacyjny i nie zastępuje OWU, KID, karty produktu, indywidualnej oferty ani analizy wymagań i potrzeb klienta.",
  sources: [
    {
      label: "Oficjalna strona produktu PZU",
      href: "https://www.pzu.pl/dla-ciebie-i-rodziny/ubezpieczenie-na-zycie-i-zdrowie/perspektywa-na-przyszlosc",
      desc: "Opis ubezpieczenia PZU Perspektywa na Przyszłość oraz aktualne dokumenty produktowe."
    },
    {
      label: "OWU PZU Perspektywa na Przyszłość",
      href: "https://www.pzu.pl/_fileserver/item/1568780",
      desc: "Ogólne warunki ubezpieczenia: definicje, zakres, premie, wykup, wyłączenia i zasady umowy."
    },
    {
      label: "Dokument KID",
      href: "https://www.pzu.pl/_fileserver/item/1552363",
      desc: "Kluczowe informacje o produkcie, kosztach, ryzykach i możliwych scenariuszach."
    },
    {
      label: "eZUS / PUE ZUS",
      href: "https://www.zus.pl/ezus/logowanie",
      desc: "Miejsce, w którym klient może sprawdzić swoje dane i prognozy emerytalne w systemie ZUS."
    }
  ],
  related: [
    { label: "Kalkulator życia i dożycia", href: "/kalkulator-ubezpieczenia-na-zycie-i-dozycie/", desc: "Sprawdź orientacyjną składkę i kapitał" },
    {
      label: "Ubezpieczenie na życie po 60-tce",
      href: "/poradnik/ubezpieczenie-na-zycie-po-60/",
      desc: "Jakie warianty ochrony zostają po sześćdziesiątce"
    },
    { label: "Proces zakupu polisy", href: "/poradnik/proces-zakupu-polisy/", desc: "Zobacz etapy od kalkulatora do umowy" }
  ]
};

const mortgageGuidePage: GuidePage = {
  slug: "poradnik/ubezpieczenie-na-zycie-pod-kredyt-hipoteczny",
  datePublished: "2026-06-06",
  dateModified: "2026-08-12",
  label: "Polisa pod kredyt",
  kicker: "Poradnik kredytowy",
  title: "Polisa pod kredyt hipoteczny: cesja, bank i suma",
  copy:
    "Kredyt hipoteczny często oznacza rozmowę o polisie na życie. Najważniejsze pytanie nie brzmi jednak: czy wybrać najtańszą składkę, tylko czy polisa spełnia wymagania banku i realnie zabezpiecza rodzinę.",
  image: guideProcessImage,
  calculatorHref: "/kalkulator-ubezpieczenia-na-zycie-pod-kredyt/",
  calculatorLabel: "Policz polisę pod kredyt",
  defaultProduct: "mortgage",
  summary: [
    "bank może wymagać zabezpieczenia kredytu, ale szczegóły zależą od umowy, decyzji banku i oferty",
    "polisa zewnętrzna z cesją może być alternatywą, jeśli bank zaakceptuje jej warunki",
    "suma ochrony zwykle powinna uwzględniać saldo kredytu, udział kredytobiorców i potrzeby rodziny",
    "przy porównaniu liczy się nie tylko składka, ale też zakres, wyłączenia, okres i formalności cesji"
  ],
  sections: [
    {
      heading: "Czy bank może wymagać polisy na życie?",
      lead: "Przy kredycie hipotecznym bank ocenia ryzyko spłaty i może oczekiwać dodatkowych zabezpieczeń. Trzeba jednak odróżnić wymóg zabezpieczenia od automatycznego obowiązku kupienia konkretnej polisy w banku.",
      body: [
        "W praktyce bank może zaproponować polisę w pakiecie z kredytem albo wskazać warunki, jakie musi spełnić zewnętrzne ubezpieczenie. Dlatego pierwszym krokiem jest sprawdzenie dokumentów kredytowych: decyzji, formularza informacyjnego, umowy i listy wymagań wobec polisy.",
        "UOKiK opisuje zakaz sprzedaży wiązanej w kredycie hipotecznym i rozróżnia go od sprzedaży łączonej. To ważne, bo klient powinien rozumieć, kiedy bank oferuje produkt dodatkowy, a kiedy określa warunek bezpieczeństwa kredytu.",
        "Bezpieczna zasada jest prosta: nie zakładamy z góry, że polisa musi być bankowa albo że każda polisa zewnętrzna zostanie przyjęta. Najpierw sprawdzamy wymagania banku, potem dobieramy ochronę i dokumenty pod te wymagania."
      ],
      bullets: [
        "sprawdź, czy bank wymaga samego zabezpieczenia, czy konkretnego produktu",
        "poproś o warunki akceptacji polisy zewnętrznej",
        "porównaj koszt polisy z ewentualną zmianą marży lub prowizji",
        "zapisz, od kiedy polisa ma działać i jakie dokumenty trzeba dostarczyć"
      ]
    },
    {
      heading: "Polisa bankowa czy zewnętrzna polisa z cesją",
      lead: "Polisa proponowana przy kredycie bywa wygodna, ale wygoda nie zawsze oznacza najlepsze dopasowanie do rodziny.",
      body: [
        "Polisa bankowa może być szybka organizacyjnie, bo dokumenty są znane bankowi. Zewnętrzna polisa może dawać większą elastyczność: wybór sumy, okresu, dodatków, uposażonych i ewentualnej nadwyżki ponad saldo kredytu.",
        "Porównanie powinno objąć całość kosztu kredytu i ochrony, nie tylko miesięczną składkę. Czasem bankowy produkt obniża marżę, ale sama polisa ma węższy zakres. Innym razem zewnętrzna polisa jest korzystniejsza, ale bank wymaga cesji, potwierdzenia opłacenia składki i zgodności sumy.",
        "Najlepsza decyzja zależy od sytuacji: wieku, zdrowia, liczby kredytobiorców, wysokości kredytu, dochodów, dzieci i planów spłaty. Dlatego polisa pod kredyt powinna chronić nie tylko bank, ale też ludzi, którzy zostaną z mieszkaniem i zobowiązaniami."
      ],
      bullets: [
        "sprawdź sumę, okres i wyłączenia w obu wariantach",
        "ustal, czy po spłacie kredytu polisa nadal ma sens dla rodziny",
        "porównaj koszt razem z marżą kredytu",
        "zapytaj, czy bank akceptuje cesję z wybranego ubezpieczenia"
      ]
    },
    {
      heading: "Jak działa cesja polisy na bank",
      lead: "Cesja oznacza przeniesienie lub wskazanie uprawnienia banku do świadczenia w zakresie zabezpieczenia kredytu.",
      body: [
        "Jeśli dojdzie do zdarzenia objętego polisą, bank może otrzymać świadczenie do wysokości zabezpieczonego zobowiązania. To pomaga spłacić kredyt albo jego część. Jeśli suma polisy jest wyższa niż aktualne zadłużenie, nadwyżka może trafić do uposażonych zgodnie z dokumentami.",
        "Szczegóły zawsze wynikają z dokumentów banku, polisy i potwierdzenia cesji. Dlatego warto sprawdzić, kto jest uprawniony, w jakiej kolejności wypłacane są pieniądze i co dzieje się po nadpłacie, refinansowaniu albo całkowitej spłacie kredytu.",
        "Po zamknięciu kredytu dobrze jest dopilnować zwolnienia cesji i aktualizacji uposażonych. Inaczej polisa może nadal wyglądać formalnie jak produkt pod kredyt, mimo że realna potrzeba zmieniła się na ochronę rodziny."
      ],
      bullets: [
        "ustal, czy cesja obejmuje całą sumę, czy tylko część odpowiadającą kredytowi",
        "sprawdź procedurę zwolnienia cesji po spłacie",
        "zaktualizuj uposażonych po zmianie kredytu lub sytuacji rodzinnej",
        "przechowuj potwierdzenie cesji razem z polisą"
      ]
    },
    {
      heading: "Na jaką sumę ustawić polisę pod kredyt",
      lead: "Punktem wyjścia zwykle jest saldo kredytu, ale to nie zawsze wystarczy.",
      body: [
        "Jeżeli celem jest wyłącznie zabezpieczenie banku, suma może odpowiadać saldu kredytu albo kwocie wymaganej przez bank. Jeżeli celem jest także ochrona rodziny, warto doliczyć rezerwę na koszty życia, przeprowadzkę, utrzymanie dzieci albo okres, w którym drugi kredytobiorca układa finanse od nowa.",
        "Przy dwóch kredytobiorcach trzeba zastanowić się, czy każdy ma osobną polisę, czy jedna osoba jest głównym żywicielem rodziny, oraz co stanie się z ratą po utracie jednego dochodu. Równe udziały w kredycie nie zawsze oznaczają równe ryzyko finansowe.",
        "Warto też pamiętać, że saldo kredytu z czasem maleje. Przy stałej sumie rodzina może dostać większą nadwyżkę po latach spłaty. Przy sumie dopasowywanej do salda składka lub zakres mogą wyglądać inaczej. To trzeba policzyć przed wyborem wariantu."
      ],
      bullets: [
        "saldo kredytu lub kwota wymagana przez bank",
        "dochód osoby, której zabraknięcie najmocniej uderzy w budżet",
        "koszty życia rodziny przez 12-36 miesięcy",
        "planowane nadpłaty, refinansowanie i zmiany rat"
      ]
    },
    {
      heading: "Jaki okres ochrony wybrać",
      lead: "Okres polisy powinien pasować do momentu, w którym kredyt jest realnym ciężarem dla rodziny.",
      body: [
        "Najprostszy wariant to ochrona na cały okres kredytu lub na pozostały czas spłaty. Przy wysokiej racie i małej poduszce finansowej to często najbardziej zrozumiałe rozwiązanie.",
        "Czasem wystarczy okres krótszy, jeśli rodzina ma inne aktywa, planuje szybką nadpłatę, sprzedaje nieruchomość albo kredyt będzie za kilka lat niewielki. Z drugiej strony zbyt krótka polisa może wygasnąć wtedy, gdy saldo wciąż jest wysokie.",
        "Trzeba też uwzględnić limity wieku i maksymalny okres produktu. Im starszy klient i im dłuższy okres, tym bardziej cena zależy od zdrowia, ankiety i decyzji ubezpieczyciela."
      ],
      bullets: [
        "dopasuj okres do pozostałej spłaty kredytu",
        "sprawdź maksymalny wiek końca ochrony",
        "nie skracaj okresu tylko po to, żeby obniżyć składkę",
        "wracaj do polisy po nadpłacie albo refinansowaniu"
      ]
    },
    {
      heading: "Co sprawdzić przed podpisaniem",
      lead: "Najtańsza składka nie pomoże, jeśli bank odrzuci dokumenty albo rodzina zostanie z niewystarczającą ochroną.",
      body: [
        "Przed podpisaniem warto sprawdzić OWU, wyłączenia odpowiedzialności, początek ochrony, zasady opłacania składki, dokument cesji, wymagania banku i to, czy polisa obejmuje dokładnie osobę lub osoby wskazane w kredycie.",
        "Szczególnie ważne są sytuacje nietypowe: działalność gospodarcza, praca fizyczna, choroby w historii, palenie, sport, wysoka suma ubezpieczenia albo kredyt na dwie osoby. Każdy z tych elementów może wpływać na ankietę, cenę lub decyzję.",
        "Dobra rozmowa kończy się jasną odpowiedzią na trzy pytania: czy bank przyjmie dokumenty, czy rodzina będzie miała pieniądze po spłacie zobowiązania, oraz co trzeba zrobić po zmianie kredytu."
      ],
      bullets: [
        "czy suma i okres spełniają wymagania banku",
        "czy cesja i uposażeni są ustawieni prawidłowo",
        "kiedy zaczyna się ochrona i co dzieje się przy opóźnieniu składki",
        "czy zakres obejmuje tylko śmierć, czy także dodatki zdrowotne"
      ]
    }
  ],
  checklistTitle: "Co przygotować przed rozmową o polisie pod kredyt",
  checklist: [
    "kwotę kredytu, saldo albo planowaną kwotę finansowania",
    "okres kredytu i przewidywany termin uruchomienia",
    "nazwę banku i warunki akceptacji polisy zewnętrznej",
    "informację, ilu jest kredytobiorców i kto wnosi główny dochód",
    "orientacyjne koszty życia rodziny i liczbę osób zależnych",
    "istniejące polisy grupowe, bankowe lub indywidualne"
  ],
  mistakesTitle: "Najczęstsze błędy przy polisie do kredytu",
  mistakes: [
    "wybranie polisy tylko dlatego, że jest najtańsza w pierwszym miesiącu",
    "ustawienie sumy dokładnie pod kredyt, bez żadnej rezerwy dla rodziny",
    "brak sprawdzenia, czy bank zaakceptuje zewnętrzną polisę i cesję",
    "mylenie uposażenia z cesją na bank",
    "brak aktualizacji polisy po nadpłacie, refinansowaniu albo spłacie kredytu",
    "założenie, że polisa bankowa zawsze jest najlepsza albo zawsze najgorsza"
  ],
  faqs: [
    {
      question: "Czy polisa na życie pod kredyt musi być kupiona w banku?",
      answer:
        "Nie warto tego zakładać bez sprawdzenia dokumentów. Bank może wymagać zabezpieczenia i określić warunki polisy, ale często możliwa jest polisa zewnętrzna z cesją, jeśli spełnia wymagania banku. Decydują konkretne warunki kredytu i akceptacja dokumentów przez bank."
    },
    {
      question: "Czy cesja oznacza, że rodzina nic nie dostanie?",
      answer:
        "Nie zawsze. Przy cesji bank może otrzymać świadczenie do wysokości zabezpieczonego zadłużenia. Jeśli suma polisy jest wyższa niż dług i dokumenty przewidują wypłatę nadwyżki, pozostała część może trafić do uposażonych."
    },
    {
      question: "Czy suma polisy powinna równać się kredytowi?",
      answer:
        "To dobry punkt wyjścia, ale przy rodzinie często warto doliczyć rezerwę na koszty życia, utratę dochodu i spokojne uporządkowanie finansów po śmierci kredytobiorcy."
    },
    {
      question: "Co zrobić po spłacie kredytu?",
      answer:
        "Warto poprosić bank o zwolnienie cesji, sprawdzić dokumenty polisy i zaktualizować uposażonych. Potem można zdecydować, czy polisa nadal ma chronić rodzinę, czy trzeba zmienić sumę, okres lub zakres."
    }
  ],
  verificationNote:
    "Poradnik ma charakter edukacyjny i nie zastępuje umowy kredytu, decyzji banku, OWU, indywidualnej oferty ani analizy wymagań i potrzeb klienta. Stan treści sprawdzono 6 czerwca 2026 r. na podstawie publicznych materiałów UOKiK i KNF.",
  sources: [
    {
      label: "UOKiK - ustawa o kredycie hipotecznym",
      href: "https://finanse.uokik.gov.pl/chf/ustawa-o-kredycie-hipotecznym/",
      desc: "Omówienie zasad kredytu hipotecznego, obowiązków informacyjnych oraz sprzedaży wiązanej i łączonej."
    },
    {
      label: "KNF - Droga do kredytu",
      href: "https://www.knf.gov.pl/knf/pl/komponenty/img/droga_do_kredytu_30336.pdf",
      desc: "Poradnik klienta dotyczący procesu kredytu hipotecznego, zabezpieczeń oraz dokumentów, w tym polisy i cesji."
    }
  ],
  related: [
    {
      label: "Kalkulator polisy pod kredyt",
      href: "/kalkulator-ubezpieczenia-na-zycie-pod-kredyt/",
      desc: "Dopasuj sumę do salda kredytu"
    },
    { label: "Jak działa ochrona", href: "/poradnik/jak-dziala-ochrona/", desc: "Suma, uposażeni, cesja i wypłata" },
    {
      label: "Polisa z banku czy własna",
      href: "/poradnik/polisa-z-banku-czy-wlasna/",
      desc: "Porównanie kosztów razem z marżą kredytu"
    },
    {
      label: "Polisa na 500 tys. zł",
      href: "/poradnik/ubezpieczenie-na-zycie-500-tys-cena/",
      desc: "Ile kosztuje suma równa typowemu saldu kredytu"
    }
  ]
};

// Kwoty w tym artykule pochodzą z modelu stawek kalkulatora (src/calculators/rates.ts,
// computeOchronne) — dzięki temu tekst i kalkulator zawsze pokazują te same liczby.
// Przy zmianie modelu stawek zaktualizuj tabele i dateModified.
const costGuidePage: GuidePage = {
  slug: "poradnik/ile-kosztuje-ubezpieczenie-na-zycie",
  label: "Ile kosztuje ubezpieczenie na życie",
  kicker: "Poradnik cenowy",
  title: "Ile kosztuje ubezpieczenie na życie w 2026 roku?",
  copy:
    "Najkrótsza uczciwa odpowiedź: od kilkudziesięciu do kilkuset złotych miesięcznie. Konkretna składka zależy głównie od wieku, sumy ubezpieczenia, palenia i zakresu ochrony. Poniżej pokazujemy przykładowe kwoty z naszego kalkulatora i to, co realnie liczą użytkownicy.",
  image: guideChooseImage,
  datePublished: "2026-08-12",
  dateModified: "2026-08-12",
  calculatorHref: "/kalkulator-ubezpieczenia-na-zycie/",
  calculatorLabel: "Policz swoją składkę",
  defaultProduct: "life",
  summary: [
    "35-latek zapłaci ok. 141 zł/mies. za 300 tys. zł ochrony na 20 lat (widełki 127–162 zł)",
    "cena najmocniej rośnie z wiekiem wejścia i sumą; palenie dodaje ok. 40% do składki",
    "mediana składki ze 130 realnych kalkulacji wariantu ochronnego w naszym serwisie to ok. 245 zł/mies.",
    "wszystkie kwoty to orientacja z kalkulatora — finalną składkę potwierdza doradca po krótkiej ankiecie"
  ],
  sections: [
    {
      heading: "Od czego zależy cena ubezpieczenia na życie",
      lead: "Towarzystwo wycenia przede wszystkim ryzyko wypłaty. Im dłużej i im większą kwotę ma chronić, tym wyższa składka — reszta to szczegóły, które da się świadomie ustawić.",
      body: [
        "Cztery czynniki robią większość różnicy w cenie. Pierwszy to wiek wejścia: składkę ustala się na podstawie wieku w dniu zawarcia umowy, więc każdy rok zwłoki podnosi stawkę startową. Drugi to suma ubezpieczenia, czyli kwota dla bliskich — zależność jest prawie liniowa, podwojenie sumy niemal podwaja składkę. Trzeci to papierosy (również e-papierosy): u palących stawki są wyraźnie wyższe. Czwarty to zakres, czyli umowy dodatkowe dołączone do ochrony życia.",
        "Mniejszy, ale zauważalny wpływ mają okres ochrony, stan zdrowia i historia chorób (weryfikowane w ankiecie medycznej), a przy wysokich sumach także zawód i sporty wysokiego ryzyka. W niektórych produktach znaczenie ma również to, czy polisa jest indywidualna, czy grupowa.",
        "Ważne rozróżnienie: kalkulator — nasz czy jakikolwiek inny — pokazuje orientacyjną składkę dla typowego profilu. Ostateczna kwota pojawia się po ankiecie medycznej i ocenie ryzyka, dlatego wszystkie liczby w tym artykule traktuj jako punkt wyjścia do rozmowy, nie jako ofertę."
      ],
      bullets: [
        "wiek wejścia — składka ustalana na dzień zawarcia umowy",
        "suma ubezpieczenia — zależność niemal liniowa",
        "palenie — w naszym modelu ok. +40% do składki",
        "zakres umów dodatkowych — od kilku do kilkuset złotych różnicy"
      ]
    },
    {
      heading: "Przykładowe składki według wieku",
      lead: "Scenariusz bazowy: suma 300 tys. zł, ochrona na 20 lat, osoba niepaląca, sama umowa główna na wypadek śmierci. Kwoty z naszego kalkulatora, zaokrąglone.",
      body: [
        "25-latek zapłaci ok. 81 zł miesięcznie (widełki 73–93 zł), 30-latek ok. 106 zł (96–122 zł), a 35-latek ok. 141 zł (127–162 zł). To poziom, przy którym pełnowartościowa ochrona rodziny kosztuje tyle, co abonament komórkowy z internetem.",
        "Po czterdziestce stawki rosną szybciej: 40-latek zapłaci ok. 192 zł (173–221 zł), 45-latek ok. 269 zł (242–309 zł), 50-latek ok. 377 zł (339–434 zł), a 55-latek ok. 534 zł (481–614 zł) za ten sam zakres. Różnica między wejściem w polisę w wieku 35 i 45 lat to prawie dwukrotność składki — przy tej samej sumie i okresie.",
        "Wniosek praktyczny jest prosty: jeśli ochrona jest potrzebna, odwlekanie decyzji samo w sobie kosztuje. Składki ustalonej przy zawarciu umowy późniejsze urodziny już nie podnoszą."
      ],
      bullets: [
        "25 lat — ok. 81 zł/mies. (73–93 zł)",
        "30 lat — ok. 106 zł/mies. (96–122 zł)",
        "35 lat — ok. 141 zł/mies. (127–162 zł)",
        "40 lat — ok. 192 zł/mies. (173–221 zł)",
        "45 lat — ok. 269 zł/mies. (242–309 zł)",
        "50 lat — ok. 377 zł/mies. (339–434 zł)"
      ]
    },
    {
      heading: "Ile kosztuje polisa na 100, 300, 500 tysięcy i milion złotych",
      lead: "Ten sam 35-latek, ochrona na 20 lat, bez palenia. Zmienia się tylko suma ubezpieczenia — czyli kwota, którą dostaliby bliscy.",
      body: [
        "Suma 100 tys. zł to ok. 59 zł miesięcznie, 200 tys. zł — ok. 100 zł, 300 tys. zł — ok. 141 zł. Pół miliona złotych ochrony kosztuje ok. 222 zł miesięcznie (widełki 200–256 zł), 750 tys. zł — ok. 324 zł, a okrągły milion — ok. 427 zł (384–491 zł).",
        "Jak dobrać sumę do potrzeb? Najczęściej sensowny punkt wyjścia to saldo kredytu hipotecznego plus 2–3 roczne pensje na czas, w którym rodzina musiałaby przeorganizować życie. Dla rodziny z kredytem 400 tys. zł i dochodem 8 tys. zł netto miesięcznie wychodzi właśnie okolica pół miliona — stąd popularność tej sumy.",
        "Zaniżanie sumy, żeby zbić składkę, to najdroższa forma oszczędzania: polisa na 100 tys. zł przy kredycie na 400 tys. zł chroni tylko ćwierć problemu. Lepiej dopasować zakres i okres, a sumę zostawić na poziomie realnej potrzeby."
      ],
      bullets: [
        "100 tys. zł — ok. 59 zł/mies. (53–68 zł)",
        "200 tys. zł — ok. 100 zł/mies. (90–115 zł)",
        "300 tys. zł — ok. 141 zł/mies. (127–162 zł)",
        "500 tys. zł — ok. 222 zł/mies. (200–256 zł)",
        "1 mln zł — ok. 427 zł/mies. (384–491 zł)"
      ]
    },
    {
      heading: "Palenie, zakres i okres — co naprawdę zmienia składkę",
      lead: "Trzy dźwignie, o których warto wiedzieć przed rozmową z doradcą: jedna kosztuje dużo, druga to świadomy wybór, trzecia zaskakująco mało zmienia.",
      body: [
        "Palenie: w naszym modelu 35-latek niepalący płaci ok. 141 zł, palący — ok. 197 zł za identyczną ochronę. U 45-latka różnica rośnie z 269 do 376 zł. To ok. 40% dopłaty co miesiąc przez cały okres umowy; część towarzystw po 12 miesiącach od rzucenia palenia traktuje klienta jak niepalącego — warto o to zapytać.",
        "Zakres: sama umowa główna (śmierć) dla 35-latka to ok. 141 zł. Rozszerzenie o poważne zachorowania podnosi składkę do ok. 204 zł, a pełny pakiet zdrowotny (m.in. pobyt w szpitalu, operacje, leczenie) — do ok. 366 zł. Dodatki bywają bardzo wartościowe, ale wybieraj je pod swoją sytuację, nie „wszystko, co się da\" — omówienie każdego z nich znajdziesz w poradniku o umowach dodatkowych.",
        "Okres ochrony ma najmniejszy wpływ, co wiele osób zaskakuje: ta sama polisa 35-latka na 10 lat kosztuje ok. 127 zł, na 20 lat — 141 zł, na 30 lat — 148 zł miesięcznie. Skracanie okresu, żeby zaoszczędzić kilkanaście złotych, zwykle nie ma sensu — lepiej ustawić okres tak, by pokrywał kredyt i lata, w których dzieci są na utrzymaniu."
      ],
      bullets: [
        "palenie: +40% (35 lat: 141 → 197 zł/mies.)",
        "zakres: śmierć 141 zł, +poważne zachorowania 204 zł, pełny pakiet 366 zł",
        "okres: 10 lat — 127 zł, 20 lat — 141 zł, 30 lat — 148 zł",
        "po 12 miesiącach bez papierosów zapytaj o stawkę dla niepalących"
      ]
    },
    {
      heading: "Co liczą realni użytkownicy naszego kalkulatora",
      lead: "Od maja 2026 roku użytkownicy Ochro.pl wykonali ponad 200 kalkulacji. To dane własne serwisu — anonimowe wyniki z naszego kalkulatora, nie deklaracje z ankiet.",
      body: [
        "W wariancie ochronnym (130 kalkulacji) mediana wyliczonej składki to ok. 245 zł miesięcznie. Połowa użytkowników mieści się poniżej tej kwoty, ale średnia jest znacznie wyższa (ok. 900 zł), bo spora grupa sprawdza wysokie sumy — pół miliona i więcej — oraz pełne pakiety zdrowotne. Skrajne wyniki sięgały kilkunastu tysięcy złotych przy bardzo wysokich sumach dla starszych profili.",
        "W kalkulacjach polisy pod kredyt hipoteczny (65 wyliczeń) mediana to ok. 313 zł miesięcznie — wyżej niż w wariancie ochronnym, bo sumy odpowiadają saldu kredytu, a te w ostatnich latach wyraźnie urosły.",
        "Z tych danych płynie jeden praktyczny wniosek: ludzie nie szukają najtańszej polisy „na papierze\", tylko sprawdzają realne kwoty dla swojej sytuacji — kredytu, rodziny, wieku. Jeśli Twój wynik wydaje się wysoki, zwykle da się go obniżyć zmianą zakresu albo porównaniem ofert kilku towarzystw, bez cięcia sumy."
      ],
      bullets: [
        "wariant ochronny: mediana ok. 245 zł/mies. (130 kalkulacji)",
        "polisa pod kredyt: mediana ok. 313 zł/mies. (65 kalkulacji)",
        "średnią zawyżają wysokie sumy — to nie jest „typowa\" składka",
        "dane własne Ochro.pl, maj–sierpień 2026, wyniki anonimowe"
      ]
    },
    {
      heading: "Jak zapłacić mniej za ten sam poziom ochrony",
      lead: "Składkę obniża się mądrym ustawieniem parametrów i porównaniem ofert — nie cięciem sumy, która jest całym sensem polisy.",
      body: [
        "Po pierwsze: porównuj. Przy identycznej sumie i zbliżonym zakresie różnice składek między towarzystwami sięgają 30–40%. Dwie oferty to za mało, żeby to zobaczyć — dlatego doradca przygotowuje zwykle trzy warianty: tańszy, optymalny i szerszy.",
        "Po drugie: dobierz zakres do ryzyka, które naprawdę chcesz przenieść na towarzystwo. Pełny pakiet zdrowotny potrafi kosztować więcej niż sama ochrona życia; czasem lepszym rozwiązaniem jest umowa główna z jednym lub dwoma dodatkami, które odpowiadają na Twoje realne obawy.",
        "Po trzecie: nie odkładaj decyzji, jeśli ochrona jest potrzebna — stawka wejścia rośnie z każdym rokiem. I po czwarte: jeśli palisz i planujesz rzucić, ustaw w kalendarzu przypomnienie po 12 miesiącach — przejście na stawkę dla niepalących to największa pojedyncza obniżka, jaką można dostać bez zmiany polisy."
      ],
      bullets: [
        "poproś o 3 warianty zamiast jednej oferty — różnice sięgają 30–40%",
        "zakres pod realne ryzyka, nie „wszystko, co się da\"",
        "sumy nie zaniżaj — to ona chroni rodzinę i kredyt",
        "po rzuceniu palenia wróć do tematu stawki"
      ]
    }
  ],
  checklistTitle: "Zanim poprosisz o oferty",
  checklist: [
    "policz orientacyjną składkę w kalkulatorze dla swojego wieku i sumy",
    "ustal sumę: saldo kredytu + 2–3 roczne pensje dla rodziny",
    "dobierz okres do końca kredytu i lat zależności finansowej dzieci",
    "zdecyduj, które umowy dodatkowe naprawdę odpowiadają Twoim obawom",
    "przygotuj informację o paleniu i przewlekłych chorobach — to i tak wyjdzie w ankiecie"
  ],
  mistakesTitle: "Najczęstsze błędy przy patrzeniu na cenę",
  mistakes: [
    "porównywanie samych składek bez sprawdzenia sum, definicji i wyłączeń w OWU",
    "zaniżanie sumy ubezpieczenia, żeby „wyszło taniej\" — polisa przestaje spełniać swoją rolę",
    "kupowanie najszerszego pakietu bez sprawdzenia, co obejmuje i czy jest potrzebny",
    "odkładanie decyzji na kolejny rok — stawka wejścia rośnie z wiekiem",
    "traktowanie wyniku kalkulatora jak oferty — finalną składkę ustala ocena ryzyka"
  ],
  faqs: [
    {
      question: "Ile kosztuje ubezpieczenie na życie na 300 tys. zł?",
      answer:
        "Dla 35-latka bez papierosów, przy ochronie na 20 lat, orientacyjnie ok. 141 zł miesięcznie (widełki 127–162 zł). Dla 45-latka ok. 269 zł. Dokładna kwota zależy od stanu zdrowia i wybranego zakresu — potwierdza ją doradca po krótkiej ankiecie."
    },
    {
      question: "Ile kosztuje polisa na 500 tys. zł?",
      answer:
        "Ok. 222 zł miesięcznie dla 35-latka (widełki 200–256 zł) przy ochronie na 20 lat bez dodatków. Im wyższy wiek wejścia, tym drożej — 45-latek zapłaci niemal dwukrotnie więcej za tę samą sumę."
    },
    {
      question: "O ile palenie podnosi składkę?",
      answer:
        "W naszym modelu o ok. 40%: 35-latek płaci 141 zł zamiast 197 zł, 45-latek 269 zł zamiast 376 zł. Część towarzystw po 12 miesiącach od rzucenia palenia stosuje stawki dla niepalących — warto o to dopytać."
    },
    {
      question: "Czy składka rośnie w trakcie trwania umowy?",
      answer:
        "W polisach terminowych składka jest co do zasady ustalana przy zawarciu umowy i nie rośnie z powodu kolejnych urodzin. Może się zmienić przy indeksacji sumy, zmianie zakresu albo w wariantach odnawialnych — zasady zawsze opisuje OWU konkretnego produktu."
    },
    {
      question: "Czy wynik kalkulatora to oferta?",
      answer:
        "Nie. Kalkulator pokazuje orientacyjną składkę dla typowego profilu. Wiążącą propozycję towarzystwo przedstawia po ankiecie medycznej i ocenie ryzyka. Nasz doradca porównuje wtedy warianty kilku ofert i wskazuje różnice w zakresie."
    }
  ],
  verificationNote:
    "Wszystkie kwoty pochodzą z modelu orientacyjnych stawek kalkulatora Ochro.pl (scenariusze opisane przy każdej tabeli) oraz z anonimowych statystyk kalkulacji wykonanych w serwisie od maja do sierpnia 2026 r. To nie jest oferta handlowa — finalna składka zależy od oceny ryzyka konkretnego towarzystwa.",
  sources: [
    {
      label: "Kalkulator ubezpieczenia na życie",
      href: "/kalkulator-ubezpieczenia-na-zycie/",
      desc: "Policz składkę dla swojego wieku, sumy i zakresu"
    },
    {
      label: "Cennik ubezpieczenia na życie 2026",
      href: "/ubezpieczenie-na-zycie-cennik/",
      desc: "Przykładowe ceny polis w jednym miejscu"
    },
    {
      label: "PZU — ubezpieczenia na życie i zdrowie",
      href: "https://www.pzu.pl/dla-ciebie-i-rodziny/zycie-i-zdrowie",
      desc: "Materiały produktowe towarzystwa, na których opieramy opisy"
    }
  ],
  related: [
    { label: "Cennik ubezpieczenia na życie", href: "/ubezpieczenie-na-zycie-cennik/", desc: "Przykładowe ceny i tabele składek" },
    {
      label: "Polisa na życie dla palących",
      href: "/poradnik/polisa-na-zycie-dla-palacych/",
      desc: "Ile dopłacisz za papierosy i jak to zmienić"
    },
    {
      label: "Jak dobrać sumę ubezpieczenia",
      href: "/poradnik/jak-dobrac-sume-ubezpieczenia-na-zycie/",
      desc: "Przepis na sumę zamiast zgadywania"
    },
    {
      label: "Polisa na 500 tys. zł",
      href: "/poradnik/ubezpieczenie-na-zycie-500-tys-cena/",
      desc: "Cena najczęściej wybieranej sumy ubezpieczenia"
    }
  ]
};

// Kwoty w tym artykule pochodzą z modelu stawek kalkulatora (src/calculators/rates.ts,
// computeOchronne i computeKredyt) oraz z anonimowych statystyk kalkulacji w D1.
// Przy zmianie modelu stawek zaktualizuj tabele i dateModified.
const sum500kGuidePage: GuidePage = {
  slug: "poradnik/ubezpieczenie-na-zycie-500-tys-cena",
  label: "Polisa na 500 tys. zł",
  kicker: "Poradnik sumowy",
  title: "Ubezpieczenie na życie na 500 tys. zł — ile kosztuje i dla kogo ma sens?",
  copy:
    "Pół miliona złotych to suma, która najczęściej wychodzi z prostego rachunku: saldo kredytu plus 2–3 roczne pensje dla rodziny. Sprawdź, ile taka ochrona kosztuje w różnym wieku, czym różni się od 300 tys. i 1 mln zł oraz co suma 500 tys. zł zmienia w formalnościach.",
  image: guideProtectionImage,
  datePublished: "2026-08-15",
  dateModified: "2026-08-15",
  calculatorHref: "/kalkulator-ubezpieczenia-na-zycie/",
  calculatorLabel: "Policz składkę dla 500 tys. zł",
  defaultProduct: "life",
  summary: [
    "35-latek niepalący zapłaci za 500 tys. zł na 20 lat ok. 222 zł/mies. (widełki 200–256 zł)",
    "przejście z 300 na 500 tys. zł kosztuje ok. 81 zł/mies. więcej, a podnosi ochronę o 200 tys. zł",
    "przy sumie powyżej 400 tys. zł PZU stosuje pełną ankietę medyczną zamiast skróconej",
    "wszystkie kwoty to orientacja z kalkulatora — finalną składkę potwierdza ocena ryzyka"
  ],
  sections: [
    {
      heading: "Dlaczego akurat 500 tys. zł to najczęściej rozważana suma",
      lead: "Ta kwota rzadko bierze się z sympatii do okrągłych liczb. Zwykle jest wynikiem rachunku: ile zostaje do spłaty i ile rodzina potrzebuje na przeorganizowanie życia.",
      body: [
        "Standardowy przepis na sumę to saldo zobowiązań plus 2–3 roczne dochody netto. Rodzina z kredytem 320 tys. zł i dochodem 7 tys. zł netto miesięcznie potrzebuje więc ok. 320 tys. zł na kredyt i 170–250 tys. zł na bieżące życie — razem okolice pół miliona. Ta suma nie jest ambitna, tylko policzona.",
        "Drugi powód jest praktyczny. Suma 500 tys. zł mieści się poniżej progu 1 100 000 zł, od którego PZU prosi o kwestionariusz finansowy, a przy wyższych kwotach także o dokumenty dochodowe z dwóch lat. Formalności zostają więc na poziomie ankiety medycznej, bez zaglądania w PIT-y.",
        "Trzeci to limity potrzeby ubezpieczeniowej. PZU odnosi maksymalną sumę do rocznego dochodu netto: 25-krotność przed 30. rokiem życia, 20-krotność w wieku 30–39 lat, 15-krotność w wieku 40–49 i 10-krotność w wieku 50–59. Dla pół miliona wystarczy więc dochód netto od ok. 25 tys. zł rocznie u trzydziestolatka do 50 tys. zł u pięćdziesięciolatka."
      ],
      bullets: [
        "przepis na sumę: saldo kredytu + 2–3 roczne dochody netto",
        "do 1 100 000 zł łącznej sumy PZU nie wymaga kwestionariusza finansowego",
        "limit potrzeby: 20× roczny dochód netto w wieku 30–39 lat, 15× w wieku 40–49",
        "wypłata dla uposażonych jest zwolniona z podatku i nie wchodzi do masy spadkowej"
      ]
    },
    {
      heading: "Ile kosztuje ubezpieczenie na życie na 500 tys. zł według wieku",
      lead: "Scenariusz bazowy: suma 500 tys. zł, osoba niepaląca, sama umowa główna na wypadek śmierci, ochrona na 20 lat. Kwoty z naszego kalkulatora, zaokrąglone.",
      body: [
        "25-latek zapłaci ok. 123 zł miesięcznie (widełki 110–141 zł), 30-latek ok. 165 zł (149–190 zł), a 35-latek ok. 222 zł (200–256 zł) — czyli ok. 7,30 zł dziennie za pół miliona zabezpieczenia.",
        "Po czterdziestce krzywa robi się stroma: 40-latek zapłaci ok. 308 zł (277–354 zł), 45-latek ok. 436 zł (392–501 zł), 50-latek ok. 617 zł, a 55-latek ok. 878 zł. Wejście w tę samą polisę w wieku 45 zamiast 35 lat to niemal dwukrotność składki — na cały okres umowy, bo stawkę ustala wiek polisowy w dniu zawarcia.",
        "Wydłużenie ochrony do 25 lat podnosi składkę o kilka procent: 35-latek zapłaci ok. 229 zł zamiast 222 zł, a 45-latek ok. 450 zł zamiast 436 zł. To tania różnica jak na pięć dodatkowych lat, więc okres warto ustawiać pod realną potrzebę: koniec kredytu i usamodzielnienie się dzieci."
      ],
      bullets: [
        "25 lat — ok. 123 zł/mies. (110–141 zł)",
        "30 lat — ok. 165 zł/mies. (149–190 zł)",
        "35 lat — ok. 222 zł/mies. (200–256 zł)",
        "40 lat — ok. 308 zł/mies. (277–354 zł)",
        "45 lat — ok. 436 zł/mies. (392–501 zł)",
        "50 lat — ok. 617 zł/mies. (555–709 zł), 55 lat — ok. 878 zł/mies."
      ]
    },
    {
      heading: "300 tys., 500 tys. czy 1 mln zł — ile kosztuje każdy kolejny poziom ochrony",
      lead: "Zależność jest niemal liniowa, więc łatwo policzyć, ile realnie kosztuje dołożenie kolejnych 100 czy 200 tys. zł sumy. Poniżej ten sam profil: niepalący, 20 lat ochrony.",
      body: [
        "Dla 35-latka: 300 tys. zł to ok. 141 zł miesięcznie, 400 tys. zł — ok. 181 zł, 500 tys. zł — ok. 222 zł, 600 tys. zł — ok. 263 zł, a 1 mln zł — ok. 427 zł. Każde dodatkowe 100 tys. zł sumy kosztuje w tym wieku ok. 41 zł. Przejście z 300 na 500 tys. zł to więc ok. 81 zł więcej za dwieście tysięcy dodatkowego zabezpieczenia.",
        "U starszych profili ta sama arytmetyka daje wyższe kwoty. 45-latek za 300 tys. zł zapłaci ok. 269 zł, za 500 tys. zł ok. 436 zł, a za milion ok. 854 zł. 30-latek — odpowiednio ok. 106, 165 i 313 zł.",
        "Kiedy 500 tys. zł to za mało, a kiedy za dużo? Za mało, gdy sam kredyt przekracza 450–500 tys. zł, a w domu jest dwoje małych dzieci — wtedy warto liczyć w kierunku 700–800 tys. zł. Za dużo bywa u singla bez zobowiązań i osób na utrzymaniu: tam sensowniejsza jest niższa suma, a pieniądze przesunięte na ochronę zdrowia."
      ],
      bullets: [
        "300 tys. zł (35 lat) — ok. 141 zł/mies.",
        "400 tys. zł (35 lat) — ok. 181 zł/mies.",
        "500 tys. zł (35 lat) — ok. 222 zł/mies.",
        "1 mln zł (35 lat) — ok. 427 zł/mies.",
        "każde kolejne 100 tys. zł sumy to ok. 41 zł/mies. w wieku 35 lat"
      ]
    },
    {
      heading: "Polisa na 500 tys. zł pod kredyt hipoteczny",
      lead: "Przy kredycie suma nie jest kwestią wyboru, tylko odwzorowaniem salda. Jeśli do spłaty zostaje pół miliona, to właśnie tyle powinna wynosić ochrona — z okresem dopasowanym do harmonogramu.",
      body: [
        "W kalkulatorze polisy pod kredyt suma 500 tys. zł na 25 lat kosztuje 35-latka ok. 229 zł miesięcznie (widełki 206–264 zł), 40-latka ok. 318 zł, a 45-latka ok. 450 zł. Przy okresie 30 lat — odpowiednio ok. 234, 324 i 459 zł. To ten sam produkt ochronny co polisa „rodzinna”; zmienia się głównie okres i to, że bank bywa uprawniony z cesji.",
        "Rozszerzenie zakresu ma tu sens częściej niż w polisie czysto rodzinnej. Poważne zachorowania przy sumie 500 tys. zł na 25 lat podnoszą składkę 35-latka z ok. 229 zł do ok. 333 zł, a wariant z niezdolnością do pracy — do ok. 321 zł. Przy kredycie to utrata zdolności zarobkowania, a nie śmierć, najczęściej rozkłada budżet.",
        "Kredyt maleje z każdą ratą, a suma w polisie terminowej pozostaje stała. Po kilkunastu latach ochrona bywa więc wyższa od salda — nadwyżka trafia wtedy do rodziny, a nie do banku. Jeśli celem jest wyłącznie zabezpieczenie kredytu, sumę można obniżać przy kolejnych rocznicach za zgodą PZU."
      ],
      bullets: [
        "500 tys. zł na 25 lat, 35 lat — ok. 229 zł/mies. (206–264 zł)",
        "500 tys. zł na 30 lat, 40 lat — ok. 324 zł/mies.",
        "z poważnymi zachorowaniami (35 lat, 25 lat) — ok. 333 zł/mies.",
        "z niezdolnością do pracy (35 lat, 25 lat) — ok. 321 zł/mies.",
        "suma w polisie terminowej nie maleje razem z saldem kredytu"
      ]
    },
    {
      heading: "Co suma 500 tys. zł zmienia w formalnościach",
      lead: "To jedyny próg, który przy tej kwocie realnie odczujesz: powyżej 400 tys. zł zamiast skróconej ankiety wypełnia się pełną. Reszta formalności zostaje bez zmian.",
      body: [
        "Skrócona ankieta (9 pytań) obowiązuje, gdy suma umowy podstawowej razem z wybranymi dodatkami i czynnymi polisami nie przekracza 400 tys. zł, a wiek wstępu to maksymalnie 45 lat. Pół miliona przekracza ten próg, więc wypełnia się ankietę pełną — 11 pytań zamiast 9. To różnica kilku minut rozmowy, nie osobny etap.",
        "PZU może skierować na badania medyczne; ich zakres rośnie z sumą i wiekiem, a koszt pokrywa towarzystwo. Przy sumach powyżej 50 tys. zł bywa to podstawowy pakiet laboratoryjny (m.in. morfologia, glukoza, cholesterol); szeroki panel z EKG wysiłkowym czy USG jamy brzusznej dotyczy raczej sum rzędu miliona złotych.",
        "Inaczej przy tej sumie wygląda ochrona tymczasowa. Od dnia po złożeniu wniosku i opłaceniu składki do zawarcia umowy PZU chroni na wypadek śmierci wskutek nieszczęśliwego wypadku, ale maksymalnie do 200 tys. zł — nawet jeśli we wniosku wpisano pół miliona. Pełna suma obowiązuje dopiero po zawarciu umowy."
      ],
      bullets: [
        "do 400 tys. zł sumy i 45 lat — ankieta skrócona (9 pytań)",
        "500 tys. zł — ankieta pełna (11 pytań); dodatki LZ, CC i US zawsze wymagają pełnej",
        "ewentualne badania medyczne opłaca PZU, nie klient",
        "ochrona tymczasowa przed zawarciem umowy: maksymalnie 200 tys. zł i tylko z tytułu NW",
        "po ślubie, narodzinach dziecka lub wniosku o hipotekę sumę podniesiesz bez oceny ryzyka o maks. 25%"
      ]
    },
    {
      heading: "Palenie, zakres i realne kalkulacje użytkowników",
      lead: "Przy sumie 500 tys. zł każdy procent dopłaty jest odczuwalny w złotówkach — dlatego akurat tutaj warto wiedzieć, co i o ile podnosi składkę.",
      body: [
        "Palenie to najdroższy pojedynczy czynnik, na który masz wpływ. W naszym modelu 35-latek niepalący płaci za 500 tys. zł na 20 lat ok. 222 zł, palący — ok. 311 zł. U 40-latka to 308 wobec 431 zł, u 45-latka 436 wobec 610 zł. Dopłata sięga więc 90–175 zł miesięcznie. Po 12 miesiącach bez nikotyny warto zapytać o przeliczenie stawki.",
        "Zakres działa proporcjonalnie. Sama ochrona życia na 500 tys. zł to dla 35-latka ok. 222 zł, wariant z poważnymi zachorowaniami ok. 322 zł, a pełny pakiet zdrowotny ok. 578 zł. Dla 45-latka odpowiednio ok. 436, 632 i 1 134 zł. Przy wysokiej sumie głównej dodatki potrafią kosztować więcej niż sama ochrona.",
        "Dla porównania: od maja 2026 roku użytkownicy naszego kalkulatora wykonali 131 wyliczeń wariantu ochronnego i 65 wyliczeń polisy pod kredyt. Mediana składki to odpowiednio ok. 438 zł i ok. 342 zł, a 96 ze 131 kalkulacji ochronnych dało wynik co najmniej na poziomie podstawowej polisy na 500 tys. zł dla 35-latka. To dane własne serwisu — anonimowe wyniki kalkulatora."
      ],
      bullets: [
        "palenie przy 500 tys. zł: 35 lat 222 → 311 zł, 45 lat 436 → 610 zł",
        "zakres (35 lat): śmierć 222 zł, +poważne zachorowania 322 zł, pełny pakiet 578 zł",
        "mediana kalkulacji ochronnych w serwisie: ok. 438 zł/mies. (131 wyliczeń)",
        "mediana kalkulacji pod kredyt: ok. 342 zł/mies. (65 wyliczeń)",
        "dane własne Ochro.pl, maj–sierpień 2026, wyniki anonimowe"
      ]
    }
  ],
  checklistTitle: "Zanim zdecydujesz się na sumę 500 tys. zł",
  checklist: [
    "policz potrzebę: saldo zobowiązań + 2–3 roczne dochody netto",
    "sprawdź w kalkulatorze składkę dla 500 tys. zł przy swoim wieku i okresie",
    "ustaw okres tak, by pokrywał kredyt i lata zależności finansowej dzieci",
    "zdecyduj, czy dokładasz poważne zachorowania lub niezdolność do pracy",
    "przygotuj się na pełną ankietę medyczną — 11 pytań o zdrowie i styl życia"
  ],
  mistakesTitle: "Najczęstsze błędy przy sumie pół miliona",
  mistakes: [
    "obniżanie sumy do 400 tys. zł tylko po to, by uniknąć pełnej ankiety",
    "dobieranie sumy pod wygodną składkę zamiast pod realną potrzebę rodziny",
    "zakładanie, że ochrona tymczasowa obejmuje pełne 500 tys. zł przed zawarciem umowy",
    "pełny pakiet dodatków, który przy tej sumie kosztuje więcej niż sama ochrona życia",
    "odkładanie decyzji o rok — u 40-latka to ok. 20 zł miesięcznie więcej przez cały okres"
  ],
  faqs: [
    {
      question: "Ile kosztuje ubezpieczenie na życie na 500 tys. zł?",
      answer:
        "Dla 35-latka niepalącego, przy ochronie na 20 lat i samej umowie na wypadek śmierci, ok. 222 zł miesięcznie (widełki 200–256 zł). 45-latek zapłaci ok. 436 zł, a 25-latek ok. 123 zł. Kwoty pochodzą z naszego kalkulatora — finalną składkę potwierdza PZU po ocenie ryzyka."
    },
    {
      question: "Czy przy sumie 500 tys. zł potrzebne są badania lekarskie?",
      answer:
        "Nie zawsze. Podstawą jest ankieta medyczna — przy tej sumie pełna, czyli 11 pytań. PZU może skierować na badania, których zakres rośnie z sumą i wiekiem; szeroki panel dotyczy raczej sum rzędu miliona złotych. Koszt badań pokrywa towarzystwo."
    },
    {
      question: "Czy 500 tys. zł to wystarczająca suma ubezpieczenia?",
      answer:
        "Zależy od zobowiązań i liczby osób na utrzymaniu. Przy kredycie do ok. 350 tys. zł i jednym–dwojgu dzieci zwykle tak. Przy hipotece powyżej 450 tys. zł warto policzyć wariant 700–800 tys. zł."
    },
    {
      question: "O ile droższa jest polisa na 500 tys. zł niż na 300 tys. zł?",
      answer:
        "Dla 35-latka na 20 lat to ok. 81 zł miesięcznie więcej — 222 zł zamiast 141 zł. Każde kolejne 100 tys. zł sumy kosztuje w tym wieku ok. 41 zł. U 45-latka różnica między 300 a 500 tys. zł to ok. 167 zł."
    },
    {
      question: "Czy suma 500 tys. zł wymaga dokumentów o dochodach?",
      answer:
        "Nie. Kwestionariusz finansowy PZU stosuje dopiero powyżej 1 100 000 zł łącznej sumy z wniosku i czynnych polis, a dokumenty dochodowe z dwóch lat — powyżej 1 600 000 zł. Obowiązuje natomiast limit potrzeby ubezpieczeniowej: krotność rocznego dochodu netto zależna od wieku."
    }
  ],
  verificationNote:
    "Wszystkie kwoty składek pochodzą z modelu orientacyjnych stawek kalkulatora Ochro.pl (scenariusze opisane przy każdym zestawieniu), a statystyki kalkulacji z anonimowych danych serwisu za okres maj–sierpień 2026 r. Parametry produktowe opisano na podstawie materiałów PZU; wiążące są OWU, KID oraz oferta PZU po ocenie ryzyka. To nie jest oferta handlowa w rozumieniu art. 66 Kodeksu cywilnego.",
  sources: [
    {
      label: "Kalkulator ubezpieczenia na życie",
      href: "/kalkulator-ubezpieczenia-na-zycie/",
      desc: "Policz składkę dla sumy 500 tys. zł i swojego wieku"
    },
    {
      label: "Kalkulator polisy pod kredyt",
      href: "/kalkulator-ubezpieczenia-na-zycie-pod-kredyt/",
      desc: "Wariant z sumą równą saldu kredytu i cesją na bank"
    },
    {
      label: "Cennik ubezpieczenia na życie 2026",
      href: "/ubezpieczenie-na-zycie-cennik/",
      desc: "Przykładowe ceny polis dla różnych sum i wieku"
    },
    {
      label: "PZU — ubezpieczenia na życie i zdrowie",
      href: "https://www.pzu.pl/dla-ciebie-i-rodziny/zycie-i-zdrowie",
      desc: "Materiały produktowe towarzystwa, na których opieramy opisy"
    }
  ],
  related: [
    {
      label: "Ile kosztuje ubezpieczenie na życie",
      href: "/poradnik/ile-kosztuje-ubezpieczenie-na-zycie/",
      desc: "Składki dla wszystkich sum i przedziałów wieku"
    },
    {
      label: "Polisa pod kredyt hipoteczny",
      href: "/poradnik/ubezpieczenie-na-zycie-pod-kredyt-hipoteczny/",
      desc: "Cesja, suma i okres dopasowane do kredytu"
    },
    { label: "Jak wybrać polisę", href: "/poradnik/jak-wybrac-polise/", desc: "Suma, okres i zakres krok po kroku" },
    {
      label: "Jak dobrać sumę ubezpieczenia",
      href: "/poradnik/jak-dobrac-sume-ubezpieczenia-na-zycie/",
      desc: "Sprawdź, czy pół miliona to Twoja liczba"
    }
  ]
};

// Kwoty w tym artykule pochodzą z modelu stawek kalkulatora (src/calculators/rates.ts,
// computeOchronne i computeKredyt) oraz z anonimowych statystyk kalkulacji w D1.
// Przy zmianie modelu stawek zaktualizuj tabele i dateModified.
const sumGuidePage: GuidePage = {
  slug: "poradnik/jak-dobrac-sume-ubezpieczenia-na-zycie",
  label: "Dobór sumy ubezpieczenia",
  kicker: "Poradnik sumowy",
  title: "Jak dobrać sumę ubezpieczenia na życie? Prosty przepis zamiast zgadywania",
  copy:
    "Suma ubezpieczenia to jedyna liczba w polisie, która naprawdę trafia do bliskich. Zamiast zgadywać, policz ją z trzech składników: salda zobowiązań, dwóch–trzech rocznych dochodów netto i kosztów dzieci do samodzielności. Poniżej pokazujemy ten rachunek na trzech przykładach i to, ile każdy z nich kosztuje miesięcznie.",
  image: guideChooseImage,
  datePublished: "2026-08-18",
  dateModified: "2026-08-18",
  calculatorHref: "/kalkulator-ubezpieczenia-na-zycie/",
  calculatorLabel: "Policz składkę dla swojej sumy",
  defaultProduct: "life",
  summary: [
    "przepis na sumę: saldo zobowiązań + 2–3 roczne dochody netto + koszty dzieci do samodzielności",
    "dodatkowe 100 tys. zł sumy kosztuje 35-latka ok. 41 zł/mies., a 45-latka ok. 84 zł/mies.",
    "policzone przykłady: singiel ok. 100 tys. zł (45 zł/mies.), para z kredytem po 450 tys. zł, rodzic 800 tys. zł",
    "gdy budżet nie domyka, tnij zakres i dodatki, nie sumę — to ona odpowiada za cały sens polisy",
    "wszystkie kwoty to orientacja z kalkulatora, a nie oferta — finalną składkę ustala ocena ryzyka"
  ],
  sections: [
    {
      heading: "Przepis na sumę ubezpieczenia: zobowiązania, dochód, dzieci",
      lead: "Sumy nie trzeba wyczuwać. To zwykłe dodawanie trzech pozycji, które da się wypisać na kartce w dziesięć minut.",
      body: [
        "Pierwszy składnik to saldo zobowiązań: kredyt hipoteczny, kredyt gotówkowy, karta, pożyczka rodzinna. Liczy się kwota pozostała do spłaty dzisiaj, a nie kwota, którą kiedyś pożyczyłeś. Sens jest prosty: bliscy mają zostać z mieszkaniem, a nie z ratą, której nie udźwigną z jednej pensji.",
        "Drugi składnik to dwa–trzy roczne dochody netto osoby ubezpieczanej. To pieniądze na czas, w którym rodzina musi przeorganizować życie: znaleźć pracę na pełen etat, zmienić opiekę nad dziećmi, czasem sprzedać lub zamienić mieszkanie. Dwa lata to wariant ostrożny przy dwóch pensjach w domu, trzy — gdy dochód ubezpieczonego jest głównym albo jedynym w rodzinie.",
        "Trzeci składnik dotyczy tylko rodziców: koszt utrzymania i edukacji dzieci do momentu samodzielności. Najprościej policzyć go jako miesięczną kwotę na dziecko pomnożoną przez liczbę lat, które zostały do osiemnastki lub końca studiów. Przy 1 000 zł miesięcznie i dziewięciu latach wychodzi 108 tys. zł na jedno dziecko. Od sumy tych trzech pozycji odejmij oszczędności, które realnie zostałyby w rodzinie, oraz sumy z polis, które już masz — na przykład z grupowej polisy w pracy."
      ],
      bullets: [
        "saldo zobowiązań — kwota pozostała do spłaty, nie kwota pierwotna",
        "2 roczne dochody netto przy dwóch pensjach w domu, 3 przy jednym żywicielu",
        "koszt dzieci: miesięczna kwota × liczba lat do samodzielności",
        "odejmij realne oszczędności i sumy z polis, które już działają",
        "wynik zaokrąglij w górę do pełnych 50 lub 100 tys. zł — dopłata jest niewielka"
      ]
    },
    {
      heading: "Ile kosztuje każde 100 tys. zł sumy ubezpieczenia",
      lead: "Kiedy znasz już wynik rachunku, warto wiedzieć, ile kosztuje jego ostatnie sto tysięcy. Zależność jest niemal liniowa, więc łatwo wycenić każdą korektę.",
      body: [
        "W naszym modelu (ochrona na 20 lat, osoba niepaląca, sama umowa główna na wypadek śmierci) dodatkowe 100 tys. zł sumy kosztuje ok. 21 zł miesięcznie u 25-latka, ok. 30 zł u 30-latka, ok. 41 zł u 35-latka, ok. 58 zł u 40-latka, ok. 84 zł u 45-latka i ok. 120 zł u 50-latka. Ta stawka jest w praktyce stała niezależnie od poziomu sumy: przejście z 300 na 400 tys. zł kosztuje 35-latka tyle samo, co przejście z 700 na 800 tys. zł.",
        "Dlatego zaokrąglanie sumy w górę jest tanie. Jeśli z rachunku wyszło 650 tys. zł, podniesienie do okrągłych 700 tys. zł to dla 35-latka ok. 20 zł miesięcznie więcej (284 zł zamiast 304 zł). Jeśli wyszło 452 tys. zł, warto ustawić 500 tys. zł — kilkanaście złotych różnicy przy jednoczesnym marginesie na inflację.",
        "Na drugim końcu skali działa próg minimalny. Umowę ochronną PZU zawiera się od sumy 50 000 zł, a najniższa składka to 45 zł miesięcznie — u 30-latka ta kwota obejmuje zarówno sumę 50 tys., jak i 90 tys. zł. Schodzenie poniżej stu tysięcy nie daje więc oszczędności, a wyraźnie obniża sens polisy."
      ],
      bullets: [
        "25 lat — ok. 21 zł/mies. za każde 100 tys. zł sumy",
        "30 lat — ok. 30 zł, 35 lat — ok. 41 zł, 40 lat — ok. 58 zł",
        "45 lat — ok. 84 zł, 50 lat — ok. 120 zł",
        "zaokrąglenie 650 → 700 tys. zł u 35-latka to ok. 20 zł/mies.",
        "minimalna suma w polisie ochronnej to 50 000 zł, minimalna składka 45 zł/mies."
      ]
    },
    {
      heading: "Trzy policzone przykłady: singiel, para z kredytem, rodzic",
      lead: "Ten sam przepis daje bardzo różne wyniki w zależności od tego, kto zostaje z rachunkami. Poniżej trzy sytuacje policzone do końca, ze składką z kalkulatora.",
      body: [
        "Singiel, 28 lat, bez kredytu hipotecznego i bez osób na utrzymaniu. Zobowiązania to 25 tys. zł kredytu gotówkowego, dochodu nie musi zastępować nikomu. Zostaje bufor na koszty pochówku, porządkowanie spraw i ewentualne wsparcie rodziców — realnie ok. 100 tys. zł. Taka polisa na 20 lat kosztuje ok. 45 zł miesięcznie, czyli składkę minimalną; podniesienie sumy do 150 tys. zł to ok. 57 zł. Dla singla ważniejsze od wysokiej sumy bywa zabezpieczenie własnego dochodu na wypadek choroby — czyli poważne zachorowania i niezdolność do pracy.",
        "Para z kredytem, 32 i 34 lata, saldo hipoteki 380 tys. zł, dochody 6 000 i 6 500 zł netto, bez dzieci. Kluczowe pytanie brzmi: czy zostająca osoba obsłuży ratę sama? Zwykle nie, więc każde z partnerów ubezpiecza całe saldo plus roczny własny dochód jako poduszkę: 380 tys. + ok. 72–78 tys. zł, czyli po ok. 450 tys. zł na osobę. Przy ochronie na 25 lat to ok. 176 zł miesięcznie (widełki 159–203 zł) dla młodszej osoby i ok. 198 zł (178–227 zł) dla starszej — razem ok. 374 zł za pełne zabezpieczenie mieszkania.",
        "Rodzic, 38 lat, główny żywiciel, dwoje dzieci w wieku 6 i 9 lat, hipoteka 380 tys. zł, dochód 8 000 zł netto. Rachunek: 380 tys. zł zobowiązań + 192 tys. zł (dwa roczne dochody) + ok. 250 tys. zł na dzieci do usamodzielnienia (1 000 zł miesięcznie przez 12 i 9 lat) = ok. 820 tys. zł. Po zaokrągleniu 800 tys. zł na 20 lat, czyli do momentu, gdy młodsze dziecko kończy studia — ok. 427 zł miesięcznie (384–491 zł). Suma 500 tys. zł, którą taka osoba wybrałaby intuicyjnie, pokryłaby kredyt i niecałe dwa lata życia rodziny."
      ],
      bullets: [
        "singiel 28 lat, 100 tys. zł na 20 lat — ok. 45 zł/mies. (składka minimalna)",
        "para 32 i 34 lata, po 450 tys. zł na 25 lat — ok. 176 zł i ok. 198 zł/mies.",
        "rodzic 38 lat, 800 tys. zł na 20 lat — ok. 427 zł/mies. (384–491 zł)",
        "ten sam rodzic przy 700 tys. zł — ok. 376 zł, przy 600 tys. zł — ok. 325 zł",
        "okres ustawiaj do końca kredytu i do samodzielności najmłodszego dziecka"
      ]
    },
    {
      heading: "Kiedy suma jest za wysoka — limity, progi i formalności",
      lead: "Sumy nie da się ustawić dowolnie wysoko. Towarzystwo sprawdza, czy odpowiada ona realnej sytuacji finansowej, a kilka progów zmienia sposób zawierania umowy.",
      body: [
        "Podstawowy limit to tzw. maksimum potrzeby ubezpieczeniowej, liczone jako krotność rocznego dochodu netto: 25-krotność przed 30. rokiem życia, 20-krotność w wieku 30–39 lat, 15-krotność w wieku 40–49, 10-krotność w wieku 50–59 i 7-krotność od 60 lat. Rodzic z przykładu wyżej, z dochodem 96 tys. zł rocznie netto, mieści się z sumą 800 tys. zł spokojnie — górna granica to w jego wieku ok. 1,9 mln zł.",
        "Drugi próg dotyczy ankiety medycznej. Do 400 tys. zł łącznej sumy (umowa podstawowa, wybrane dodatki i czynne polisy) i przy wieku wstępu do 45 lat wystarczy ankieta skrócona — 9 pytań. Powyżej wypełnia się ankietę pełną, czyli 11 pytań. Umowy dodatkowe LZ, CC i US zawsze wymagają pełnej. To różnica kilku minut rozmowy, więc nie warto pod nią przycinać sumy.",
        "Trzeci próg to ocena finansowa. Do 1 100 000 zł łącznej sumy PZU nie prosi o żadne dokumenty dochodowe. Między 1 100 000 a 1 600 000 zł dochodzi kwestionariusz finansowy, a powyżej 1 600 000 zł także dokumenty dochodowe z dwóch lat. Warto też pamiętać o ochronie tymczasowej: między złożeniem wniosku a zawarciem umowy PZU chroni na wypadek śmierci wskutek nieszczęśliwego wypadku, ale maksymalnie do 200 tys. zł — niezależnie od sumy wpisanej we wniosku."
      ],
      bullets: [
        "limit potrzeby: 25× roczny dochód netto do 30 lat, 20× w wieku 30–39, 15× w wieku 40–49",
        "do 400 tys. zł i 45 lat — ankieta skrócona (9 pytań), powyżej pełna (11 pytań)",
        "do 1 100 000 zł sumy bez kwestionariusza finansowego i dokumentów dochodowych",
        "ochrona tymczasowa przed zawarciem umowy: maks. 200 tys. zł i tylko z tytułu wypadku",
        "koszt ewentualnych badań medycznych pokrywa PZU, nie klient"
      ]
    },
    {
      heading: "Suma nie jest ustalona raz na zawsze",
      lead: "Życie zmienia rachunek: kredyt maleje, dzieci dorastają, dochód rośnie. Polisa terminowa daje kilka sposobów na nadążenie za tymi zmianami.",
      body: [
        "Najważniejszy jest mechanizm podwyższenia bez ponownej oceny ryzyka. Po ważnym zdarzeniu życiowym — ślubie, narodzinach lub adopcji dziecka, złożeniu wniosku o kredyt hipoteczny — sumę można podnieść maksymalnie o 25% aktualnej wartości, łącznie do 200 tys. zł, składając wniosek w ciągu trzech miesięcy od zdarzenia. To dobra ścieżka dla osób, których stan zdrowia zmienił się od czasu zawarcia umowy. Dla podwyższonej części sumy obowiązują osobne okresy wyłączeń.",
        "Poza tymi zdarzeniami sumę można podwyższać i obniżać od pierwszej rocznicy polisy, za zgodą PZU i po ocenie ryzyka. Osobnym narzędziem jest indeksacja: coroczne podnoszenie sumy i składki o wskaźnik, dzięki czemu ochrona nie traci realnej wartości przez inflację. Można wybrać wariant lub z niej zrezygnować, ale brak decyzji najpóźniej 14 dni przed rocznicą oznacza wariant standardowy — warto o tym wiedzieć, zanim składka wzrośnie bez zapowiedzi.",
        "Druga strona medalu: suma w polisie terminowej nie maleje razem z saldem kredytu. Po kilkunastu latach ochrona bywa więc wyższa niż dług, a nadwyżka trafia do uposażonych, nie do banku. Świadczenie dla uposażonych jest zwolnione z podatku od spadków i darowizn oraz od podatku od zysków kapitałowych i nie wchodzi do masy spadkowej, więc trafia do wskazanych osób szybciej niż majątek dzielony w postępowaniu spadkowym."
      ],
      bullets: [
        "po ślubie, narodzinach dziecka lub wniosku o hipotekę: +25% sumy bez oceny ryzyka",
        "podwyższenie bez oceny ryzyka to łącznie maks. 200 tys. zł, wniosek w 3 miesiące",
        "zmiana sumy z oceną ryzyka — od pierwszej rocznicy, za zgodą PZU",
        "indeksacja chroni realną wartość sumy; rezygnację trzeba zgłosić 14 dni przed rocznicą",
        "wypłata dla uposażonych jest wolna od podatku i nie wchodzi do masy spadkowej"
      ]
    },
    {
      heading: "Gdy budżet nie domyka: co ciąć zamiast sumy",
      lead: "Najczęstszy błąd polega na tym, że suma wychodzi z rachunku, a potem jest cięta do wygodnej składki. Zwykle da się osiągnąć ten sam efekt inaczej.",
      body: [
        "Zacznij od zakresu — tu są największe pieniądze. Dla 35-latka suma 600 tys. zł na 20 lat kosztuje ok. 263 zł miesięcznie przy samej ochronie życia, ok. 382 zł z poważnymi zachorowaniami i ok. 684 zł w pełnym pakiecie zdrowotnym. Rezygnacja z części dodatków obniża składkę o setki złotych, a suma dla rodziny zostaje nietknięta. Dodatki wybieraj pod konkretne obawy, nie pod zasadę „im więcej, tym lepiej”.",
        "Okres daje niewiele. Ta sama polisa 35-latka na 600 tys. zł kosztuje ok. 238 zł przy dziesięciu latach ochrony, 263 zł przy dwudziestu i 277 zł przy trzydziestu. Skracanie ochrony, żeby zaoszczędzić kilkanaście złotych, kończy się polisą, która wygasa, zanim dzieci się usamodzielnią. Wyraźnie większa dźwignia jest po stronie palenia: 35-latek niepalący płaci za 600 tys. zł ok. 263 zł, palący ok. 368 zł, a 45-latek odpowiednio ok. 520 i 728 zł.",
        "Dla porównania — realne kalkulacje w naszym serwisie. Od maja 2026 roku użytkownicy wykonali 132 wyliczenia wariantu ochronnego i 66 wyliczeń polisy pod kredyt hipoteczny. Mediana wyliczonej składki to odpowiednio ok. 438 zł i ok. 342 zł miesięcznie. To poziom odpowiadający sumom rzędu kilkuset tysięcy złotych z dodatkami, a nie minimalnym polisom — czyli ludzie liczą raczej realną potrzebę niż najtańszy wariant. To dane własne serwisu: anonimowe wyniki kalkulatora, nie deklaracje z ankiet."
      ],
      bullets: [
        "zakres (35 lat, 600 tys. zł): śmierć 263 zł, +poważne zachorowania 382 zł, pełny pakiet 684 zł",
        "okres (35 lat, 600 tys. zł): 10 lat — 238 zł, 20 lat — 263 zł, 30 lat — 277 zł",
        "palenie przy 600 tys. zł: 35 lat 263 → 368 zł, 45 lat 520 → 728 zł",
        "mediana kalkulacji ochronnych w serwisie: ok. 438 zł/mies. (132 wyliczenia)",
        "mediana kalkulacji pod kredyt: ok. 342 zł/mies. (66 wyliczeń), dane maj–sierpień 2026"
      ]
    }
  ],
  checklistTitle: "Policz swoją sumę w pięciu krokach",
  checklist: [
    "wypisz saldo wszystkich zobowiązań — hipoteka, kredyty, karty, pożyczki",
    "pomnóż swój roczny dochód netto przez 2 (dwie pensje w domu) lub 3 (jeden żywiciel)",
    "dolicz koszt dzieci: miesięczna kwota × lata do ich samodzielności",
    "odejmij realne oszczędności i sumy z polis, które już masz, w tym grupowej z pracy",
    "zaokrąglij wynik w górę i sprawdź jego składkę w kalkulatorze dla swojego wieku"
  ],
  mistakesTitle: "Najczęstsze błędy przy doborze sumy",
  mistakes: [
    "dobieranie sumy pod wygodną składkę zamiast pod policzoną potrzebę rodziny",
    "ubezpieczanie tylko kredytu i pominięcie kosztów życia rodziny przez kolejne lata",
    "liczenie pierwotnej kwoty kredytu zamiast salda pozostałego do spłaty",
    "zakładanie, że grupowa polisa z pracy wystarczy — jej suma zwykle pokrywa ułamek potrzeby",
    "ustawianie sumy raz i niewracanie do niej po narodzinach dziecka czy nowym kredycie"
  ],
  faqs: [
    {
      question: "Jak obliczyć sumę ubezpieczenia na życie?",
      answer:
        "Dodaj trzy pozycje: saldo wszystkich zobowiązań, dwa–trzy roczne dochody netto oraz koszt utrzymania dzieci do ich samodzielności. Od wyniku odejmij oszczędności i sumy z polis, które już masz. Wynik zaokrąglij w górę do pełnych 50 lub 100 tys. zł."
    },
    {
      question: "Jaka suma ubezpieczenia na życie jest optymalna przy kredycie hipotecznym?",
      answer:
        "Punktem wyjścia jest całe saldo kredytu, a nie połowa — zwykle osoba, która zostaje, nie obsłuży raty z jednej pensji. Do tego warto dodać co najmniej roczny dochód jako poduszkę. Przy saldzie 380 tys. zł daje to ok. 450 tys. zł na osobę, czyli ok. 176 zł miesięcznie dla 32-latka przy ochronie na 25 lat."
    },
    {
      question: "Czy suma 100 tys. zł wystarczy?",
      answer:
        "Dla osoby bez kredytu i bez osób na utrzymaniu zwykle tak — pokrywa drobne zobowiązania i koszty uporządkowania spraw. Przy hipotece albo dzieciach to za mało: taka suma spłaci część kredytu i nie zostawi nic na życie rodziny. Poniżej 100 tys. zł oszczędność i tak jest pozorna, bo obowiązuje składka minimalna 45 zł miesięcznie."
    },
    {
      question: "Ile kosztuje podniesienie sumy ubezpieczenia o 100 tys. zł?",
      answer:
        "W naszym modelu (20 lat ochrony, osoba niepaląca, sama umowa główna) ok. 21 zł miesięcznie u 25-latka, ok. 41 zł u 35-latka, ok. 84 zł u 45-latka i ok. 120 zł u 50-latka. Stawka jest w praktyce taka sama niezależnie od poziomu, na którym podnosisz sumę."
    },
    {
      question: "Czy sumę ubezpieczenia można później zmienić?",
      answer:
        "Tak. Po ślubie, narodzinach dziecka albo wniosku o kredyt hipoteczny sumę można podnieść o maks. 25% jej wartości (łącznie do 200 tys. zł) bez ponownej oceny ryzyka, składając wniosek w ciągu trzech miesięcy. Poza tym zmiany są możliwe od pierwszej rocznicy za zgodą PZU, a indeksacja co roku podnosi sumę wraz ze składką."
    }
  ],
  verificationNote:
    "Wszystkie kwoty składek pochodzą z modelu orientacyjnych stawek kalkulatora Ochro.pl (scenariusze opisane przy każdym zestawieniu), a statystyki kalkulacji z anonimowych danych serwisu za okres maj–sierpień 2026 r. Przykłady rodzinne są ilustracją metody, a nie rekomendacją dla konkretnej osoby. Parametry produktowe opisano na podstawie materiałów PZU; wiążące są OWU, KID oraz oferta PZU po ocenie ryzyka. To nie jest oferta handlowa w rozumieniu art. 66 Kodeksu cywilnego.",
  sources: [
    {
      label: "Kalkulator ubezpieczenia na życie",
      href: "/kalkulator-ubezpieczenia-na-zycie/",
      desc: "Sprawdź składkę dla policzonej przez siebie sumy"
    },
    {
      label: "Kalkulator polisy pod kredyt",
      href: "/kalkulator-ubezpieczenia-na-zycie-pod-kredyt/",
      desc: "Wariant z sumą równą saldu kredytu i cesją na bank"
    },
    {
      label: "Cennik ubezpieczenia na życie 2026",
      href: "/ubezpieczenie-na-zycie-cennik/",
      desc: "Przykładowe ceny polis dla różnych sum i wieku"
    },
    {
      label: "PZU — ubezpieczenia na życie i zdrowie",
      href: "https://www.pzu.pl/dla-ciebie-i-rodziny/zycie-i-zdrowie",
      desc: "Materiały produktowe towarzystwa, na których opieramy opisy"
    }
  ],
  related: [
    {
      label: "Ile kosztuje ubezpieczenie na życie",
      href: "/poradnik/ile-kosztuje-ubezpieczenie-na-zycie/",
      desc: "Składki dla wszystkich sum i przedziałów wieku"
    },
    {
      label: "Polisa na 500 tys. zł",
      href: "/poradnik/ubezpieczenie-na-zycie-500-tys-cena/",
      desc: "Najczęściej wybierana suma — cena i formalności"
    },
    {
      label: "Polisa pod kredyt hipoteczny",
      href: "/poradnik/ubezpieczenie-na-zycie-pod-kredyt-hipoteczny/",
      desc: "Suma i okres dopasowane do harmonogramu kredytu"
    },
    { label: "Jak wybrać polisę", href: "/poradnik/jak-wybrac-polise/", desc: "Suma, okres i zakres krok po kroku" }
  ]
};

// Kwoty w tym artykule pochodzą z modelu stawek kalkulatora (src/calculators/rates.ts,
// computeOchronne). Progi ankiety, badań i oceny finansowej pochodzą z materiałów PZU
// (functions/api/_chat-knowledge.ts). Przy zmianie modelu stawek zaktualizuj tabele i dateModified.
const noExamGuidePage: GuidePage = {
  slug: "poradnik/ubezpieczenie-na-zycie-bez-badan",
  label: "Polisa bez badań lekarskich",
  kicker: "Poradnik formalny",
  title: "Ubezpieczenie na życie bez badań lekarskich — kiedy wystarczy ankieta?",
  copy:
    "W większości typowych sytuacji polisę na życie zawiera się bez ani jednego skierowania do laboratorium — wystarczy ankieta medyczna wypełniona przy wniosku. To, czy trafisz na badania, rozstrzygają dwa parametry: suma ubezpieczenia i wiek. Poniżej pokazujemy konkretne progi w PZU, orientacyjne składki dla sum mieszczących się w „samej ankiecie\" i to, co naprawdę oznacza zatajenie choroby.",
  image: guideAddonsImage,
  datePublished: "2026-08-22",
  dateModified: "2026-08-22",
  calculatorHref: "/kalkulator-ubezpieczenia-na-zycie/",
  calculatorLabel: "Policz składkę bez podawania danych",
  defaultProduct: "life",
  summary: [
    "skrócona ankieta (9 pytań) wystarcza, gdy suma nie przekracza 400 tys. zł, a wiek wstępu 45 lat",
    "powyżej tych progów jest pełna ankieta (11 pytań), a przy wyższych sumach — badania, które opłaca PZU",
    "300 tys. zł ochrony dla 35-latka to orientacyjnie ok. 141 zł/mies. — cała ta kwota mieści się w progu „bez badań\"",
    "zatajenie choroby, o którą pytano, może kosztować odmowę wypłaty, jeśli śmierć nastąpi w ciągu 3 lat od zawarcia umowy"
  ],
  sections: [
    {
      heading: "Czy da się kupić ubezpieczenie na życie bez badań lekarskich?",
      lead: "Tak — i to nie jest wyjątek, tylko standardowa ścieżka. Badania są dodatkiem uruchamianym przy wyższych sumach, a nie warunkiem wejścia w polisę.",
      body: [
        "Ocena ryzyka w indywidualnym ubezpieczeniu na życie ma trzy poziomy szczegółowości. Najprostszy to samo oświadczenie o stanie zdrowia — kilka zdań we wniosku, bez osobnego kwestionariusza. Drugi to ankieta medyczna: zestaw pytań o choroby, leczenie, leki, palenie, wzrost i wagę oraz zawód, na które odpowiada się przy zawieraniu umowy. Trzeci to badania medyczne, na które towarzystwo kieruje dopiero wtedy, gdy suma albo wiek wychodzą poza rutynowy zakres.",
        "W praktyce ogromna większość umów kończy się na drugim poziomie. Osoba, która chce zabezpieczyć rodzinę kwotą 200–400 tys. zł i ma mniej niż 46 lat, mieści się w kryteriach skróconej ankiety i nie zobaczy żadnego skierowania. Dopiero przy wyższych sumach — pół miliona, milion i więcej — pojawia się pytanie o badania.",
        "Warto rozróżnić dwa pojęcia, które w rozmowach zlewają się w jedno. „Bez badań\" oznacza tylko tyle, że nie jedziesz do punktu pobrań. Nie oznacza, że towarzystwo nie pyta o zdrowie — pyta zawsze, a odpowiedzi z ankiety mają dokładnie taką samą wagę jak wyniki z laboratorium. To ważne przy czytaniu ofert reklamowanych hasłem „bez pytań o zdrowie\": w indywidualnych polisach na życie takie produkty po prostu nie istnieją."
      ],
      bullets: [
        "poziom 1: samo oświadczenie o stanie zdrowia — przy najniższych sumach",
        "poziom 2: ankieta medyczna (9 lub 11 pytań) — ścieżka typowa",
        "poziom 3: badania medyczne — przy wysokich sumach i wyższym wieku",
        "w każdym wariancie towarzystwo pyta o zdrowie; różni się tylko forma weryfikacji"
      ]
    },
    {
      heading: "Kiedy wystarczy skrócona ankieta — progi 400 tys. zł i 45 lat",
      lead: "W indywidualnych ubezpieczeniach na życie PZU granica między krótszą a pełną ankietą jest opisana wprost: liczy się łączna suma ubezpieczenia i wiek wstępu.",
      body: [
        "Skrócona ankieta to 9 pytań. Stosuje się ją, gdy suma umowy podstawowej powiększona o sumy wybranych umów dodatkowych (miesięczna wypłata dla bliskich oraz dodatkowe terminowe na życie) i o sumy Twoich czynnych polis nie przekracza 400 000 zł, a wiek wstępu wynosi maksymalnie 45 lat. Oba warunki muszą być spełnione jednocześnie — 46-latek z sumą 200 tys. zł trafia już na pełną ankietę, podobnie jak 30-latek z sumą 600 tys. zł.",
        "Pełna ankieta to 11 pytań i pojawia się po przekroczeniu któregokolwiek z tych parametrów. To wciąż nie są badania — to dłuższy kwestionariusz. Osobna zasada dotyczy trzech umów dodatkowych: leczenia za granicą, ciężkich chorób i utraty sprawności. Przy każdej z nich pełna ankieta jest wymagana zawsze, niezależnie od sumy umowy głównej. W drugą stronę działa dodatek na złamania kości — ten zawierany jest bez oceny ryzyka.",
        "Kluczowy szczegół, o którym łatwo zapomnieć: sumy się sumują. Jeśli masz już polisę na 250 tys. zł i dokupujesz drugą na 200 tys. zł, do progu liczy się 450 tys. zł, a nie 200 tys. zł. Podobnie liczy się wiek polisowy, który w PZU ustala się według roku kalendarzowego urodzenia, a nie dokładnej daty — osoba urodzona w grudniu 1980 r. przez cały 2026 rok ma wiek polisowy 46 lat.",
        "Jest też ścieżka jeszcze prostsza od ankiety. W ubezpieczeniu na życie i dożycie umowę można zawrzeć na podstawie samych oświadczeń o stanie zdrowia, gdy suma nie przekracza 150 000 zł i nie dokładasz dodatków innych niż leczenie szpitalne, operacje lub złamania. W dożywotnim ubezpieczeniu na życie ten próg to 30 000 zł. Ocena ryzyka dziecka opiera się na ankiecie i dokumentacji medycznej — dzieci nie kieruje się na badania; przy dodatku na ciężkie choroby dziecka ankieta ma tylko 8 pytań."
      ],
      bullets: [
        "skrócona ankieta (9 pytań): suma łączna ≤ 400 000 zł i wiek wstępu ≤ 45 lat",
        "pełna ankieta (11 pytań): po przekroczeniu któregokolwiek progu",
        "dodatki leczenie za granicą, ciężkie choroby, utrata sprawności — zawsze pełna ankieta",
        "samo oświadczenie: na życie i dożycie do 150 000 zł, dożywotnie do 30 000 zł",
        "do progu wliczają się sumy polis, które już masz",
        "dziecko — ankieta i dokumentacja, bez badań"
      ]
    },
    {
      heading: "Ile kosztuje polisa, która mieści się w progu bez badań",
      lead: "Scenariusz: ochrona na 20 lat, osoba niepaląca, sama umowa główna na wypadek śmierci. Kwoty orientacyjne z naszego kalkulatora, zaokrąglone.",
      body: [
        "Dla 35-latka suma 100 tys. zł to ok. 59 zł miesięcznie, 200 tys. zł — ok. 100 zł, 300 tys. zł — ok. 141 zł (widełki 127–162 zł), a 400 tys. zł, czyli dokładnie górna granica skróconej ankiety — ok. 181 zł (163–209 zł). Cały ten przedział mieści się w ścieżce bez badań, a jednocześnie pokrywa większość realnych potrzeb: saldo kredytu plus dwa–trzy roczne dochody dla rodziny.",
        "Wiek zmienia te kwoty mocniej niż cokolwiek innego. Ta sama suma 400 tys. zł kosztuje 30-latka ok. 136 zł miesięcznie, 40-latka ok. 250 zł, a 45-latka ok. 352 zł (317–405 zł). U 46-latka, czyli już poza progiem skróconej ankiety, ta sama polisa to ok. 381 zł. Różnica w składce między 45 a 46 rokiem życia jest niewielka — istotniejsze jest to, że zmienia się formalna ścieżka: zamiast 9 pytań pojawia się 11.",
        "Przekroczenie progu sumy nie oznacza gorszych warunków, tylko dłuższą procedurę. Dla 35-latka 500 tys. zł to ok. 222 zł miesięcznie zamiast 181 zł przy 400 tys. zł — 41 zł różnicy za 100 tys. zł dodatkowej ochrony. Jeżeli tyle właśnie potrzebujesz, nie ma powodu, by zbijać sumę do 400 tys. zł wyłącznie po to, żeby uniknąć dłuższego kwestionariusza. Suma ma odpowiadać potrzebie, a nie progowi administracyjnemu."
      ],
      bullets: [
        "35 lat, 100 tys. zł — ok. 59 zł/mies. (53–68 zł)",
        "35 lat, 200 tys. zł — ok. 100 zł/mies. (90–115 zł)",
        "35 lat, 300 tys. zł — ok. 141 zł/mies. (127–162 zł)",
        "35 lat, 400 tys. zł — ok. 181 zł/mies. (163–209 zł)",
        "30 lat, 400 tys. zł — ok. 136 zł/mies.; 40 lat — ok. 250 zł; 45 lat — ok. 352 zł",
        "przekroczenie progu: 500 tys. zł dla 35-latka to ok. 222 zł/mies."
      ]
    },
    {
      heading: "Kiedy PZU kieruje na badania i jak one wyglądają",
      lead: "Badania nie są karą ani sygnałem, że coś jest nie tak. To standardowy element wyceny wysokich sum — i to towarzystwo za nie płaci.",
      body: [
        "Zakres badań rośnie razem z sumą ubezpieczenia i wiekiem. Przy sumach powyżej 50 000 zł w grę wchodzą podstawowe pozycje: morfologia, glukoza, cholesterol. Przy sumach rzędu miliona złotych i wyżej panel jest znacznie szerszy — pojawiają się m.in. USG jamy brzusznej, EKG wysiłkowe, ECHO serca, badanie anty-HIV, a u mężczyzn PSA. Na badania kierowana jest wyłącznie osoba dorosła.",
        "Koszt badań pokrywa PZU, a terminy umawia współpracująca firma Saneo — nie musisz szukać placówki ani płacić z góry, żeby potem czekać na zwrot. Z perspektywy klienta jest to zwykle jedna wizyta w punkcie pobrań, czasem rozszerzona o badanie obrazowe przy najwyższych sumach.",
        "Przy naprawdę dużych kwotach dochodzi drugi rodzaj weryfikacji — ocena finansowa, która sprawdza, czy suma odpowiada Twojej sytuacji dochodowej. Do 1 100 000 zł łącznej sumy nie ma jej wcale. Powyżej tej kwoty i do 1 600 000 zł wypełnia się kwestionariusz finansowy, a powyżej 1 600 000 zł dochodzą dokumenty potwierdzające dochód z dwóch lat. Maksymalna potrzeba ubezpieczeniowa liczona jest jako krotność rocznego dochodu netto i zależy od wieku: przed 30. rokiem życia to 25-krotność, w przedziale 30–39 lat — 20-krotność, 40–49 — 15-krotność, 50–59 — 10-krotność, a od 60 lat — 7-krotność.",
        "Po zakończonej ocenie towarzystwo ma trzy możliwe odpowiedzi: umowa na warunkach standardowych, umowa na warunkach zmienionych (np. niższa suma albo wyższa składka) lub odmowa. Zmienione warunki to nie to samo co odmowa — bardzo często oznaczają po prostu inną cenę za tę samą ochronę."
      ],
      bullets: [
        "powyżej 50 000 zł sumy: m.in. morfologia, glukoza, cholesterol",
        "przy sumach od 1 mln zł: USG jamy brzusznej, EKG wysiłkowe, ECHO serca, anty-HIV, PSA u mężczyzn",
        "koszt badań pokrywa PZU, terminy umawia Saneo",
        "ocena finansowa: brak do 1,1 mln zł, kwestionariusz do 1,6 mln zł, dokumenty dochodu powyżej",
        "limit sumy to krotność rocznego dochodu netto — od 25× przed 30. r.ż. do 7× po 60."
      ]
    },
    {
      heading: "Co naprawdę oznacza zatajenie choroby w ankiecie",
      lead: "To jedyny fragment tego tematu, który wymaga stanowczości: niepełna ankieta nie jest sprytnym skrótem, tylko ryzykiem przerzuconym na rodzinę.",
      body: [
        "Zasada w ubezpieczeniu na życie w PZU jest opisana konkretnie. Jeżeli ubezpieczony zataił znane sobie okoliczności, o które był pytany, a śmierć nastąpi w ciągu 3 lat od zawarcia umowy i będzie skutkiem właśnie tych okoliczności, świadczenie może nie zostać wypłacone w pełnej wysokości. Trzy elementy muszą wystąpić łącznie: pytanie w ankiecie, świadome przemilczenie i związek przyczynowy ze zdarzeniem.",
        "Odwrotna strona tej zasady jest równie ważna, a mówi się o niej rzadziej. Rzetelnie wypełniona ankieta zamyka temat: po upływie wskazanego okresu i przy braku związku przyczynowego towarzystwo nie wraca do historii medycznej sprzed umowy. Uczciwa odpowiedź o nadciśnieniu czy przebytej operacji nie jest więc podnoszeniem ryzyka odmowy — jest jego eliminowaniem.",
        "Osobno funkcjonują wyłączenia niezwiązane z ankietą, np. samobójstwo w ciągu 2 lat od zawarcia umowy, działania wojenne czy popełnienie umyślnego przestępstwa. Pełną listę zawsze zawiera OWU konkretnego produktu i warto ją przejrzeć przed podpisem, a nie po zdarzeniu.",
        "Praktyczna wskazówka: jeśli nie pamiętasz szczegółów leczenia sprzed lat, napisz w ankiecie to, co wiesz, zamiast zaznaczać „nie\". Doradca może dopytać, a towarzystwo poprosić o dokumentację. Dłuższa procedura na starcie jest zawsze tańsza niż spór o wypłatę."
      ],
      bullets: [
        "zatajenie działa przeciw wypłacie, gdy śmierć nastąpi w ciągu 3 lat i wynika z zatajonej okoliczności",
        "samobójstwo w ciągu 2 lat od zawarcia umowy — odrębne wyłączenie ustawowo-umowne",
        "rzetelna ankieta zamyka temat historii medycznej na przyszłość",
        "nie pamiętasz szczegółów — opisz, co wiesz, zamiast zaznaczać „nie\""
      ]
    },
    {
      heading: "Choroby przewlekłe w ankiecie i ochrona w czasie oczekiwania",
      lead: "Cukrzyca, nadciśnienie, choroby tarczycy czy przebyty nowotwór nie przekreślają wniosku automatycznie, ale wpływają na przebieg oceny.",
      body: [
        "Nie da się z góry powiedzieć, czy konkretna choroba zakończy się warunkami standardowymi, zmienionymi czy odmową — decyzja zależy od rozpoznania, wyrównania choroby, przyjmowanych leków, wyników badań kontrolnych, czasu od zakończenia leczenia i wieku. Ta sama diagnoza może dać różne wyniki u dwóch osób. Dlatego uczciwa odpowiedź brzmi: to sprawdza się we wniosku, a nie prognozuje w artykule.",
        "Co można zrobić, żeby ocena poszła sprawnie? Przygotuj wypisy, wyniki ostatnich badań kontrolnych i listę przyjmowanych leków, zanim złożysz wniosek. Przy chorobie przewlekłej ta dokumentacja i tak zostanie poproszona, a dostarczona od razu skraca całą procedurę o tygodnie. Jeśli towarzystwo zaproponuje warunki zmienione, poproś doradcę o porównanie: czasem niższa suma z jednym dodatkiem daje lepszą realną ochronę niż wyższa suma z podwyższoną składką.",
        "W czasie oczekiwania na decyzję nie jesteś zupełnie bez ochrony. Od dnia następującego po złożeniu wniosku i opłaceniu składki, aż do zawarcia umowy, działa ochrona tymczasowa — obejmuje śmierć wskutek nieszczęśliwego wypadku, trwa maksymalnie 90 dni, a jej suma równa jest sumie początkowej z wniosku, nie więcej niż 200 000 zł. To zabezpieczenie wąskie, ale realne i darmowe w tym sensie, że mieści się w opłaconej składce.",
        "I jeszcze jedna rzecz, która przydaje się później: sumę można podwyższyć bez ponownej oceny ryzyka po ważnym zdarzeniu życiowym — ślubie, narodzinach lub adopcji dziecka albo złożeniu wniosku o kredyt hipoteczny. Limit to 25% aktualnej sumy, łącznie nie więcej niż 200 000 zł, a wniosek trzeba złożyć w ciągu 3 miesięcy od zdarzenia. To najprostszy sposób na zwiększenie ochrony bez wracania do ankiety."
      ],
      bullets: [
        "decyzję przy chorobie przewlekłej rozstrzyga ocena ryzyka — nie da się jej przewidzieć z góry",
        "przygotuj wypisy, wyniki kontrolne i listę leków przed złożeniem wniosku",
        "ochrona tymczasowa: śmierć wskutek wypadku, do 90 dni, suma maks. 200 000 zł",
        "podwyższenie sumy bez oceny ryzyka po ślubie, narodzinach dziecka lub wniosku o hipotekę: do 25% sumy, maks. 200 000 zł, w 3 miesiące od zdarzenia"
      ]
    }
  ],
  checklistTitle: "Zanim wypełnisz ankietę medyczną",
  checklist: [
    "policz orientacyjną składkę w kalkulatorze — bez podawania danych osobowych",
    "zsumuj polisy, które już masz, i sprawdź, czy łącznie mieścisz się w progu 400 tys. zł",
    "sprawdź swój wiek polisowy — w PZU liczony według roku urodzenia, nie daty",
    "zbierz wypisy, wyniki ostatnich badań i listę leków, jeśli leczysz się przewlekle",
    "przejrzyj listę wyłączeń i karencji w OWU produktu, który wybierasz"
  ],
  mistakesTitle: "Najczęstsze błędy przy ankiecie i badaniach",
  mistakes: [
    "zaznaczanie „nie\" przy pytaniu, którego się nie rozumie, zamiast dopytania doradcy",
    "obniżanie sumy poniżej realnej potrzeby tylko po to, by ominąć dłuższy kwestionariusz",
    "pomijanie polis, które już się ma — progi liczą się łącznie i i tak wyjdą przy ocenie",
    "traktowanie skierowania na badania jak złej wiadomości; koszt pokrywa towarzystwo",
    "zakładanie, że po złożeniu wniosku działa już pełna ochrona — na starcie jest tylko ochrona tymczasowa"
  ],
  faqs: [
    {
      question: "Czy można kupić ubezpieczenie na życie bez badań lekarskich?",
      answer:
        "Tak. W PZU przy łącznej sumie do 400 000 zł i wieku wstępu do 45 lat wystarczy skrócona ankieta medyczna — 9 pytań, bez skierowania do laboratorium. Powyżej tych progów jest pełna ankieta (11 pytań), a badania pojawiają się dopiero przy wyższych sumach."
    },
    {
      question: "Od jakiej sumy ubezpieczenia potrzebne są badania?",
      answer:
        "Zakres badań rośnie z sumą i wiekiem. Przy sumach powyżej 50 000 zł mogą to być podstawowe badania (morfologia, glukoza, cholesterol), a przy sumach od miliona złotych szeroki panel z USG jamy brzusznej, EKG wysiłkowym i ECHO serca. Konkretny zakres wskazuje towarzystwo po analizie wniosku."
    },
    {
      question: "Kto płaci za badania do polisy na życie?",
      answer:
        "W indywidualnych ubezpieczeniach na życie PZU koszt badań pokrywa towarzystwo, a terminy umawia współpracująca firma Saneo. Klient nie płaci za nie z własnej kieszeni ani nie musi szukać placówki."
    },
    {
      question: "Co się stanie, jeśli zataję chorobę w ankiecie?",
      answer:
        "Jeżeli zatajono znane okoliczności, o które pytano, a śmierć nastąpi w ciągu 3 lat od zawarcia umowy i będzie ich skutkiem, świadczenie może nie zostać wypłacone w pełnej wysokości. Rzetelna odpowiedź jest więc zabezpieczeniem interesu rodziny, a nie ryzykiem dla wnioskodawcy."
    },
    {
      question: "Czy z cukrzycą albo nadciśnieniem dostanę polisę na życie?",
      answer:
        "Nie da się tego przesądzić z góry. Choroba przewlekła nie wyklucza umowy — towarzystwo może zaproponować warunki standardowe, zmienione (np. niższa suma lub wyższa składka) albo odmówić. Znaczenie ma rozpoznanie, wyrównanie choroby, leczenie i wyniki badań kontrolnych. Warto przygotować dokumentację przed złożeniem wniosku."
    },
    {
      question: "Ile kosztuje polisa mieszcząca się w progu bez badań?",
      answer:
        "Orientacyjnie: 35-latek niepalący zapłaci ok. 141 zł miesięcznie za 300 tys. zł ochrony na 20 lat i ok. 181 zł za 400 tys. zł. Dla 45-latka ta sama suma 400 tys. zł to ok. 352 zł. Finalną składkę ustala towarzystwo po ocenie ryzyka."
    }
  ],
  verificationNote:
    "Progi ankiety, zakresy badań, zasady oceny finansowej i wyłączenia opisano na podstawie materiałów produktowych PZU Życie SA (2025/2026), na których opieramy treści serwisu. Kwoty składek pochodzą z modelu orientacyjnych stawek kalkulatora Ochro.pl dla scenariuszy podanych przy każdej tabeli. To nie jest oferta handlowa — finalne warunki, sumę i składkę ustala PZU po ocenie ryzyka, a wiążące pozostają OWU i KID danego produktu.",
  sources: [
    {
      label: "Kalkulator ubezpieczenia na życie",
      href: "/kalkulator-ubezpieczenia-na-zycie/",
      desc: "Policz orientacyjną składkę dla swojego wieku i sumy"
    },
    {
      label: "Cennik ubezpieczenia na życie 2026",
      href: "/ubezpieczenie-na-zycie-cennik/",
      desc: "Przykładowe składki zebrane w jednym miejscu"
    },
    {
      label: "PZU — ubezpieczenia na życie i zdrowie",
      href: "https://www.pzu.pl/dla-ciebie-i-rodziny/zycie-i-zdrowie",
      desc: "Materiały produktowe towarzystwa, na których opieramy opisy"
    }
  ],
  related: [
    {
      label: "Proces zakupu polisy",
      href: "/poradnik/proces-zakupu-polisy/",
      desc: "Od kalkulatora, przez ankietę, po dokument polisy"
    },
    {
      label: "Ile kosztuje ubezpieczenie na życie",
      href: "/poradnik/ile-kosztuje-ubezpieczenie-na-zycie/",
      desc: "Składki według wieku, sumy, palenia i zakresu"
    },
    {
      label: "Karencja i wyłączenia w polisie",
      href: "/poradnik/karencja-wylaczenia-polisa-na-zycie/",
      desc: "Okresy wyczekiwania i granice ochrony w OWU"
    },
    {
      label: "Polisa na życie dla palących",
      href: "/poradnik/polisa-na-zycie-dla-palacych/",
      desc: "Deklaracja palenia w ankiecie i wpływ na składkę"
    }
  ]
};

// Kwoty w tym artykule pochodzą z modelu stawek kalkulatora (src/calculators/rates.ts,
// computeOchronne i computeKredyt; mnożnik palenia RATES.ochronne.smokerFactor) oraz
// z anonimowych statystyk kalkulacji w D1. Przy zmianie modelu zaktualizuj tabele i dateModified.
const smokersGuidePage: GuidePage = {
  slug: "poradnik/polisa-na-zycie-dla-palacych",
  label: "Polisa dla palących",
  kicker: "Poradnik cenowy",
  title: "Polisa na życie dla palących — ile dopłacisz i jak wrócić do niższych stawek",
  copy:
    "Palenie jest jednym z niewielu czynników wyceny polisy, na które masz realny wpływ — i jednym z najdroższych. W naszym modelu składka palącego jest o ok. 40% wyższa od składki osoby niepalącej przy identycznej sumie i zakresie. Poniżej pokazujemy, ile to konkretnie kosztuje w różnym wieku, jak deklaruje się palenie we wniosku i co da się zrobić po rzuceniu.",
  image: guideProtectionImage,
  datePublished: "2026-08-28",
  dateModified: "2026-08-28",
  calculatorHref: "/kalkulator-ubezpieczenia-na-zycie/",
  calculatorLabel: "Policz składkę dla palącego",
  defaultProduct: "life",
  summary: [
    "palący 35-latek zapłaci za 300 tys. zł ochrony na 20 lat ok. 197 zł/mies. zamiast 141 zł",
    "dopłata rośnie z wiekiem i sumą: 45-latek dokłada ok. 107 zł/mies., a przy sumie 500 tys. zł — ok. 89 zł/mies.",
    "w skali całej 20-letniej umowy to ok. 13 400 zł (35 lat, 300 tys. zł) i ok. 25 700 zł (45 lat, 300 tys. zł)",
    "po rzuceniu palenia warto wrócić do tematu taryfy — nowa wycena jako niepalący bywa niższa mimo o rok starszego wieku"
  ],
  sections: [
    {
      heading: "Dlaczego palacz płaci więcej za ubezpieczenie na życie",
      lead: "Towarzystwo nie ocenia stylu życia moralnie, tylko statystycznie: wycenia prawdopodobieństwo wypłaty w okresie umowy. Palenie przesuwa je na tyle wyraźnie, że taryfy dla palących i niepalących są w praktyce dwoma osobnymi cennikami.",
      body: [
        "Składkę ustala się na dzień zawarcia umowy, na podstawie wieku, sumy ubezpieczenia, okresu ochrony, zakresu i informacji ze wniosku — w tym oświadczenia o paleniu. Dla dwóch osób w tym samym wieku, z tą samą sumą i tym samym zakresem, jedyną różnicą bywa właśnie ta jedna odpowiedź w ankiecie, a różnica w składce sięga kilkudziesięciu procent.",
        "W modelu naszego kalkulatora mnożnik palenia wynosi 1,4, czyli ok. 40% dopłaty względem osoby niepalącej. Jest to założenie modelu — stawki bazowe skalibrowaliśmy na realnych wycenach PZU, ale żadna z ofert, do których mieliśmy dostęp, nie dotyczyła osoby palącej, więc sam mnożnik traktuj jako rząd wielkości, a nie jako taryfę konkretnego towarzystwa. Kierunek i skala są typowe dla rynku; dokładną stawkę poznasz po ocenie ryzyka.",
        "Deklaracja palenia to informacja, o którą wprost pyta wniosek — i dlatego ma znaczenie także po latach. Zgodnie z warunkami PZU Życie zatajenie znanych okoliczności, o które pytano, może oznaczać, że towarzystwo nie wypłaci pełnej sumy ubezpieczenia, jeśli śmierć nastąpi w ciągu 3 lat od zawarcia umowy i była ich skutkiem. Oszczędność 56 zł miesięcznie nie jest tego warta."
      ],
      bullets: [
        "taryfa dla palących i niepalących to w praktyce dwa różne cenniki",
        "w naszym modelu palenie to mnożnik 1,4 (ok. +40%) — założenie kalibracyjne, nie taryfa PZU",
        "stawkę ustala wiek i status w dniu zawarcia umowy, nie po latach",
        "zatajenie okoliczności, o które pytano, może kosztować wypłatę pełnej sumy przy śmierci w ciągu 3 lat od zawarcia",
        "finalną składkę zawsze potwierdza ocena ryzyka towarzystwa"
      ]
    },
    {
      heading: "Ile kosztuje ubezpieczenie na życie dla palących według wieku",
      lead: "Scenariusz bazowy: suma 300 tys. zł, ochrona na 20 lat, sama umowa główna na wypadek śmierci. Zmienia się tylko odpowiedź na pytanie o papierosy. Kwoty z naszego kalkulatora, zaokrąglone.",
      body: [
        "25-latek niepalący zapłaci ok. 81 zł miesięcznie, palący — ok. 113 zł (widełki 102–130 zł). U 30-latka jest to 106 zł wobec 149 zł, a u 35-latka 141 zł wobec 197 zł (widełki 177–226 zł). W tym wieku dopłata za palenie to 32–56 zł miesięcznie, czyli mniej więcej tyle, ile kosztuje kilka paczek papierosów.",
        "Po czterdziestce rośnie już nie tylko składka, ale i sama dopłata, bo procent liczy się od coraz wyższej podstawy. 40-latek palący zapłaci ok. 269 zł zamiast 192 zł, 45-latek ok. 376 zł zamiast 269 zł, 50-latek ok. 528 zł zamiast 377 zł, a 55-latek ok. 747 zł zamiast 534 zł. Różnica u 55-latka to 213 zł miesięcznie — za dokładnie tę samą ochronę.",
        "Praktyczny wniosek: u palącego 45-latka sama dopłata za papierosy (107 zł) jest wyższa niż cała składka niepalącego 30-latka (106 zł) przy tej samej sumie i okresie. Jeśli rozważasz polisę i jednocześnie myślisz o rzuceniu palenia, kolejność tych dwóch decyzji ma wymierną cenę."
      ],
      bullets: [
        "25 lat — 81 zł (niepalący) wobec ok. 113 zł/mies. (palący)",
        "30 lat — 106 zł wobec ok. 149 zł/mies.",
        "35 lat — 141 zł wobec ok. 197 zł/mies. (widełki 177–226 zł)",
        "40 lat — 192 zł wobec ok. 269 zł/mies.",
        "45 lat — 269 zł wobec ok. 376 zł/mies. (widełki 339–433 zł)",
        "55 lat — 534 zł wobec ok. 747 zł/mies."
      ]
    },
    {
      heading: "Ile palenie kosztuje przy wyższych sumach i w skali całej umowy",
      lead: "Dopłata jest procentowa, więc rośnie razem z sumą ubezpieczenia. Poniżej ten sam 35-latek, ochrona na 20 lat, sama umowa główna — zmienia się wyłącznie kwota dla bliskich.",
      body: [
        "Przy sumie 100 tys. zł palący zapłaci ok. 82 zł zamiast 59 zł, przy 200 tys. zł — ok. 140 zł zamiast 100 zł, przy 300 tys. zł — ok. 197 zł zamiast 141 zł. Pół miliona ochrony to ok. 311 zł zamiast 222 zł, a milion — ok. 597 zł zamiast 427 zł. Dopłata rośnie odpowiednio: 23 zł, 40 zł, 56 zł, 89 zł i 170 zł miesięcznie.",
        "W skali całej umowy te kwoty przestają być drobne. 35-latek z sumą 300 tys. zł na 20 lat zapłaci przez cały okres ok. 13 400 zł więcej niż osoba niepaląca; przy sumie 500 tys. zł — ok. 21 400 zł więcej. U 45-latka z sumą 300 tys. zł różnica sięga ok. 25 700 zł. To pieniądze, które przy identycznej ochronie mogłyby zostać w domowym budżecie.",
        "Dla porównania skali: w 136 kalkulacjach wariantu ochronnego wykonanych w naszym kalkulatorze mediana wyliczonej składki to ok. 438 zł miesięcznie, a połowa wyników mieści się między ok. 204 a 867 zł (dane własne Ochro.pl, wyniki anonimowe, wszystkie profile i zakresy łącznie). Czterdziestoprocentowa dopłata przy takich kwotach to realnie kilkaset złotych rocznie."
      ],
      bullets: [
        "100 tys. zł (35 lat) — 59 zł wobec ok. 82 zł/mies.",
        "300 tys. zł (35 lat) — 141 zł wobec ok. 197 zł/mies.",
        "500 tys. zł (35 lat) — 222 zł wobec ok. 311 zł/mies.",
        "1 mln zł (35 lat) — 427 zł wobec ok. 597 zł/mies.",
        "przez 20 lat umowy: ok. 13 400 zł dopłaty (300 tys. zł) i ok. 21 400 zł (500 tys. zł)",
        "mediana składki ze 136 realnych kalkulacji wariantu ochronnego: ok. 438 zł/mies."
      ]
    },
    {
      heading: "Zakres i polisa pod kredyt — gdzie dopłata za palenie boli najbardziej",
      lead: "Mnożnik działa na całą składkę, więc im szerszy zakres, tym większa kwota dopłaty. Najmocniej widać to w pełnych pakietach zdrowotnych i w polisach zabezpieczających kredyt hipoteczny.",
      body: [
        "Dla 35-latka z sumą 300 tys. zł na 20 lat sama umowa główna to 141 zł (niepalący) wobec 197 zł (palący). Rozszerzenie o poważne zachorowania podnosi te kwoty do 204 i 285 zł, a pełny pakiet zdrowotny — do 366 i 512 zł. Dopłata rośnie z 56 zł do 146 zł miesięcznie, choć procent pozostaje ten sam.",
        "U 45-latka te same warianty to 269 zł wobec 376 zł (sama śmierć), 390 zł wobec 546 zł (z poważnymi zachorowaniami) i 699 zł wobec 979 zł (pełny pakiet). Jeśli budżet jest napięty, warto najpierw ustalić sumę i okres pod realną potrzebę, a dopiero potem dokładać dodatki — zamiast ciąć sumę, która jest całym sensem polisy.",
        "Podobnie działa to w polisie pod kredyt hipoteczny. Dla 35-latka z kredytem 400 tys. zł na 25 lat orientacyjna składka to ok. 187 zł miesięcznie dla osoby niepalącej i ok. 262 zł dla palącej (widełki 236–301 zł). Bank nie może narzucić konkretnego ubezpieczyciela — może wymagać cesji — więc porównanie ofert ma tu dodatkowy sens: przy identycznej sumie różnice między towarzystwami potrafią sięgać kilkudziesięciu procent."
      ],
      bullets: [
        "35 lat, sama śmierć: 141 zł wobec 197 zł/mies.",
        "35 lat, + poważne zachorowania: 204 zł wobec 285 zł/mies.",
        "35 lat, pełny pakiet zdrowotny: 366 zł wobec 512 zł/mies.",
        "45 lat, pełny pakiet zdrowotny: 699 zł wobec 979 zł/mies.",
        "kredyt 400 tys. zł na 25 lat (35 lat): 187 zł wobec ok. 262 zł/mies."
      ]
    },
    {
      heading: "E-papierosy, IQOS i saszetki nikotynowe w ankiecie medycznej",
      lead: "Najczęstsze pytanie palących brzmi: „czy podgrzewacz to jeszcze palenie?\". Odpowiedź jest prostsza, niż się wydaje — nie interpretuj pytania, tylko opisz stan faktyczny.",
      body: [
        "Wnioski ubezpieczeniowe pytają dziś zwykle o wyroby nikotynowe szerzej niż o same papierosy klasyczne — obejmuje to również e-papierosy, podgrzewacze tytoniu i inne formy przyjmowania nikotyny. Dokładna definicja oraz to, jak wpływa na taryfę, zależy od konkretnego produktu i jest opisana w ankiecie oraz OWU. Jeśli formularz nie jest dla Ciebie jednoznaczny, dopytaj doradcę przed podpisaniem i poproś, żeby odpowiedź została zapisana zgodnie z rzeczywistością.",
        "Sam wniosek nie kończy się na oświadczeniu. W PZU Życie skrócona ankieta (9 pytań) wystarcza, gdy suma umowy podstawowej wraz z wybranymi dodatkami i czynnymi polisami nie przekracza 400 tys. zł, a wiek wstępu 45 lat. Powyżej tych progów obowiązuje ankieta pełna (11 pytań), a przy wyższych sumach dochodzą badania medyczne — ich koszt pokrywa PZU, a zakres rośnie wraz z sumą i wiekiem.",
        "Warto też pamiętać, że deklaracja dotyczy stanu na dzień składania wniosku. Jeśli zaczniesz palić w trakcie trwania umowy, nie unieważnia to zawartej polisy — składka została już ustalona. Jeśli natomiast w momencie wniosku palisz i to zataisz, ryzykujesz sporem o wypłatę w najgorszym możliwym momencie: gdy o świadczenie wystąpi rodzina."
      ],
      bullets: [
        "ankieta pyta zwykle o wyroby nikotynowe, nie tylko o papierosy klasyczne",
        "definicję dla konkretnego produktu rozstrzyga ankieta i OWU — dopytaj przed podpisaniem",
        "skrócona ankieta w PZU: 9 pytań do sumy 400 tys. zł i wieku wstępu 45 lat",
        "powyżej progów — pełna ankieta (11 pytań), a przy wyższych sumach badania na koszt PZU",
        "deklaracja dotyczy dnia złożenia wniosku; późniejsze zmiany nie unieważniają polisy"
      ]
    },
    {
      heading: "Jak wrócić do niższych stawek po rzuceniu palenia",
      lead: "Składka raz ustalona nie obniża się sama — o zmianę taryfy trzeba wystąpić. Zasady zależą od produktu, ale sam rachunek warto zrobić już po roku bez nikotyny.",
      body: [
        "Rynkową praktyką jest wymaganie co najmniej 12 miesięcy bez nikotyny, zanim towarzystwo rozważy zastosowanie stawek dla niepalących. Czy i na jakich warunkach przelicza taryfę w trakcie trwania umowy, rozstrzygają warunki konkretnego produktu — dlatego pierwszy krok to zwykłe pytanie do doradcy lub towarzystwa, a nie założenie, że „się nie da\".",
        "Druga droga to policzenie nowej polisy. Palący 35-latek płaci za 300 tys. zł na 20 lat ok. 197 zł miesięcznie; ten sam człowiek rok później, już jako niepalący 36-latek, dostanie w naszym kalkulatorze ok. 151 zł — czyli ok. 46 zł mniej mimo starszego wieku. U 45-latka jest to 376 zł wobec ok. 291 zł jako niepalący 46-latek, czyli ok. 85 zł oszczędności miesięcznie.",
        "Jest jednak warunek, o którym trzeba wiedzieć: nowa umowa to nowa ocena ryzyka i nowe liczenie terminów — m.in. dwuletniego wyłączenia samobójstwa i trzyletniego okresu, w którym znaczenie mają zatajone okoliczności. Dlatego nigdy nie wypowiadaj starej polisy, zanim nowa nie zostanie zawarta, a jeśli w międzyczasie zmienił się Twój stan zdrowia, rekalkulacja istniejącej umowy może być rozwiązaniem bezpieczniejszym niż zaczynanie od zera."
      ],
      bullets: [
        "typowy próg to 12 miesięcy bez nikotyny — zasady rozstrzyga produkt i OWU",
        "składka nie obniża się automatycznie: trzeba wystąpić o przeliczenie",
        "35 lat: 197 zł jako palący wobec ok. 151 zł jako niepalący rok później",
        "45 lat: 376 zł jako palący wobec ok. 291 zł jako niepalący rok później",
        "nowa polisa = nowa ocena ryzyka i nowe terminy — starej nie wypowiadaj przedwcześnie"
      ]
    }
  ],
  checklistTitle: "Zanim poprosisz o ofertę jako osoba paląca",
  checklist: [
    "policz w kalkulatorze dwa warianty — jako palący i jako niepalący — żeby zobaczyć wysokość dopłaty",
    "ustal sumę pod realną potrzebę (saldo kredytu + 2–3 roczne dochody), zanim zaczniesz szukać oszczędności",
    "zadeklaruj palenie zgodnie ze stanem faktycznym, łącznie z e-papierosami i podgrzewaczami",
    "poproś doradcę o porównanie kilku ofert — taryfy dla palących różnią się między towarzystwami",
    "jeśli planujesz rzucić, ustaw przypomnienie po 12 miesiącach i wróć do tematu stawki"
  ],
  mistakesTitle: "Najczęstsze błędy palących przy zakupie polisy",
  mistakes: [
    "zatajenie palenia we wniosku — przy śmierci w ciągu 3 lat od zawarcia może kosztować wypłatę pełnej sumy",
    "obniżanie sumy ubezpieczenia, żeby zrównoważyć dopłatę za palenie — polisa przestaje pokrywać kredyt i potrzeby rodziny",
    "założenie, że po rzuceniu palenia składka obniży się sama — o przeliczenie trzeba wystąpić",
    "wypowiedzenie starej polisy przed zawarciem nowej, tańszej — okres bez ochrony i nowa ocena ryzyka",
    "odkładanie decyzji o polisie „do czasu, aż rzucę\" — stawka wejścia rośnie z każdym rokiem wieku"
  ],
  faqs: [
    {
      question: "O ile droższe jest ubezpieczenie na życie dla palących?",
      answer:
        "W modelu naszego kalkulatora o ok. 40%. Dla 35-latka z sumą 300 tys. zł na 20 lat to ok. 197 zł zamiast 141 zł miesięcznie, dla 45-latka ok. 376 zł zamiast 269 zł. Mnożnik palenia jest założeniem naszego modelu — dokładną taryfę ustala towarzystwo po ocenie ryzyka."
    },
    {
      question: "Czy e-papierosy i podgrzewacze liczą się jako palenie?",
      answer:
        "Wnioski ubezpieczeniowe pytają zwykle o wyroby nikotynowe szerzej niż o same papierosy klasyczne, więc e-papierosy i podgrzewacze tytoniu zazwyczaj mieszczą się w tym pytaniu. Dokładną definicję dla danego produktu zawiera ankieta i OWU — jeśli masz wątpliwość, opisz stan faktyczny i dopytaj doradcę przed podpisaniem wniosku."
    },
    {
      question: "Czy po rzuceniu palenia składka obniży się sama?",
      answer:
        "Nie. Składka jest ustalana przy zawarciu umowy i nie zmienia się automatycznie. Po co najmniej 12 miesiącach bez nikotyny warto zapytać towarzystwo o przeliczenie taryfy albo porównać koszt nowej polisy — w naszym kalkulatorze 35-latek płaci wtedy ok. 151 zł zamiast 197 zł, mimo że jest o rok starszy."
    },
    {
      question: "Co się stanie, jeśli zataję palenie we wniosku?",
      answer:
        "Zatajenie znanych okoliczności, o które pytano, jest podstawą do odmowy wypłaty pełnej sumy, jeśli śmierć nastąpi w ciągu 3 lat od zawarcia umowy i była ich skutkiem. Przy dopłacie rzędu kilkudziesięciu złotych miesięcznie ryzyko jest nieproporcjonalne — świadczenie ma trafić do rodziny bez sporów."
    },
    {
      question: "Czy osoba paląca w ogóle dostanie polisę na życie?",
      answer:
        "Palenie samo w sobie nie zamyka drogi do ubezpieczenia — wpływa przede wszystkim na wysokość składki. Wniosek przechodzi standardową ocenę ryzyka: ankietę medyczną, a przy wyższych sumach i wieku także badania, których koszt pokrywa PZU. O ewentualnych zwyżkach lub ograniczeniach zakresu decyduje towarzystwo indywidualnie."
    }
  ],
  verificationNote:
    "Kwoty składek pochodzą z modelu orientacyjnych stawek kalkulatora Ochro.pl (scenariusze opisane przy każdym zestawieniu). Stawki bazowe skalibrowano na realnych wycenach PZU, natomiast mnożnik palenia (1,4) jest założeniem modelu — realna dopłata u konkretnego ubezpieczyciela może się różnić. Statystyki kalkulacji to anonimowe dane własne serwisu. To nie jest oferta handlowa: finalną składkę, zakres i ewentualne zwyżki ustala towarzystwo po ocenie ryzyka, a pełne zasady opisują OWU.",
  sources: [
    {
      label: "Kalkulator ubezpieczenia na życie",
      href: "/kalkulator-ubezpieczenia-na-zycie/",
      desc: "Policz składkę w wariancie dla palących i niepalących"
    },
    {
      label: "Cennik ubezpieczenia na życie 2026",
      href: "/ubezpieczenie-na-zycie-cennik/",
      desc: "Przykładowe ceny polis w jednym miejscu"
    },
    {
      label: "PZU — ubezpieczenia na życie i zdrowie",
      href: "https://www.pzu.pl/dla-ciebie-i-rodziny/zycie-i-zdrowie",
      desc: "Materiały produktowe towarzystwa, na których opieramy opisy"
    }
  ],
  related: [
    {
      label: "Ile kosztuje ubezpieczenie na życie",
      href: "/poradnik/ile-kosztuje-ubezpieczenie-na-zycie/",
      desc: "Składki według wieku, sumy, palenia i zakresu"
    },
    {
      label: "Polisa bez badań lekarskich",
      href: "/poradnik/ubezpieczenie-na-zycie-bez-badan/",
      desc: "Kiedy wystarczy ankieta, a kiedy są badania"
    },
    {
      label: "Polisa na 500 tys. zł",
      href: "/poradnik/ubezpieczenie-na-zycie-500-tys-cena/",
      desc: "Cena najczęściej wybieranej sumy ubezpieczenia"
    },
    {
      label: "Polisa pod kredyt hipoteczny",
      href: "/poradnik/ubezpieczenie-na-zycie-pod-kredyt-hipoteczny/",
      desc: "Suma, okres i cesja przy kredycie"
    }
  ]
};

const bankPolicyGuidePage: GuidePage = {
  slug: "poradnik/polisa-z-banku-czy-wlasna",
  label: "Polisa z banku czy własna",
  kicker: "Poradnik kredytowy",
  title: "Polisa do kredytu: z banku czy własna? Różnice, cesja i zmiana w trakcie",
  copy:
    "Przy kredycie hipotecznym bank może wymagać ubezpieczenia na życie jako zabezpieczenia, ale nie musi to być polisa kupiona w banku. Ten tekst pokazuje, na czym polega różnica między ofertą bankową a polisą własną z cesją, jak policzyć prawdziwy koszt obu wariantów razem z marżą kredytu i jak wygląda zmiana polisy w trakcie spłaty.",
  image: guideChooseImage,
  datePublished: "2026-08-28",
  dateModified: "2026-08-28",
  calculatorHref: "/kalkulator-ubezpieczenia-na-zycie-pod-kredyt/",
  calculatorLabel: "Policz własną polisę pod kredyt",
  defaultProduct: "mortgage",
  summary: [
    "bank może wymagać zabezpieczenia kredytu, ale UOKiK opisuje zakaz sprzedaży wiązanej — sam produkt zwykle da się wybrać",
    "porównuj nie samą składkę, lecz sumę: składka plus różnica w racie wynikająca z marży (0,2 p.p. przy 400 tys. zł na 25 lat to ok. 51 zł/mies.)",
    "własna polisa 35-latka na 400 tys. zł i 25 lat to ok. 187 zł/mies. w naszym modelu, przy zakresie ograniczonym do śmierci",
    "polisa zawarta wcześniej jest tańsza na zawsze — po latach ta sama ochrona kosztuje więcej, bo liczy się wiek z dnia zawarcia umowy"
  ],
  sections: [
    {
      heading: "Czy bank może narzucić własną polisę do kredytu hipotecznego?",
      lead: "Trzeba rozdzielić dwie rzeczy: wymóg zabezpieczenia kredytu i wymóg kupienia konkretnego produktu u konkretnego sprzedawcy. To pierwsze jest normalną praktyką kredytową, to drugie ma swoje granice.",
      body: [
        "Bank ocenia ryzyko spłaty kredytu rozłożonego na 20–30 lat i może oczekiwać, że w razie śmierci kredytobiorcy zobowiązanie zostanie pokryte z innego źródła niż majątek rodziny. Stąd wymóg polisy na życie z cesją praw na bank. Sam wymóg jest zrozumiały — pytanie brzmi, czy musi go spełniać akurat polisa z oferty tego banku.",
        "UOKiK opisuje zakaz sprzedaży wiązanej w kredycie hipotecznym i odróżnia go od sprzedaży łączonej. W praktyce oznacza to, że bank zwykle określa warunki, jakie ma spełnić ubezpieczenie (minimalna suma, okres, zakres, forma cesji), a nie nazwę ubezpieczyciela. Bank może natomiast uzależnić od skorzystania ze swojej oferty warunki cenowe kredytu, na przykład wysokość marży — i to jest legalne, o ile jest jasno pokazane w ofercie.",
        "Dlatego pierwszy krok jest zawsze ten sam i nie wymaga żadnej wiedzy ubezpieczeniowej: poproś bank o pisemną listę wymagań wobec polisy zewnętrznej oraz o symulację raty w obu wariantach — z ubezpieczeniem banku i bez niego. Dopiero mając te dwie kartki, można cokolwiek porównywać. Bez nich rozmowa sprowadza się do tego, kto ładniej opowiada."
      ],
      bullets: [
        "poproś o pisemne warunki akceptacji polisy zewnętrznej (suma, okres, zakres, cesja)",
        "poproś o symulację raty w wariancie z polisą banku i z polisą własną",
        "sprawdź, czy różnica dotyczy marży, prowizji, czy obu naraz",
        "ustal termin: do kiedy polisa i cesja mają być dostarczone przed uruchomieniem kredytu",
        "zapytaj, czy bank akceptuje polisę terminową ze stałą sumą, czy wymaga sumy malejącej"
      ]
    },
    {
      heading: "Polisa bankowa a polisa własna — czym naprawdę się różnią",
      lead: "Różnica rzadko sprowadza się do ceny. Częściej dotyczy tego, czyja jest umowa, co się z nią dzieje po spłacie kredytu i kto decyduje o zakresie ochrony.",
      body: [
        "Ubezpieczenie oferowane przy kredycie ma zwykle formę umowy grupowej: stroną umowy jest bank, a kredytobiorca przystępuje do niej jako ubezpieczony. Polisa własna to umowa indywidualna — to Ty jesteś właścicielem umowy, wskazujesz uposażonych, a bankowi ustanawiasz jedynie cesję praw do świadczenia w wysokości salda zadłużenia.",
        "Ta różnica staje się widoczna dopiero w dwóch momentach. Pierwszy: gdy spłacasz kredyt. Cesja z polisy własnej wygasa, a ochrona zostaje z Tobą na dotychczasowych warunkach i za dotychczasową składkę — przy grupowej z reguły kończy się razem z kredytem. Drugi: gdy refinansujesz kredyt w innym banku. Polisę własną przenosisz, zmieniając tylko cesjonariusza; ochrony grupowej nie da się zabrać ze sobą.",
        "Jest też różnica w tym, co obejmuje ochrona i ile z niej trafia do rodziny. Suma w polisie kredytowej często równa się saldu zadłużenia i maleje razem z nim, więc po dziesięciu latach spłaty bliscy dostają odpowiednio mniej. W polisie własnej możesz ustawić sumę stałą i wyższą niż saldo — wtedy bank zaspokaja się do wysokości długu, a nadwyżka trafia do uposażonych. Przy tej samej składce to zwykle najważniejsza różnica dla rodziny."
      ],
      bullets: [
        "grupowa: stroną umowy jest bank, Ty przystępujesz — indywidualna: umowa jest Twoja",
        "po spłacie kredytu polisa własna zostaje, ochrona grupowa zwykle wygasa",
        "przy refinansowaniu w innym banku polisę własną przenosisz, zmieniając cesjonariusza",
        "suma malejąca = mniej pieniędzy dla rodziny z każdym rokiem spłaty",
        "suma stała powyżej salda: bank bierze dług, nadwyżkę dostają uposażeni",
        "w polisie własnej sam wybierasz zakres i ewentualne umowy dodatkowe"
      ]
    },
    {
      heading: "Ile kosztuje własna polisa pod kredyt hipoteczny",
      lead: "Scenariusz bazowy: suma równa kwocie kredytu, okres równy okresowi kredytowania, osoba niepaląca, zakres ograniczony do śmierci. Kwoty z naszego kalkulatora, zaokrąglone.",
      body: [
        "Dla kredytu na 400 tys. zł i 25 lat składka wynosi ok. 140 zł miesięcznie dla 30-latka, 187 zł dla 35-latka, 258 zł dla 40-latka i 364 zł dla 45-latka. Przy 300 tys. zł te same profile to odpowiednio 110, 145, 198 i 277 zł, a przy 500 tys. zł — 171, 229, 318 i 450 zł. Widać wyraźnie, że wiek kredytobiorcy zmienia składkę mocniej niż sama kwota kredytu.",
        "Okres kredytowania ma zaskakująco mały wpływ. Ten sam 35-latek z sumą 400 tys. zł zapłaci ok. 164 zł przy ochronie na 10 lat, 181 zł przy 20 latach i 191 zł przy 30 latach. Nie ma więc sensu skracać ochrony poniżej okresu kredytu, żeby zbić składkę — oszczędność jest symboliczna, a luka w zabezpieczeniu realna.",
        "Wyraźnie za to podnosi cenę rozszerzenie zakresu i palenie. Dorzucenie poważnego zachorowania do polisy 35-latka z sumą 400 tys. zł podnosi składkę ze 187 do ok. 271 zł, a niezdolności do pracy — do ok. 262 zł. Osoba paląca przy tym samym profilu zapłaci ok. 262 zł zamiast 187 zł. Warto wiedzieć, że bank do samej cesji zwykle potrzebuje wyłącznie ochrony na wypadek śmierci — dodatki kupuje się dla siebie, nie dla banku."
      ],
      bullets: [
        "kredyt 300 tys. zł na 25 lat — 110 zł (30 lat), 145 zł (35 lat), 198 zł (40 lat), 277 zł (45 lat)",
        "kredyt 400 tys. zł na 25 lat — 140 zł, 187 zł, 258 zł, 364 zł",
        "kredyt 500 tys. zł na 25 lat — 171 zł, 229 zł, 318 zł, 450 zł",
        "okres ochrony zmienia niewiele: 10 lat to 164 zł, 30 lat — 191 zł (35 lat, 400 tys. zł)",
        "poważne zachorowanie w pakiecie: 271 zł zamiast 187 zł",
        "osoba paląca przy tym samym profilu: ok. 262 zł zamiast 187 zł"
      ]
    },
    {
      heading: "Prawdziwy koszt to składka plus marża, a nie sama składka",
      lead: "Najczęstszy błąd w tym porównaniu polega na zestawieniu składki bankowej ze składką własną i pominięciu tego, co bank robi z marżą, gdy rezygnujesz z jego oferty.",
      body: [
        "Policzmy to na konkretnym przykładzie. Kredyt 400 tys. zł na 25 lat przy oprocentowaniu 7% to rata ok. 2827 zł. Jeżeli rezygnacja z ubezpieczenia banku podnosi marżę o 0,2 p.p., rata rośnie do ok. 2878 zł, czyli o 51 zł miesięcznie. Przy 0,3 p.p. jest to ok. 2904 zł, czyli 77 zł miesięcznie więcej. To arytmetyka rat annuitetowych przy stałym oprocentowaniu, a nie oferta jakiegokolwiek banku — Twoje liczby podstaw z własnej symulacji.",
        "Dopiero teraz porównanie ma sens. Jeżeli własna polisa kosztuje 187 zł, a bankowa 220 zł, ale rezygnacja z bankowej podnosi ratę o 51 zł, to wariant zewnętrzny kosztuje realnie 238 zł wobec 220 zł — i wychodzi drożej mimo niższej składki. Przy różnicy marży 0,1 p.p. albo przy braku takiej różnicy wynik odwraca się na korzyść polisy własnej.",
        "Do rachunku warto dopisać jeszcze jedną pozycję, której nie widać w tabelce: co zostaje po spłacie kredytu. Polisa własna po 25 latach nadal działa i nadal kosztuje tyle, ile w dniu zawarcia. Ochrona grupowa kończy się razem z kredytem, a nowa polisa kupowana w wieku 60 lat jest wielokrotnie droższa niż ta sama ochrona kupiona w wieku 35 lat. Jeśli oba warianty wychodzą kosztowo podobnie, ta różnica zwykle przeważa."
      ],
      bullets: [
        "policz: składka + różnica w racie, a nie sama składka",
        "400 tys. zł na 25 lat przy 7% — rata ok. 2827 zł; +0,2 p.p. marży to ok. 51 zł/mies.",
        "przy +0,3 p.p. różnica w racie rośnie do ok. 77 zł/mies.",
        "podstaw własne liczby z symulacji banku — powyższe to arytmetyka, nie oferta",
        "dopisz do rachunku wartość ochrony, która zostaje po spłacie kredytu"
      ]
    },
    {
      heading: "Jak zmienić polisę bankową na własną w trakcie kredytu",
      lead: "Zmiana w trakcie spłaty jest możliwa i najczęściej opłacalna wtedy, gdy różnica w marży była niewielka albo już wygasła. Kolejność kroków ma tu znaczenie większe niż sama decyzja.",
      body: [
        "Zacznij od dokumentów kredytowych: sprawdź, czy umowa przewiduje podwyższenie marży po rezygnacji z ubezpieczenia banku i czy określa minimalne warunki polisy zastępczej. Następnie zawrzyj nową polisę i doprowadź ją do stanu, w którym działa — jest opłacona, a ochrona się rozpoczęła. Dopiero potem złóż w banku wniosek o zmianę zabezpieczenia razem z polisą, dowodem opłacenia i wnioskiem o cesję. Bank potwierdza przyjęcie cesji, a ubezpieczyciel odnotowuje ją na polisie. Rezygnację z ubezpieczenia bankowego składasz na samym końcu, po pisemnym potwierdzeniu z banku.",
        "Najkosztowniejszy błąd to odwrócenie tej kolejności. Wypowiedzenie starej ochrony przed uruchomieniem nowej daje okres bez zabezpieczenia, a w skrajnym przypadku — naruszenie warunków umowy kredytu. Druga pułapka to złożenie wniosku o nową polisę bez sprawdzenia, czy przejdzie ocenę ryzyka: jeśli w międzyczasie doszło do choroby, nowa polisa może być droższa, ograniczona albo niedostępna, a starej już nie ma.",
        "Warto też wiedzieć, kiedy zmiana się nie opłaca. Wraz ze spłatą kredytu saldo maleje, więc kusi obniżenie sumy ubezpieczenia — ale w nowej umowie liczy się aktualny wiek. W naszym modelu 35-latek z sumą 400 tys. zł na 25 lat płaci 187 zł; ten sam człowiek po dziesięciu latach, mając 45 lat i saldo 260 tys. zł na 15 lat, zapłaci za nową polisę ok. 228 zł — mimo znacznie niższej sumy. Jeżeli masz starą, tanią polisę, zwykle lepiej zostawić ją w spokoju i ewentualnie wystąpić o obniżenie sumy w istniejącej umowie, zamiast kupować nową."
      ],
      bullets: [
        "sprawdź w umowie kredytu zapis o marży i minimalne wymogi polisy zastępczej",
        "najpierw uruchom nową polisę, dopiero potem wypowiadaj starą ochronę",
        "skompletuj dokumenty: polisa, dowód opłacenia, wniosek o cesję",
        "poczekaj na pisemne potwierdzenie przyjęcia cesji przez bank",
        "nie kupuj nowej polisy tylko po to, by obniżyć sumę — wiek podnosi stawkę"
      ]
    }
  ],
  checklistTitle: "Zanim wybierzesz polisę do kredytu",
  checklist: [
    "wyciągnij z banku pisemne warunki akceptacji polisy zewnętrznej i symulację raty w obu wariantach",
    "policz koszt całkowity: składka plus różnica w racie z tytułu marży",
    "ustal, czy suma ma być stała, czy malejąca razem z saldem kredytu",
    "sprawdź, co dzieje się z ochroną po spłacie kredytu i przy refinansowaniu",
    "dopilnuj terminów: polisa i cesja muszą być gotowe przed uruchomieniem kredytu"
  ],
  mistakesTitle: "Najczęstsze błędy przy wyborze polisy do kredytu",
  mistakes: [
    "porównywanie samej składki bez uwzględnienia różnicy w marży kredytu",
    "wypowiedzenie ubezpieczenia bankowego przed uruchomieniem polisy własnej",
    "skracanie okresu ochrony poniżej okresu kredytu dla oszczędności rzędu kilkunastu złotych",
    "przyjęcie sumy równej saldu zadłużenia bez zastanowienia, ile zostaje dla rodziny",
    "kupowanie nowej polisy po latach tylko po to, by obniżyć sumę — wiek zjada oszczędność"
  ],
  faqs: [
    {
      question: "Czy bank może wymagać polisy na życie do kredytu hipotecznego?",
      answer:
        "Bank może wymagać ubezpieczenia jako zabezpieczenia kredytu i określić warunki, jakie polisa ma spełniać. UOKiK opisuje jednak zakaz sprzedaży wiązanej w kredycie hipotecznym i odróżnia go od sprzedaży łączonej — bank może więc powiązać swoją ofertę z korzystniejszą marżą, ale wymagania wobec polisy powinny być podane wprost. Poproś o nie na piśmie, razem z symulacją raty w obu wariantach."
    },
    {
      question: "Polisa z banku czy własna — co wychodzi taniej?",
      answer:
        "To zależy od tego, o ile bank podnosi marżę przy rezygnacji ze swojej oferty. Przy kredycie 400 tys. zł na 25 lat i oprocentowaniu 7% podwyżka marży o 0,2 p.p. to ok. 51 zł miesięcznie więcej w racie. Własna polisa 35-latka na tę sumę kosztuje w naszym modelu ok. 187 zł miesięcznie, więc dopiero suma obu pozycji mówi, który wariant jest tańszy."
    },
    {
      question: "Czy mogę zmienić polisę bankową na własną w trakcie kredytu?",
      answer:
        "Zwykle tak, o ile nowa polisa spełnia wymagania banku, a Ty ustanowisz na niej cesję. Kolejność jest ważna: najpierw zawierasz i opłacasz nową polisę, potem składasz w banku wniosek o zmianę zabezpieczenia z cesją, czekasz na pisemne potwierdzenie, a rezygnację ze starej ochrony składasz na końcu. Sprawdź wcześniej w umowie kredytu, czy zmiana wpływa na marżę."
    },
    {
      question: "Co się dzieje z polisą po spłacie kredytu?",
      answer:
        "Cesja na bank wygasa wraz ze spłatą zobowiązania. Polisa indywidualna zostaje z Tobą na dotychczasowych warunkach — nadal chroni bliskich, a świadczenie trafia w całości do wskazanych uposażonych. Ochrona w ramach umowy grupowej przy kredycie z reguły kończy się razem z kredytem, a nowa polisa zawierana kilkanaście lat później jest wyraźnie droższa, bo liczy się wiek z dnia zawarcia umowy."
    },
    {
      question: "Czy suma ubezpieczenia musi maleć razem z saldem kredytu?",
      answer:
        "To zależy od wymagań konkretnego banku — część akceptuje sumę stałą, o ile nie jest niższa niż saldo zadłużenia. Suma stała bywa korzystniejsza dla rodziny: bank zaspokaja się do wysokości długu, a nadwyżka trafia do uposażonych. Przy sumie malejącej z każdym rokiem spłaty zostaje dla bliskich odpowiednio mniej. Zapytaj bank wprost, zanim ustalisz sumę w polisie."
    }
  ],
  verificationNote:
    "Kwoty składek pochodzą z modelu orientacyjnych stawek kalkulatora Ochro.pl dla scenariuszy opisanych przy każdym zestawieniu (suma równa kwocie kredytu, osoba niepaląca, zakres ograniczony do śmierci, o ile nie zaznaczono inaczej). Przykłady wpływu marży na ratę to arytmetyka rat annuitetowych przy przyjętym oprocentowaniu 7% — nie są ofertą ani prognozą żadnego banku; podstaw własne liczby z symulacji kredytowej. Opis zasad bankowych ma charakter ogólny: wiążące są dokumenty kredytowe Twojego banku, a zakres i wyłączenia ochrony — OWU. To nie jest oferta handlowa ani porada inwestycyjna; finalną składkę ustala towarzystwo po ocenie ryzyka.",
  sources: [
    {
      label: "Kalkulator polisy pod kredyt",
      href: "/kalkulator-ubezpieczenia-na-zycie-pod-kredyt/",
      desc: "Policz składkę dla swojej kwoty i okresu kredytu"
    },
    {
      label: "Cennik ubezpieczenia na życie 2026",
      href: "/ubezpieczenie-na-zycie-cennik/",
      desc: "Przykładowe składki w jednym zestawieniu"
    },
    {
      label: "PZU — ubezpieczenia na życie i zdrowie",
      href: "https://www.pzu.pl/dla-ciebie-i-rodziny/zycie-i-zdrowie",
      desc: "Materiały produktowe towarzystwa, na których opieramy opisy"
    }
  ],
  related: [
    {
      label: "Polisa pod kredyt hipoteczny",
      href: "/poradnik/ubezpieczenie-na-zycie-pod-kredyt-hipoteczny/",
      desc: "Suma, okres i cesja krok po kroku"
    },
    {
      label: "Ile kosztuje ubezpieczenie na życie",
      href: "/poradnik/ile-kosztuje-ubezpieczenie-na-zycie/",
      desc: "Składki według wieku, sumy i zakresu"
    },
    {
      label: "Jak działa ochrona",
      href: "/poradnik/jak-dziala-ochrona/",
      desc: "Suma, uposażeni, cesja i wypłata świadczenia"
    },
    {
      label: "Polisa na 500 tys. zł",
      href: "/poradnik/ubezpieczenie-na-zycie-500-tys-cena/",
      desc: "Ile kosztuje suma równa typowemu saldu kredytu"
    }
  ]
};

// Kwoty w tym artykule pochodzą z modelu stawek kalkulatora (src/calculators/rates.ts,
// computeOchronne), a parametry karencji i wyłączeń — z bazy wiedzy produktowej
// (functions/api/_chat-knowledge.ts, materiały PZU). Przy zmianie modelu lub OWU
// zaktualizuj liczby i dateModified.
const exclusionsGuidePage: GuidePage = {
  slug: "poradnik/karencja-wylaczenia-polisa-na-zycie",
  label: "Karencja i wyłączenia",
  kicker: "Poradnik OWU",
  title: "Karencja i wyłączenia w polisie na życie — co naprawdę oznaczają",
  copy:
    "Karencja to okres na początku umowy, w którym część ochrony jeszcze nie działa. Wyłączenia to sytuacje, w których ubezpieczyciel nie wypłaci świadczenia nawet po latach opłacania składki. Oba pojęcia brzmią groźnie, a w praktyce są krótką, policzalną listą — poniżej pokazujemy, jak wygląda w indywidualnych polisach PZU i gdzie sprawdzić ją w swojej umowie.",
  image: guideProtectionImage,
  datePublished: "2026-08-31",
  dateModified: "2026-08-31",
  calculatorHref: "/kalkulator-ubezpieczenia-na-zycie/",
  calculatorLabel: "Policz składkę swojej polisy",
  defaultProduct: "life",
  summary: [
    "karencja dotyczy zwykle wybranych dodatków, a nie podstawowej ochrony na wypadek śmierci",
    "w umowie dodatkowej leczenia za granicą karencja wynosi 90 dni, a ciężkie choroby dziecka są objęte ochroną bez karencji",
    "katalog wyłączeń przy śmierci mieści się w kilku punktach: wojna, terror, umyślne przestępstwo, samobójstwo w ciągu 2 lat i zatajenie okoliczności ze skutkiem w ciągu 3 lat",
    "rozszerzenie zakresu to więcej definicji do sprawdzenia i wyższa składka — u 35-latka z sumą 300 tys. zł ok. 141 zł za samą śmierć i ok. 204 zł z poważnym zachorowaniem"
  ],
  sections: [
    {
      heading: "Czym jest karencja w ubezpieczeniu na życie i po co istnieje",
      lead: "Karencja, nazywana też okresem wyczekiwania, to czas liczony od zawarcia umowy, w którym dane świadczenie jeszcze nie przysługuje albo przysługuje w zawężonym zakresie — mimo że składka jest już opłacana.",
      body: [
        "Mechanizm jest prosty: przez wskazaną liczbę dni ochrona obejmuje wyłącznie zdarzenia nagłe, najczęściej następstwa nieszczęśliwego wypadku, a nie stany, które mogły rozwijać się już przed podpisaniem umowy. Chodzi o antyselekcję, czyli sytuację, w której umowę kupuje się dopiero po pierwszych objawach choroby. Gdyby karencji nie było, koszt takich przypadków rozłożyłby się na składki wszystkich pozostałych ubezpieczonych.",
        "Karencji nie należy mylić z trzema innymi terminami z tej samej okolicy umowy. Prawo odstąpienia to 30 dni dla konsumenta (7 dni dla przedsiębiorcy) na wycofanie się z zawartej umowy. Okres wypowiedzenia to miesiąc, po którym umowa się kończy, a niewykorzystana składka wraca. Ochrona tymczasowa działa w drugą stronę: obowiązuje jeszcze przed zawarciem umowy — od dnia po złożeniu wniosku i opłaceniu składki, obejmuje śmierć wskutek nieszczęśliwego wypadku, trwa do 90 dni, a suma odpowiada wnioskowanej, ale nie więcej niż 200 000 zł.",
        "Najważniejsza informacja praktyczna brzmi tak: w indywidualnym ubezpieczeniu na życie podstawowa ochrona na wypadek śmierci co do zasady nie ma ogólnej karencji — działa od dnia rozpoczęcia ochrony wskazanego w polisie. Okresy wyczekiwania pojawiają się przy wybranych umowach dodatkowych i przy podwyższeniu sumy w trakcie trwania umowy. To zupełnie inna sytuacja niż w wielu polisach grupowych z pracy, gdzie karencje są szersze i dotyczą także zdarzeń podstawowych."
      ],
      bullets: [
        "karencja = okres, w którym dane świadczenie jeszcze nie przysługuje lub przysługuje tylko po wypadku",
        "odstąpienie od umowy: 30 dni dla konsumenta, 7 dni dla przedsiębiorcy",
        "wypowiedzenie: w każdym czasie, okres 1 miesiąc, zwrot składki za niewykorzystany okres",
        "ochrona tymczasowa przed zawarciem umowy: śmierć wskutek NW, do 90 dni, maks. 200 000 zł",
        "podstawowa ochrona na wypadek śmierci startuje bez ogólnej karencji"
      ]
    },
    {
      heading: "Ile trwa karencja — typowe okresy przy poszczególnych zdarzeniach",
      lead: "Zamiast jednego terminu obowiązuje kilka, przypisanych do konkretnych umów dodatkowych i sytuacji. Poniżej te, które w materiałach PZU są opisane wprost.",
      body: [
        "Umowa dodatkowa leczenia za granicą ma karencję 90 dni od zawarcia. W tym czasie ochroną objęte są wyłącznie stany będące następstwem nieszczęśliwego wypadku. Analogiczne 90 dni obowiązuje przy przejściu z wariantu Standard na Premium — ale tylko dla zakresu, który jest dostępny wyłącznie w Premium; to, co obejmował już Standard, działa bez przerwy. Sama ochrona danego stanu chorobowego trwa potem do 36 miesięcy lub do wyczerpania sumy.",
        "Nie wszystkie dodatki mają okres wyczekiwania. Umowa obejmująca ciężkie choroby dziecka działa bez karencji i bez wymogu przeżycia określonego czasu od diagnozy — to wyjątek, o który warto pytać, bo bywa argumentem przy porównywaniu ofert. Z kolei przy leczeniu szpitalnym rolę filtra pełni nie karencja, lecz minimalna długość pobytu: świadczenie należy się od 2 dni pobytu po wypadku i od 4 dni po chorobie, w limicie 180 dni w roku.",
        "Osobną kategorią są terminy liczone od zawarcia umowy, które formalnie są ograniczeniami odpowiedzialności, a działają jak karencja. Samobójstwo jest wyłączone przez pierwsze 2 lata trwania umowy. Zatajenie okoliczności, o które pytano w ankiecie, ma znaczenie, jeśli śmierć nastąpi w ciągu 3 lat od zawarcia i będzie ich skutkiem. Podobnie przy podwyższeniu sumy ubezpieczenia w trakcie umowy — dla podwyższonej części obowiązują osobne, 3-letnie i 2-letnie okresy wyłączeń, liczone od momentu podwyższenia, a nie od początku polisy."
      ],
      bullets: [
        "leczenie za granicą — karencja 90 dni, w tym czasie ochrona tylko po nieszczęśliwym wypadku",
        "zmiana wariantu Standard → Premium — 90 dni karencji na zakres dostępny wyłącznie w Premium",
        "ciężkie choroby dziecka — bez karencji i bez wymogu przeżycia",
        "leczenie szpitalne — nie karencja, ale minimum 2 dni pobytu po wypadku i 4 dni po chorobie",
        "samobójstwo — wyłączone przez pierwsze 2 lata od zawarcia umowy",
        "podwyższenie sumy — osobne 3-letnie i 2-letnie okresy wyłączeń dla podwyższonej części"
      ]
    },
    {
      heading: "Katalog wyłączeń — czego polisa na życie nie obejmuje",
      lead: "Wyłączenie to trwała granica ochrony, nieusuwalna upływem czasu. Wbrew obiegowej opinii lista przy podstawowej ochronie życia jest krótka i w dużej części dotyczy sytuacji skrajnych.",
      body: [
        "Przy świadczeniu z tytułu śmierci ubezpieczyciel nie wypłaci pełnej sumy w pięciu przypadkach: działania wojenne; czynny udział w aktach terroru lub masowych rozruchach; popełnienie albo usiłowanie popełnienia umyślnego przestępstwa; samobójstwo w ciągu 2 lat od zawarcia umowy; zatajenie znanych okoliczności, o które pytano we wniosku, jeśli śmierć nastąpiła w ciągu 3 lat od zawarcia i była ich skutkiem. To cały rdzeń katalogu — reszta zapisów w OWU doprecyzowuje definicje, a nie dokłada nowych powodów odmowy.",
        "Więcej wyłączeń mają umowy dodatkowe, bo dotyczą węższych, precyzyjnie zdefiniowanych zdarzeń. W dodatku obejmującym uszczerbek na zdrowiu po wypadku najczęstszym problemem są zawody i sporty wysokiego ryzyka, których nie podano we wniosku — dlatego wspinaczkę, nurkowanie, motocykl czy pracę na wysokości zgłasza się od razu, a nie po zdarzeniu. Przy ciężkich chorobach kluczowa jest lista jednostek: wariant Standard obejmuje 20 chorób, Premium około 67, a łączna wypłata jest ograniczona do 200% sumy, z limitem 100% z jednej grupy chorób.",
        "Najbardziej rozbudowany katalog ma leczenie za granicą, bo produkt organizuje kosztowną terapię w zagranicznych klinikach. Wyłączone są m.in. nowotwory związane z HIV/AIDS, większość nowotworów skóry poza czerniakiem, terapia genowa, rehabilitacja i leczenie uzdrowiskowe oraz przeszczep wątroby będący następstwem alkoholowej choroby wątroby. Jest też warunek formalny, o którym łatwo zapomnieć: ochrona zakłada zamieszkanie w Polsce i nieprzebywanie za granicą dłużej niż 183 dni w roku poprzedzającym zachorowanie."
      ],
      bullets: [
        "śmierć: wojna, terror, umyślne przestępstwo, samobójstwo w 2 lata, zatajenie ze skutkiem w 3 lata",
        "uszczerbek po wypadku: sporty i zawody wysokiego ryzyka niepodane we wniosku",
        "ciężkie choroby: liczy się lista jednostek (20 w Standardzie, ok. 67 w Premium) i limit 200% sumy",
        "leczenie za granicą: m.in. nowotwory skóry poza czerniakiem, terapia genowa, rehabilitacja, uzdrowisko",
        "leczenie za granicą: wymóg zamieszkania w Polsce i maks. 183 dni pobytu za granicą przed zachorowaniem"
      ]
    },
    {
      heading: "Jak czytać OWU, żeby w 20 minut wiedzieć, co masz",
      lead: "OWU to kilkadziesiąt stron, ale nie trzeba czytać ich liniowo. Wystarczy odwiedzić cztery miejsca w ustalonej kolejności — reszta to doprecyzowania.",
      body: [
        "Zacznij od słownika definicji, zwykle na początku dokumentu. To tam rozstrzyga się, czy „nieszczęśliwy wypadek”, „zawał serca” albo „pobyt w szpitalu” znaczą to, co intuicyjnie zakładasz. Większość sporów o wypłatę nie dotyczy złej woli ubezpieczyciela, tylko rozbieżności między definicją potoczną a umowną. Drugim przystankiem jest sekcja o zakresie odpowiedzialności, trzecim — wyłączenia i ograniczenia, czwartym — opis, jakie dokumenty trzeba złożyć przy zgłoszeniu roszczenia i w jakim terminie.",
        "Przy każdej umowie dodatkowej sprawdź osobno trzy liczby: sumę ubezpieczenia, procent tej sumy wypłacany za konkretne zdarzenie i limit łączny. Dodatek operacyjny wypłaca od 5% do 100% sumy w zależności od klasy zabiegu, przy limicie 500% sumy w całym okresie — sama „suma 100 000 zł” nie mówi więc jeszcze nic o kwocie, którą realnie zobaczysz po pojedynczym zdarzeniu. Ta sama zasada dotyczy dodatków szpitalnych i wypadkowych.",
        "Na koniec: masz 30 dni od zawarcia umowy na odstąpienie od niej jako konsument. To wystarczający czas, żeby spokojnie przeczytać OWU już po podpisaniu i zadać pytania. Jeśli coś w definicjach nie zgadza się z tym, co usłyszałeś przy sprzedaży, jest to najlepszy moment na reakcję. Warto też pamiętać o corocznej indeksacji: brak decyzji do 14 dni przed rocznicą polisy oznacza przyjęcie wariantu standardowego, czyli podwyższenie sumy i składki o wskaźnik."
      ],
      bullets: [
        "kolejność czytania: definicje → zakres odpowiedzialności → wyłączenia → zgłoszenie roszczenia",
        "przy każdym dodatku sprawdź sumę, procent za zdarzenie i limit łączny",
        "operacje: 5–100% sumy wg klasy zabiegu, łącznie do 500% sumy",
        "30 dni na odstąpienie to realny czas na przeczytanie dokumentów po podpisaniu",
        "indeksacja: brak decyzji 14 dni przed rocznicą = wariant standardowy",
        "KID to skrót najważniejszych warunków — dobre uzupełnienie, ale nie zastępuje OWU"
      ]
    },
    {
      heading: "Szerszy zakres to więcej definicji i wyższa składka",
      lead: "Karencje i wyłączenia rosną razem z zakresem, bo każdy dodatek wnosi własny słownik pojęć. Warto zestawić to z ceną — poniżej kwoty z naszego kalkulatora dla sumy 300 tys. zł i ochrony na 20 lat, osoba niepaląca.",
      body: [
        "Sama ochrona na wypadek śmierci to dla 35-latka ok. 141 zł miesięcznie (widełki 127–162 zł), a dla 45-latka ok. 269 zł (242–309 zł). Ten wariant ma najkrótszą listę wyłączeń i żadnej ogólnej karencji — jeśli zależy Ci na maksymalnej przewidywalności wypłaty, to jest właśnie ten poziom prostoty.",
        "Dołożenie poważnego zachorowania podnosi składkę 35-latka do ok. 204 zł, a 45-latka do ok. 390 zł. Pełny pakiet zdrowotny to odpowiednio ok. 366 zł i ok. 699 zł miesięcznie. Wraz z tym wzrostem pojawiają się kolejne definicje: lista chorób, limity procentowe, minimalne długości pobytu w szpitalu. To nie jest argument przeciwko dodatkom — to argument za tym, żeby wybierać je świadomie i wiedzieć, na jakie konkretnie zdarzenia się umawiasz.",
        "Podobnie działa leczenie za granicą, jedyny dodatek z wyraźnie opisaną karencją 90 dni. W naszym modelu podnosi składkę 35-latka ze 141 zł do ok. 231 zł w wariancie Standard i ok. 248 zł w Premium; u 45-latka odpowiednio do ok. 381 zł i 402 zł. Przy takiej dopłacie warto poświęcić kwadrans na przeczytanie samego katalogu wyłączeń tej umowy przed podpisaniem, a nie po zachorowaniu."
      ],
      bullets: [
        "35 lat, 300 tys. zł, 20 lat — sama śmierć ok. 141 zł/mies. (127–162 zł)",
        "35 lat — z poważnym zachorowaniem ok. 204 zł, pełny pakiet ok. 366 zł",
        "45 lat — sama śmierć ok. 269 zł, z poważnym zachorowaniem ok. 390 zł, pełny pakiet ok. 699 zł",
        "leczenie za granicą (35 lat): ok. 231 zł Standard, ok. 248 zł Premium — z karencją 90 dni",
        "im szerszy zakres, tym więcej definicji i limitów do sprawdzenia w OWU"
      ]
    }
  ],
  checklistTitle: "Zanim podpiszesz — pięć rzeczy do sprawdzenia",
  checklist: [
    "wypisz, które umowy dodatkowe mają karencję i ile dni trwa w każdej z nich",
    "przeczytaj definicje zdarzeń, na które faktycznie się umawiasz — nie tylko ich nazwy",
    "zgłoś we wniosku sporty i zawód podwyższonego ryzyka, zanim staną się powodem wyłączenia",
    "odpowiedz na wszystkie pytania ankiety zgodnie z prawdą — zatajenie ma znaczenie przez 3 lata",
    "zapisz w kalendarzu datę końca karencji i datę rocznicy polisy (indeksacja, zmiany zakresu)"
  ],
  mistakesTitle: "Najczęstsze błędy przy karencjach i wyłączeniach",
  mistakes: [
    "zakładanie, że karencja obejmuje całą polisę — w indywidualnej ochronie życia dotyczy zwykle wybranych dodatków",
    "mylenie karencji z okresem odstąpienia od umowy i z okresem wypowiedzenia",
    "podwyższenie sumy w trakcie umowy bez sprawdzenia, że dla podwyższonej części biegną osobne okresy wyłączeń",
    "przemilczenie sportu lub zawodu we wniosku — to najczęstszy powód sporu przy dodatkach wypadkowych",
    "czytanie wyłącznie tabeli świadczeń, z pominięciem słownika definicji, w którym rozstrzyga się zakres"
  ],
  faqs: [
    {
      question: "Czy ubezpieczenie na życie ma karencję od pierwszego dnia?",
      answer:
        "Podstawowa ochrona na wypadek śmierci w indywidualnej polisie co do zasady działa od dnia rozpoczęcia ochrony wskazanego w polisie, bez ogólnej karencji. Okresy wyczekiwania dotyczą wybranych umów dodatkowych — na przykład leczenia za granicą, gdzie wynoszą 90 dni — oraz części sumy podwyższonej w trakcie trwania umowy. Dokładne terminy dla konkretnego produktu opisuje OWU."
    },
    {
      question: "Ile trwa karencja przy poważnym zachorowaniu?",
      answer:
        "To zależy od konkretnej umowy dodatkowej i produktu, dlatego termin sprawdza się w OWU tego dodatku. W materiałach PZU wprost opisana karencja 90 dni dotyczy leczenia za granicą, a ciężkie choroby dziecka objęte są ochroną bez karencji i bez wymogu przeżycia określonego czasu od diagnozy."
    },
    {
      question: "Czy ubezpieczyciel wypłaci świadczenie po samobójstwie?",
      answer:
        "Samobójstwo jest wyłączone przez pierwsze 2 lata od zawarcia umowy. Po upływie tego okresu wyłączenie z tego tytułu przestaje obowiązywać. Jest to jeden z pięciu podstawowych punktów katalogu wyłączeń przy świadczeniu z tytułu śmierci."
    },
    {
      question: "Co się stanie, jeśli zataję chorobę w ankiecie medycznej?",
      answer:
        "Zatajenie znanych okoliczności, o które pytano we wniosku, ma znaczenie, jeśli śmierć nastąpi w ciągu 3 lat od zawarcia umowy i będzie ich skutkiem — wtedy ubezpieczyciel może odmówić wypłaty pełnej sumy. Warto pamiętać, że koszt badań medycznych pokrywa ubezpieczyciel, więc rzetelna ankieta nie oznacza dodatkowego wydatku. Szerzej opisujemy to w poradniku o polisie bez badań."
    },
    {
      question: "Czy karencja obowiązuje też przy podwyższeniu sumy ubezpieczenia?",
      answer:
        "Tak. Przy podwyższeniu sumy w trakcie trwania umowy dla podwyższonej części obowiązują osobne 3-letnie i 2-letnie okresy wyłączeń, liczone od momentu podwyższenia. Dotychczasowa część sumy pozostaje objęta ochroną na wcześniejszych zasadach."
    }
  ],
  verificationNote:
    "Opisy karencji, wyłączeń i limitów pochodzą z materiałów produktowych PZU Życie SA, na których opieramy bazę wiedzy serwisu, a kwoty składek — z modelu orientacyjnych stawek kalkulatora Ochro.pl (suma 300 tys. zł, ochrona na 20 lat, osoba niepaląca). Wiążące są zawsze OWU i KID konkretnego produktu oraz decyzja ubezpieczyciela po ocenie ryzyka. Ten artykuł nie jest ofertą handlową.",
  sources: [
    {
      label: "Kalkulator ubezpieczenia na życie",
      href: "/kalkulator-ubezpieczenia-na-zycie/",
      desc: "Policz składkę dla swojego wieku, sumy i zakresu"
    },
    {
      label: "Umowy dodatkowe",
      href: "/umowy-dodatkowe/",
      desc: "Zakres, sumy i limity poszczególnych dodatków"
    },
    {
      label: "PZU — ubezpieczenia na życie i zdrowie",
      href: "https://www.pzu.pl/dla-ciebie-i-rodziny/zycie-i-zdrowie",
      desc: "Materiały produktowe towarzystwa, w tym OWU i KID"
    }
  ],
  related: [
    {
      label: "Jak działa ochrona w polisie",
      href: "/poradnik/jak-dziala-ochrona/",
      desc: "Suma, uposażeni i wypłata świadczenia krok po kroku"
    },
    {
      label: "Ubezpieczenie na życie bez badań",
      href: "/poradnik/ubezpieczenie-na-zycie-bez-badan/",
      desc: "Ankieta medyczna, progi badań i skutki zatajenia"
    },
    {
      label: "Ile kosztuje ubezpieczenie na życie",
      href: "/poradnik/ile-kosztuje-ubezpieczenie-na-zycie/",
      desc: "Przykładowe składki według wieku, sumy i zakresu"
    },
    {
      label: "Umowy dodatkowe do polisy",
      href: "/poradnik/ubezpieczenia-dodatkowe/",
      desc: "Które rozszerzenia mają sens i ile kosztują"
    }
  ]
};

// Kwoty w tym artykule pochodzą z modelu stawek kalkulatora (src/calculators/rates.ts,
// computeOchronne, computeKredyt i computeWsparcie). Model dożywotniego jest kotwiczony
// na składce minimalnej 125 zł — przy zmianie stawek zaktualizuj tabele i dateModified.
const after60GuidePage: GuidePage = {
  slug: "poradnik/ubezpieczenie-na-zycie-po-60",
  label: "Polisa po 60-tce",
  kicker: "Poradnik 60+",
  title: "Ubezpieczenie na życie po 60-tce — co jeszcze można kupić i za ile?",
  copy:
    "Po sześćdziesiątce rynek się zawęża, ale nie zamyka. Terminową polisę na życie w PZU zawrzesz do 65. roku życia, dożywotnią — nawet do 80. Poniżej pokazujemy, jakie okresy i sumy są jeszcze dostępne, ile orientacyjnie kosztują i kiedy każdy z wariantów naprawdę się opłaca.",
  image: guideRetirementImage,
  datePublished: "2026-09-01",
  dateModified: "2026-09-01",
  calculatorHref: "/kalkulator-ubezpieczenia-na-cale-zycie/",
  calculatorLabel: "Policz składkę dożywotnią",
  defaultProduct: "life",
  summary: [
    "polisę terminową zawrzesz do 65. r.ż., a ochrona trwa najdłużej do 70. r.ż. — po 60-tce zostaje maksymalnie 10 lat",
    "60-latek zapłaci orientacyjnie ok. 464 zł/mies. za 200 tys. zł ochrony na 10 lat (widełki 417–533 zł)",
    "ochronę dożywotnią zawrzesz do 80. r.ż.; suma 30 tys. zł w wieku 60 lat to ok. 233 zł/mies., a do tej sumy zwykle wystarczy oświadczenie o stanie zdrowia",
    "po 60-tce sumę dobiera się pod konkretny cel: resztę kredytu, wyrównanie spadku albo koszty końcowe — nie pod „jak najwyższą kwotę\""
  ],
  sections: [
    {
      heading: "Czy po 60. roku życia można jeszcze kupić polisę na życie?",
      lead: "Tak, ale liczba dostępnych wariantów jest mniejsza niż dekadę wcześniej i to wiek decyduje, które drzwi są jeszcze otwarte.",
      body: [
        "W ofercie PZU, na której opieramy kalkulatory, są po sześćdziesiątce dwie ścieżki. Pierwsza to terminowe ubezpieczenie na życie (Ochrona Każdego Dnia): wiek wstępu 18–65 lat, okres od 5 do 30 lat, ale nie dłużej niż do rocznicy w roku, w którym wiek polisowy osiąga 70 lat. Arytmetyka jest prosta — 60-latek kupi maksymalnie 10 lat ochrony, 62-latek 8 lat, a 65-latek dokładnie 5 lat, czyli tyle, ile wynosi minimum. Po 65. urodzinach ta ścieżka się zamyka.",
        "Druga to ubezpieczenie dożywotnie (Wsparcie Najbliższych): wiek wstępu 13–80 lat, umowa na czas nieokreślony, ochrona do końca życia. Nie wybiera się tu okresu — jest za to określony czas opłacania składki, zależny od wieku wejścia. Ten produkt zostaje dostępny także wtedy, gdy terminowej już się nie kupi.",
        "Jest jeszcze ograniczenie, o którym rzadko mówi się na starcie: przy wysokich sumach PZU stosuje ocenę finansową, a maksymalna potrzeba ubezpieczeniowa od 60. roku życia to 7-krotność rocznego dochodu netto (przed trzydziestką — 25-krotność). Kwestionariusz finansowy pojawia się powyżej 1,1 mln zł łącznej sumy z wniosku i czynnych polis."
      ],
      bullets: [
        "terminowa (Ochrona Każdego Dnia): wiek wstępu 18–65 lat, suma od 50 000 zł",
        "ochrona terminowa najdłużej do roku, w którym wiek polisowy = 70 lat",
        "dożywotnia (Wsparcie Najbliższych): wiek wstępu 13–80 lat, brak minimalnej sumy",
        "ocena finansowa: maks. potrzeba ubezpieczeniowa od 60. r.ż. to 7× roczny dochód netto",
        "składka minimalna: 45 zł/mies. w terminowej, 125 zł/mies. w dożywotniej"
      ]
    },
    {
      heading: "Ile kosztuje polisa terminowa po 60-tce — przykładowe składki",
      lead: "Scenariusz: osoba niepaląca, sama umowa główna na wypadek śmierci, maksymalny dostępny okres ochrony dla danego wieku. Kwoty orientacyjne z naszego kalkulatora, zaokrąglone.",
      body: [
        "Dla 60-latka i ochrony na 10 lat suma 100 tys. zł to ok. 240 zł miesięcznie (widełki 216–276 zł), 150 tys. zł — ok. 352 zł, 200 tys. zł — ok. 464 zł (417–533 zł), a 300 tys. zł — ok. 687 zł. Zależność sumy i składki jest niemal liniowa, więc pierwszym pytaniem nie jest „ile chcę płacić\", tylko „jaką dziurę w domowych finansach ma zasypać ta wypłata\".",
        "Rok zwłoki po sześćdziesiątce kosztuje podwójnie: rośnie stawka i skraca się maksymalny okres ochrony. Ta sama suma 200 tys. zł to ok. 464 zł miesięcznie dla 60-latka (10 lat), ok. 542 zł dla 62-latka (8 lat) i ok. 614 zł dla 65-latka (5 lat). Dla porównania 55-latek za 200 tys. zł na 15 lat zapłaci ok. 351 zł, a 50-latek za tę samą sumę na 20 lat — ok. 257 zł.",
        "Palenie działa jak wcześniej: w naszym modelu to ok. 40% dopłaty. 60-latek niepalący płaci za 200 tys. zł ok. 464 zł, palący — ok. 649 zł; u 65-latka odpowiednio 614 i 859 zł. Jeśli rzuciłeś palenie, zapytaj o zasady rekalkulacji — część towarzystw po 12 miesiącach bez nikotyny stosuje stawki dla niepalących."
      ],
      bullets: [
        "60 lat, 10 lat ochrony: 100 tys. zł — ok. 240 zł/mies., 200 tys. zł — ok. 464 zł, 300 tys. zł — ok. 687 zł",
        "62 lata, 8 lat ochrony, 200 tys. zł — ok. 542 zł/mies. (488–623 zł)",
        "65 lat, 5 lat ochrony, 200 tys. zł — ok. 614 zł/mies. (552–706 zł)",
        "palenie: 60 lat i 200 tys. zł to ok. 649 zł zamiast 464 zł",
        "dla porównania: 55 lat na 15 lat — ok. 351 zł, 50 lat na 20 lat — ok. 257 zł"
      ]
    },
    {
      heading: "Ochrona dożywotnia po 60 — jak działa i ile kosztuje",
      lead: "Dożywotnia polisa nie ma daty końcowej: wypłata dla bliskich jest pewna, a składkę opłaca się tylko przez określony czas, zależny od wieku wejścia.",
      body: [
        "Harmonogram opłacania składki wynika wprost z warunków produktu. Przy wieku wstępu 56–60 lat składkę płaci się do rocznicy w roku ukończenia 80 lat, przy 61–65 lat — do 85 lat, a od 66. roku życia przez 15 lat. Potem ochrona trwa dalej, bez kolejnych wpłat — inaczej niż w polisie terminowej, która po prostu wygasa.",
        "Orientacyjne składki z kalkulatora dla osoby niepalącej: 60-latek zapłaci ok. 233 zł miesięcznie przy sumie 30 tys. zł, ok. 358 zł przy 50 tys. zł i ok. 670 zł przy 100 tys. zł. Dla 65-latka te same sumy to odpowiednio ok. 315, 494 i 943 zł, a dla 70-latka — ok. 434 zł przy 30 tys. zł i ok. 694 zł przy 50 tys. zł. Powyżej 70. roku życia warto od razu poprosić o kalkulację indywidualną.",
        "Zanim podpiszesz, zrób jedno mnożenie. Przy tych stawkach 60-latek z sumą 50 tys. zł wpłaciłby przez 20 lat opłacania ok. 86 tys. zł — więcej, niż wynosi sama suma. Dożywotnia polisa nie jest więc sposobem na pomnożenie pieniędzy i nie tak trzeba ją oceniać. Jej sens to pewna wypłata dla wskazanych uposażonych, zwolniona z podatku od spadków i darowizn i poza masą spadkową. Dlatego po 60-tce dobiera się ją do policzonego celu — kosztów końcowych, wyrównania udziałów między dziećmi, spłaty niewielkiego zobowiązania — a nie do „jak najwyższej sumy\"."
      ],
      bullets: [
        "60 lat: 30 tys. zł — ok. 233 zł/mies., 50 tys. zł — ok. 358 zł, 100 tys. zł — ok. 670 zł",
        "65 lat: 30 tys. zł — ok. 315 zł/mies., 50 tys. zł — ok. 494 zł, 100 tys. zł — ok. 943 zł",
        "70 lat: 30 tys. zł — ok. 434 zł/mies., 50 tys. zł — ok. 694 zł",
        "składkę płacisz do 80. r.ż. (wstęp 56–60), do 85. r.ż. (wstęp 61–65) lub 15 lat (wstęp od 66)",
        "do sumy 30 000 zł zwykle wystarczy oświadczenie o stanie zdrowia, bez pełnej ankiety",
        "świadczenie dla uposażonych: wolne od podatku od spadków i darowizn, poza masą spadkową"
      ]
    },
    {
      heading: "Ankieta, badania i formalności po sześćdziesiątce",
      lead: "Wiek sam w sobie nie jest przeszkodą — przeszkodą bywa niedopowiedziana historia zdrowia. Warto wiedzieć z góry, o co zapyta towarzystwo.",
      body: [
        "Po 45. roku życia obowiązuje ankieta pełna (11 pytań) — skrócona dotyczy tylko młodszych wnioskodawców z sumą do 400 tys. zł. Przy wyższych sumach PZU może skierować na badania, a ich koszt pokrywa towarzystwo, nie klient. Zakres rośnie z sumą: od podstawowych badań krwi powyżej 50 tys. zł po szeroki panel (m.in. USG jamy brzusznej, EKG wysiłkowe, ECHO serca, PSA u mężczyzn) przy sumach rzędu miliona złotych.",
        "Po ocenie ryzyka są trzy wyniki: umowa na warunkach standardowych, na zmienionych (niższa suma albo wyższa składka) albo odmowa. Ta trzecia możliwość istnieje realnie — ale nie warto jej wyprzedzać rezygnacją z próby, zwłaszcza przy umiarkowanych sumach, gdzie wymagania są łagodniejsze.",
        "O uczciwości w ankiecie decydują dwie zasady. Zatajenie znanych okoliczności, o które towarzystwo pytało, może pozbawić bliskich pełnej wypłaty, jeżeli śmierć nastąpi w ciągu 3 lat od zawarcia umowy i będzie ich skutkiem. Wśród standardowych wyłączeń jest też samobójstwo w ciągu 2 lat od zawarcia umowy, działania wojenne, czynny udział w aktach terroru i popełnienie umyślnego przestępstwa. Poza tym ochrona działa na całym świecie, całą dobę, a od dnia po złożeniu wniosku i opłaceniu składki obowiązuje ochrona tymczasowa na wypadek śmierci wskutek nieszczęśliwego wypadku — do 90 dni, z sumą do 200 tys. zł."
      ],
      bullets: [
        "od 46. r.ż. obowiązuje ankieta pełna (11 pytań), niezależnie od sumy",
        "koszt badań medycznych pokrywa PZU; zakres rośnie z sumą i wiekiem",
        "możliwe wyniki oceny: warunki standardowe, zmienione (niższa suma / wyższa składka) albo odmowa",
        "zatajenie ze skutkiem w ciągu 3 lat od zawarcia umowy może ograniczyć wypłatę",
        "ochrona tymczasowa po złożeniu wniosku: do 90 dni, suma do 200 000 zł, tylko wypadki"
      ]
    },
    {
      heading: "Umowy dodatkowe po 60 — co jeszcze można dokupić",
      lead: "Większość rozszerzeń ma własne, niższe niż w umowie głównej limity wieku. Po sześćdziesiątce lista się skraca, więc warto sprawdzić ją zanim ułożysz budżet.",
      body: [
        "Nadal dostępne po 60-tce są m.in.: śmierć wskutek nieszczęśliwego wypadku (NW) i wypadku komunikacyjnego (WK) — do 74. roku życia, uszczerbek po wypadku (TU) — do 70. roku życia, a ciężkie choroby (CC), leczenie szpitalne (LS), operacje (OP) i złamania (ZK) — do 65. roku życia. Umowa utraty sprawności (US) jest już zamknięta, bo dostępna tylko do 60. roku życia, a leczenie za granicą (LZ) zawrzesz do 64. roku życia, z ochroną do 65 lat.",
        "Rozszerzenia kosztują po sześćdziesiątce wyraźnie więcej niż u młodszych. Widać to u 60-latka z sumą 200 tys. zł na 10 lat: sama ochrona życia to ok. 464 zł miesięcznie, wariant z poważnymi zachorowaniami — ok. 672 zł, pełny pakiet zdrowotny — ok. 1 205 zł. Leczenie za granicą ma własną tabelę: w przedziale 60–64 lata to 158 zł miesięcznie w wariancie Standard i 189 zł w Premium.",
        "Zasada jest prosta: najpierw ustaw sumę na życie, potem dokładaj rozszerzenia. Odwrotna kolejność kończy się rozbudowanym pakietem świadczeń przy sumie, która nie pokrywa nawet zobowiązań."
      ],
      bullets: [
        "NW i WK (śmierć wskutek wypadku) — do 74. r.ż.; TU (uszczerbek po wypadku) — do 70. r.ż.",
        "CC (ciężkie choroby), LS (szpital), OP (operacje), ZK (złamania) — do 65. r.ż.",
        "LZ (leczenie za granicą) — wstęp do 64. r.ż., składka 60–64 lata: 158 zł Standard / 189 zł Premium",
        "US (utrata sprawności) — tylko do 60. r.ż.",
        "60 lat, 200 tys. zł na 10 lat: sama śmierć ok. 464 zł, +poważne zachorowania ok. 672 zł, pełny pakiet ok. 1 205 zł"
      ]
    },
    {
      heading: "Jak dobrać sumę i wariant po 60-tce",
      lead: "Po sześćdziesiątce cel polisy jest zwykle węższy i łatwiejszy do policzenia niż w wieku trzydziestu lat — to dobra wiadomość dla portfela.",
      body: [
        "Zacznij od pytania, co dokładnie ma sfinansować wypłata. Po 60-tce najczęstsze odpowiedzi to: reszta kredytu, zabezpieczenie współmałżonka, który zostałby z jedną emeryturą zamiast dwóch, wyrównanie udziałów między dziećmi (np. gdy mieszkanie przypada jednemu z nich) oraz koszty końcowe. Każdy z tych celów ma konkretną kwotę — i to ona, a nie okrągła liczba, powinna być sumą ubezpieczenia.",
        "Potem wybierz wariant. Cel z datą końcową — kredyt spłacany jeszcze przez 8 lat, dziecko kończące studia — to polisa terminowa. Cel bez daty, dotyczący tego, co zostanie po Tobie niezależnie od momentu, to ochrona dożywotnia. Częsta i sensowna kombinacja: niewielka dożywotnia na koszty końcowe plus terminowa na resztę kredytu. Dla kredytu liczy się to osobno — 60-latek z saldem 150 tys. zł na 10 lat zapłaci orientacyjnie ok. 352 zł miesięcznie, przy saldzie 200 tys. zł — ok. 464 zł.",
        "Na koniec dwie rzeczy techniczne. Sprawdź, co dzieje się z polisą grupową z pracy — zwykle wygasa lub zmienia warunki wraz z końcem zatrudnienia, a wiele osób dowiaduje się o tym za późno. I zdecyduj świadomie w sprawie indeksacji: coroczne podwyższanie sumy i składki chroni wypłatę przed inflacją, ale podnosi koszt; brak decyzji na 14 dni przed rocznicą oznacza wariant standardowy."
      ],
      bullets: [
        "policz cel w złotówkach: saldo kredytu, luka w budżecie współmałżonka, wyrównanie spadku, koszty końcowe",
        "cel z datą końcową → terminowa; cel bez daty → dożywotnia; często dobrze działa kombinacja obu",
        "kredyt 150 tys. zł na 10 lat dla 60-latka — ok. 352 zł/mies.; 200 tys. zł — ok. 464 zł/mies.",
        "sprawdź, co dzieje się z polisą grupową po zakończeniu pracy",
        "indeksacja: chroni sumę przed inflacją, ale podnosi składkę — decyzję zgłoś najpóźniej 14 dni przed rocznicą"
      ]
    }
  ],
  checklistTitle: "Przygotuj to przed rozmową z doradcą",
  checklist: [
    "dokładny wiek i informację, czy palisz (również e-papierosy i podgrzewacze)",
    "cel polisy przeliczony na złotówki: saldo kredytu, koszty końcowe, kwota dla współmałżonka",
    "listę chorób przewlekłych i przyjmowanych leków — i tak wyjdą w pełnej ankiecie",
    "informację o polisie grupowej z pracy oraz o innych czynnych polisach (liczą się do limitów sumy)",
    "orientacyjny budżet miesięczny — pamiętaj o minimach: 45 zł w terminowej, 125 zł w dożywotniej"
  ],
  mistakesTitle: "Najczęstsze błędy po 60-tce",
  mistakes: [
    "odkładanie decyzji o rok — po 60-tce rośnie składka i jednocześnie skraca się maksymalny okres ochrony",
    "traktowanie polisy dożywotniej jak lokaty — przy wysokim wieku wstępu suma wpłat potrafi przekroczyć sumę ubezpieczenia",
    "kupowanie szerokiego pakietu dodatków przy sumie na życie, która nie pokrywa nawet zobowiązań",
    "zakładanie, że polisa grupowa z pracy będzie działać także po przejściu na emeryturę",
    "pomijanie chorób w ankiecie „bo to drobiazg\" — zatajenie może ograniczyć wypłatę przez pierwsze 3 lata"
  ],
  faqs: [
    {
      question: "Do jakiego wieku można kupić ubezpieczenie na życie?",
      answer:
        "Terminowe ubezpieczenie na życie w PZU zawrzesz do 65. roku życia, a ochrona trwa najdłużej do roku, w którym wiek polisowy osiąga 70 lat. Ubezpieczenie dożywotnie można zawrzeć do 80. roku życia i trwa ono do końca życia. Po 65. roku życia dożywotnia jest więc zwykle jedyną dostępną formą ochrony na życie."
    },
    {
      question: "Ile kosztuje ubezpieczenie na życie dla 60-latka?",
      answer:
        "Orientacyjnie ok. 240 zł miesięcznie za 100 tys. zł ochrony na 10 lat i ok. 464 zł za 200 tys. zł (widełki 417–533 zł) — dla osoby niepalącej, z samą umową główną. Palenie podnosi te kwoty o ok. 40%. Ostateczną składkę potwierdza doradca po ankiecie i ocenie ryzyka."
    },
    {
      question: "Czy po 60-tce trzeba robić badania lekarskie?",
      answer:
        "Nie zawsze, ale jest to bardziej prawdopodobne niż u młodszych. Od 46. roku życia obowiązuje pełna ankieta medyczna (11 pytań), a przy wyższych sumach towarzystwo kieruje na badania — ich koszt pokrywa PZU. Przy dożywotniej polisie z sumą do 30 000 zł zwykle wystarczy samo oświadczenie o stanie zdrowia."
    },
    {
      question: "Terminowa czy dożywotnia po 60. roku życia?",
      answer:
        "Decyduje cel. Terminowa jest tańsza i pasuje, gdy chronisz coś z datą końcową — resztę kredytu albo lata do usamodzielnienia się dziecka. Dożywotnia kosztuje więcej, ale wypłata jest pewna niezależnie od momentu śmierci, więc sprawdza się przy kosztach końcowych i wyrównaniu spadku. Często najlepiej działa połączenie obu."
    },
    {
      question: "Czy ubezpieczyciel może odmówić polisy ze względu na wiek lub chorobę?",
      answer:
        "Tak. Po ocenie ryzyka towarzystwo może zawrzeć umowę na warunkach standardowych, na zmienionych (niższa suma albo wyższa składka) lub odmówić. Przy umiarkowanych sumach wymagania są jednak łagodniejsze, a przy dożywotniej polisie do 30 000 zł zwykle wystarcza oświadczenie o stanie zdrowia — dlatego warto sprawdzić kilka wariantów, zanim uzna się temat za zamknięty."
    }
  ],
  verificationNote:
    "Kwoty składek pochodzą z modelu orientacyjnych stawek kalkulatora Ochro.pl dla scenariuszy opisanych przy każdym zestawieniu (osoba niepaląca, sama umowa główna, o ile nie zaznaczono inaczej). Model ochrony dożywotniej jest przybliżeniem opartym na składce minimalnej 125 zł/mies., więc przy tym wariancie różnice wobec finalnej oferty bywają większe. Limity wieku, zasady ankiety i wyłączenia podajemy za materiałami produktowymi PZU — wiążące są OWU i KID. To nie jest oferta handlowa; finalną składkę ustala towarzystwo po ocenie ryzyka.",
  sources: [
    {
      label: "Kalkulator dożywotniego ubezpieczenia na życie",
      href: "/kalkulator-ubezpieczenia-na-cale-zycie/",
      desc: "Policz orientacyjną składkę ochrony do końca życia dla wieku 13–80 lat"
    },
    {
      label: "Kalkulator ubezpieczenia na życie",
      href: "/kalkulator-ubezpieczenia-na-zycie/",
      desc: "Wariant terminowy — dostępny do 65. roku życia"
    },
    {
      label: "Cennik ubezpieczenia na życie 2026",
      href: "/ubezpieczenie-na-zycie-cennik/",
      desc: "Przykładowe składki dla różnych wieków i sum w jednym miejscu"
    },
    {
      label: "PZU — ubezpieczenia na życie i zdrowie",
      href: "https://www.pzu.pl/dla-ciebie-i-rodziny/zycie-i-zdrowie",
      desc: "Materiały produktowe towarzystwa, na których opieramy limity wieku i zasady"
    }
  ],
  related: [
    {
      label: "Ubezpieczenie na życie po 50-tce",
      href: "/poradnik/ubezpieczenie-na-zycie-po-50/",
      desc: "Wariant dla dekady, w której wyborów jest jeszcze więcej"
    },
    {
      label: "Ile kosztuje ubezpieczenie na życie",
      href: "/poradnik/ile-kosztuje-ubezpieczenie-na-zycie/",
      desc: "Składki według wieku, sumy, palenia i zakresu"
    },
    {
      label: "Karencja i wyłączenia w polisie",
      href: "/poradnik/karencja-wylaczenia-polisa-na-zycie/",
      desc: "Co oznaczają okresy wyczekiwania i czego polisa nie obejmuje"
    },
    {
      label: "Kalkulator dożywotniego ubezpieczenia",
      href: "/kalkulator-ubezpieczenia-na-cale-zycie/",
      desc: "Sprawdź składkę ochrony na całe życie"
    }
  ]
};

const guidePages: Record<GuideRoute, GuidePage> = {
  guideAfter60: after60GuidePage,
  guideExclusions: exclusionsGuidePage,
  guideBankPolicy: bankPolicyGuidePage,
  guideSmokers: smokersGuidePage,
  guideNoExam: noExamGuidePage,
  guideSum: sumGuidePage,
  guide500k: sum500kGuidePage,
  guideCost: costGuidePage,
  guideAfter50: {
    slug: "poradnik/ubezpieczenie-na-zycie-po-50",
  datePublished: "2026-06-30",
  dateModified: "2026-08-12",
    label: "Polisa po 50-tce",
    kicker: "Poradnik 50+",
    title: "Polisa po 50-tce: jak dobrać ochronę bez przepłacania?",
    copy:
      "Po 50 najważniejsze są prostota, sensowna suma i jasne wyłączenia. Sprawdź, kiedy lepsza jest polisa terminowa, kiedy dożywotnia, a kiedy dodatki chorobowe podnoszą cenę bardziej niż pomagają.",
    image: guideRetirementImage,
    calculatorHref: "/kalkulator-ubezpieczenia-na-zycie/",
    calculatorLabel: "Policz składkę",
    defaultProduct: "life",
    summary: [
      "składka po 50 mocno zależy od wieku, zdrowia, sumy i okresu ochrony",
      "warto porównać ochronę terminową, polisę dożywotnią i wybrane dodatki",
      "czasem lepiej mieć prostszą polisę z wyższą sumą niż wiele drogich rozszerzeń",
      "dobry punkt startu to 3 warianty: tańszy, optymalny i szerszy"
    ],
    sections: [
      {
        heading: "Po 50 zacznij od celu, nie od produktu",
        lead: "Ten sam budżet może dać zupełnie inny efekt, zależnie od tego, czy chcesz zabezpieczyć rodzinę, kredyt, partnera, koszty pogrzebu czy spokojny kapitał dla bliskich.",
        body: [
          "Najpierw trzeba nazwać problem. Jeżeli utrzymujesz rodzinę albo partnera, najważniejsza może być suma wypłacana po śmierci. Jeżeli dzieci są już dorosłe, ale masz kredyt lub inne zobowiązania, suma powinna odpowiadać realnemu ciężarowi finansowemu.",
          "Jeżeli zależy Ci głównie na tym, żeby bliscy mieli pieniądze niezależnie od momentu śmierci, warto rozmawiać także o ochronie dożywotniej. Taka polisa ma inny sens niż terminowa ochrona pod dochód lub kredyt.",
          "Dopiero po ustaleniu celu ma sens rozmowa o dodatkach: ciężkich chorobach, pobycie w szpitalu, operacjach czy NNW. Dodatki mogą pomóc, ale nie powinny zjadać budżetu, jeśli przez to podstawowa suma na życie robi się za niska."
        ],
        bullets: [
          "rodzina i partner: sprawdź, ile pieniędzy realnie byłoby potrzebne",
          "kredyt: policz saldo i okres pozostały do spłaty",
          "dorosłe dzieci: oceń, czy celem jest wsparcie bliskich czy spadek kosztów",
          "zdrowie: dodatki dobieraj według definicji i limitów, nie według samej nazwy"
        ]
      },
      {
        heading: "Terminowa czy dożywotnia: dwie różne potrzeby",
        lead: "Polisa terminowa zwykle chroni przez wybrany okres, a dożywotnia ma zostawić świadczenie bliskim niezależnie od wieku. To nie są zamienniki jeden do jednego.",
        body: [
          "Terminowe ubezpieczenie na życie bywa dobre, gdy ryzyko ma konkretny horyzont: kilka lub kilkanaście lat kredytu, czas do emerytury partnera, okres prowadzenia firmy albo moment, w którym majątek rodziny jest jeszcze niewystarczający.",
          "Polisa dożywotnia może być rozważana, gdy celem jest pieniądz dla bliskich po śmierci, bez wiązania ochrony z końcem konkretnego kredytu lub okresem utrzymywania dzieci. Zwykle wymaga spokojniejszego omówienia składki, sumy i zasad utrzymania umowy.",
          "Po 50 bardzo łatwo porównać złe rzeczy: np. wysoką sumę na krótki okres z niższą sumą dożywotnią. Dlatego prosimy o 3 warianty, które mają ten sam cel, ale różnią się budżetem i zakresem."
        ],
        bullets: [
          "terminowa: najczęściej pod dochód, kredyt lub określony etap życia",
          "dożywotnia: częściej pod wsparcie bliskich i koszty końcowe",
          "mieszana: część ochrony terminowo, część jako spokojniejszy fundament",
          "każdy wariant trzeba sprawdzić z OWU i decyzją ubezpieczyciela"
        ]
      },
      {
        heading: "Jak ustalić sumę ubezpieczenia po 50",
        lead: "Najtańsza składka nie pomoże, jeśli suma będzie symboliczna. Z drugiej strony zbyt wysoka suma może niepotrzebnie podnieść cenę albo utrudnić ocenę ryzyka.",
        body: [
          "Dobrą metodą jest podział na warstwy. Pierwsza warstwa to zobowiązania: kredyt, pożyczki, leasing, pomoc dzieciom lub inne stałe obciążenia. Druga to kilka lub kilkanaście miesięcy kosztów życia partnera albo rodziny.",
          "Trzecia warstwa to cele dodatkowe: pieniądze na organizację spraw po śmierci, wsparcie wnuków, dopięcie spraw majątkowych albo zabezpieczenie firmy. Nie każda osoba potrzebuje wszystkich warstw, ale każda powinna świadomie zdecydować, czego oczekuje od polisy.",
          "W wieku 50-65 warto sprawdzić kilka sum, np. niższą, średnią i wysoką. Czasem różnica w składce jest akceptowalna, a czasem lepiej wybrać krótszy okres albo prostszy zakres."
        ],
        bullets: [
          "saldo kredytu i inne zobowiązania",
          "koszty życia partnera lub rodziny przez określony czas",
          "koszty organizacyjne i rezerwa dla bliskich",
          "budżet miesięczny, który da się utrzymać przez lata"
        ]
      },
      {
        heading: "Zdrowie, ankieta i dodatki: gdzie najłatwiej przepłacić",
        lead: "Po 50 ankieta medyczna i historia leczenia mają większe znaczenie. To normalne, ale wymaga spokojnego porównania wariantów.",
        body: [
          "Ubezpieczyciel może pytać o choroby przewlekłe, leki, zabiegi, badania, wzrost, wagę, palenie, zawód i aktywności. Przy wyższych sumach albo szerszych dodatkach może pojawić się potrzeba dokumentacji lub dodatkowej oceny ryzyka.",
          "Dodatki chorobowe i szpitalne mogą być wartościowe, ale trzeba znać definicje świadczeń, karencje, limity i wyłączenia. Samo hasło „ciężkie choroby” nie wystarcza, bo wypłata zależy od konkretnych zapisów OWU.",
          "Jeżeli budżet jest ograniczony, często lepiej najpierw zabezpieczyć główną sumę na życie, a dopiero potem rozszerzać zakres. Wariant szerszy powinien pokazywać, ile kosztują dodatki i co dokładnie dają."
        ],
        bullets: [
          "porównuj definicje chorób, karencje i limity",
          "sprawdź, czy dodatek działa po chorobie czy po wypadku",
          "pytaj o wpływ palenia, BMI, leczenia i zawodu na składkę",
          "nie obniżaj podstawowej sumy tylko po to, żeby dodać kilka drobnych świadczeń"
        ]
      },
      {
        heading: "Jak rozmawiać z doradcą, żeby dostać sensowne warianty",
        lead: "Najlepsza rozmowa po 50 nie kończy się jedną składką. Powinna pokazać kompromis między ceną, sumą, okresem i dodatkami.",
        body: [
          "Poproś o wariant tańszy, optymalny i szerszy. Tańszy pokazuje minimalny sensowny zakres. Optymalny powinien być punktem równowagi między ceną a ochroną. Szerszy pozwala zobaczyć, ile kosztują dodatki i czy warto je utrzymać.",
          "Przed rozmową przygotuj wiek, informację o paleniu, główne zobowiązania, cel polisy i orientacyjny budżet miesięczny. Nie musisz znać nazw produktów. Ważniejsze jest to, żeby doradca zrozumiał, co ma chronić polisa.",
          "Po otrzymaniu propozycji sprawdź nie tylko składkę. Zobacz sumę, okres, uposażonych, wyłączenia, karencje, dodatki i zasady utrzymania umowy. Dopiero wtedy cena ma kontekst."
        ],
        bullets: [
          "wariant tańszy: najprostszy zakres i kontrola budżetu",
          "wariant optymalny: rozsądna suma i najważniejsze dodatki",
          "wariant szerszy: pełniejsza ochrona do świadomego porównania",
          "finalna propozycja zawsze zależy od OWU, ankiety i decyzji ubezpieczyciela"
        ]
      }
    ],
    checklistTitle: "Co przygotować przed rozmową",
    checklist: [
      "wiek i informację, czy palisz papierosy",
      "saldo kredytu lub inne zobowiązania, które chcesz zabezpieczyć",
      "orientacyjny budżet miesięczny na składkę",
      "cel polisy: rodzina, partner, kredyt, koszty końcowe, kapitał",
      "informację o obecnej polisie grupowej lub bankowej",
      "najważniejsze kwestie zdrowotne, które mogą mieć wpływ na ankietę"
    ],
    mistakesTitle: "Czego unikać po 50",
    mistakes: [
      "wyboru polisy tylko dlatego, że ma najniższą składkę",
      "dokładania wielu dodatków przy zbyt niskiej sumie na życie",
      "porównywania polisy terminowej z dożywotnią bez wspólnego celu",
      "zakładania, że polisa grupowa wystarczy na rodzinę lub kredyt",
      "odkładania decyzji, gdy potrzeba ochrony jest już konkretna"
    ],
    faqs: [
      {
        question: "Czy po 50 można jeszcze kupić ubezpieczenie na życie?",
        answer:
          "Tak, ale dostępność wariantów, suma, okres i składka zależą od wieku, zdrowia, celu polisy oraz decyzji ubezpieczyciela. Warto porównać kilka opcji zamiast zakładać, że istnieje tylko jedna droga."
      },
      {
        question: "Czy po 60 lepsza jest polisa terminowa czy dożywotnia?",
        answer:
          "To zależy od celu. Terminowa ochrona częściej pasuje do kredytu, dochodu lub konkretnego okresu ryzyka. Dożywotnia może mieć sens, gdy celem jest wsparcie bliskich niezależnie od momentu śmierci."
      },
      {
        question: "Czy im więcej dodatków, tym lepsza polisa?",
        answer:
          "Nie zawsze. Dodatki są dobre, jeśli odpowiadają na realne ryzyko i mają jasne definicje. Przy ograniczonym budżecie ważniejsza może być wyższa suma podstawowa niż szeroka lista drobnych świadczeń."
      },
      {
        question: "Czy trzeba robić badania medyczne?",
        answer:
          "Nie zawsze. Ubezpieczyciel może poprosić o ankietę, dokumentację lub badania w zależności od wieku, sumy, zakresu i historii zdrowia. Decyzja zapada indywidualnie."
      }
    ],
    related: [
      { label: "Otrzymaj 3 warianty", href: "#formularz-start", desc: "Porównaj cenę, sumę i zakres ochrony" },
      { label: "Ubezpieczenie na całe życie", href: "/kalkulator-ubezpieczenia-na-cale-zycie/", desc: "Sprawdź ochronę dożywotnią" },
      {
        label: "Ubezpieczenie na życie po 60-tce",
        href: "/poradnik/ubezpieczenie-na-zycie-po-60/",
        desc: "Co jeszcze można kupić po sześćdziesiątce i za ile"
      }
    ]
  },
  guideChoose: {
    slug: "poradnik/jak-wybrac-polise",
  datePublished: "2026-05-30",
  dateModified: "2026-08-12",
    label: "Jak wybrać polisę",
    kicker: "Poradnik",
    title: "Jak wybrać ubezpieczenie na życie i nie przepłacić?",
    copy:
      "Zanim zostawisz telefon albo kupisz polisę w banku, sprawdź, co naprawdę wpływa na cenę. Ten poradnik pokazuje, jak dobrać sumę, zakres i budżet, a potem poprosić doradcę o 3 rozsądne warianty ochrony.",
    image: guideChooseImage,
    summary: [
      "najpierw policz realną potrzebę finansową, a nie miesięczną składkę",
      "oddziel ochronę życia od dodatków chorobowych, wypadkowych i zdrowotnych",
      "sprawdź OWU, definicje świadczeń, karencje i wyłączenia odpowiedzialności",
      "poproś o wariant tańszy, optymalny i szerszy zamiast jednej przypadkowej propozycji"
    ],
    sections: [
      {
        heading: "Zacznij od celu, nie od produktu",
        lead: "Ubezpieczenie na życie może zabezpieczać rodzinę, kredyt, wspólnika, dziecko albo przyszły kapitał. Każdy z tych celów wymaga trochę innej konstrukcji.",
        body: [
          "Jeśli głównym problemem jest utrata dochodu, najważniejsza będzie odpowiednio wysoka suma ubezpieczenia na wypadek śmierci. Jeżeli w tle jest kredyt hipoteczny, część sumy może odpowiadać saldu zadłużenia, a część może zostać jako bufor dla rodziny.",
          "Jeżeli klient ma już polisę grupową w pracy, warto sprawdzić nie tylko składkę, ale też realne sumy świadczeń. Wiele grupówek pomaga przy mniejszych zdarzeniach, ale nie rozwiązuje problemu wysokiego kredytu, kilkuletnich kosztów życia albo utrzymania dzieci.",
          "Inny cel ma polisa na życie i dożycie: tam ochrona miesza się z budowaniem kapitału. Jeszcze inny cel ma ubezpieczenie dożywotnie, które często wybiera się po to, aby zostawić bliskim określoną kwotę niezależnie od wieku."
        ],
        bullets: [
          "rodzina i dzieci: zwykle potrzebują wysokiej sumy ochronnej",
          "kredyt: kluczowe są saldo, okres spłaty i wymagania banku",
          "firma: warto pomyśleć o wspólnikach, zobowiązaniach i płynności",
          "kapitał: trzeba rozumieć wykup, premie i konsekwencje rezygnacji"
        ]
      },
      {
        heading: "Policz sumę ubezpieczenia w kilku warstwach",
        lead: "Najczęstszy błąd to wybór przypadkowej kwoty, np. 100 000 zł, tylko dlatego, że wygląda rozsądnie. Suma powinna wynikać z potrzeb.",
        body: [
          "Pierwsza warstwa to zobowiązania: kredyt hipoteczny, pożyczki, leasingi, karty kredytowe, alimenty lub inne stałe obciążenia. Druga warstwa to koszty życia rodziny przez czas potrzebny na poukładanie sytuacji po utracie dochodu.",
          "Trzecia warstwa to cele specjalne: edukacja dzieci, koszty przeprowadzki, leczenie, opieka nad bliską osobą lub zabezpieczenie działalności gospodarczej. Dopiero suma tych elementów pokazuje, czy potrzebujesz 200 000 zł, 500 000 zł czy 1 000 000 zł.",
          "Przy bardzo wysokich sumach ubezpieczyciel może poprosić o dokładniejszą ocenę ryzyka, dokumenty finansowe albo badania. To normalne, bo suma musi być uzasadniona sytuacją klienta."
        ],
        bullets: [
          "saldo kredytu i innych zobowiązań",
          "12-60 miesięcy kosztów życia rodziny",
          "wydatki na dzieci, leczenie lub zmianę mieszkania",
          "rezerwa na podatki, firmę lub wspólne zobowiązania"
        ]
      },
      {
        heading: "Dobierz okres ochrony do momentu największego ryzyka",
        lead: "Polisa terminowa ma chronić wtedy, gdy bliscy są najbardziej zależni od Twojego dochodu.",
        body: [
          "Dla rodziców takim okresem może być czas do usamodzielnienia się dzieci. Dla kredytobiorcy: okres spłaty kredytu lub przynajmniej najdroższe pierwsze lata zobowiązania. Dla przedsiębiorcy: czas największego zadłużenia firmy albo okres, w którym rodzina nie ma innych aktywów.",
          "Zbyt krótki okres może zostawić rodzinę bez ochrony w najważniejszym momencie. Zbyt długi okres może niepotrzebnie podnosić składkę, jeśli ryzyko finansowe naturalnie spada, bo kredyt maleje, dzieci dorastają, a oszczędności rosną.",
          "W produktach ochronnych parametry graniczne zależą od OWU. W materiałach produktowych Ochrony Każdego Dnia pojawia się m.in. wiek wstępu 18-65 lat i okres 5-30 lat, nie dłużej niż do rocznicy umowy w roku, w którym wiek polisowy wynosi 70 lat."
        ]
      },
      {
        heading: "Cena jest ważna, ale najtańsza polisa bywa pozorna",
        lead: "Niższa składka może wynikać z niższej sumy, krótszego okresu, braku dodatków albo węższych definicji świadczeń.",
        body: [
          "Porównując propozycje, trzeba oddzielić umowę podstawową od dodatków. Sama ochrona życia może być prosta i relatywnie tania, ale ciężkie choroby, hospitalizacja, operacje, NNW lub leczenie za granicą mają własne limity i definicje.",
          "Warto poprosić o rozbicie składki na części. Dzięki temu widać, ile kosztuje śmierć, ile kosztują choroby, ile NNW, a ile pakiet zdrowotny. Czasem lepiej podnieść sumę na życie i zrezygnować z dodatku, który w praktyce niewiele wnosi.",
          "Kalkulator pomaga ustalić budżet, ale finalna składka zależy od ankiety, wieku, zdrowia, zawodu, hobby, palenia i decyzji ubezpieczyciela. Dlatego wynik z kalkulatora traktujemy jako punkt rozmowy, a nie gotową ofertę."
        ]
      },
      {
        heading: "OWU czytaj przez definicje i wyłączenia",
        lead: "Najważniejsze rzeczy w polisie często nie są na pierwszej stronie propozycji, tylko w OWU i karcie produktu.",
        body: [
          "Przy ochronie życia sprawdź, kiedy ubezpieczyciel wypłaca świadczenie, kto jest uposażonym, jak zmienia się suma i kiedy odpowiedzialność nie działa. Przy dodatkach chorobowych najważniejsze są definicje chorób, okresy karencji, limity wypłat i to, czy świadczenie można dostać więcej niż raz.",
          "Jeśli w decyzji ubezpieczyciela pojawi się zwyżka, wyłączenie lub ograniczenie, trzeba ocenić, czy polisa nadal odpowiada potrzebie. Sama akceptacja wniosku nie zawsze oznacza, że zakres jest tak szeroki, jak klient zakładał.",
          "Dobra rozmowa z doradcą powinna kończyć się nie tylko składką, ale też zrozumieniem, co kupujesz, czego nie kupujesz i które elementy trzeba będzie aktualizować w przyszłości."
        ],
        bullets: [
          "definicja śmierci, nieszczęśliwego wypadku i choroby",
          "karencje oraz okresy przeżycia",
          "wyłączenia odpowiedzialności i limity wypłat",
          "zasady zmiany uposażonych, cesji i opłacania składki"
        ]
      }
    ],
    checklistTitle: "Lista przed wyborem polisy",
    checklist: [
      "spisz saldo kredytu i inne zobowiązania",
      "policz miesięczne koszty życia rodziny",
      "ustal, przez ile lat bliscy potrzebowaliby wsparcia",
      "sprawdź, jaką ochronę masz już w pracy lub banku",
      "zdecyduj, czy dodatki mają chronić dochód, zdrowie czy dzieci",
      "porównaj nie tylko cenę, ale też definicje, karencje i wyłączenia"
    ],
    mistakesTitle: "Najczęstsze błędy",
    mistakes: [
      "wybór sumy tylko pod składkę, bez liczenia realnej potrzeby",
      "założenie, że polisa grupowa wystarczy na kredyt i rodzinę",
      "dokładanie wielu dodatków kosztem zbyt niskiej sumy na życie",
      "brak aktualizacji uposażonych po ślubie, rozwodzie, dziecku lub spłacie kredytu",
      "porównywanie ofert bez OWU i bez definicji chorób"
    ],
    faqs: [
      {
        question: "Czy lepiej wybrać jedną wysoką polisę czy kilka mniejszych?",
        answer:
          "To zależy od celu. Jedna wysoka polisa bywa prostsza, ale kilka polis może rozdzielić potrzeby: osobno kredyt, osobno rodzina, osobno ochrona dożywotnia. Ważne, aby łączna suma była uzasadniona i akceptowalna w ocenie ryzyka."
      },
      {
        question: "Czy warto zaczynać od kalkulatora?",
        answer:
          "Tak, kalkulator pomaga ustalić budżet i sprawdzić, czy oczekiwana suma jest realna przy danym wieku i okresie. Nie zastępuje jednak ankiety ani finalnej decyzji ubezpieczyciela."
      },
      {
        question: "Co jeśli ubezpieczyciel zaproponuje zwyżkę?",
        answer:
          "Zwyżka nie musi przekreślać polisy. Trzeba sprawdzić, czy przy nowej cenie zakres nadal ma sens, czy da się zmienić sumę albo okres, i czy inny wariant lepiej odpowie na potrzebę."
      }
    ],
    related: [
      { label: "Otrzymaj 3 warianty", href: "#formularz", desc: "Tańszy, optymalny i szerszy zakres do rozmowy" },
      { label: "Zobacz cennik", href: "/ubezpieczenie-na-zycie-cennik/", desc: "Orientacyjne przykłady według wieku i sumy" },
      { label: "Ubezpieczenia dodatkowe", href: "/poradnik/ubezpieczenia-dodatkowe/", desc: "Sprawdź, które dodatki mają sens" }
    ]
  },
  guideProtection: {
    slug: "poradnik/jak-dziala-ochrona",
  datePublished: "2026-05-30",
  dateModified: "2026-08-12",
    label: "Jak działa ochrona",
    kicker: "Poradnik",
    title: "Jak działa ochrona w polisie na życie?",
    copy:
      "Ochrona w ubezpieczeniu na życie to obietnica wypłaty świadczenia po spełnieniu warunków z umowy. Prosta idea ma kilka ważnych szczegółów: suma, uposażeni, okres ochrony, wyłączenia i ocena ryzyka.",
    image: guideProtectionImage,
    summary: [
      "umowa podstawowa wypłaca sumę ubezpieczenia po śmierci ubezpieczonego",
      "pieniądze trafiają do osób uposażonych lub zgodnie z dyspozycją cesji",
      "ochrona może działać na całym świecie, ale dodatki mają własne terytoria i limity",
      "świadczenie zależy od OWU, ankiety, decyzji ubezpieczyciela i terminowego opłacania składki"
    ],
    sections: [
      {
        heading: "Umowa podstawowa: najprostszy mechanizm wypłaty",
        lead: "W klasycznej polisie ochronnej najważniejszym ryzykiem jest śmierć ubezpieczonego w okresie trwania ochrony.",
        body: [
          "Jeśli zdarzenie mieści się w zakresie umowy, ubezpieczyciel wypłaca aktualną sumę ubezpieczenia osobom wskazanym jako uposażone. Dzięki temu pieniądze mogą trafić bezpośrednio do bliskich, często poza standardowym postępowaniem spadkowym.",
          "W materiałach produktowych Ochrony Każdego Dnia wskazane są parametry takie jak suma od 50 000 zł, minimalna składka 45 zł miesięcznie, wiek wstępu 18-65 lat i okres ochrony 5-30 lat. To parametry produktu, ale finalne warunki zawsze zależą od oceny ryzyka.",
          "Polisa ochronna nie służy przede wszystkim inwestowaniu. Jej zadaniem jest stworzenie finansowego pomostu: spłacić kredyt, utrzymać rodzinę, pokryć koszty organizacyjne i dać bliskim czas na spokojne decyzje."
        ]
      },
      {
        heading: "Uposażeni i cesja: kto dostaje pieniądze",
        lead: "Uposażony to osoba lub podmiot wskazany do otrzymania świadczenia.",
        body: [
          "Można wskazać jednego uposażonego albo kilka osób z procentowym podziałem świadczenia. Warto aktualizować te dane po zmianach życiowych: ślubie, rozwodzie, narodzinach dziecka, śmierci bliskiej osoby albo spłacie kredytu.",
          "Przy kredycie hipotecznym często pojawia się cesja na bank. Oznacza to, że świadczenie może w pierwszej kolejności zabezpieczać spłatę zobowiązania. Po spłacie kredytu warto sprawdzić, czy cesja została zwolniona i czy uposażeni są aktualni.",
          "Dobrze dobrana polisa nie kończy się na wpisaniu nazwisk. Trzeba jeszcze zrozumieć, kto, w jakiej kolejności i na jakiej podstawie otrzyma pieniądze."
        ],
        bullets: [
          "uposażonych można zwykle zmieniać w trakcie umowy",
          "bank może być uprawniony przy cesji pod kredyt",
          "procentowy podział świadczenia powinien być jasny",
          "brak aktualizacji uposażonych może utrudnić rodzinie sytuację"
        ]
      },
      {
        heading: "Okres ochrony i początek odpowiedzialności",
        lead: "Ochrona nie zawsze działa od tej samej chwili dla wszystkich ryzyk.",
        body: [
          "Dla umowy podstawowej odpowiedzialność zaczyna się zgodnie z dokumentami produktu, po spełnieniu warunków takich jak złożenie wniosku, akceptacja i opłacenie składki. Dodatki mogą mieć karencje, czyli okresy, w których ochrona jeszcze nie działa albo działa w ograniczony sposób.",
          "W materiałach Ochrony Każdego Dnia występuje ochrona tymczasowa na wypadek śmierci wskutek nieszczęśliwego wypadku po złożeniu wniosku i opłaceniu składki. Jest ograniczona czasowo i kwotowo, dlatego nie należy mylić jej z pełną polisą.",
          "Przy dodatkach zdrowotnych trzeba sprawdzać osobne zasady: od kiedy działa ciężka choroba, od którego dnia liczony jest szpital, czy obowiązuje okres przeżycia i jakie zdarzenia są wyłączone."
        ]
      },
      {
        heading: "Ocena ryzyka: dlaczego ubezpieczyciel pyta o zdrowie",
        lead: "Ankieta medyczna, zawód i hobby nie są formalnością. To podstawa decyzji o cenie i zakresie.",
        body: [
          "Ubezpieczyciel może zapytać o historię leczenia, rozpoznania, leki, zabiegi, wzrost, wagę, palenie, sport, podróże, zawód i wcześniejsze polisy. Przy wyższych sumach albo szerszym zakresie może poprosić o dokumentację lub badania.",
          "Po ocenie ryzyka możliwe są różne decyzje: warunki standardowe, wyższa składka, wyłączenie konkretnego ryzyka, odroczenie decyzji albo odmowa zawarcia umowy. Dlatego wynik kalkulatora powinien być traktowany jako orientacja, nie jako gwarancja.",
          "Najlepiej odpowiadać dokładnie i spokojnie. Niepełne informacje mogą utrudnić późniejszą wypłatę świadczenia albo wydłużyć proces obsługi roszczenia."
        ]
      },
      {
        heading: "Wyłączenia i karencje: kiedy świadczenie może nie zostać wypłacone",
        lead: "Każda polisa ma granice odpowiedzialności. Ich znajomość jest tak samo ważna jak wysokość składki.",
        body: [
          "Wyłączenia odpowiedzialności opisują sytuacje, w których ubezpieczyciel nie wypłaci świadczenia albo ograniczy wypłatę. Mogą dotyczyć np. okoliczności zdarzenia, stanu zdrowia sprzed umowy, działań pod wpływem alkoholu, sportów wysokiego ryzyka lub innych sytuacji wymienionych w OWU.",
          "Karencje najczęściej dotyczą ryzyk chorobowych, urodzenia dziecka, hospitalizacji lub wybranych dodatków. Oznaczają, że przez określony czas od zawarcia umowy ochrona dla danego zdarzenia nie działa.",
          "Dlatego przy porównywaniu ofert warto pytać nie tylko: ile wynosi składka, ale też: kiedy dokładnie polisa wypłaci pieniądze, ile trzeba czekać i które sytuacje są poza ochroną."
        ],
        bullets: [
          "sprawdź osobno umowę podstawową i każdy dodatek",
          "zwróć uwagę na karencję, limit i definicję zdarzenia",
          "czytaj decyzję ubezpieczyciela razem z OWU",
          "pytaj o skutki wcześniejszych chorób i leczenia"
        ]
      }
    ],
    checklistTitle: "Co sprawdzić, żeby rozumieć ochronę",
    checklist: [
      "kto jest ubezpieczonym, ubezpieczającym i uposażonym",
      "jaka jest aktualna suma ubezpieczenia i czy może się zmieniać",
      "od kiedy działa pełna ochrona i czy jest ochrona tymczasowa",
      "jakie są wyłączenia odpowiedzialności",
      "czy dodatki mają karencje, limity i osobne definicje",
      "jak wygląda zgłoszenie roszczenia i jakie dokumenty będą potrzebne"
    ],
    mistakesTitle: "Czego nie zakładać z góry",
    mistakes: [
      "że każde zdarzenie chorobowe oznacza automatyczną wypłatę",
      "że ochrona podstawowa i dodatki mają te same warunki",
      "że uposażeni aktualizują się sami po zmianach rodzinnych",
      "że cesja na bank znika automatycznie bez formalności",
      "że wynik kalkulatora jest decyzją ubezpieczyciela"
    ],
    faqs: [
      {
        question: "Czy polisa na życie działa za granicą?",
        answer:
          "Klasyczna ochrona życia często działa całodobowo na całym świecie, ale trzeba sprawdzić OWU. Dodatki, zwłaszcza szpitalne lub leczenie za granicą, mogą mieć własne terytorium, limity i procedury."
      },
      {
        question: "Czy świadczenie zawsze trafia poza spadkiem?",
        answer:
          "Świadczenie wypłacane uposażonym co do zasady trafia zgodnie z dyspozycją w polisie. Jeśli uposażonych nie ma albo dyspozycja jest nieaktualna, mogą wejść w grę zasady opisane w OWU i przepisach."
      },
      {
        question: "Czy można zmienić sumę ubezpieczenia?",
        answer:
          "Zależy od produktu i momentu umowy. Podwyższenie sumy może wymagać nowej oceny ryzyka, a obniżenie może wpływać na sens ochrony. Warto pytać o to przy aktualizacji polisy."
      }
    ],
    related: [
      { label: "Jak wybrać polisę", href: "/poradnik/jak-wybrac-polise/", desc: "Przejdź od zasad do decyzji" },
      {
        label: "Polisa bez badań lekarskich",
        href: "/poradnik/ubezpieczenie-na-zycie-bez-badan/",
        desc: "Progi ankiety i kiedy potrzebne są badania"
      },
      {
        label: "Karencja i wyłączenia",
        href: "/poradnik/karencja-wylaczenia-polisa-na-zycie/",
        desc: "Okresy wyczekiwania i granice ochrony w OWU"
      }
    ]
  },
  guideAddons: {
    slug: "poradnik/ubezpieczenia-dodatkowe",
  datePublished: "2026-05-30",
  dateModified: "2026-08-12",
    label: "Ubezpieczenia dodatkowe",
    kicker: "Poradnik",
    title: "Ubezpieczenia dodatkowe: które rozszerzenia mają sens?",
    copy:
      "Dodatki mogą zamienić prostą polisę na życie w ochronę przy chorobie, wypadku, hospitalizacji albo leczeniu za granicą. Trzeba jednak rozumieć limity, definicje i cenę każdego rozszerzenia.",
    image: guideAddonsImage,
    summary: [
      "dodatki nie zastępują wysokiej sumy na życie, tylko rozszerzają ochronę",
      "najważniejsze są definicje chorób, limity, karencje i zasady wypłaty",
      "część dodatków można dobrać dla partnera lub dziecka",
      "szerszy zakres zwiększa składkę, dlatego warto wybierać dodatki pod realne ryzyka"
    ],
    sections: [
      {
        heading: "Ciężkie choroby: wypłata po diagnozie, ale według definicji z OWU",
        lead: "Dodatek ciężkich chorób jest jednym z najczęściej wybieranych rozszerzeń, bo choroba może zatrzymać dochód i zwiększyć koszty leczenia.",
        body: [
          "Najważniejsza nie jest sama liczba chorób w tabeli, lecz ich definicje. Dwa produkty mogą używać tej samej nazwy choroby, ale różnić się warunkami wypłaty, wymaganym stadium, okresem przeżycia lub wyłączeniami.",
          "W materiałach produktowych PZU dla dodatku ciężkich chorób pojawiają się warianty Standard i Premium. Standard obejmuje mniejszą listę chorób, Premium szerszą listę. Suma dla dorosłych może być wysoka, ale finalny zakres i cena zależą od oceny ryzyka.",
          "Ten dodatek ma sens szczególnie wtedy, gdy klient nie ma dużej poduszki finansowej, prowadzi firmę, ma kredyt albo chce zabezpieczyć czas leczenia i rekonwalescencji."
        ],
        bullets: [
          "sprawdź listę chorób i definicje, nie tylko nazwę wariantu",
          "zapytaj o karencję i okres przeżycia",
          "sprawdź, czy świadczenie można otrzymać więcej niż raz",
          "porównaj sumę dodatku z realnymi kosztami leczenia i utraty dochodu"
        ]
      },
      {
        heading: "Szpital i operacje: dobre przy nagłych kosztach, ale limitowane",
        lead: "Dodatki szpitalne i operacyjne często dają szybkie świadczenie, ale działają według konkretnych tabel.",
        body: [
          "Przy leczeniu szpitalnym ważne jest, od którego dnia pobytu przysługuje świadczenie, czy pobyt wynika z choroby czy wypadku, ile dni maksymalnie może być wypłacone i czy są osobne świadczenia za OIOM, rekonwalescencję lub kartę apteczną.",
          "Przy operacjach chirurgicznych świadczenie zwykle zależy od klasy operacji. To znaczy, że nie każda operacja daje wypłatę całej sumy. Część procedur może dawać określony procent sumy ubezpieczenia.",
          "Te dodatki mają sens, jeśli klient chce pieniędzy na koszty okołoszpitalne, dojazdy, opiekę nad dziećmi, prywatną rehabilitację albo czasową przerwę w pracy."
        ]
      },
      {
        heading: "NNW, uszczerbek i złamania: ochrona przy zdarzeniach nagłych",
        lead: "Dodatki wypadkowe pomagają przy trwałym uszczerbku, złamaniach, śmierci w wypadku albo wypadku komunikacyjnym.",
        body: [
          "Przy NNW trzeba sprawdzić tabelę uszczerbków i sposób liczenia świadczenia. Wysoka suma wygląda atrakcyjnie, ale realna wypłata często zależy od procentu uszczerbku albo rodzaju zdarzenia.",
          "Wypadek komunikacyjny może mieć osobny zakres i osobne limity. Dla osób dużo jeżdżących samochodem, motocyklem albo pracujących w ruchu drogowym taki dodatek może być ważniejszy niż dla osób pracujących zdalnie.",
          "Złamania i drobniejsze urazy nie powinny przesłonić najważniejszej funkcji polisy. Jeśli budżet jest ograniczony, zwykle najpierw warto zadbać o sumę na życie i poważne ryzyka."
        ]
      },
      {
        heading: "Leczenie za granicą: nie tylko pieniądze, ale organizacja leczenia",
        lead: "Ten dodatek jest szczególny, bo często obejmuje organizację procesu leczenia, drugą opinię medyczną i finansowanie w zagranicznych placówkach.",
        body: [
          "W materiałach produktowych leczenia za granicą pojawiają się limity w euro oraz warianty różniące się terytorium ochrony. Standard może działać w świecie bez USA, a Premium obejmować cały świat.",
          "To rozwiązanie ma sens dla osób, które chcą zabezpieczyć dostęp do kosztownego leczenia przy wybranych poważnych chorobach. Trzeba jednak dokładnie sprawdzić katalog chorób, procedur, wyłączeń i sposób uruchomienia świadczenia.",
          "Nie jest to zwykły pakiet prywatnej opieki medycznej. To dodatek na duże, rzadkie i kosztowne sytuacje, więc powinien być omawiany osobno."
        ],
        bullets: [
          "sprawdź limit w euro i terytorium ochrony",
          "zapytaj o drugą opinię medyczną",
          "sprawdź katalog chorób i procedur",
          "ustal, czy dodatek obejmuje dziecko lub partnera"
        ]
      },
      {
        heading: "Ochrona partnera i dzieci: wygoda jednej rozmowy, ale osobne limity",
        lead: "Rodzinne dodatki są praktyczne, ale nie zawsze zastąpią osobne polisy dla dorosłych.",
        body: [
          "Dziecko może być objęte m.in. dodatkiem ciężkich chorób, hospitalizacji, operacji, uszczerbku albo leczenia za granicą dziecka. Zakres, wiek wstępu i limity zależą od OWU.",
          "Partner lub małżonek może mieć wybrane rozszerzenia w ramach tej samej rozmowy o ochronie. Trzeba jednak pamiętać, że suma i definicje dla osoby dodatkowo ubezpieczonej mogą różnić się od zakresu osoby głównej.",
          "Jeśli partner również utrzymuje rodzinę lub ma własny kredyt, osobna polisa ochronna może być lepszym fundamentem niż sam dodatek rodzinny."
        ]
      }
    ],
    checklistTitle: "Jak wybierać dodatki",
    checklist: [
      "najpierw ustal, czy suma na życie jest wystarczająca",
      "wybierz 2-3 ryzyka najważniejsze dla pracy, rodziny i zdrowia",
      "porównaj definicje chorób, nie tylko liczbę pozycji w tabeli",
      "sprawdź karencje, limity roczne i limity łączne",
      "zapytaj, czy dodatek działa dla dziecka lub partnera",
      "poproś o pokazanie, ile każdy dodatek zwiększa składkę"
    ],
    mistakesTitle: "Kiedy dodatki są źle dobrane",
    mistakes: [
      "gdy klient ma dużo drobnych świadczeń, ale zbyt niską sumę na życie",
      "gdy dodatek chorobowy ma definicje, których klient nie rozumie",
      "gdy pakiet rodzinny zastępuje ochronę dorosłego, który utrzymuje dom",
      "gdy wybór wynika z nazwy dodatku, a nie z realnego ryzyka",
      "gdy nie sprawdzono karencji i wyłączeń"
    ],
    faqs: [
      {
        question: "Czy warto brać wszystkie dodatki?",
        answer:
          "Nie zawsze. Szeroki pakiet daje większy zakres, ale podnosi składkę. Dodatki powinny odpowiadać na konkretne ryzyka: kredyt, dzieci, choroby w rodzinie, pracę fizyczną, aktywny tryb życia albo brak oszczędności."
      },
      {
        question: "Czy dodatek ciężkich chorób wypłaca za każdą chorobę?",
        answer:
          "Nie. Wypłata zależy od listy chorób, definicji w OWU, wyłączeń, karencji, okresu przeżycia i dokumentacji medycznej. Sama nazwa choroby w rozmowie potocznej nie wystarcza."
      },
      {
        question: "Czy dodatki można dokupić po czasie?",
        answer:
          "W wielu produktach jest to możliwe, ale może wymagać oceny ryzyka i spełnienia warunków umowy podstawowej. Dlatego warto zaplanować docelowy zakres już przy pierwszej rozmowie."
      }
    ],
    related: [
      { label: "Kalkulator ochronny", href: "/kalkulator-ubezpieczenia-na-zycie/", desc: "Sprawdź bazę dla polisy" },
      { label: "Jak działa ochrona", href: "/poradnik/jak-dziala-ochrona/", desc: "Zrozum umowę podstawową" },
      { label: "Poradnik wyboru", href: "/poradnik/jak-wybrac-polise/", desc: "Ułóż sumę, okres i zakres" }
    ]
  },
  guideMortgage: mortgageGuidePage,
  guideRetirement: retirementGuidePage,
  guideProcess: {
    slug: "poradnik/proces-zakupu-polisy",
  datePublished: "2026-05-30",
  dateModified: "2026-08-12",
    label: "Proces zakupu",
    kicker: "Poradnik",
    title: "Proces zakupu polisy: od kalkulatora do ochrony",
    copy:
      "Zakup ubezpieczenia na życie nie powinien być szybkim kliknięciem bez zrozumienia. Najpierw ustala się potrzebę i budżet, potem przechodzi ankietę, ocenę ryzyka i dopiero wtedy akceptuje finalne warunki.",
    image: guideProcessImage,
    summary: [
      "kalkulator daje punkt odniesienia, a nie finalną ofertę",
      "analiza potrzeb porządkuje rodzinę, kredyt, dochód i dodatki",
      "ankieta medyczna może zmienić składkę lub zakres",
      "po wystawieniu polisy trzeba pilnować składki, uposażonych i aktualizacji"
    ],
    sections: [
      {
        heading: "Krok 1: orientacyjna kalkulacja",
        lead: "Pierwszy etap to sprawdzenie, jaki budżet jest realny przy wieku, sumie i okresie ochrony.",
        body: [
          "Kalkulator pomaga szybko zobaczyć różnicę między sumą 100 000 zł, 300 000 zł i 500 000 zł, a także wpływ wieku lub okresu ochrony. To dobry moment, aby odrzucić warianty zbyt drogie albo zbyt słabe.",
          "Wynik nie jest finalną ceną. Nie uwzględnia pełnej ankiety, wszystkich zwyżek, decyzji ubezpieczyciela, wszystkich wyłączeń ani aktualnych indywidualnych warunków.",
          "Dobry wynik z kalkulatora powinien prowadzić do rozmowy: czy suma wystarczy, czy okres jest właściwy, czy potrzebne są dodatki i czy budżet jest stabilny."
        ]
      },
      {
        heading: "Krok 2: analiza potrzeb klienta",
        lead: "To najważniejsza część rozmowy, bo chronić trzeba realne życie, nie abstrakcyjny produkt.",
        body: [
          "Doradca powinien zapytać o rodzinę, dzieci, kredyt, miesięczne koszty, dochody, działalność gospodarczą, dotychczasowe polisy i cel ochrony. Na tej podstawie można dobrać sumę, okres i dodatki.",
          "Analiza potrzeb pomaga też ustalić, czego nie kupować. Jeśli klient ma ograniczony budżet, lepiej wybrać mocny fundament niż rozbudowany pakiet z niskimi sumami.",
          "W tej części warto powiedzieć o istniejącej grupówce, polisie bankowej, prywatnym pakiecie medycznym i historii leczenia. To skraca proces i zmniejsza ryzyko nietrafionej propozycji."
        ],
        bullets: [
          "dochody i koszty rodziny",
          "kredyty, leasingi i inne zobowiązania",
          "liczba osób zależnych finansowo",
          "istniejące polisy i benefity",
          "oczekiwany cel: rodzina, kredyt, zdrowie, kapitał"
        ]
      },
      {
        heading: "Krok 3: wniosek i ankieta medyczna",
        lead: "Wniosek zbiera dane potrzebne do oceny ryzyka. To etap, na którym warto odpowiadać dokładnie.",
        body: [
          "Ankieta może obejmować pytania o wzrost, wagę, palenie, choroby, leki, zabiegi, hospitalizacje, diagnostykę, sport, zawód i podróże. Przy wyższych sumach lub wybranych dodatkach ubezpieczyciel może poprosić o dokumentację albo badania.",
          "Nie warto ukrywać informacji. W ubezpieczeniach problem często pojawia się nie przy zawieraniu umowy, ale przy wypłacie świadczenia. Rzetelna ankieta pomaga uniknąć sporów.",
          "Jeżeli klient nie pamięta dat albo nazw leków, lepiej sprawdzić dokumentację niż zgadywać. Dokładność jest ważniejsza niż szybkość."
        ]
      },
      {
        heading: "Krok 4: decyzja ubezpieczyciela",
        lead: "Po ocenie ryzyka ubezpieczyciel może zaakceptować wniosek standardowo albo zaproponować inne warunki.",
        body: [
          "Możliwe decyzje to m.in. akceptacja standardowa, zwyżka składki, wyłączenie konkretnego ryzyka, ograniczenie sumy, odroczenie albo odmowa. Każdą decyzję trzeba przeczytać razem z zakresem i OWU.",
          "Zwyżka nie zawsze oznacza, że oferta jest zła. Czasem nadal warto ją przyjąć, jeśli ryzyko jest ważne i trudno byłoby uzyskać lepsze warunki gdzie indziej. Innym razem lepiej zmienić sumę, zakres albo produkt.",
          "Na tym etapie dobrze wrócić do pierwotnego celu: czy propozycja nadal zabezpiecza rodzinę, kredyt, zdrowie albo kapitał w sposób, którego klient potrzebuje."
        ]
      },
      {
        heading: "Krok 5: polisa, płatność i aktualizacje",
        lead: "Ochrona wymaga nie tylko podpisania dokumentów, ale też pilnowania składek i zmian życiowych.",
        body: [
          "Po akceptacji warunków klient otrzymuje dokument polisy, OWU i informacje o płatnościach. Trzeba sprawdzić dane osobowe, sumy, uposażonych, cesję, dodatki, częstotliwość składki i datę początku ochrony.",
          "W kolejnych latach warto aktualizować polisę po narodzinach dziecka, zmianie kredytu, spłacie zobowiązania, zmianie pracy, rozwodzie, ślubie albo znaczącym wzroście kosztów życia.",
          "Polisa, która była dobra pięć lat temu, nie zawsze pasuje do obecnej sytuacji. Najlepsza ochrona żyje razem z budżetem rodziny."
        ],
        bullets: [
          "sprawdź uposażonych i procenty",
          "zapisz termin składki",
          "zachowaj polisę i OWU",
          "po spłacie kredytu sprawdź cesję",
          "wracaj do sumy ubezpieczenia co 1-2 lata"
        ]
      }
    ],
    checklistTitle: "Co przygotować przed rozmową",
    checklist: [
      "wiek, zawód i podstawowe informacje o zdrowiu",
      "saldo kredytu i okres pozostały do spłaty",
      "miesięczne koszty rodziny",
      "wysokość dochodu, który trzeba zabezpieczyć",
      "informacje o polisach grupowych i bankowych",
      "lista leków, diagnoz i ważniejszych badań, jeśli były"
    ],
    mistakesTitle: "Co wydłuża proces",
    mistakes: [
      "niepełna ankieta medyczna",
      "brak informacji o istniejących polisach",
      "wybór zbyt wysokiej sumy bez uzasadnienia",
      "nieczytanie decyzji ubezpieczyciela",
      "brak aktualizacji cesji i uposażonych po zmianach życiowych"
    ],
    faqs: [
      {
        question: "Ile trwa zakup polisy na życie?",
        answer:
          "Prosta polisa może być przygotowana szybko, ale przy wyższych sumach, chorobach w historii albo dodatkach proces może potrwać dłużej, bo ubezpieczyciel może poprosić o dokumenty lub badania."
      },
      {
        question: "Czy trzeba robić badania lekarskie?",
        answer:
          "Nie zawsze. Badania zależą od wieku, sumy, zakresu i odpowiedzi w ankiecie. Często wystarczy ankieta, ale przy większym ryzyku ubezpieczyciel może poprosić o dodatkową ocenę."
      },
      {
        question: "Czy po wysłaniu wniosku ochrona już działa?",
        answer:
          "Nie należy tego zakładać. Czasem działa ograniczona ochrona tymczasowa, np. na wypadek śmierci wskutek nieszczęśliwego wypadku, ale pełna ochrona zaczyna się zgodnie z dokumentami i decyzją ubezpieczyciela."
      }
    ],
    related: [
      {
        label: "Polisa bez badań lekarskich",
        href: "/poradnik/ubezpieczenie-na-zycie-bez-badan/",
        desc: "Progi ankiety i kiedy potrzebne są badania"
      },
      { label: "Kalkulator na życie", href: "/kalkulator-ubezpieczenia-na-zycie/", desc: "Zacznij od orientacyjnej składki" },
      { label: "Jak działa ochrona", href: "/poradnik/jak-dziala-ochrona/", desc: "Zrozum, co dzieje się po zawarciu polisy" }
    ]
  }
};

function GuideVariantCta({ route, placement }: { route: GuideRoute; placement: string }) {
  return (
    <aside className="guide-variant-cta" aria-label="Zamów porównanie wariantów">
      <div>
        <p className="section-kicker">Bez zobowiązań</p>
        <h2>Chcesz zobaczyć 3 warianty dla siebie?</h2>
        <p>
          Doradca przygotuje wariant tańszy, optymalny i szerszy, żeby łatwiej porównać cenę z zakresem
          ochrony.
        </p>
      </div>
      <a
        className="primary-button guide-variant-button"
        href="#formularz-start"
        onClick={() => trackEvent("poradnik_cta_click", { route, placement })}
      >
        Otrzymaj 3 warianty
        <ArrowRight size={19} aria-hidden="true" />
      </a>
    </aside>
  );
}

function getRouteFromPathname(pathname: string): RouteKey {
  const seg = pathname.replace(/^\/+|\/+$/g, "");
  const productRoute = Object.values(PRODUCTS).find((product) => product?.slug === seg);

  if (productRoute) {
    return productRoute.id;
  }

  if (seg === "ubezpieczenie-na-zycie-cennik") {
    return "pricing";
  }

  if (seg === "ubezpieczenie-na-zycie") {
    return "life";
  }

  if (seg === "umowy-dodatkowe") {
    return "addons";
  }

  if (seg === "poradnik") {
    return "guideHub";
  }

  for (const id of guideRouteKeys) {
    if (guidePages[id]?.slug === seg) {
      return id;
    }
  }

  if (seg === "faq") {
    return "faq";
  }

  if (seg === "kontakt") {
    return "contact";
  }

  if (seg === "o-nas") {
    return "about";
  }

  if (seg === "polityka-prywatnosci") {
    return "privacy";
  }

  if (seg === "polityka-cookies") {
    return "cookies";
  }

  if (seg === "regulamin") {
    return "terms";
  }

  if (seg === "admin") {
    return "admin";
  }

  return "home";
}

function getRoute(): RouteKey {
  if (typeof window === "undefined") {
    return "home";
  }
  return getRouteFromPathname(window.location.pathname);
}

function upsertMeta(name: string, content: string) {
  let meta = document.querySelector(`meta[name="${name}"]`);
  if (!meta) {
    meta = document.createElement("meta");
    meta.setAttribute("name", name);
    document.head.appendChild(meta);
  }
  meta.setAttribute("content", content);
}

function upsertCanonical(href: string) {
  let link = document.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", href);
}

function setRobotsMeta(content: string | null) {
  const meta = document.querySelector('meta[name="robots"]');
  if (!content) {
    meta?.remove();
    return;
  }
  upsertMeta("robots", content);
}

function upsertJsonLd(id: string, payload: unknown) {
  let script = document.getElementById(id) as HTMLScriptElement | null;
  if (!script) {
    script = document.createElement("script");
    script.id = id;
    script.type = "application/ld+json";
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(payload);
}

function removeJsonLd(id: string) {
  document.getElementById(id)?.remove();
}

function uniqueFaqs(items: ProductFaq[]): ProductFaq[] {
  const seen = new Set<string>();
  return items.filter((item) => {
    const key = normalize(item.question);
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
}

function getRouteFaqs(route: RouteKey): ProductFaq[] {
  if (route === "faq") {
    return getFaqPageItems();
  }
  if (isGuideRoute(route)) {
    return guidePages[route].faqs;
  }
  if (route === "addons") {
    return uniqueFaqs([...additionalContractFaqs, ...faqs]);
  }
  if (isStaticRoute(route)) {
    return faqs;
  }
  return uniqueFaqs([...PRODUCTS[route].faqs, ...faqs]);
}

type StructuredDataEntry = {
  id: string;
  data: Record<string, unknown>;
};

function getFaqJsonLd(route: RouteKey): Record<string, unknown> | null {
  if (
    route === "privacy" ||
    route === "cookies" ||
    route === "terms" ||
    route === "admin" ||
    route === "contact" ||
    route === "about" ||
    route === "guideHub"
  ) {
    return null;
  }

  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: getRouteFaqs(route)
      .slice(0, 24)
      .map((item) => ({
        "@type": "Question",
        name: item.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: item.answer
        }
      }))
  };
}

function getAgencyJsonLd(): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "InsuranceAgency",
    name: "Ochro.pl",
    url: "https://ochro.pl/",
    telephone: COMPANY.phoneHref.replace("tel:", ""),
    email: COMPANY.email,
    taxID: COMPANY.nip,
    areaServed: "PL",
    description:
      "Kalkulatory i edukacyjne opisy ubezpieczeń na życie, pod kredyt, na życie i dożycie, zdrowotnych oraz dożywotnich."
  };
}

const GUIDE_AUTHOR_JSONLD = {
  "@type": "Person",
  name: "Martyna Mostek",
  jobTitle: "Doradca ubezpieczeniowy",
  url: "https://ochro.pl/o-nas/"
};

function getGuideArticleJsonLd(route: GuideRoute): Record<string, unknown> {
  const page = guidePages[route];
  const meta = staticMeta[route];
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: page.title,
    description: meta.description,
    inLanguage: "pl-PL",
    mainEntityOfPage: meta.canonical,
    datePublished: page.datePublished,
    dateModified: page.dateModified,
    author: GUIDE_AUTHOR_JSONLD,
    publisher: {
      "@type": "Organization",
      name: "Ochro.pl",
      url: "https://ochro.pl/",
      logo: { "@type": "ImageObject", url: "https://ochro.pl/og-image.png" }
    },
    image: "https://ochro.pl/og-image.png"
  };
}

function getGuideBreadcrumbJsonLd(route: GuideRoute): Record<string, unknown> {
  const page = guidePages[route];
  const meta = staticMeta[route];
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Strona główna", item: "https://ochro.pl/" },
      { "@type": "ListItem", position: 2, name: "Poradnik", item: "https://ochro.pl/poradnik/" },
      { "@type": "ListItem", position: 3, name: page.label, item: meta.canonical }
    ]
  };
}

export function getRouteStructuredData(route: RouteKey): StructuredDataEntry[] {
  const faqJsonLd = getFaqJsonLd(route);
  return [
    ...(faqJsonLd ? [{ id: "ochro-faq-schema", data: faqJsonLd }] : []),
    ...(isGuideRoute(route)
      ? [
          { id: "ochro-article-schema", data: getGuideArticleJsonLd(route) },
          { id: "ochro-breadcrumb-schema", data: getGuideBreadcrumbJsonLd(route) }
        ]
      : []),
    ...(route === "guideHub"
      ? [
          {
            id: "ochro-breadcrumb-schema",
            data: {
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Strona główna", item: "https://ochro.pl/" },
                { "@type": "ListItem", position: 2, name: "Poradnik", item: "https://ochro.pl/poradnik/" }
              ]
            }
          }
        ]
      : []),
    { id: "ochro-agency-schema", data: getAgencyJsonLd() }
  ];
}

const fontScaleOptions: Array<{ value: FontScale; label: string; ariaLabel: string; className: string }> = [
  { value: "normal", label: "A", ariaLabel: "Ustaw standardowy rozmiar tekstu", className: "font-button-sm" },
  { value: "large", label: "A", ariaLabel: "Powiększ tekst", className: "font-button-md" },
  { value: "xlarge", label: "A", ariaLabel: "Ustaw największy rozmiar tekstu", className: "font-button-lg" }
];

type Theme = "light" | "dark";

const THEME_STORAGE_KEY = "ochro-theme-choice";

function getInitialTheme(): Theme {
  if (typeof window === "undefined") {
    return "light";
  }
  const saved = window.localStorage.getItem(THEME_STORAGE_KEY);
  if (saved === "light" || saved === "dark") {
    return saved;
  }
  return "light";
}

function useTheme(): [Theme, () => void] {
  const [theme, setTheme] = useState<Theme>("light");

  useEffect(() => {
    setTheme(getInitialTheme());
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) {
      meta.setAttribute("content", theme === "dark" ? "#0f141d" : "#202939");
    }
  }, [theme]);

  const toggle = () => setTheme((current) => (current === "dark" ? "light" : "dark"));
  return [theme, toggle];
}

function ThemeToggle({ theme, onToggle }: { theme: Theme; onToggle: () => void }) {
  const dark = theme === "dark";
  return (
    <button
      className="theme-toggle"
      type="button"
      aria-label={dark ? "Włącz tryb jasny" : "Włącz tryb ciemny"}
      aria-pressed={dark}
      title={dark ? "Tryb jasny" : "Tryb ciemny"}
      onClick={onToggle}
    >
      {dark ? <Sun size={18} aria-hidden="true" /> : <Moon size={18} aria-hidden="true" />}
    </button>
  );
}

type NavLink = {
  label: string;
  href: string;
  children?: Array<{ label: string; href: string; desc?: string }>;
};

const navLinks: NavLink[] = [
  {
    label: "Kalkulator",
    href: "/kalkulator-ubezpieczenia-na-zycie/",
    children: [
      { label: "Na życie", href: "/kalkulator-ubezpieczenia-na-zycie/", desc: "Szybka wycena ochrony" },
      { label: "Pod kredyt", href: "/kalkulator-ubezpieczenia-na-zycie-pod-kredyt/", desc: "Suma pod saldo kredytu" },
      { label: "Na życie i dożycie", href: "/kalkulator-ubezpieczenia-na-zycie-i-dozycie/", desc: "Kapitał i ochrona" },
      { label: "Zdrowotny", href: "/kalkulator-ubezpieczenia-zdrowotnego/", desc: "Pakiet medyczny" },
      { label: "Na całe życie", href: "/kalkulator-ubezpieczenia-na-cale-zycie/", desc: "Ochrona dożywotnia" }
    ]
  },
  {
    label: "Produkty",
    href: "/ubezpieczenie-na-zycie/#oferta",
    children: [
      { label: "Oferta główna", href: "/ubezpieczenie-na-zycie/#oferta", desc: "Rodzaje polis i dla kogo są" },
      { label: "Ochrona rodziny", href: "/kalkulator-ubezpieczenia-na-zycie/", desc: "Terminowe ubezpieczenie na życie" },
      { label: "Pod kredyt", href: "/kalkulator-ubezpieczenia-na-zycie-pod-kredyt/", desc: "Polisa z cesją na bank" },
      { label: "Życie i dożycie", href: "/kalkulator-ubezpieczenia-na-zycie-i-dozycie/", desc: "Ochrona i kapitał na przyszłość" },
      { label: "Na całe życie", href: "/kalkulator-ubezpieczenia-na-cale-zycie/", desc: "Dożywotnie wsparcie bliskich" },
      { label: "Pakiet zdrowotny", href: "/kalkulator-ubezpieczenia-zdrowotnego/", desc: "Prywatna opieka medyczna" },
      { label: "Umowy dodatkowe", href: "/umowy-dodatkowe/", desc: "Choroby, szpital, operacje, LZ" }
    ]
  },
  {
    label: "Cennik",
    href: "/ubezpieczenie-na-zycie-cennik/",
    children: [
      { label: "Cennik 2026", href: "/ubezpieczenie-na-zycie-cennik/", desc: "Przykładowe składki" },
      { label: "Tabele cen", href: "/ubezpieczenie-na-zycie-cennik/#cennik", desc: "Wiek, suma i zakres" },
      { label: "Przykładowe kalkulacje", href: "/ubezpieczenie-na-zycie-cennik/#przyklady", desc: "Gotowe scenariusze" },
      { label: "Co wpływa na składkę", href: "/ubezpieczenie-na-zycie-cennik/#metodologia", desc: "Metodologia wyceny" }
    ]
  },
  {
    label: "Poradnik",
    href: "/poradnik/",
    children: [
      { label: "Wszystkie artykuły", href: "/poradnik/", desc: "Pełna lista tematów poradnika" },
      {
        label: "Ile kosztuje polisa",
        href: "/poradnik/ile-kosztuje-ubezpieczenie-na-zycie/",
        desc: "Przykładowe składki 2026"
      },
      { label: "Polisa po 50-tce", href: "/poradnik/ubezpieczenie-na-zycie-po-50/", desc: "Terminowa, dożywotnia i dodatki" },
      { label: "Jak wybrać polisę", href: "/poradnik/jak-wybrac-polise/", desc: "Przewodnik krok po kroku" },
      { label: "Jak działa ochrona", href: "/poradnik/jak-dziala-ochrona/", desc: "Podstawy dla klienta" },
      {
        label: "Polisa pod kredyt",
        href: "/poradnik/ubezpieczenie-na-zycie-pod-kredyt-hipoteczny/",
        desc: "Bank, cesja i suma ochrony"
      },
      { label: "Ubezpieczenia dodatkowe", href: "/poradnik/ubezpieczenia-dodatkowe/", desc: "Rozszerzenia ochrony" },
      { label: "Oszczędzanie na emeryturę", href: "/poradnik/oszczedzanie-na-emeryture/", desc: "Kapitał, ochrona i dożycie" },
      { label: "Proces zakupu", href: "/poradnik/proces-zakupu-polisy/", desc: "Od kalkulatora do polisy" }
    ]
  },
  {
    label: "FAQ",
    href: "/faq/"
  },
  {
    label: "Kontakt",
    href: "/kontakt/",
    children: [
      { label: "Kontakt z doradcą", href: "/kontakt/", desc: "Telefon i formularz zgłoszenia" },
      { label: "O nas", href: "/o-nas/", desc: "Operator, adres i zasady serwisu" },
      { label: "Regulamin", href: "/regulamin/", desc: "Zasady korzystania z serwisu" },
      { label: "Prywatność", href: "/polityka-prywatnosci/", desc: "RODO i administrator danych" }
    ]
  }
];

type SearchEntry = { title: string; desc: string; href: string; keywords: string };

const searchIndex: SearchEntry[] = [
  {
    title: "Strona główna",
    desc: "Kalkulatory, cennik i przewodnik po ubezpieczeniach",
    href: "/",
    keywords: "start główna ubezpieczenia kalkulatory cennik"
  },
  ...Object.values(PRODUCTS).map((p) => ({
    title: `Kalkulator: ${p.cardTitle}`,
    desc: p.cardCopy,
    href: `/${p.slug}/`,
    keywords: `${p.cardTitle} ${p.heroTitle} kalkulator składka oblicz`
  })),
  {
    title: "Cennik ubezpieczenia na życie",
    desc: "Przykładowe ceny polis 2026",
    href: "/ubezpieczenie-na-zycie-cennik/",
    keywords: "cennik cena ceny składka koszt ile kosztuje widełki"
  },
  {
    title: "Poradnik: ubezpieczenie na życie po 50",
    desc: "Jak dobrać polisę po 50: suma, okres, zdrowie, dodatki i warianty ochrony",
    href: "/poradnik/ubezpieczenie-na-zycie-po-50/",
    keywords: "ubezpieczenie na życie po 50 po pięćdziesiątce 50 plus 60 lat składka zdrowie dożywotnie terminowe"
  },
  {
    title: "Poradnik: jak wybrać polisę",
    desc: "Suma, okres, dodatki, cena i OWU krok po kroku",
    href: "/poradnik/jak-wybrac-polise/",
    keywords: "poradnik przewodnik jak wybrać polisę dobrać suma zakres cena owu"
  },
  {
    title: "Poradnik: jak działa ochrona",
    desc: "Suma, uposażeni, wypłata, karencje i wyłączenia",
    href: "/poradnik/jak-dziala-ochrona/",
    keywords: "jak działa ochrona polisa na życie uposażeni wypłata świadczenie karencja wyłączenia"
  },
  {
    title: "Poradnik: polisa pod kredyt hipoteczny",
    desc: "Bank, cesja, suma ubezpieczenia i polisa zewnętrzna",
    href: "/poradnik/ubezpieczenie-na-zycie-pod-kredyt-hipoteczny/",
    keywords:
      "kredyt hipoteczny polisa na życie pod kredyt cesja na bank ubezpieczenie zewnętrzne bank suma saldo"
  },
  {
    title: "Poradnik: ubezpieczenia dodatkowe",
    desc: "Ciężkie choroby, szpital, operacje, NNW i leczenie za granicą",
    href: "/poradnik/ubezpieczenia-dodatkowe/",
    keywords: "dodatki ubezpieczenia dodatkowe choroby szpital operacje nnw uszczerbek leczenie za granicą"
  },
  {
    title: "Poradnik: oszczędzanie na emeryturę",
    desc: "Kapitał na przyszłość, życie i dożycie, premie, waloryzacja i wykup",
    href: "/poradnik/oszczedzanie-na-emeryture/",
    keywords: "emerytura oszczędzanie na emeryturę życie i dożycie kapitał polisa perspektywa składka premie wykup"
  },
  {
    title: "Poradnik: proces zakupu polisy",
    desc: "Od kalkulatora i analizy potrzeb do ankiety oraz dokumentu polisy",
    href: "/poradnik/proces-zakupu-polisy/",
    keywords: "proces zakupu polisy wniosek ankieta medyczna ocena ryzyka dokument polisy doradca"
  },
  {
    title: "Produkty ubezpieczeniowe",
    desc: "Ubezpieczenie ochronne, pod kredyt, dożycie, zdrowotne i dożywotnie",
    href: "/ubezpieczenie-na-zycie/#oferta",
    keywords: "produkty zakresy ubezpieczenia ochronne kredyt dożycie zdrowotne dożywotnie"
  },
  {
    title: "Umowy dodatkowe",
    desc: "Ciężkie choroby, szpital, operacje, uszczerbek, leczenie za granicą",
    href: "/umowy-dodatkowe/",
    keywords: "umowy dodatkowe dodatki choroby szpital operacje uszczerbek złamania leczenie za granicą dziecko partner"
  },
  {
    title: "Najczęstsze pytania (FAQ)",
    desc: "Odpowiedzi o cenach, zakresie, ankiecie i OWU",
    href: "/faq/",
    keywords: "faq pytania pomoc wątpliwości zaawansowane owu ankieta medyczna cesja"
  },
  {
    title: "Kontakt",
    desc: "Umów rozmowę z doradcą",
    href: "/kontakt/",
    keywords: "kontakt formularz spotkanie doradca telefon umów"
  },
  {
    title: "O nas",
    desc: "Operator serwisu, dane firmy, adres i sposób działania Ochro.pl",
    href: "/o-nas/",
    keywords: "o nas firma operator martyna mostek nip regon adres dane działalność ochro"
  },
  {
    title: "Polityka prywatności",
    desc: "RODO, administrator danych i prawa osoby, której dane dotyczą",
    href: "/polityka-prywatnosci/",
    keywords: "rodo prywatność prywatnosc dane administrator polityka"
  },
  {
    title: "Polityka cookies",
    desc: "Cookies, localStorage i preferencje prywatności",
    href: "/polityka-cookies/",
    keywords: "cookies ciasteczka zgoda localstorage prywatność prywatnosc"
  },
  {
    title: "Regulamin serwisu",
    desc: "Zasady korzystania z kalkulatorów i formularza",
    href: "/regulamin/",
    keywords: "regulamin zasady oferta informacyjnie kalkulator"
  }
];

const normalize = (value: string) =>
  value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");

function searchSite(query: string): SearchEntry[] {
  const tokens = normalize(query).split(/\s+/).filter(Boolean);
  if (tokens.length === 0) {
    return [];
  }
  return searchIndex.filter((entry) => {
    const haystack = normalize(`${entry.title} ${entry.desc} ${entry.keywords}`);
    return tokens.every((token) => haystack.includes(token));
  });
}

function SiteSearch({
  inputId,
  variant,
  onNavigate
}: {
  inputId: string;
  variant: "desktop" | "mobile";
  onNavigate?: () => void;
}) {
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState(() => variant === "mobile");
  const inputRef = useRef<HTMLInputElement>(null);
  const trimmed = query.trim();
  const results = trimmed ? searchSite(trimmed).slice(0, 6) : [];
  const isExpanded = variant === "mobile" || expanded;
  const panelId = `${inputId}-panel`;

  useEffect(() => {
    if (isExpanded && variant === "desktop") {
      inputRef.current?.focus();
    }
  }, [isExpanded, variant]);

  useEffect(() => {
    if (variant !== "desktop" || !isExpanded) {
      return undefined;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setExpanded(false);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isExpanded, variant]);

  return (
    <form
      className={`site-search site-search-${variant}${isExpanded ? " is-expanded" : ""}`}
      role="search"
      onSubmit={(event) => {
        event.preventDefault();
        if (!isExpanded) {
          setExpanded(true);
          return;
        }
        if (results[0]) {
          onNavigate?.();
          window.location.assign(results[0].href);
        }
      }}
      onBlur={(event) => {
        if (variant === "desktop" && !event.currentTarget.contains(event.relatedTarget as Node | null)) {
          setExpanded(false);
        }
      }}
    >
      {variant === "desktop" ? (
        <>
          <button
            className="site-search-trigger"
            type="button"
            aria-label={isExpanded ? "Zamknij wyszukiwarkę" : "Otwórz wyszukiwarkę"}
            aria-expanded={isExpanded}
            aria-controls={panelId}
            onClick={() => setExpanded((current) => !current)}
          >
            {isExpanded ? <X className="site-search-icon" size={18} aria-hidden="true" /> : <Search className="site-search-icon" size={18} aria-hidden="true" />}
          </button>
          {isExpanded && (
            <div className="site-search-panel" id={panelId}>
              <div className="site-search-field">
                <Search className="site-search-icon" size={17} aria-hidden="true" />
                <input
                  ref={inputRef}
                  id={inputId}
                  className="site-search-input"
                  type="search"
                  autoComplete="off"
                  placeholder="Szukaj: kalkulator, cennik..."
                  aria-label="Szukaj na stronie"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                />
              </div>
              {trimmed.length > 0 && (
                <div className="site-search-results" role="listbox" aria-label="Wyniki wyszukiwania">
                  {results.length === 0 ? (
                    <p className="site-search-empty">Brak wyników dla "{trimmed}".</p>
                  ) : (
                    results.map((result) => (
                      <a
                        className="site-search-result"
                        key={result.href + result.title}
                        href={result.href}
                        onClick={onNavigate}
                      >
                        <strong>{result.title}</strong>
                        <span>{result.desc}</span>
                      </a>
                    ))
                  )}
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        <>
          <button
            className="site-search-trigger"
            type="submit"
            aria-label="Szukaj na stronie"
            aria-controls={inputId}
          >
            <Search className="site-search-icon" size={18} aria-hidden="true" />
          </button>
          <input
            ref={inputRef}
            id={inputId}
            className="site-search-input"
            type="search"
            autoComplete="off"
            placeholder="Szukaj: kalkulator, cennik..."
            aria-label="Szukaj na stronie"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          {trimmed.length > 0 && (
            <div className="site-search-results" role="listbox" aria-label="Wyniki wyszukiwania">
              {results.length === 0 ? (
                <p className="site-search-empty">Brak wyników dla "{trimmed}".</p>
              ) : (
                results.map((result) => (
                  <a
                    className="site-search-result"
                    key={result.href + result.title}
                    href={result.href}
                    onClick={onNavigate}
                  >
                    <strong>{result.title}</strong>
                    <span>{result.desc}</span>
                  </a>
                ))
              )}
            </div>
          )}
        </>
      )}
    </form>
  );
}

function Header() {
  const [fontScale, setFontScale] = useState<FontScale>("normal");

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    const saved = window.localStorage.getItem("ochro-font-scale");
    setFontScale(saved === "large" || saved === "xlarge" ? saved : "normal");
  }, []);

  const [theme, toggleTheme] = useTheme();
  const [menuOpen, setMenuOpen] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(0);
  const headerRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (fontScale === "normal") {
      document.documentElement.removeAttribute("data-font-scale");
    } else {
      document.documentElement.setAttribute("data-font-scale", fontScale);
    }
    window.localStorage.setItem("ochro-font-scale", fontScale);
  }, [fontScale]);

  useEffect(() => {
    if (!menuOpen) {
      return;
    }
    const measure = () => setHeaderHeight(headerRef.current?.offsetHeight ?? 0);
    measure();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setMenuOpen(false);
      }
    };
    const onResize = () => {
      if (window.innerWidth > 900) {
        setMenuOpen(false);
      } else {
        measure();
      }
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    window.addEventListener("resize", onResize);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onResize);
    };
  }, [menuOpen]);

  const closeMenu = () => setMenuOpen(false);

  return (
    <header className="site-header" aria-label="Główna nawigacja" ref={headerRef}>
      <div className="header-inner">
        <div className="header-main">
          <a className="brand" href="/" aria-label="ochro.pl - strona główna">
            <img className="brand-logo" src="/ochro-logo.svg?v=24" alt="" />
          </a>
          <div className="header-utility">
            <div className="prefs-tools">
              <div className="font-tools" aria-label="Rozmiar tekstu">
                {fontScaleOptions.map((option) => (
                  <button
                    className={`font-button ${option.className}${fontScale === option.value ? " is-active" : ""}`}
                    type="button"
                    key={option.value}
                    aria-label={option.ariaLabel}
                    aria-pressed={fontScale === option.value}
                    onClick={() => setFontScale(option.value)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
              <ThemeToggle theme={theme} onToggle={toggleTheme} />
            </div>
            <a className="helpline" href={COMPANY.phoneHref} onClick={() => trackPhoneClick("header")}>
              <Phone size={17} aria-hidden="true" />
              <span>Infolinia</span>
              <strong>{COMPANY.phoneDisplay}</strong>
            </a>
          </div>
          <div className="header-actions">
            <a className="header-cta header-cta-primary" href="/kontakt/#formularz-start">
              Umów spotkanie
              <ArrowRight size={17} aria-hidden="true" />
            </a>
            <a className="header-cta header-cta-secondary" href="/kalkulator-ubezpieczenia-na-zycie/">
              <Calculator size={18} aria-hidden="true" />
              Kalkulator
            </a>
          </div>
          <button
            className="nav-toggle"
            type="button"
            aria-label={menuOpen ? "Zamknij menu" : "Otwórz menu"}
            aria-expanded={menuOpen}
            aria-controls="mobile-menu"
            onClick={() => setMenuOpen((open) => !open)}
          >
            {menuOpen ? <X size={24} aria-hidden="true" /> : <Menu size={24} aria-hidden="true" />}
          </button>
        </div>
        <nav className="header-nav" aria-label="Sekcje strony">
          <span className="header-nav-spacer" aria-hidden="true" />
          <div className="header-nav-links">
            {navLinks.map((link) => (
              <div className="nav-item" key={link.label}>
                <a
                  className="nav-link"
                  href={link.href}
                  aria-haspopup={link.children ? "true" : undefined}
                >
                  {link.label}
                  {link.children && <ChevronDown size={15} aria-hidden="true" />}
                </a>
                {link.children && (
                  <div className="nav-dropdown" aria-label={`${link.label} - podmenu`}>
                    {link.children.map((child) => (
                      <a className="nav-dropdown-link" key={child.label + child.href} href={child.href}>
                        <strong>{child.label}</strong>
                        {child.desc && <span>{child.desc}</span>}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
          <SiteSearch inputId="search-desktop" variant="desktop" />
        </nav>
      </div>

      <div
        id="mobile-menu"
        className={`mobile-menu${menuOpen ? " is-open" : ""}`}
        style={{ top: headerHeight ? `${headerHeight}px` : undefined }}
        onClick={closeMenu}
      >
        <div className="mobile-menu-panel" onClick={(event) => event.stopPropagation()}>
          <SiteSearch inputId="search-mobile" variant="mobile" onNavigate={closeMenu} />
          <nav className="mobile-nav" aria-label="Menu">
            {navLinks.map((link) => (
              <div className="mobile-nav-group" key={link.label}>
                <a className="mobile-nav-parent" href={link.href} onClick={closeMenu}>
                  {link.label}
                </a>
                {link.children && (
                  <div className="mobile-subnav">
                    {link.children.map((child) => (
                      <a key={child.label + child.href} href={child.href} onClick={closeMenu}>
                        {child.label}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>
          <a
            className="mobile-helpline"
            href={COMPANY.phoneHref}
            onClick={() => {
              trackPhoneClick("mobile_menu");
              closeMenu();
            }}
          >
            <Phone size={18} aria-hidden="true" />
            <span>Infolinia</span>
            <strong>{COMPANY.phoneDisplay}</strong>
          </a>
          <div className="mobile-cta">
            <a className="header-cta header-cta-primary" href="/kontakt/#formularz-start" onClick={closeMenu}>
              Umów spotkanie
              <ArrowRight size={17} aria-hidden="true" />
            </a>
            <a
              className="header-cta header-cta-secondary"
              href="/kalkulator-ubezpieczenia-na-zycie/"
              onClick={closeMenu}
            >
              <Calculator size={18} aria-hidden="true" />
              Kalkulator
            </a>
          </div>
          <div className="mobile-prefs">
            <div className="mobile-fonttools font-tools" aria-label="Rozmiar tekstu">
              {fontScaleOptions.map((option) => (
                <button
                  className={`font-button ${option.className}${fontScale === option.value ? " is-active" : ""}`}
                  type="button"
                  key={option.value}
                  aria-label={option.ariaLabel}
                  aria-pressed={fontScale === option.value}
                  onClick={() => setFontScale(option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <ThemeToggle theme={theme} onToggle={toggleTheme} />
          </div>
        </div>
      </div>
    </header>
  );
}

function Hero({
  kicker,
  title,
  copy,
  primaryHref = "#kalkulatory",
  primaryText = "Zobacz też",
  secondaryHref = "/ubezpieczenie-na-zycie-cennik/",
  secondaryText = "Zobacz cennik"
}: {
  kicker: string;
  title: string;
  copy: string;
  primaryHref?: string;
  primaryText?: string;
  secondaryHref?: string;
  secondaryText?: string;
}) {
  return (
    <section
      className="hero"
      style={{
        backgroundImage: `linear-gradient(90deg, rgba(32, 41, 57, 0.93), rgba(32, 41, 57, 0.66) 48%, rgba(242, 106, 33, 0.08)), url(${heroImage})`
      }}
    >
      <div className="hero-inner">
        <p className="eyebrow">
          <ShieldCheck size={18} aria-hidden="true" />
          {kicker}
        </p>
        <h1>{title}</h1>
        <p className="hero-copy">{copy}</p>
        <div className="hero-actions">
          <a className="primary-button" href={primaryHref}>
            {primaryText}
            <ArrowRight size={19} aria-hidden="true" />
          </a>
          <a className="ghost-button" href={secondaryHref}>
            {secondaryText}
            <Calculator size={18} aria-hidden="true" />
          </a>
        </div>
        <div className="hero-proof" aria-label="Najważniejsze informacje">
          <span>5 kalkulatorów</span>
          <span>Orientacyjne składki</span>
          <span>Kontakt bez zobowiązań</span>
        </div>
        <p className="hero-legal">
          Treści, ceny i wyniki kalkulatorów mają charakter informacyjny. Nie stanowią oferty w rozumieniu
          art. 66 §1 Kodeksu cywilnego ani rekomendacji ubezpieczeniowej; finalne warunki wymagają rozmowy,
          analizy potrzeb i dokumentów ubezpieczyciela.
        </p>
      </div>
    </section>
  );
}

function PricingTables() {
  const sampleMonthly = pricingByAge.find((row) => row.age === "35 lat")?.su300 ?? calcOchronneMonthly(35, 300000);
  const sampleAmount = sampleMonthly.replace(/\s*zł$/, "");

  return (
    <section className="section price-section" id="cennik">
      <div className="pricing-showcase">
        <div className="pricing-showcase-copy">
          <p className="section-kicker">Cennik 2026</p>
          <h2>Ile kosztuje ubezpieczenie na życie? Przykłady z kalkulatora.</h2>
          <p>
            Zamiast suchej tabeli pokazujemy najpierw warunki, na których liczymy przykłady. Poniższe ceny
            dotyczą ochrony na wypadek śmierci, bez dodatków zdrowotnych, leczenia za granicą i zwyżek po
            ocenie ryzyka. Pełne omówienie czynników ceny znajdziesz w poradniku{" "}
            <a href="/poradnik/ile-kosztuje-ubezpieczenie-na-zycie/">ile kosztuje ubezpieczenie na życie</a>.
          </p>
        </div>

        <div className="pricing-snapshot" aria-label="Przykładowa kalkulacja">
          <span className="pricing-snapshot-label">Przykład</span>
          <strong>
            {sampleAmount}
            <small>zł/mies.</small>
          </strong>
          <ul>
            <li>35 lat</li>
            <li>300 000 zł sumy</li>
            <li>okres 30 lat</li>
          </ul>
        </div>
      </div>

      <div className="pricing-parameter-grid" aria-label="Parametry wyceny">
        {productParameterRows.map((row) => (
          <article className="pricing-parameter-card" key={row.label}>
            <span>{row.label}</span>
            <strong>{row.value}</strong>
            <p>{row.note}</p>
          </article>
        ))}
      </div>

      <div className="pricing-table-grid">
        <div className="table-wrap">
          <h3>Przykłady według wieku</h3>
          <table>
            <thead>
              <tr>
                <th>Wiek</th>
                <th>100 000 zł</th>
                <th>300 000 zł</th>
                <th>500 000 zł</th>
              </tr>
            </thead>
            <tbody>
              {pricingByAge.map((row) => (
                <tr key={row.age}>
                  <td>{row.age}</td>
                  <td>{row.su100}</td>
                  <td>{row.su300}</td>
                  <td>{row.su500}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="table-wrap">
          <h3>Przykłady według sumy ubezpieczenia</h3>
          <table>
            <thead>
              <tr>
                <th>Suma ubezpieczenia</th>
                <th>35 lat</th>
                <th>45 lat</th>
                <th>55 lat</th>
              </tr>
            </thead>
            <tbody>
              {pricingBySum.map((row) => (
                <tr key={row.sum}>
                  <td>{row.sum}</td>
                  <td>{row.age35}</td>
                  <td>{row.age45}</td>
                  <td>{row.age55}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <p className="disclaimer">
        Przykłady nie obejmują ciężkich chorób, leczenia szpitalnego, operacji, NNW, uszczerbku ani
        innych umów dodatkowych. Takie rozszerzenia wyceniamy osobno, bo mają własne limity, warianty i
        definicje w OWU.
      </p>
    </section>
  );
}

function Methodology() {
  return (
    <section className="section method-section" id="metodologia">
      <div
        className="method-hero"
        style={{
          backgroundImage: `linear-gradient(90deg, rgba(32, 41, 57, 0.92), rgba(32, 41, 57, 0.62), rgba(32, 41, 57, 0.2)), url(${contactImage})`
        }}
      >
        <div className="method-hero-copy">
          <p className="section-kicker">Cena polisy</p>
          <h2>Co realnie wpływa na składkę?</h2>
          <p>
            Najpierw ustalamy, jaką kwotę mają dostać bliscy i na jak długo ma działać ochrona.
            Potem ubezpieczyciel potwierdza warunki w krótkiej ankiecie. Dzięki temu dostajesz realną
            propozycję, a nie obietnicę z tabelki.
          </p>
        </div>
      </div>
      <div className="method-grid">
        {comparisonRows.map((item, index) => {
          const Icon = item.Icon;
          return (
            <article className="method-card" key={item.label}>
              <div className="method-card-top">
                <span className="method-icon">
                  <Icon size={22} aria-hidden="true" />
                </span>
                <span className="method-index">{String(index + 1).padStart(2, "0")}</span>
              </div>
              <div className="method-card-main">
                <strong>{item.value}</strong>
                <h3>{item.label}</h3>
              </div>
              <p>{item.note}</p>
            </article>
          );
        })}
      </div>
      <p className="disclaimer">
        Kwoty na stronie są orientacyjne. Finalną składkę potwierdza ubezpieczyciel po wyborze zakresu
        i sprawdzeniu informacji z ankiety.
      </p>
    </section>
  );
}

function TrustSection() {
  const items = [
    "Podajemy widełki, a nie obietnicę ceny.",
    "Oddzielamy edukację od finalnej oferty.",
    "Zastrzegamy, że dane na stronie nie stanowią oferty handlowej.",
    "Wskazujemy czynniki, które zwykle zmieniają składkę.",
    "Zbieramy tylko dane potrzebne do kontaktu."
  ];

  return (
    <section className="data-section" id="zaufanie">
      <div>
        <p className="section-kicker">Zaufanie</p>
        <h2>Temat finansowy wymaga jasnych zasad.</h2>
      </div>
      <div className="data-copy">
        <p>
          Ubezpieczenie wpływa na finanse rodziny, dlatego strona musi być ostrożna, konkretna i
          czytelna. Zamiast obiecywać najniższą cenę, pokazujemy zakresy i zapraszamy do weryfikacji w
          rozmowie.
        </p>
        <ul className="check-list">
          {items.map((item) => (
            <li key={item}>
              <Check size={18} aria-hidden="true" />
              {item}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function ImageHeading({
  kicker,
  title,
  copy,
  image,
  variant = "wide"
}: {
  kicker: string;
  title: string;
  copy: string;
  image: string;
  variant?: "wide" | "split";
}) {
  return (
    <div
      className={`image-heading image-heading-${variant}`}
      style={{
        backgroundImage: `linear-gradient(90deg, rgba(32, 41, 57, 0.92), rgba(32, 41, 57, 0.64), rgba(32, 41, 57, 0.18)), url(${image})`
      }}
    >
      <div className="image-heading-copy">
        <p className="section-kicker">{kicker}</p>
        <h2>{title}</h2>
        <p>{copy}</p>
      </div>
    </div>
  );
}

function InsuranceEducationSection() {
  return (
    <section className="section education-section" id="jak-dziala">
      <ImageHeading
        kicker="Ubezpieczenie na życie"
        title="Najpierw zrozum ochronę, potem wybierz wariant."
        copy="Dobra strona ubezpieczeniowa powinna odpowiedzieć na pytania, które klient ma przed rozmową: co właściwie kupuje, kiedy polisa wypłaca pieniądze, ile może kosztować i gdzie kryją się ograniczenia. Poniżej zebraliśmy podstawy w języku decyzji, nie w języku OWU."
        image={educationImage}
      />
      <div className="info-grid">
        {basicInfoCards.map((item) => (
          <article className="info-card" key={item.title}>
            <span className="icon-badge">
              <ShieldCheck size={22} aria-hidden="true" />
            </span>
            <h3>{item.title}</h3>
            <p>{item.copy}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

function OfferOverviewSection() {
  return (
    <section className="section offer-section" id="oferta">
      <ImageHeading
        kicker="Produkty"
        title="Jakie ubezpieczenia możesz u nas policzyć i omówić?"
        copy="Punktem wyjścia są produkty ochronne, oszczędnościowo-ochronne, dożywotnie, zdrowotne oraz polisy pod kredyt. Każdy zakres zaczyna się od celu: zabezpieczyć rodzinę, bank, zdrowie, przyszły kapitał albo koszty po śmierci."
        image={offerImage}
        variant="split"
      />
      <div className="offer-grid">
        {offerHighlights.map((item) => {
          const product = PRODUCTS[item.id];
          const Icon = product.Icon;
          return (
            <article className="offer-card" key={item.id}>
              <div className="offer-card-top">
                <span className="icon-badge">
                  <Icon size={23} aria-hidden="true" />
                </span>
                <span className="offer-badge">{item.badge}</span>
              </div>
              <h3>{item.title}</h3>
              <p>{item.copy}</p>
              <ul className="chip-list">
                {item.bullets.map((bullet) => (
                  <li key={bullet}>{bullet}</li>
                ))}
              </ul>
              <a className="text-link" href={item.href}>
                Sprawdź
                <ArrowRight size={17} aria-hidden="true" />
              </a>
            </article>
          );
        })}
      </div>
    </section>
  );
}

function AddonsSection() {
  return (
    <section className="section addons-section" id="dodatki">
      <ImageHeading
        kicker="Ubezpieczenia dodatkowe"
        title="Dodatki, które zmieniają polisę z samej wypłaty po śmierci w szerszą ochronę."
        copy="Materiały produktowe pokazują, że umowę podstawową można rozbudować o kilkanaście ryzyk dla ubezpieczonego, małżonka, partnera i dzieci. To właśnie dodatki najczęściej decydują, czy polisa pomaga także przy chorobie, wypadku, hospitalizacji albo leczeniu za granicą."
        image={addonsImage}
      />
      <div className="addon-grid">
        {addonCards.map((item) => (
          <article className="addon-card" key={item.title}>
            <h3>{item.title}</h3>
            <p>{item.copy}</p>
            {item.points && (
              <ul className="check-list">
                {item.points.map((point) => (
                  <li key={point}>
                    <Check size={17} aria-hidden="true" />
                    {point}
                  </li>
                ))}
              </ul>
            )}
          </article>
        ))}
      </div>
      <p className="disclaimer">
        Zakres, limity, karencje, definicje chorób i zasady wypłaty zależą od aktualnych OWU. Dlatego
        dodatki dobieramy dopiero po ustaleniu, czy klient potrzebuje pieniędzy na leczenie, zastąpienie
        dochodu, spłatę zobowiązań czy ochronę dzieci.
      </p>
      <a className="inline-cta section-inline-cta" href="/umowy-dodatkowe/">
        Zobacz pełną stronę o umowach dodatkowych
        <ArrowRight size={17} aria-hidden="true" />
      </a>
    </section>
  );
}

function AdditionalContractsPage({ injected }: { injected: LeadContext | null }) {
  return (
    <>
      <section
        className="guide-hero addons-offer-hero"
        style={{
          backgroundImage: `linear-gradient(90deg, rgba(32, 41, 57, 0.94), rgba(32, 41, 57, 0.68), rgba(32, 41, 57, 0.2)), url(${addonsImage})`
        }}
      >
        <div className="guide-hero-copy">
          <p className="eyebrow">
            <ShieldCheck size={18} aria-hidden="true" />
            Umowy dodatkowe
          </p>
          <h1>Umowy dodatkowe do ubezpieczenia na życie.</h1>
          <p>
            Tak, oferujemy rozszerzenia podobne ideą do strony PRU, ale oparte na produktach PZU Życie SA i
            aktualnych OWU. To dodatki, które mogą uzupełnić polisę o chorobę, wypadek, szpital, operację,
            leczenie za granicą oraz ochronę partnera albo dziecka.
          </p>
          <div className="hero-actions">
            <a className="primary-button" href="#lista-umow">
              Zobacz dodatki
              <ArrowRight size={19} aria-hidden="true" />
            </a>
            <a className="ghost-button" href="/kontakt/#formularz-start">
              Dobierz z doradcą
              <Phone size={18} aria-hidden="true" />
            </a>
          </div>
        </div>
        <aside className="guide-hero-card addons-hero-card" aria-label="Najważniejsze informacje">
          <span>W skrócie</span>
          <ul>
            <li>dla ubezpieczonego, partnera i dzieci</li>
            <li>choroby, szpital, operacje, wypadki i leczenie za granicą</li>
            <li>możliwe przy starcie polisy albo jako dosprzedaż</li>
            <li>finalny zakres zależy od umowy podstawowej i oceny ryzyka</li>
          </ul>
        </aside>
      </section>

      <section className="section addon-offer-section" id="zakres">
        <ImageHeading
          kicker="Oferta dodatków"
          title="Nie każda polisa musi mieć każdy dodatek. Dobra polisa ma te, które faktycznie pracują dla klienta."
          copy="Umowy dodatkowe są osobnymi rozszerzeniami do umowy podstawowej. Najpierw ustalamy fundament: sumę na życie, kredyt, rodzinę i budżet. Dopiero potem dokładamy ryzyka zdrowotne, wypadkowe i rodzinne."
          image={guideAddonsImage}
        />
        <div className="addons-audience-grid">
          {additionalContractGroups.map((group) => {
            const Icon = group.Icon;
            return (
              <article className="addon-audience-card" key={group.title}>
                <span className="icon-badge">
                  <Icon size={23} aria-hidden="true" />
                </span>
                <h2>{group.title}</h2>
                <p>{group.copy}</p>
                <ul className="clean-list">
                  {group.facts.map((fact) => (
                    <li key={fact}>{fact}</li>
                  ))}
                </ul>
              </article>
            );
          })}
        </div>
      </section>

      <section className="section addon-list-section" id="lista-umow">
        <div className="section-heading compact">
          <p className="section-kicker">Lista umów</p>
          <h2>Jakie umowy dodatkowe możemy omówić?</h2>
          <p>
            Poniżej są najważniejsze rozszerzenia z materiałów PZU Życie. Opisy są klientowskie, ale celowo
            pokazują też kody, sumy i ograniczenia, żeby strona była konkretna, a nie tylko reklamowa.
          </p>
        </div>
        <div className="addons-offer-grid">
          {additionalContractCards.map((item) => (
            <article className="addon-offer-card" key={item.code + item.title}>
              <div className="addon-offer-card-top">
                <span>{item.code}</span>
                <small>{item.audience}</small>
              </div>
              <h3>{item.title}</h3>
              <p>{item.copy}</p>
              <dl className="addon-meta-list">
                <div>
                  <dt>Suma / limit</dt>
                  <dd>{item.sum}</dd>
                </div>
                <div>
                  <dt>Okres</dt>
                  <dd>{item.period}</dd>
                </div>
              </dl>
              <ul className="check-list">
                {item.facts.map((fact) => (
                  <li key={fact}>
                    <Check size={17} aria-hidden="true" />
                    {fact}
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>

      <section className="section addon-method-section" id="jak-dobieramy">
        <div className="guide-duo addon-guide-duo">
          <article>
            <p className="section-kicker">Metodyka</p>
            <h2>Jak dobieramy dodatki do polisy?</h2>
            <p>
              Dodatki nie są ozdobą do umowy. Każdy powinien mieć jasne zadanie: zabezpieczyć dochód po
              chorobie, pomóc przy hospitalizacji, podnieść wypłatę po wypadku albo objąć ochroną dziecko czy
              partnera.
            </p>
            <ul className="check-list">
              {additionalContractChecklist.map((item) => (
                <li key={item}>
                  <Check size={18} aria-hidden="true" />
                  {item}
                </li>
              ))}
            </ul>
          </article>
          <article>
            <p className="section-kicker">Ważne</p>
            <h2>Kiedy dodatku nie warto wybierać?</h2>
            <ul className="clean-list">
              <li>gdy obniża sumę na życie poniżej realnej potrzeby rodziny</li>
              <li>gdy klient nie rozumie definicji choroby, karencji albo limitu wypłat</li>
              <li>gdy dubluje ochronę, którą klient już ma w pracy lub w innej polisie</li>
              <li>gdy budżet na składkę jest niestabilny i istnieje ryzyko przerwania umowy</li>
              <li>gdy nazwa dodatku brzmi dobrze, ale OWU wyklucza najważniejsze dla klienta sytuacje</li>
            </ul>
          </article>
        </div>

        <div className="table-wrap addon-table-wrap">
          <h3>Parametry do pierwszej rozmowy</h3>
          <table>
            <thead>
              <tr>
                <th>Kod</th>
                <th>Zakres</th>
                <th>Dla kogo</th>
                <th>Suma lub limit</th>
              </tr>
            </thead>
            <tbody>
              {additionalContractCards.map((item) => (
                <tr key={`row-${item.code}-${item.title}`}>
                  <td>{item.code}</td>
                  <td>{item.title}</td>
                  <td>{item.audience}</td>
                  <td>{item.sum}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p>
            Tabela jest punktem orientacyjnym. Przed zawarciem umowy klient powinien otrzymać OWU, dokumenty
            produktu i finalną propozycję po analizie potrzeb oraz ocenie ryzyka.
          </p>
        </div>
      </section>

      <CalculatorHub
        heading="Najpierw policz umowę podstawową"
        intro="Dodatki dobiera się do konkretnej polisy, dlatego najlepiej zacząć od kalkulatora ochrony, kredytu albo polisy z kapitałem."
      />
      <FaqSection
        items={additionalContractFaqs}
        kicker="FAQ umów dodatkowych"
        title="Najczęstsze pytania o dodatki."
        intro="Krótko, konkretnie i z naciskiem na to, co trzeba sprawdzić przed decyzją."
        defaultExpanded
      />
      <LeadSection defaultProduct="addons" injected={injected} />
    </>
  );
}

function BuyingProcessSection() {
  return (
    <section className="section process-section" id="proces">
      <div className="process-shell">
        <div className="process-heading">
          <p className="section-kicker">Proces</p>
          <h2>Od kalkulatora do polisy.</h2>
          <p>Krótka ścieżka: najpierw budżet, potem finalne warunki od ubezpieczyciela.</p>
        </div>
        <div className="process-path" aria-label="Etapy zakupu polisy">
          {buyingSteps.map((step, index) => {
            const Icon = step.Icon;
            return (
              <article className="process-step" key={step.title}>
                <span className="process-step-icon">
                  <Icon size={21} aria-hidden="true" />
                </span>
                <small>{index + 1}</small>
                <h3>{step.title}</h3>
                <p>{step.copy}</p>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function QuoteExamplesSection() {
  return (
    <section className="section quote-section" id="przyklady">
      <div className="section-heading">
        <p className="section-kicker">Przykładowe kalkulacje</p>
        <h2>Zobacz, jak kalkulator przekłada parametry na składkę.</h2>
        <p>
          Poniższe scenariusze korzystają z tego samego mechanizmu co kalkulatory na stronie. To szybki
          punkt odniesienia przed wejściem w szczegóły i ustawieniem własnych parametrów.
        </p>
      </div>
      <div className="quote-grid">
        {quoteCards.map((quote) => {
          const product = PRODUCTS[quote.id];
          const result = product.compute(quote.input as CalcInputs);
          return (
            <article className="quote-card" key={quote.id}>
              <span>{product.cardTitle}</span>
              <h3>{quote.title}</h3>
              <ul>
                {quote.params.map((param) => (
                  <li key={param}>{param}</li>
                ))}
              </ul>
              <strong>
                ok. {Math.round(result.amount).toLocaleString("pl-PL")} {result.amountSuffix}
              </strong>
              <p>{result.note}</p>
              <a className="text-link" href={`/${product.slug}/`}>
                Sprawdź
                <Calculator size={17} aria-hidden="true" />
              </a>
            </article>
          );
        })}
      </div>
    </section>
  );
}

function ProductDeepDive({ productId }: { productId: ProductId }) {
  const product = PRODUCTS[productId];
  const detail = offerDetails[productId];

  return (
    <section className="section product-detail-section" id="szczegoly">
      <div className="section-heading">
        <p className="section-kicker">Szczegóły produktu</p>
        <h2>{product.cardTitle}: kiedy ma sens i co sprawdzić?</h2>
        <p>{detail.lead}</p>
      </div>

      <div className="detail-layout">
        <article className="detail-panel">
          <h3>Dla kogo?</h3>
          <ul className="check-list">
            {detail.bestFor.map((item) => (
              <li key={item}>
                <Check size={18} aria-hidden="true" />
                {item}
              </li>
            ))}
          </ul>
        </article>

        <article className="detail-panel">
          <h3>Co obejmuje?</h3>
          <ul className="check-list">
            {detail.scope.map((item) => (
              <li key={item}>
                <Check size={18} aria-hidden="true" />
                {item}
              </li>
            ))}
          </ul>
        </article>
      </div>

      <div className="parameter-grid" aria-label="Najważniejsze parametry produktu">
        {detail.parameters.map((row) => (
          <article className="parameter-card" key={row.label}>
            <span>{row.label}</span>
            <strong>{row.value}</strong>
            <p>{row.note}</p>
          </article>
        ))}
      </div>

      <div className="decision-box">
        <div>
          <p className="section-kicker">Przed rozmową</p>
          <h3>Odpowiedz sobie na cztery pytania.</h3>
        </div>
        <ul className="clean-list">
          {detail.decision.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function MortgageIntentSection() {
  return (
    <section className="section product-detail-section" id="polisa-pod-kredyt">
      <div className="section-heading compact">
        <p className="section-kicker">Pod kredyt hipoteczny</p>
        <h2>Polisa z cesją na bank, ale policzona pod Twoją sytuację.</h2>
        <p>
          Ta strona jest przygotowana dla osób, które porównują polisę bankową z indywidualnym
          ubezpieczeniem na życie pod kredyt. Najpierw liczysz orientacyjną składkę, potem ustalamy,
          czy zakres spełnia wymagania banku i czy warto zostawić dodatkową kwotę dla rodziny.
        </p>
      </div>

      <div className="info-grid">
        <article className="info-card">
          <span className="icon-badge">
            <Home size={22} aria-hidden="true" />
          </span>
          <h3>Co zwykle sprawdza bank?</h3>
          <p>
            Najczęściej liczy się suma ubezpieczenia, okres ochrony, cesja praw z polisy, potwierdzenie
            opłacenia składki i zgodność dokumentów z wymaganiami kredytu.
          </p>
        </article>
        <article className="info-card">
          <span className="icon-badge">
            <Calculator size={22} aria-hidden="true" />
          </span>
          <h3>Jak dobrać sumę?</h3>
          <p>
            Punktem wyjścia jest saldo kredytu. Przy rodzinie lub jednym głównym dochodzie często warto
            sprawdzić wariant wyższy, żeby po spłacie długu została rezerwa.
          </p>
        </article>
        <article className="info-card">
          <span className="icon-badge">
            <FileText size={22} aria-hidden="true" />
          </span>
          <h3>Co przygotować do rozmowy?</h3>
          <p>
            Kwotę kredytu, okres spłaty, nazwę banku, wiek kredytobiorców i informację, czy polisa ma
            chronić tylko bank, czy także domowy budżet.
          </p>
        </article>
        <article className="info-card">
          <span className="icon-badge">
            <ShieldCheck size={22} aria-hidden="true" />
          </span>
          <h3>Kiedy kliknąć formularz?</h3>
          <p>
            Gdy masz orientacyjny wynik, zostaw telefon w formularzu kontaktowym. Doradca doprecyzuje
            parametry podczas rozmowy i przygotuje warianty pod bank oraz domowy budżet.
          </p>
        </article>
      </div>

      <div className="decision-box">
        <div>
          <p className="section-kicker">Szybki test</p>
          <h3>Kiedy sprawdzić polisę pod kredyt?</h3>
        </div>
        <ul className="clean-list">
          <li>przed podpisaniem umowy kredytowej lub wyborem polisy proponowanej przez bank</li>
          <li>przy aneksie, refinansowaniu albo zmianie salda kredytu</li>
          <li>gdy chcesz porównać cenę samej ochrony życia z dodatkami zdrowotnymi</li>
        </ul>
      </div>
    </section>
  );
}

function FaqSection({
  items = faqs,
  kicker = "FAQ",
  title = "Najczęstsze pytania.",
  intro,
  defaultExpanded = false
}: {
  items?: ProductFaq[];
  kicker?: string;
  title?: string;
  intro?: string;
  defaultExpanded?: boolean;
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const hasMore = items.length > 5;
  const visibleItems = expanded || !hasMore ? items : items.slice(0, 7);

  return (
    <section className="section faq-section" id="faq">
      <div className="section-heading compact">
        <p className="section-kicker">{kicker}</p>
        <h2>{title}</h2>
        {intro && <p>{intro}</p>}
      </div>
      <div className={`faq-list${!expanded && hasMore ? " is-collapsed" : ""}`}>
        {visibleItems.map((item, index) => {
          const preview = !expanded && hasMore && index > 4;
          const previewClass = preview ? `faq-preview faq-preview-${index - 4}` : undefined;
          return (
            <details className={previewClass} key={item.question}>
              <summary>
                {item.question}
                <ChevronDown size={18} aria-hidden="true" />
              </summary>
              {!preview && <p>{item.answer}</p>}
            </details>
          );
        })}
      </div>
      {hasMore && !expanded && (
        <button
          className="faq-more-button"
          type="button"
          onClick={() => setExpanded(true)}
        >
          Zobacz więcej
          <ChevronDown size={17} aria-hidden="true" />
        </button>
      )}
    </section>
  );
}

function LeadSection({
  defaultProduct = "life",
  injected = null
}: {
  defaultProduct?: string;
  injected?: LeadContext | null;
}) {
  const [form, setForm] = useState<LeadForm>(() => ({ ...initialForm, product: defaultProduct }));
  const [quoteContext, setQuoteContext] = useState<LeadContext | null>(null);
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [statusMessage, setStatusMessage] = useState("");
  const [consentErrors, setConsentErrors] = useState<{
    requiredConsent?: string;
  }>({});
  const requiredConsentRef = useRef<HTMLInputElement | null>(null);
  const formStartedRef = useRef(false);

  const updateForm = <K extends keyof LeadForm>(key: K, value: LeadForm[K]) => {
    if (!formStartedRef.current && ["name", "phone", "city", "message"].includes(String(key))) {
      formStartedRef.current = true;
      trackEvent("formularz_start", {
        product: form.product,
        source: quoteContext ? "calculator_transfer" : "main_form"
      });
    }

    setForm((current) => ({
      ...current,
      [key]: value
    }));
  };

  const updateRequiredConsent = (checked: boolean) => {
    setForm((current) => ({
      ...current,
      contactConsent: checked,
      privacyConsent: checked
    }));

    if (checked) {
      setConsentErrors((current) => ({
        ...current,
        requiredConsent: undefined
      }));
    }
  };

  // Gdy użytkownik kliknie CTA w kalkulatorze, zapisz kalkulację w tle i pokaż krótką kartę w formularzu.
  useEffect(() => {
    if (injected) {
      setQuoteContext(injected);
      setForm((current) => ({
        ...current,
        product: injected.product,
        message: current.message.startsWith("Kalkulator:") ? "" : current.message
      }));
    }
  }, [injected]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setStatusMessage("");

    if (!hasMinimumPhoneDigits(form.phone)) {
      setStatus("error");
      setStatusMessage("Podaj numer telefonu z co najmniej 9 cyframi.");
      return;
    }

    const nextConsentErrors: typeof consentErrors = {};

    if (!form.contactConsent || !form.privacyConsent) {
      nextConsentErrors.requiredConsent =
        "Zaznacz zgodę na kontakt i potwierdzenie informacji o przetwarzaniu danych.";
    }

    if (Object.keys(nextConsentErrors).length > 0) {
      setStatus("idle");
      setConsentErrors(nextConsentErrors);
      requiredConsentRef.current?.focus({ preventScroll: true });
      requiredConsentRef.current?.scrollIntoView({ block: "center", behavior: "smooth" });
      return;
    }

    setConsentErrors({});
    setStatus("submitting");

    const productLabel =
      LEAD_PRODUCT_OPTIONS.find((option) => option.value === form.product)?.label ?? form.product;

    const message = [
      quoteContext?.message,
      form.message.trim() ? `Dodatkowe informacje od klienta: ${form.message.trim()}` : ""
    ]
      .filter(Boolean)
      .join("\n\n") || form.message;

    const payload = {
      ...form,
      message,
      selectedNeeds: [form.product],
        source: getAttributionSource(),
      recommendation: productLabel,
      legalVersion: PRIVACY_VERSION,
      contactConsentText: CONTACT_CONSENT_TEXT,
      privacyConsentText: PRIVACY_CONSENT_TEXT,
      marketingConsentText: ""
    };

    try {
      await submitLead(payload);

      setStatus("success");
      setStatusMessage("Dzięki! Zgłoszenie zapisane — Martyna oddzwoni zwykle w ciągu 30 minut (pn–pt 9–18).");
      trackEvent("lead_success", { product: form.product, source: quoteContext ? "calculator_transfer" : "main_form" });
      setForm({ ...initialForm, product: form.product });
      setQuoteContext(null);
    } catch {
      trackEvent("lead_error", { product: form.product, source: quoteContext ? "calculator_transfer" : "main_form" });
      setStatus("error");
      setStatusMessage("Nie udało się wysłać formularza. Spróbuj ponownie za chwilę albo zadzwoń:");
    }
  };

  return (
    <section className="lead-band" id="formularz">
      <div className="form-intro">
        <p className="section-kicker">Kontakt</p>
        <h2>Otrzymaj 3 warianty.</h2>
        <p>Zostaw telefon. Doradca oddzwoni i porówna wariant tańszy, optymalny oraz szerszy.</p>
        <div className="contact-lines">
          <span>
            <Clock3 size={18} aria-hidden="true" />
            Krótka rozmowa bez zobowiązań
          </span>
          <span>
            <Mail size={18} aria-hidden="true" />
            Zgłoszenie trafia bezpośrednio do doradcy
          </span>
        </div>
        <div className="contact-highlight">
          <strong>0 zł</strong>
          <span>za konsultację</span>
        </div>
      </div>

      <form className="lead-form lead-form-simple" id="formularz-start" onSubmit={handleSubmit}>
        <div className="lead-simple-head">
          <strong>Wystarczy numer telefonu</strong>
          <span>Szczegóły polisy omówisz dopiero w rozmowie z doradcą.</span>
        </div>

        <AdvisorCard variant="compact" />

        <div className="field-grid lead-simple-fields">
          <label aria-label="Telefon">
            <input
              required
              type="tel"
              aria-label="Telefon"
              autoComplete="tel"
              inputMode="tel"
              pattern="(?:[^0-9]*[0-9]){9,}[^0-9]*"
              title="Podaj numer telefonu z co najmniej 9 cyframi."
              value={form.phone}
              onChange={(event) => updateForm("phone", formatPhoneInput(event.target.value))}
              placeholder="Telefon"
            />
          </label>
          <label aria-label="Imię">
            <input
              aria-label="Imię"
              autoComplete="given-name"
              value={form.name}
              onChange={(event) => updateForm("name", event.target.value)}
              placeholder="Imię (opcjonalnie)"
            />
          </label>
        </div>

        {quoteContext && (
          <div className="lead-quote-summary" role="status">
            <span>Kalkulacja dodana do zgłoszenia</span>
            <strong>
              {quoteContext.summary.title}: {quoteContext.summary.amount}
            </strong>
            <ul>
              {quoteContext.summary.details.slice(0, 4).map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
            <p>Uzupełnij telefon i wyślij. Pełne parametry kalkulacji trafią do doradcy w tle.</p>
          </div>
        )}

        <label className="honeypot" aria-hidden="true">
          Strona internetowa
          <input
            tabIndex={-1}
            autoComplete="off"
            value={form.website}
            onChange={(event) => updateForm("website", event.target.value)}
          />
        </label>

        <div className="consents">
          <label className={consentErrors.requiredConsent ? "has-error" : undefined}>
            <input
              ref={requiredConsentRef}
              name="requiredConsent"
              type="checkbox"
              checked={form.contactConsent && form.privacyConsent}
              onChange={(event) => updateRequiredConsent(event.target.checked)}
              aria-invalid={Boolean(consentErrors.requiredConsent)}
              aria-describedby={consentErrors.requiredConsent ? "required-consent-error" : undefined}
            />
            <span>
              Zgadzam się na telefon w sprawie mojego zgłoszenia i znam informację o przetwarzaniu danych.
            </span>
            {consentErrors.requiredConsent && (
              <p className="consent-error" id="required-consent-error" role="alert">
                {consentErrors.requiredConsent}
              </p>
            )}
          </label>
          <details className="consent-details">
            <summary>Pełna treść zgody i informacja o danych</summary>
            <small>
              {CONTACT_CONSENT_TEXT} {PRIVACY_CONSENT_TEXT} Szczegóły opisuje{" "}
              <a href="/polityka-prywatnosci/" target="_blank" rel="noopener noreferrer">
                polityka prywatności
              </a>
              .
            </small>
          </details>
        </div>

        <button className="submit-button lead-submit-button" type="submit" disabled={status === "submitting"}>
          {status === "submitting" ? "Wysyłanie..." : "Otrzymaj 3 warianty"}
          <ArrowRight size={19} aria-hidden="true" />
        </button>

        {statusMessage && (
          <p className={`form-status ${status === "error" ? "is-error" : "is-success"}`} role="status">
            {statusMessage}
            {status === "error" && (
              <>
                {" "}
                <a href={COMPANY.phoneHref} onClick={() => trackPhoneClick("form_error")}>
                  {COMPANY.phoneDisplay}
                </a>
              </>
            )}
          </p>
        )}
      </form>
    </section>
  );
}

function LifeGuide() {
  const guideItems = [
    {
      title: "Najpierw suma, potem dodatki",
      copy: "Podstawą jest kwota, która realnie pomoże rodzinie. Dopiero potem dobieramy choroby, NNW, szpital czy niezdolność do pracy."
    },
    {
      title: "Porównuj definicje, nie tylko cenę",
      copy: "Tańsza polisa może mieć węższą listę chorób, dłuższe karencje albo mniej korzystne wyłączenia odpowiedzialności."
    },
    {
      title: "Nie odkładaj ankiety",
      copy: "Stan zdrowia i wiek są kluczowe. Dla wielu osób wcześniejsza decyzja oznacza większy wybór wariantów."
    }
  ];

  return (
    <section className="section">
      <div className="section-heading">
        <p className="section-kicker">Poradnik</p>
        <h2>Jak wybrać ubezpieczenie na życie?</h2>
        <p>
          Dobra polisa nie zaczyna się od hasła “najtańsza”. Zaczyna się od pytania, co ma się stać
          z finansami rodziny, jeśli zabraknie dochodu, zdrowia albo czasu na decyzje.
        </p>
      </div>
      <div className="process-grid">
        {guideItems.map((item, index) => (
          <article className="process-item" key={item.title}>
            <span>{index + 1}</span>
            <h3>{item.title}</h3>
            <p>{item.copy}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

function formatPlDate(iso: string) {
  const [year, month, day] = iso.split("-").map(Number);
  const months = [
    "stycznia", "lutego", "marca", "kwietnia", "maja", "czerwca",
    "lipca", "sierpnia", "września", "października", "listopada", "grudnia"
  ];
  if (!year || !month || !day) return iso;
  return `${day} ${months[month - 1]} ${year} r.`;
}

function GuideArticlePage({ route, injected }: { route: GuideRoute; injected: LeadContext | null }) {
  const page = guidePages[route];
  const isAfter50Landing = route === "guideAfter50";

  return (
    <>
      <section
        className="guide-hero"
        style={{
          backgroundImage: `linear-gradient(90deg, rgba(32, 41, 57, 0.94), rgba(32, 41, 57, 0.7), rgba(32, 41, 57, 0.22)), url(${page.image})`
        }}
      >
        <div className="guide-hero-copy">
          <p className="eyebrow">
            <FileText size={18} aria-hidden="true" />
            {page.kicker}
          </p>
          <h1>{page.title}</h1>
          <p>{page.copy}</p>
          <p className="guide-meta">
            <span>Martyna Mostek — doradca ubezpieczeniowy</span>
            <span>Opublikowano {formatPlDate(page.datePublished)}</span>
            {page.dateModified !== page.datePublished && (
              <span>Aktualizacja {formatPlDate(page.dateModified)}</span>
            )}
          </p>
          <div className="hero-actions">
            {isAfter50Landing ? (
              <>
                <a
                  className="primary-button"
                  href="#formularz-start"
                  onClick={() => trackEvent("poradnik_cta_click", { route, placement: "hero" })}
                >
                  Otrzymaj 3 warianty
                  <ArrowRight size={19} aria-hidden="true" />
                </a>
                <a className="ghost-button" href="#poradnik">
                  Czytaj poradnik
                  <ArrowRight size={18} aria-hidden="true" />
                </a>
              </>
            ) : (
              <>
                <a className="primary-button" href="#poradnik">
                  Czytaj poradnik
                  <ArrowRight size={19} aria-hidden="true" />
                </a>
                <a
                  className="ghost-button"
                  href="#formularz-start"
                  onClick={() => trackEvent("poradnik_cta_click", { route, placement: "hero" })}
                >
                  Otrzymaj 3 warianty
                  <ArrowRight size={18} aria-hidden="true" />
                </a>
              </>
            )}
          </div>
        </div>
        <aside className="guide-hero-card" aria-label="Najważniejsze wnioski">
          <span>W skrócie</span>
          <ul>
            {page.summary.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </aside>
      </section>

      {isAfter50Landing && <LeadSection defaultProduct={page.defaultProduct ?? "life"} injected={injected} />}

      <section className="section guide-article-section" id="poradnik">
        <div className="guide-article-layout">
          <article className="guide-article">
            {page.sections.map((section, index) => (
              <div className="guide-article-chunk" key={section.heading}>
                <section className="guide-block" id={`guide-${index + 1}`}>
                  <span className="guide-step">{String(index + 1).padStart(2, "0")}</span>
                  <div>
                    <h2>{section.heading}</h2>
                    <p className="guide-lead">{section.lead}</p>
                    {section.body.map((paragraph) => (
                      <p key={paragraph}>{paragraph}</p>
                    ))}
                    {section.bullets && (
                      <ul className="check-list">
                        {section.bullets.map((bullet) => (
                          <li key={bullet}>
                            <Check size={18} aria-hidden="true" />
                            {bullet}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </section>
                {(index === 1 || index === 3) && <GuideVariantCta route={route} placement={`article_${index + 1}`} />}
              </div>
            ))}
          </article>

          <aside className="guide-sidebar">
            <div className="guide-sidebar-card">
              <p className="section-kicker">Na tej stronie</p>
              <nav aria-label="Spis treści poradnika">
                {page.sections.map((section, index) => (
                  <a href={`#guide-${index + 1}`} key={section.heading}>
                    {section.heading}
                  </a>
                ))}
              </nav>
            </div>
            <div className="guide-sidebar-card guide-author-card">
              <p className="section-kicker">Autor</p>
              <AdvisorCard variant="full" />
              <p>
                Treść przygotowana przez doradcę, który odbiera zgłoszenia z tego serwisu.{" "}
                <a href="/o-nas/">Więcej o Martynie</a>
              </p>
            </div>
            <div className="guide-sidebar-card guide-contact-card">
              <ShieldCheck size={24} aria-hidden="true" />
              <h2>Otrzymaj 3 warianty ochrony</h2>
              <p>Tańszy, optymalny i szerszy wariant. Bez kupowania online i bez zobowiązań.</p>
              <a
                className="inline-cta guide-contact-cta"
                href="#formularz-start"
                onClick={() => trackEvent("poradnik_cta_click", { route, placement: "sidebar" })}
              >
                Poproś o warianty
                <ArrowRight size={17} aria-hidden="true" />
              </a>
            </div>
          </aside>
        </div>

        <div className="guide-duo">
          <article>
            <h2>{page.checklistTitle}</h2>
            <ul className="check-list">
              {page.checklist.map((item) => (
                <li key={item}>
                  <Check size={18} aria-hidden="true" />
                  {item}
                </li>
              ))}
            </ul>
          </article>
          <article>
            <h2>{page.mistakesTitle}</h2>
            <ul className="clean-list">
              {page.mistakes.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </article>
        </div>
        {page.sources && (
          <aside className="guide-sources" aria-label="Źródła i weryfikacja">
            <div>
              <p className="section-kicker">Źródła i weryfikacja</p>
              <h2>Na czym oparto ten poradnik?</h2>
              {page.verificationNote && <p>{page.verificationNote}</p>}
            </div>
            <div className="guide-source-list">
              {page.sources.map((source) => (
                <a href={source.href} target="_blank" rel="noreferrer" key={source.href}>
                  <span>
                    <strong>{source.label}</strong>
                    <small>{source.desc}</small>
                  </span>
                  <ArrowRight size={18} aria-hidden="true" />
                </a>
              ))}
            </div>
          </aside>
        )}
      </section>

      <section className="section guide-related-section">
        <div className="section-heading compact">
          <p className="section-kicker">Dalej</p>
          <h2>Powiązane tematy.</h2>
        </div>
        <div className="guide-related-grid">
          {page.related.map((item) => (
            <a className="coverage-card link-card" href={item.href} key={item.href}>
              <span className="icon-badge">
                <ArrowRight size={22} aria-hidden="true" />
              </span>
              <span>
                <strong>{item.label}</strong>
                <small>{item.desc}</small>
              </span>
            </a>
          ))}
        </div>
      </section>

      <FaqSection
        items={page.faqs}
        kicker="FAQ poradnika"
        title="Pytania do tego tematu."
        intro="Krótko wyjaśniamy kwestie, które zwykle pojawiają się przy rozmowie z klientem."
        defaultExpanded
      />
      {!isAfter50Landing && <LeadSection defaultProduct={page.defaultProduct ?? "life"} injected={injected} />}
    </>
  );
}

// Hub poradnika: lista wszystkich artykułów generowana z guidePages — nowy artykuł
// pojawia się tu automatycznie, bez osobnej edycji.
function GuideHubPage({ injected }: { injected: LeadContext | null }) {
  const entries = guideRouteKeys
    .map((key) => ({ key, page: guidePages[key] }))
    .sort((a, b) => (a.page.datePublished < b.page.datePublished ? 1 : -1));

  return (
    <>
      <section className="section guide-hub-intro">
        <div className="section-heading">
          <p className="section-kicker">Poradnik</p>
          <h1>Poradnik ubezpieczeń na życie.</h1>
          <p>
            Praktyczne artykuły o cenach, sumach, zakresie ochrony i procesie zakupu — pisane przez
            doradcę, który na co dzień obsługuje zgłoszenia z tego serwisu. Bez żargonu i bez
            obiecywania cudów.
          </p>
        </div>
        <div className="guide-hub-grid">
          {entries.map(({ key, page }) => (
            <a className="coverage-card link-card guide-hub-card" href={`/${page.slug}/`} key={key}>
              <span>
                <strong>{page.label}</strong>
                <small>{page.copy.length > 150 ? `${page.copy.slice(0, 147)}…` : page.copy}</small>
                <small className="guide-hub-date">
                  {formatPlDate(page.dateModified !== page.datePublished ? page.dateModified : page.datePublished)}
                </small>
              </span>
              <ArrowRight size={20} aria-hidden="true" />
            </a>
          ))}
        </div>
      </section>
      <LeadSection defaultProduct="life" injected={injected} />
    </>
  );
}

const legalPages: Record<
  LegalPageKind,
  {
    kicker: string;
    title: string;
    intro: string;
    sections: Array<{ heading: string; paragraphs?: string[]; items?: string[] }>;
  }
> = {
  privacy: {
    kicker: "RODO",
    title: "Polityka prywatności",
    intro: `Administratorem danych osobowych przekazywanych przez formularz jest ${COMPANY.displayName}, NIP: ${COMPANY.nip}. Dokument opisuje, jakie dane zbieramy, po co je przetwarzamy i jakie prawa przysługują osobie korzystającej z serwisu.`,
    sections: [
      {
        heading: "Administrator i kontakt",
        paragraphs: [
          `Administratorem danych jest ${COMPANY.displayName}, NIP: ${COMPANY.nip}, REGON: ${COMPANY.regon}, operator serwisu Ochro.pl.`,
          `W sprawach danych osobowych można skontaktować się telefonicznie pod numerem ${COMPANY.phoneDisplay}, e-mailowo pod adresem ${COMPANY.email} albo przez formularz kontaktowy dostępny w serwisie.`
        ]
      },
      {
        heading: "Zakres danych",
        items: [
          "Dane identyfikacyjne i kontaktowe: imię i nazwisko, telefon, kod pocztowy.",
          "Dane z treści zgłoszenia: preferowana pora kontaktu, wiadomość, typ produktu wynikający ze strony lub kalkulatora oraz parametry przekazane z kalkulatora.",
          "Dane techniczne i dowodowe: źródło zgłoszenia, data wysłania, zgody, wersja klauzuli, kraj IP i User-Agent przeglądarki.",
          "Dane nieobowiązkowe podane dobrowolnie w polu wiadomości.",
          "Dane analityczne po wyrażeniu zgody: losowy identyfikator sesji, odwiedzone podstrony, zdarzenia kalkulatora i formularza, typ urządzenia, źródło wejścia oraz kraj IP; bez treści formularza i bez numeru telefonu.",
          "Dane marketingowe po wyrażeniu zgody: techniczne identyfikatory pikseli reklamowych, zdarzenia konwersji, parametry kampanii i źródła wejścia; bez treści formularza i bez danych kontaktowych.",
          "Dane z czatu (Wirtualny Asystent): treść rozmowy wpisana przez użytkownika oraz odpowiedzi asystenta, a także skrót (hash) adresu IP i kraj wykorzystywane do ograniczania nadużyć."
        ]
      },
      {
        heading: "Cele i podstawy prawne",
        items: [
          "Obsługa zgłoszenia i kontakt w sprawie ubezpieczenia - art. 6 ust. 1 lit. b lub f RODO.",
          "Udokumentowanie zgód, rozliczalność i obrona przed roszczeniami - art. 6 ust. 1 lit. f RODO.",
          "Kontakt marketingowy dotyczący podobnych produktów, gdy wyrażono osobną zgodę - art. 6 ust. 1 lit. a RODO oraz właściwe przepisy o komunikacji elektronicznej.",
          "Analityka korzystania ze strony, kalkulatorów i formularza po wyrażeniu zgody analitycznej - art. 6 ust. 1 lit. a RODO.",
          "Pomiar skuteczności kampanii i remarketing po wyrażeniu zgody marketingowej - art. 6 ust. 1 lit. a RODO.",
          "Bezpieczeństwo serwisu, ochrona przed spamem i nadużyciami - art. 6 ust. 1 lit. f RODO."
        ]
      },
      {
        heading: "Odbiorcy danych",
        paragraphs: [
          "Dane mogą być powierzane dostawcom hostingu, narzędzi IT, poczty, telefonii, automatyzacji, CRM, analityki i reklamy internetowej (np. Google lub Meta, jeżeli integracje są skonfigurowane i użytkownik wyraził zgodę). W działaniu Wirtualnego Asystenta korzystamy także z dostawcy modelu AI (OpenRouter oraz dostawca modelu, m.in. Google) - szczegóły w sekcji o Wirtualnym Asystencie poniżej. Jeżeli użytkownik zdecyduje się kontynuować rozmowę o konkretnym produkcie, dane mogą zostać przekazane właściwym dystrybutorom lub zakładom ubezpieczeń po podaniu dodatkowych informacji w toku obsługi."
        ]
      },
      {
        heading: "Przekazywanie poza EOG",
        paragraphs: [
          "Jeżeli dostawca technologiczny przetwarza dane poza Europejskim Obszarem Gospodarczym, stosowane są wymagane mechanizmy ochrony, w szczególności standardowe klauzule umowne albo inne podstawy przewidziane przez RODO."
        ]
      },
      {
        heading: "Wirtualny Asystent (czat AI)",
        paragraphs: [
          "W serwisie działa Wirtualny Asystent - czat oparty na sztucznej inteligencji, który odpowiada na pytania o ubezpieczenia. Treść rozmowy (pytania wpisane przez użytkownika oraz odpowiedzi asystenta) jest zapisywana, aby obsłużyć zapytanie, poprawiać jakość odpowiedzi i chronić serwis przed nadużyciami - na podstawie prawnie uzasadnionego interesu administratora (art. 6 ust. 1 lit. f RODO).",
          "Aby wygenerować odpowiedź, treść rozmowy jest przekazywana dostawcy modelu AI (OpenRouter oraz dostawca modelu, m.in. Google), którzy mogą przetwarzać ją poza Europejskim Obszarem Gospodarczym - z zastosowaniem mechanizmów ochrony wymaganych przez RODO, takich jak standardowe klauzule umowne. Do ograniczania nadużyć (limitów zapytań) zapisujemy też skrót (hash) adresu IP oraz kraj.",
          "Prosimy nie podawać w czacie danych szczególnych kategorii (np. szczegółowych informacji o zdrowiu) ani danych osobowych innych osób, jeżeli nie jest to konieczne. Czat ma charakter informacyjny, nie stanowi oferty ani porady i nie podejmuje wobec użytkownika decyzji wywołujących skutki prawne."
        ]
      },
      {
        heading: "Okres przechowywania",
        items: [
          "Zgłoszenia kontaktowe przechowujemy co do zasady do 24 miesięcy od ostatniego kontaktu, chyba że dalsze przechowywanie jest potrzebne do obrony roszczeń lub wykonania obowiązku prawnego.",
          "Zgody marketingowe przechowujemy do czasu ich wycofania albo ustania celu, dla którego zostały zebrane.",
          "Dane analityczne przechowujemy co do zasady do 12 miesięcy, a następnie usuwamy lub agregujemy.",
          "Dane techniczne potrzebne do bezpieczeństwa i rozliczalności są przechowywane przez okres adekwatny do ryzyka i sposobu obsługi zgłoszeń.",
          "Zapisane rozmowy z Wirtualnym Asystentem przechowujemy co do zasady do 12 miesięcy, a następnie są usuwane lub anonimizowane, chyba że są potrzebne do obrony roszczeń."
        ]
      },
      {
        heading: "Prawa użytkownika",
        items: [
          "Prawo dostępu do danych, sprostowania, usunięcia, ograniczenia przetwarzania i przenoszenia danych.",
          "Prawo sprzeciwu wobec przetwarzania opartego na prawnie uzasadnionym interesie.",
          "Prawo wycofania zgody w dowolnym momencie bez wpływu na zgodność wcześniejszego przetwarzania.",
          "Prawo wniesienia skargi do Prezesa Urzędu Ochrony Danych Osobowych."
        ]
      },
      {
        heading: "Dobrowolność i automatyzacja",
        paragraphs: [
          "Podanie danych jest dobrowolne, ale bez danych oznaczonych jako wymagane nie da się obsłużyć zgłoszenia. Serwis nie podejmuje wobec użytkownika zautomatyzowanych decyzji wywołujących skutki prawne."
        ]
      }
    ]
  },
  cookies: {
    kicker: "Cookies",
    title: "Polityka cookies",
    intro:
      "Serwis używa mechanizmów podobnych do cookies, przede wszystkim localStorage, aby zapamiętać preferencje użytkownika. Google tag może działać w trybie zgody z domyślnie zablokowanym przechowywaniem danych, a pełna analityka lub marketing mogą zostać uruchomione dopiero po uzyskaniu odpowiedniej zgody.",
    sections: [
      {
        heading: "Czym są cookies i podobne technologie",
        paragraphs: [
          "Cookies, localStorage i podobne technologie pozwalają zapisać lub odczytać informacje z urządzenia użytkownika. W serwisie wykorzystujemy je w zakresie niezbędnym do działania strony i zapamiętania preferencji."
        ]
      },
      {
        heading: "Aktualnie używane mechanizmy",
        items: [
          "ochro-font-scale - zapamiętuje wybrany rozmiar tekstu.",
          "ochro-theme-choice - zapamiętuje wybrany motyw (jasny lub ciemny).",
          "ochro-cookie-consent - zapamiętuje decyzję dotyczącą preferencji cookies.",
          "ochro-analytics-session - losowy identyfikator sesji używany wyłącznie po zgodzie analitycznej.",
          "Zdarzenia analityczne wysyłane do pierwszostronnego endpointu /api/analytics: wejścia na podstrony, użycie kalkulatora, kliknięcia CTA, kliknięcia telefonu, start formularza i skuteczne wysłanie zgłoszenia; bez treści formularza i bez danych kontaktowych.",
          "Google Analytics 4 może zostać uruchomiony po zgodzie analitycznej, jeżeli serwis ma skonfigurowany identyfikator pomiaru.",
          "Meta Pixel i konwersje Google Ads mogą zostać uruchomione po zgodzie marketingowej, jeżeli serwis ma skonfigurowane identyfikatory reklamowe.",
          "ochro-chat-v1, ochro-chat-seen, ochro-chat-cid - zapamiętują rozmowę z Wirtualnym Asystentem i identyfikator rozmowy w obrębie sesji przeglądarki.",
          "Techniczne mechanizmy hostingu i zabezpieczeń Cloudflare mogą być używane do bezpieczeństwa, dostępności i ochrony przed nadużyciami."
        ]
      },
      {
        heading: "Kategorie zgód",
        items: [
          "Niezbędne - potrzebne do działania serwisu, bezpieczeństwa i zapamiętania podstawowych preferencji.",
          "Analityczne - pomagają mierzyć korzystanie ze strony i są aktywowane dopiero po zgodzie użytkownika.",
          "Marketingowe - pomagają mierzyć skuteczność kampanii i remarketingu; są ładowane wyłącznie po osobnej zgodzie marketingowej i tylko wtedy, gdy identyfikatory reklamowe są skonfigurowane."
        ]
      },
      {
        heading: "Zmiana ustawień",
        paragraphs: [
          "Użytkownik może zmienić decyzję w przeglądarce, usunąć dane strony albo ponownie wyświetlić baner preferencji przyciskiem poniżej."
        ]
      }
    ]
  },
  terms: {
    kicker: "Regulamin",
    title: "Regulamin serwisu",
    intro:
      "Regulamin określa zasady korzystania z Ochro.pl, kalkulatorów ubezpieczeniowych i formularza kontaktowego. Usługi w serwisie są świadczone drogą elektroniczną i mają charakter bezpłatny, informacyjny oraz kontaktowy.",
    sections: [
      {
        heading: "Operator serwisu",
        paragraphs: [
          `Operatorem i usługodawcą serwisu jest ${COMPANY.displayName}, NIP: ${COMPANY.nip}, REGON: ${COMPANY.regon}. Serwis działa pod adresem ochro.pl i służy do prezentowania informacji o ubezpieczeniach oraz przyjmowania zgłoszeń kontaktowych.`,
          `Kontakt w sprawach działania serwisu jest możliwy telefonicznie pod numerem ${COMPANY.phoneDisplay}, e-mailowo pod adresem ${COMPANY.email} albo przez formularz dostępny pod adresem https://ochro.pl/kontakt/. Aktualne dane rejestrowe przedsiębiorcy można zweryfikować w CEIDG po numerze NIP.`
        ]
      },
      {
        heading: "Rodzaje usług elektronicznych",
        items: [
          "Udostępnianie treści edukacyjnych i informacyjnych o ubezpieczeniach.",
          "Udostępnianie kalkulatorów pokazujących orientacyjne składki lub przykładowe budżety.",
          "Obsługa formularza kontaktowego, w którym użytkownik może poprosić o rozmowę lub dokładniejszą kalkulację.",
          "Zapamiętywanie podstawowych preferencji strony, takich jak rozmiar tekstu, motyw i wybór dotyczący cookies."
        ]
      },
      {
        heading: "Zawarcie i zakończenie korzystania",
        paragraphs: [
          "Umowa o świadczenie usług drogą elektroniczną zostaje zawarta z chwilą rozpoczęcia korzystania z wybranej funkcji serwisu, np. wejścia na stronę, użycia kalkulatora albo wysłania formularza. Korzystanie nie wymaga założenia konta ani dokonania płatności.",
          "Użytkownik może w każdej chwili zakończyć korzystanie z usług przez opuszczenie serwisu, zamknięcie karty przeglądarki albo niewysłanie formularza. Wysłane zgłoszenie kontaktowe jest obsługiwane oddzielnie, zgodnie z informacją o przetwarzaniu danych."
        ]
      },
      {
        heading: "Charakter informacji",
        items: [
          "Treści, tabele, kalkulatory i przykłady mają charakter informacyjny i edukacyjny.",
          "Dane na stronie nie stanowią oferty w rozumieniu art. 66 §1 Kodeksu cywilnego, gwarancji ceny ani zapewnienia zawarcia umowy.",
          "Serwis nie zastępuje indywidualnej analizy wymagań i potrzeb klienta, OWU, karty produktu, decyzji ubezpieczyciela ani dokumentów przekazywanych przed zawarciem umowy.",
          "Serwis nie jest oficjalnym serwisem żadnego zakładu ubezpieczeń; nazwy produktów mogą być używane poglądowo do identyfikacji kategorii rynkowych.",
          "Finalne warunki ubezpieczenia zależą od dokumentów ubezpieczyciela, oceny ryzyka, ankiety, możliwych badań, wyłączeń, karencji i decyzji stron."
        ]
      },
      {
        heading: "Ubezpieczenia i dalszy kontakt",
        items: [
          "Kalkulator nie zawiera umowy ubezpieczenia i nie uruchamia ochrony ubezpieczeniowej.",
          "Jeżeli rozmowa przejdzie w etap dystrybucji ubezpieczenia, klient przed zawarciem umowy otrzyma wymagane informacje, w szczególności o statusie dystrybutora, rejestrze pośredników, zakładzie ubezpieczeń, wynagrodzeniu, procedurze reklamacyjnej oraz dokumentach produktu.",
          "Przed podpisaniem umowy należy zapoznać się z OWU, dokumentem zawierającym informacje o produkcie ubezpieczeniowym, zakresem ochrony, wyłączeniami odpowiedzialności, karencjami, składką i zasadami wypłaty świadczeń.",
          "Ochrona ubezpieczeniowa zaczyna się wyłącznie na zasadach określonych w dokumentach ubezpieczyciela, po spełnieniu warunków wskazanych w tych dokumentach."
        ]
      },
      {
        heading: "Formularz kontaktowy",
        items: [
          "Wysłanie formularza jest prośbą o kontakt i nie zobowiązuje użytkownika do zakupu produktu.",
          "Użytkownik powinien podawać dane prawdziwe, aktualne i własne albo mieć podstawę do przekazania danych innej osoby.",
          "W polu wiadomości nie należy podawać danych szczególnych kategorii, w tym szczegółowych danych medycznych, jeżeli nie są potrzebne do pierwszego kontaktu.",
          "Zgody marketingowe są dobrowolne i mogą zostać wycofane w dowolnym momencie."
        ]
      },
      {
        heading: "Wymagania techniczne",
        items: [
          "Aktualna przeglądarka internetowa z włączoną obsługą JavaScript.",
          "Dostęp do internetu oraz możliwość połączenia z domeną ochro.pl.",
          "Aktywne podstawowe mechanizmy pamięci przeglądarki, jeżeli użytkownik chce zapisać preferencje tekstu lub cookies.",
          "Możliwość odbierania połączeń telefonicznych, jeżeli użytkownik prosi o kontakt zwrotny."
        ]
      },
      {
        heading: "Ryzyka techniczne",
        items: [
          "Korzystanie z internetu wiąże się z typowymi ryzykami, takimi jak przerwy w transmisji, błędy przeglądarki, złośliwe oprogramowanie, phishing albo nieuprawniony dostęp do urządzenia użytkownika.",
          "Użytkownik powinien korzystać z aktualnego oprogramowania, chronić swoje urządzenie i nie przekazywać danych przez podejrzane linki lub wiadomości podszywające się pod serwis.",
          "Serwis może czasowo nie działać z powodu aktualizacji, prac technicznych, awarii dostawców hostingu albo zdarzeń niezależnych od operatora."
        ]
      },
      {
        heading: "Zakazane działania",
        items: [
          "Przekazywanie danych nieprawdziwych lub danych osób trzecich bez podstawy prawnej.",
          "Używanie formularza do spamu, automatycznych nadużyć, testów obciążeniowych lub prób obejścia zabezpieczeń.",
          "Dostarczanie treści bezprawnych, naruszających prawa osób trzecich albo dobre obyczaje.",
          "Kopiowanie elementów serwisu w zakresie naruszającym prawa autorskie, znaki towarowe lub zasady uczciwej konkurencji."
        ]
      },
      {
        heading: "Reklamacje i kontakt",
        items: [
          `Reklamacje dotyczące działania serwisu można zgłaszać telefonicznie pod numerem ${COMPANY.phoneDisplay}, e-mailowo pod adresem ${COMPANY.email} albo przez formularz kontaktowy na stronie https://ochro.pl/kontakt/.`,
          "Zgłoszenie powinno zawierać dane umożliwiające odpowiedź, opis problemu, adres strony, na której wystąpił problem, oraz oczekiwany sposób rozwiązania sprawy.",
          "Odpowiedź jest udzielana bez zbędnej zwłoki, co do zasady w terminie do 14 dni od otrzymania kompletnego zgłoszenia. Jeżeli sprawa wymaga dłuższej analizy, użytkownik otrzyma informację o przewidywanym terminie odpowiedzi.",
          "Reklamacje dotyczące konkretnej umowy ubezpieczenia, decyzji ubezpieczyciela lub świadczenia są rozpatrywane według zasad wskazanych w dokumentach właściwego zakładu ubezpieczeń."
        ]
      },
      {
        heading: "Dane osobowe i cookies",
        paragraphs: [
          "Zasady przetwarzania danych osobowych opisuje Polityka prywatności dostępna pod adresem https://ochro.pl/polityka-prywatnosci/.",
          "Zasady używania cookies, localStorage i podobnych technologii opisuje Polityka cookies dostępna pod adresem https://ochro.pl/polityka-cookies/."
        ]
      },
      {
        heading: "Odpowiedzialność i prawa użytkownika",
        items: [
          "Operator dokłada staranności, aby treści były aktualne i zrozumiałe, ale nie gwarantuje nieprzerwanego działania serwisu ani zgodności przykładowych wyliczeń z finalną decyzją ubezpieczyciela.",
          "Użytkownik powinien podejmować decyzję o ubezpieczeniu dopiero po zapoznaniu się z dokumentami produktu i po wyjaśnieniu wątpliwości z doradcą lub ubezpieczycielem.",
          "Regulamin nie wyłącza ani nie ogranicza praw konsumenta wynikających z bezwzględnie obowiązujących przepisów prawa."
        ]
      },
      {
        heading: "Zmiany regulaminu",
        paragraphs: [
          "Regulamin może być aktualizowany, jeżeli zmienią się funkcje serwisu, przepisy, sposób obsługi zgłoszeń lub dostawcy technologiczni. Aktualna wersja jest publikowana na tej stronie."
        ]
      }
    ]
  }
};

function LegalDocumentPage({ kind }: { kind: LegalPageKind }) {
  const page = legalPages[kind];

  return (
    <section className="section legal-page">
      <div className="legal-hero">
        <p className="section-kicker">{page.kicker}</p>
        <h1>{page.title}</h1>
        <p>{page.intro}</p>
        <span>Ostatnia aktualizacja: {LEGAL_UPDATED_AT}</span>
      </div>
      <div className="legal-document">
        {page.sections.map((section) => (
          <section key={section.heading} className="legal-document-section">
            <h2>{section.heading}</h2>
            {section.paragraphs?.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
            {section.items && (
              <ul>
                {section.items.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            )}
          </section>
        ))}
        {kind === "cookies" && (
          <button
            className="secondary-button"
            type="button"
            onClick={() => {
              window.localStorage.removeItem(COOKIE_STORAGE_KEY);
              window.location.reload();
            }}
          >
            <Settings size={18} aria-hidden="true" />
            Zmień wybór cookies
          </button>
        )}
      </div>
    </section>
  );
}

function CookieBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    setVisible(!window.localStorage.getItem(COOKIE_STORAGE_KEY));
    syncTrackingIntegrations();
  }, []);

  const savePreference = ({ analytics, marketing }: { analytics: boolean; marketing: boolean }) => {
    window.localStorage.setItem(
      COOKIE_STORAGE_KEY,
      JSON.stringify({
        necessary: true,
        analytics,
        marketing,
        updatedAt: new Date().toISOString(),
        version: PRIVACY_VERSION
      })
    );
    // Powiadom czatbota, że może się pokazać (chowa się, dopóki baner cookies jest widoczny).
    window.dispatchEvent(new Event("ochro-cookie-consent"));
    syncTrackingIntegrations();
    if (analytics || marketing) {
      trackEvent("page_view");
    }
    setVisible(false);
  };

  if (!visible) {
    return null;
  }

  return (
    <div className="cookie-banner" role="dialog" aria-label="Ustawienia cookies">
      <div>
        <strong>Prywatność i cookies</strong>
        <p>
          Używamy niezbędnych ustawień strony. Google tag działa w trybie zgody, a analitykę i piksele reklamowe uruchomimy dopiero po zgodzie.
          Szczegóły są w{" "}
          <a href="/polityka-cookies/">polityce cookies</a>.
        </p>
      </div>
      <div className="cookie-actions">
        <button
          className="secondary-button"
          type="button"
          onClick={() => savePreference({ analytics: false, marketing: false })}
        >
          <X size={18} aria-hidden="true" />
          Tylko niezbędne
        </button>
        <button
          className="secondary-button"
          type="button"
          onClick={() => savePreference({ analytics: true, marketing: false })}
        >
          <Check size={18} aria-hidden="true" />
          Tylko analityczne
        </button>
        <button
          className="submit-button"
          type="button"
          onClick={() => savePreference({ analytics: true, marketing: true })}
        >
          <Check size={18} aria-hidden="true" />
          Akceptuj wszystkie
        </button>
      </div>
    </div>
  );
}

function Footer() {
  const year = new Date().getFullYear();
  const footerColumns = [
    {
      title: "Kalkulator i ceny",
      links: [
        { label: "Kalkulator ubezpieczenia", href: "/kalkulator-ubezpieczenia-na-zycie/" },
        { label: "Cennik ubezpieczenia na życie", href: "/ubezpieczenie-na-zycie-cennik/" },
        { label: "Przykładowe kalkulacje", href: "/ubezpieczenie-na-zycie-cennik/#przyklady" },
        { label: "Co wpływa na składkę", href: "/ubezpieczenie-na-zycie-cennik/#metodologia" },
        { label: "FAQ", href: "/faq/" }
      ]
    },
    {
      title: "Ubezpieczenia",
      links: [
        { label: "Oferta ubezpieczeń", href: "/ubezpieczenie-na-zycie/#oferta" },
        { label: "Ochrona rodziny", href: "/kalkulator-ubezpieczenia-na-zycie/" },
        { label: "Polisa pod kredyt", href: "/kalkulator-ubezpieczenia-na-zycie-pod-kredyt/" },
        { label: "Życie i dożycie", href: "/kalkulator-ubezpieczenia-na-zycie-i-dozycie/" },
        { label: "Umowy dodatkowe", href: "/umowy-dodatkowe/" },
        { label: "Ubezpieczenie zdrowotne", href: "/kalkulator-ubezpieczenia-zdrowotnego/" },
        { label: "Ubezpieczenie na całe życie", href: "/kalkulator-ubezpieczenia-na-cale-zycie/" }
      ]
    },
    {
      title: "Warto wiedzieć",
      links: [
        { label: "Polisa po 50-tce", href: "/poradnik/ubezpieczenie-na-zycie-po-50/" },
        { label: "Jak wybrać polisę", href: "/poradnik/jak-wybrac-polise/" },
        { label: "Jak działa ochrona", href: "/poradnik/jak-dziala-ochrona/" },
        { label: "Polisa pod kredyt", href: "/poradnik/ubezpieczenie-na-zycie-pod-kredyt-hipoteczny/" },
        { label: "Ubezpieczenia dodatkowe", href: "/poradnik/ubezpieczenia-dodatkowe/" },
        { label: "Oszczędzanie na emeryturę", href: "/poradnik/oszczedzanie-na-emeryture/" },
        { label: "Proces zakupu", href: "/poradnik/proces-zakupu-polisy/" }
      ]
    },
    {
      title: "Serwis i kontakt",
      links: [
        { label: "O nas", href: "/o-nas/" },
        { label: "Kontakt z doradcą", href: "/kontakt/" },
        { label: "Regulamin serwisu", href: "/regulamin/" },
        { label: "Polityka prywatności", href: "/polityka-prywatnosci/" },
        { label: "Polityka cookies", href: "/polityka-cookies/" }
      ]
    }
  ];

  return (
    <footer className="footer">
      <div className="footer-top">
        <div className="footer-brand-panel">
          <span className="footer-brand-row">
            <img className="footer-logo" src="/ochro-logo-white.svg?v=24" alt="ochro.pl" />
          </span>
          <p>
            Kalkulator i materiały edukacyjne o ubezpieczeniach na życie, zdrowie, pod kredyt i na
            przyszłość. Pomagamy uporządkować potrzeby przed rozmową o finalnej ofercie.
          </p>
          <div className="footer-trust-row" aria-label="Najważniejsze zasady serwisu">
            <span>
              <ShieldCheck size={16} aria-hidden="true" />
              Orientacyjne kalkulacje
            </span>
            <span>
              <LockKeyhole size={16} aria-hidden="true" />
              Dane tylko do kontaktu
            </span>
            <span>
              <FileText size={16} aria-hidden="true" />
              Informacja, nie oferta
            </span>
          </div>
        </div>

        <address className="footer-contact-card">
          <p className="footer-kicker">Kontakt</p>
          <a className="footer-phone" href={COMPANY.phoneHref} onClick={() => trackPhoneClick("footer")}>
            <Phone size={20} aria-hidden="true" />
            {COMPANY.phoneDisplay}
          </a>
          <a className="footer-mail" href={COMPANY.emailHref}>
            <Mail size={18} aria-hidden="true" />
            {COMPANY.email}
          </a>
          <span>Oddzwonimy po wysłaniu formularza.</span>
          <span>Poniedziałek - piątek, godz. 9.00-18.00</span>
          <div className="footer-action-row">
            <a className="footer-button footer-button-primary" href="/kontakt/#formularz-start">
              Umów kontakt
              <ArrowRight size={18} aria-hidden="true" />
            </a>
            <a className="footer-button footer-button-secondary" href="/kalkulator-ubezpieczenia-na-zycie/">
              <Calculator size={18} aria-hidden="true" />
              Kalkulator
            </a>
          </div>
        </address>
      </div>

      <nav className="footer-nav" aria-label="Linki w stopce">
        {footerColumns.map((column) => (
          <section className="footer-column" key={column.title}>
            <h2>{column.title}</h2>
            <ul>
              {column.links.map((link) => (
                <li key={link.href + link.label}>
                  <a href={link.href}>{link.label}</a>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </nav>

      <div className="footer-bottom">
        <div className="footer-operator">
          <span className="footer-operator-label">Operator serwisu</span>
          <span>
            {COMPANY.displayName}, NIP: {COMPANY.nip}, REGON: {COMPANY.regon}.
            <br />
            Serwis nie jest oficjalnym serwisem żadnego zakładu ubezpieczeń.
          </span>
          <div className="footer-disclaimer-copy">
            <p>
              Serwis ma charakter informacyjny i umożliwia wysłanie zgłoszenia kontaktowego. Dane, ceny,
              przykłady i kalkulacje nie są ofertą w rozumieniu art. 66 §1 Kodeksu cywilnego.
            </p>
            <p>
              Nie zawierasz umowy ubezpieczenia przez sam formularz. Zgłoszenie oznacza prośbę o kontakt i
              przygotowanie rozmowy o możliwej ochronie.
            </p>
            <p>
              Finalna propozycja wymaga ustalenia wymagań i potrzeb klienta, oceny ryzyka, aktualnych dokumentów
              ubezpieczyciela oraz akceptacji warunków przez strony.
            </p>
          </div>
        </div>
        <div className="footer-legal-links">
          <a href="/o-nas/">O nas</a>
          <a href="/regulamin/">Regulamin</a>
          <a href="/polityka-prywatnosci/">Prywatność</a>
          <a href="/polityka-cookies/">Cookies</a>
        </div>
      </div>

      <p className="footer-copyright">Copyright © {year} Ochro.pl. Wszelkie prawa zastrzeżone.</p>
    </footer>
  );
}

function HomePage({
  injected,
  setInjected
}: {
  injected: LeadContext | null;
  setInjected: (ctx: LeadContext) => void;
}) {
  return (
    <>
      <ProductCalculator
        config={PRODUCTS.ochronne}
        headingLevel="h1"
        isPrimary
        className="home-calculator"
      />
      <CalculatorHub
        activeId="ochronne"
        showActive={false}
        kicker="Dopasuj zakres"
        heading="Policz inną ochronę"
        intro="Wybierz wariant ochrony dopasowany do innej potrzeby i od razu sprawdź orientacyjną składkę."
      />
      <LeadSection defaultProduct="life" injected={injected} />
      <InsuranceEducationSection />
      <OfferOverviewSection />
      <QuoteExamplesSection />
      <PricingTables />
      <BuyingProcessSection />
      <TrustSection />
      <FaqSection />
    </>
  );
}

function CalcRoute({
  config,
  injected,
  setInjected,
  paidLanding = false
}: {
  config: (typeof PRODUCTS)[ProductId];
  injected: LeadContext | null;
  setInjected: (ctx: LeadContext) => void;
  paidLanding?: boolean;
}) {
  const faqItems = uniqueFaqs([...config.faqs, ...faqs]);

  if (paidLanding) {
    return (
      <>
        <ProductCalculator config={config} headingLevel="h1" isPrimary />
        <TrustSection />
        <LeadSection defaultProduct={config.leadProduct} injected={injected} />
        <FaqSection items={faqItems} />
      </>
    );
  }

  return (
    <>
      <ProductCalculator
        config={config}
        headingLevel="h1"
        isPrimary
      />
      <CalculatorHub
        activeId={config.id}
        showActive={false}
        kicker="Dopasuj zakres"
        heading="Policz inną ochronę"
        intro="Wybierz wariant ochrony dopasowany do innej potrzeby i od razu sprawdź orientacyjną składkę."
      />
      <ProductDeepDive productId={config.id} />
      {config.id === "kredyt" && <MortgageIntentSection />}
      {config.id === "ochronne" && (
        <>
          <Methodology />
          <PricingTables />
        </>
      )}
      <BuyingProcessSection />
      <LeadSection defaultProduct={config.leadProduct} injected={injected} />
      <FaqSection items={faqItems} />
    </>
  );
}

function FaqPage() {
  return (
    <>
      <section className="section faq-page-intro">
        <div className="section-heading">
          <p className="section-kicker">Centrum FAQ</p>
          <h1>Najczęstsze pytania o ubezpieczenia i kalkulatory.</h1>
          <p>
            Zebraliśmy pytania z kalkulatorów, strony o cenach i rozmów o ochronie. Są tu też bardziej
            zaawansowane tematy: ankieta medyczna, cesja na bank, wyłączenia, OWU, limity świadczeń i
            decyzja ubezpieczyciela.
          </p>
        </div>
        <div className="faq-topic-grid" aria-label="Zakres pytań FAQ">
          <article>
            <span>Kalkulatory</span>
            <strong>Wynik orientacyjny, składka i parametry</strong>
          </article>
          <article>
            <span>Zakres ochrony</span>
            <strong>Suma, dodatki, karencje i wyłączenia</strong>
          </article>
          <article>
            <span>Decyzja ubezpieczyciela</span>
            <strong>Ankieta, dokumenty, zwyżki i cesja</strong>
          </article>
        </div>
      </section>
      <FaqSection
        items={getFaqPageItems()}
        kicker="Pytania i odpowiedzi"
        title="Pełne FAQ."
        intro="Otwórz interesujące pytanie i sprawdź, co warto wiedzieć przed rozmową o finalnej propozycji."
        defaultExpanded
      />
    </>
  );
}

function ContactPage({ injected }: { injected: LeadContext | null }) {
  return (
    <>
      <section className="section contact-page-intro" id="kontakt">
        <div className="section-heading">
          <p className="section-kicker">Kontakt</p>
          <h1>Porozmawiajmy o dokładniejszej kalkulacji.</h1>
          <p>
            Wyślij krótki formularz albo zadzwoń. Zgłoszenie oznacza prośbę o kontakt, a nie zawarcie
            umowy ubezpieczenia.
          </p>
        </div>
        <div className="contact-page-grid">
          <a
            className="contact-page-card contact-page-card-primary"
            href={COMPANY.phoneHref}
            onClick={() => trackPhoneClick("contact_page")}
          >
            <Phone size={22} aria-hidden="true" />
            <span>Telefon</span>
            <strong>{COMPANY.phoneDisplay}</strong>
            <p>Poniedziałek - piątek, godz. 9.00-18.00</p>
          </a>
          <a className="contact-page-card" href={COMPANY.emailHref}>
            <Mail size={22} aria-hidden="true" />
            <span>E-mail</span>
            <strong>{COMPANY.email}</strong>
            <p>Adres w domenie serwisu do spraw strony, zgłoszeń i danych osobowych.</p>
          </a>
          <article className="contact-page-card">
            <BadgeCheck size={22} aria-hidden="true" />
            <span>Operator serwisu</span>
            <strong>{COMPANY.displayName}</strong>
            <p>NIP: {COMPANY.nip}, REGON: {COMPANY.regon}.</p>
          </article>
        </div>
      </section>
      <LeadSection defaultProduct="life" injected={injected} />
    </>
  );
}

function AboutPage() {
  const facts = [
    { label: "Operator serwisu", value: COMPANY.displayName },
    { label: "NIP", value: COMPANY.nip },
    { label: "REGON", value: COMPANY.regon },
    { label: "Telefon", value: COMPANY.phoneDisplay },
    { label: "E-mail", value: COMPANY.email }
  ];

  const workflow = [
    {
      title: "Kalkulator porządkuje punkt startu",
      copy: "Wyniki pokazują orientacyjny budżet dla wybranych parametrów. Nie są ofertą i nie uruchamiają ochrony ubezpieczeniowej."
    },
    {
      title: "Rozmowa doprecyzowuje potrzeby",
      copy: "Przed finalną propozycją trzeba ustalić cel polisy, sumę, okres, dodatki, stan zdrowia, zobowiązania i budżet klienta."
    },
    {
      title: "Dokumenty ubezpieczyciela są decydujące",
      copy: "O zakresie ochrony, wyłączeniach, karencjach, składce i rozpoczęciu odpowiedzialności decydują OWU, KID, wniosek i decyzja ubezpieczyciela."
    }
  ];

  const reporting = [
    "koszt kampanii, kliknięcia, wyświetlenia, CTR i średni CPC",
    "liczbę wejść na stronę, użycie kalkulatorów oraz wysłane formularze",
    "porównanie skuteczności kampanii z realnymi zgłoszeniami z ochro.pl"
  ];

  return (
    <>
      <section
        className="about-hero"
        style={{
          backgroundImage: `linear-gradient(90deg, rgba(32, 41, 57, 0.94), rgba(32, 41, 57, 0.72), rgba(32, 41, 57, 0.26)), url(${offerImage})`
        }}
      >
        <div className="about-hero-copy">
          <p className="eyebrow">
            <BadgeCheck size={18} aria-hidden="true" />
            O nas
          </p>
          <h1>Ochro.pl to serwis doradcy ubezpieczeniowego.</h1>
          <p>
            Pomagamy klientom wstępnie oszacować budżet ubezpieczenia, uporządkować potrzeby i
            przygotować się do rozmowy o ochronie życia, zdrowia, kredytu i kapitału na przyszłość.
          </p>
          <div className="hero-actions">
            <a className="primary-button" href="/kontakt/#formularz-start">
              Skontaktuj się
              <ArrowRight size={19} aria-hidden="true" />
            </a>
            <a className="ghost-button" href="/regulamin/">
              Zasady serwisu
              <FileText size={18} aria-hidden="true" />
            </a>
          </div>
        </div>
        <aside className="about-portrait-card" aria-label="Doradca prowadzący serwis">
          <img src={aboutPortraitImage} alt="Martyna Mostek, doradca ubezpieczeniowy" />
          <span>Doradca ubezpieczeniowy</span>
          <strong>Martyna Mostek</strong>
          <small>Operator serwisu Ochro.pl</small>
        </aside>
      </section>

      <section className="section about-section">
        <div className="about-layout">
          <article className="about-main">
            <p className="section-kicker">Transparentnie</p>
            <h2>Kto prowadzi serwis?</h2>
            <p>
              Operatorem Ochro.pl jest {COMPANY.displayName}. Serwis służy do edukacji, wstępnych
              kalkulacji i przyjmowania zgłoszeń kontaktowych dotyczących ubezpieczeń indywidualnych.
            </p>
            <p>
              Ochro.pl nie jest oficjalnym serwisem żadnego zakładu ubezpieczeń i nie zawiera umów
              online. Formularz oznacza prośbę o kontakt, a finalna propozycja wymaga analizy
              wymagań i potrzeb klienta oraz dokumentów ubezpieczyciela.
            </p>
            <div className="about-workflow">
              {workflow.map((item, index) => (
                <article key={item.title}>
                  <span>{index + 1}</span>
                  <h3>{item.title}</h3>
                  <p>{item.copy}</p>
                </article>
              ))}
            </div>
          </article>

          <aside className="about-data-card" aria-label="Dane firmy">
            <h2>Dane firmy</h2>
            <dl>
              {facts.map((item) => (
                <div key={item.label}>
                  <dt>{item.label}</dt>
                  <dd>{item.value}</dd>
                </div>
              ))}
            </dl>
            <div className="about-contact-actions">
              <a href={COMPANY.phoneHref} onClick={() => trackPhoneClick("about_page")}>
                <Phone size={18} aria-hidden="true" />
                Zadzwoń
              </a>
              <a href={COMPANY.emailHref}>
                <Mail size={18} aria-hidden="true" />
                Napisz e-mail
              </a>
            </div>
            <p>
              Dane rejestrowe przedsiębiorcy można zweryfikować w CEIDG po numerze NIP.
            </p>
          </aside>
        </div>
      </section>

      <section className="section about-compliance-section">
        <div className="section-heading compact">
          <p className="section-kicker">Jak działamy</p>
          <h2>Serwis, dane i raportowanie.</h2>
          <p>
            Strona ma prosty model: edukacja, kalkulator i kontakt z doradcą. Wewnętrzny panel
            pomaga obsługiwać zgłoszenia oraz mierzyć skuteczność kampanii, ale nie jest narzędziem
            udostępnianym klientom ani zewnętrzną platformą reklamową.
          </p>
        </div>
        <div className="about-card-grid">
          <article>
            <ShieldCheck size={24} aria-hidden="true" />
            <h3>Ochrona danych</h3>
            <p>
              Dane z formularza są wykorzystywane do obsługi zgłoszenia i kontaktu w sprawie
              ubezpieczenia. Szczegóły opisuje polityka prywatności.
            </p>
            <a className="inline-cta" href="/polityka-prywatnosci/">
              Polityka prywatności
              <ArrowRight size={17} aria-hidden="true" />
            </a>
          </article>
          <article>
            <Calculator size={24} aria-hidden="true" />
            <h3>Kalkulatory</h3>
            <p>
              Kalkulatory pokazują orientacyjne wyniki na podstawie wybranych parametrów. Ostateczna
              składka zależy od oceny ryzyka i dokumentów produktu.
            </p>
            <a className="inline-cta" href="/kalkulator-ubezpieczenia-na-zycie/">
              Przejdź do kalkulatora
              <ArrowRight size={17} aria-hidden="true" />
            </a>
          </article>
          <article>
            <FileText size={24} aria-hidden="true" />
            <h3>Raportowanie reklam</h3>
            <p>
              Wewnętrzne raporty reklamowe służą tylko do analizy własnych kampanii Ochro.pl. Nie
              tworzymy, nie edytujemy i nie sprzedajemy kampanii klientom przez ten serwis.
            </p>
            <ul>
              {reporting.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </article>
        </div>
      </section>
    </>
  );
}

function PricingPage({
  injected,
  setInjected
}: {
  injected: LeadContext | null;
  setInjected: (ctx: LeadContext) => void;
}) {
  return (
    <>
      <Hero
        kicker="Ubezpieczenie na życie cennik"
        title="Ubezpieczenie na życie cennik 2026"
        copy="Zobacz orientacyjne ceny polis według wieku i sumy ubezpieczenia. Potem użyj kalkulatora, żeby sprawdzić budżet bliższy Twojej sytuacji."
        primaryHref="#cennik"
        primaryText="Zobacz tabele"
        secondaryHref="/kalkulator-ubezpieczenia-na-zycie/"
        secondaryText="Użyj kalkulatora"
      />
      <PricingTables />
      <QuoteExamplesSection />
      <Methodology />
      <AddonsSection />
      <CalculatorHub heading="Policz swoją składkę" intro="Wybierz kalkulator dopasowany do Twojej sytuacji." />
      <FaqSection />
      <LeadSection defaultProduct="life" injected={injected} />
    </>
  );
}

function LifePage({
  injected,
  setInjected
}: {
  injected: LeadContext | null;
  setInjected: (ctx: LeadContext) => void;
}) {
  return (
    <>
      <Hero
        kicker="Przewodnik"
        title="Ubezpieczenie na życie bez chaosu"
        copy="Zrozum, jak dobrać sumę, zakres i budżet. Strona wspiera kalkulatory i cennik, ale przede wszystkim pomaga podjąć spokojniejszą decyzję."
        primaryHref="/kalkulator-ubezpieczenia-na-zycie/"
        primaryText="Przejdź do kalkulatora"
        secondaryHref="/ubezpieczenie-na-zycie-cennik/"
        secondaryText="Sprawdź cennik"
      />
      <LifeGuide />
      <InsuranceEducationSection />
      <OfferOverviewSection />
      <AddonsSection />
      <CalculatorHub heading="Policz swoją składkę" intro="Wybierz kalkulator dopasowany do Twojej sytuacji." />
      <QuoteExamplesSection />
      <PricingTables />
      <BuyingProcessSection />
      <TrustSection />
      <FaqSection />
      <LeadSection defaultProduct="life" injected={injected} />
    </>
  );
}

function App({ initialRoute }: { initialRoute?: RouteKey } = {}) {
  const [route] = useState<RouteKey>(() => initialRoute ?? getRoute());
  const [injected, setInjected] = useState<LeadContext | null>(null);
  const [paidLanding, setPaidLanding] = useState(false);

  useEffect(() => {
    if (isPaidLandingVisit()) {
      setPaidLanding(true);
    }
  }, []);

  useEffect(() => {
    const getHashTarget = () => {
      const hash = window.location.hash.slice(1);
      if (!hash) {
        return null;
      }

      try {
        return document.getElementById(decodeURIComponent(hash));
      } catch {
        return null;
      }
    };

    const scrollToCurrentHash = (force = false) => {
      const target = getHashTarget();
      if (target) {
        if (!force && target.getBoundingClientRect().top < -16) {
          return;
        }
        target.scrollIntoView({ block: "start" });
      }
    };

    const frame = window.location.hash
      ? window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => scrollToCurrentHash(false));
        })
      : 0;

    const onHashChange = () => {
      if (!window.location.hash) {
        window.scrollTo({ top: 0, left: 0 });
        return;
      }
      scrollToCurrentHash(true);
    };

    window.addEventListener("hashchange", onHashChange);

    return () => {
      if (frame) {
        window.cancelAnimationFrame(frame);
      }
      window.removeEventListener("hashchange", onHashChange);
    };
  }, [route]);

  useEffect(() => {
    const meta = getRouteMeta(route);
    document.title = meta.title;
    upsertMeta("description", meta.description);
    upsertCanonical(meta.canonical);
    setRobotsMeta(route === "admin" ? "noindex,nofollow" : null);
    const structuredData = getRouteStructuredData(route);
    const faqJsonLd = structuredData.find((entry) => entry.id === "ochro-faq-schema");
    if (faqJsonLd) {
      upsertJsonLd(faqJsonLd.id, faqJsonLd.data);
    } else {
      removeJsonLd("ochro-faq-schema");
    }
    structuredData
      .filter((entry) => entry.id !== "ochro-faq-schema")
      .forEach((entry) => upsertJsonLd(entry.id, entry.data));
    trackEvent("page_view");
  }, [route]);

  // Panel /admin/ to samodzielna aplikacja CRM — bez marketingowej powłoki serwisu.
  if (route === "admin") {
    return <AdminApp />;
  }

  let content;
  if (route === "home") {
    content = <HomePage injected={injected} setInjected={setInjected} />;
  } else if (route === "pricing") {
    content = <PricingPage injected={injected} setInjected={setInjected} />;
  } else if (route === "life") {
    content = <LifePage injected={injected} setInjected={setInjected} />;
  } else if (route === "addons") {
    content = <AdditionalContractsPage injected={injected} />;
  } else if (route === "faq") {
    content = <FaqPage />;
  } else if (route === "contact") {
    content = <ContactPage injected={injected} />;
  } else if (route === "about") {
    content = <AboutPage />;
  } else if (route === "guideHub") {
    content = <GuideHubPage injected={injected} />;
  } else if (isGuideRoute(route)) {
    content = <GuideArticlePage route={route} injected={injected} />;
  } else if (route === "privacy") {
    content = <LegalDocumentPage kind="privacy" />;
  } else if (route === "cookies") {
    content = <LegalDocumentPage kind="cookies" />;
  } else if (route === "terms") {
    content = <LegalDocumentPage kind="terms" />;
  } else {
    content = <CalcRoute config={PRODUCTS[route]} injected={injected} setInjected={setInjected} paidLanding={paidLanding} />;
  }

  return (
    <div className="site-shell">
      <Header />
      <main>{content}</main>
      <Footer />
      <CookieBanner />
      <ChatWidget />
    </div>
  );
}

export default App;
